import { SvelteMap } from "svelte/reactivity";
import { LONG_LIVED_SETTLE_MS, outlivesCleanTurnEnd } from "./long-lived-tool-call.js";
import { newerWindows } from "./newer-window.svelte.js";
import { TrackedStreamMap } from "./tracked-stream-map.svelte.js";
import { rewriteLiveOutputEntries } from "./live-output-rewrite.js";
import { toast } from "svelte-sonner";
import { stripDateStamp } from "./strip-date-stamp.js";
import { forwardedTurnCards } from "./forwarded-turn-cards.svelte.js";
import type { AgentCommandInput } from "./agent-command-input.js";
import { isHiddenReliveRow, markLegacyRelivePairs } from "./relive-rows.js";
import {
  finishedLineDrawn,
  finishedTaskIdOf,
  isTaskNotificationItem,
  toolCardIndexOf,
} from "./task-notification-prompt.js";
import { asDrawnPromptRow } from "./harness-prompt-rows.js";
import { refusedHumanRows, survivesLedgerFloor } from "./ledger-floor.js";
import { reportKpi, reportUiFailure } from "../../report-failure.js";
import { reportNoticeWithoutText } from "./notice-without-text.js";
import { isSessionStreamKey } from "../../session-progress.js";
import type { SharedHeldPrompt } from "../../ws-client-types.js";
import {
  reconnectBackoffMs,
  type CyboTransferDetail,
  type NoticeDetail,
  type TaskAlertCard,
} from "../../core/client.js";
import { authState, isArchivedForMe, workspaceState } from "../../core/state.svelte.js";
import { DaemonDrops } from "./daemon-drops.js";
import { MaskedDaemons } from "./daemon-masked-rows.svelte.js";
import {
  hiddenElsewhere,
  readHiddenSplit,
  type HiddenUnreadByWorkspace,
} from "../../core/hidden-unread.js";
import { openOwned, sealOwned, viewerCacheKeySegment } from "../../cache/local-owner.js";
import { cacheEnabled } from "../../cache/enabled.js";
import {
  agentTimelineKey,
  sessionWorkKey,
  cyboDmLedgerKey,
  getPage,
  putPage,
  deletePage,
  reconcileLedgerWindow,
} from "../../cache/pages.js";
import type { CachedPage } from "../../cache/idb.js";
import { isDeltaGap, markScopeSynced, planOpen, scopeSyncedAt } from "../../cache/delta.js";
import { deleteAgentPromptOutbox, getAgentPromptOutbox } from "../../cache/agent-prompt-outbox.js";
import { withTimeout } from "../../components/session/with-timeout.js";
import { curateLastActivity, type CuratedActivity } from "./activity-curator.js";
import { renderedStreamEntries } from "./rendered-stream-entries.js";
import { JOINS_RUNNING_TURN } from "./held-prompt.js";
import { subagentIdOfItem, subagentLabelOfItem } from "./subagent-launch.js";
import { echoIsAttachmentSuperset } from "./user-message-attachments.js";
import { PageHoles, type PageHole } from "./page-holes.svelte.js";
import {
  HOLE_KEEP_MAX_ROWS,
  PREVIEW_HOLE_KEEP_MAX_ROWS,
  committedRowsMissingFromPage,
  deliberateDrop,
  pageHoleIsServerCut,
  rowsBelowPageFloor,
  type PageCutMarks,
} from "./page-floor.js";
import { unmergedOverHeld } from "./coalesced-rows.js";
import {
  echoIsDistinct,
  foldedProviderIds,
  seqsFoldedInto,
  providerIdsAfter,
  sameAuthor,
  settledTextOf,
  settledWordsOf,
  wordsSettleThisSend,
} from "./prompt-echo.js";
import { evaluateTimelineSnapshot } from "./timeline-monotonic-guard.js";
import { joinAbove, seqlessHeldAbove } from "./seqless-page-retention.js";
import { TranscriptInvariantReporter } from "../../transcript-invariant.js";
import { type ReportSubject, agentSubject, workspaceSubject } from "../../report-subject.js";
import {
  duplicatePromptBubbles,
  outOfOrderRows,
  promptsBelowLiveRow,
  unattributedTwinBubbles,
} from "./transcript-checks.js";
import { TurnPrompts, type PromptVerdict } from "./turn-prompts.js";
import { journal, journalRun, pageRange, screenRows } from "./transcript-journal.js";
import type { Attachment } from "../../types.js";
import type { MessageCard, WorkSummary } from "../../core/types.js";
import { rowWork } from "../../row-work.svelte.js";
import type { PromptAuthor } from "../../turn-author-identity.js";
import { accessibleOnlineDaemons, pickDefaultDaemon } from "./daemon-select.js";
import { isMe, type ViewerIdentity } from "../../session-scope.js";
import { isSessionReadDenied, SESSION_READ_DENIED_REASON } from "../../session-read-denied.js";
import { sessionStreamKey } from "../../session-progress.js";
import { entrySession, projectSessionEntries } from "../../session-projection.js";
import { queuedTailStart } from "./history/queued-tail.js";
import { TurnProgressReports, turnStallSender } from "./turn-progress.js";
import {
  type IdleTurnFacts,
  idleTurnFacts,
  judgeIdleTurn,
  silenceReason,
} from "./turn-liveness.js";
import {
  isRuntimeExitRow,
  isSystemErrorText,
  storedFailureOf,
  turnFailureCardIndex,
} from "./system-error-text.js";
import { storedFailureKind } from "./stored-failure-kind.js";
import type {
  AgentEvent,
  AgentAttention,
  AgentAttentionReason,
  AgentTimelineItem,
  AgentUsage,
  AgentPermissionRequest,
  AgentPermissionResponse,
  AgentRuntimeInfo,
  AgentMode,
  AgentSlashCommand,
  ProviderInfo,
  Cybo,
  Daemon,
  DaemonMeta,
  DaemonAccessEntry,
  DaemonWireRow,
  TurnEnd,
  ToolCallDetail,
} from "./types.js";
import { normalizeDaemonRow } from "./types.js";
import { isUsageLimitFailure } from "./usage-limit.js";
import {
  attentionReasonForTurnEvent,
  badgeForReason,
  PAUSED_BADGE,
  reconcileAttentionFromSnapshot,
  type AgentTurnEventType,
  type AttentionBadge,
} from "./attention-badge.js";
import { CoverageBook } from "./history/coverage-book.js";
import {
  passableInReply,
  seamAround,
  bodyOfChunks,
  chunksOf,
  highestSeq,
  ledgerChunk,
  rowsFollowEachOther,
  rowsWith,
  seqRangeOf,
  seqsOf,
  takeRowIntoLedger,
  takeDeltaIntoLedgerBody,
  takeRowIntoLedgerBody,
  withTextBody,
} from "./history/rows.js";
import {
  VIEW_MAX_ENTRIES,
  compactionCardFor,
  fuseCompactionRun,
  headChainsTo,
  insertIndexForTs,
  rebuildApplied,
  stampCommittedSeq,
  takeRowIntoLedgerText,
  turnWindow,
  updatesEntityOnScreen,
} from "./history/view-ops.js";
import {
  ConversationHistory,
  HistoryInvariantError,
  type ConversationTarget,
  type ConversationWrite,
  type JudgedWriter,
} from "./history/conversation-history.js";
import { WriteJudge } from "./history/write-judge.js";
import { PromptOutbox } from "./history/outbox.svelte.js";
import {
  dropViewOfOldLife,
  lifeRetired,
  notePageProvenance,
  rearmTimelineGone,
  olderPagination,
  olderProof,
  pageIncomplete,
} from "./mirror-reset.js";
export { catchUpAgent, readCatchUpGap, type CatchUpGap } from "./history/catch-up.js";
export { rewindConversation } from "./history/rewind.js";
import type {
  AgentControlVersions,
  AgentStream,
  LedgerSide,
  OlderPageMeta,
  RecoveryHold,
  StreamEntry,
  TextBody,
  TextChunk,
  TextPart,
  TranscriptRecovery,
  TurnStatus,
} from "./history/types.js";
export type {
  AgentControlVersions,
  AgentStream,
  LedgerSide,
  RecoveryHold,
  StreamEntry,
  TextBody,
  TextChunk,
  TextPart,
  TranscriptRecovery,
  TurnStatus,
} from "./history/types.js";
import type { RouteEntry } from "./route-entry.js";
import { accountSync, conversationSync, type SyncSession } from "./sync-session.js";
import { isAgentUnreachableFailure, isNeverDeliveredFailure } from "./agent-unreachable.js";
import { isTaskCardFor, taskCardId } from "./task-card.js";
import { activityMachineId } from "../../machine-avatar.js";
import {
  anchorCards,
  reanchorCards,
  cardsKeptByPage,
  rowCard,
  rowPassesCard,
  seatCard,
  statusCard,
  withoutAnchor,
  isFailureCardFor,
  pendingSeqOf,
} from "./status-card.js";

/** The held prompt's inline state while its computer restarts. */
export const RECONNECTING_STATUS = "Reconnecting…";

// agentId → when this client created it (noteSessionCreatedHere); cleared with every stream.
const sessionsCreatedHere = conversationSync.collection(
  "sessionsCreatedHere",
  new Map<string, number>(),
);

const DM_PREFIX_RE = /^\[DM from [^\]]+\]:\s*/;
function stripDmPrefix(text: string): string {
  return text.replace(DM_PREFIX_RE, "");
}

// A channel @-mention never reaches the timeline as the human's raw words: the
// daemon wraps them in a big prompt ENVELOPE (buildChannelMentionPrompt,
// server/cyborg/cybo-mention-invoke.ts) — channel/participants/caller framing
// plus every untrusted span inside a nonce fence — and that whole scaffold is
// persisted as the turn's `user_message` row. Rendered raw it's a wall of
// internal prompt text where the human's actual request should be. Pull just the
// request back out for display.
//
// The request is the payload of the "request"-kind fenceUntrusted the daemon
// builds from `${author} mentioned you:` (server/cyborg/security/prompt-fence.ts):
// the fixed framing sentence, then BEGIN/END UNTRUSTED <nonce> around the ask
// VERBATIM. The nonce is per-call random and the \1 backreference ties BEGIN to
// its own END, so this can only lift the genuine request span, never the framing.
const MENTION_REQUEST_RE =
  /mentioned you:\s*\[Direct message from [\s\S]*?their request[\s\S]*?\nBEGIN UNTRUSTED ([0-9a-f]{16})\n([\s\S]*?)\nEND UNTRUSTED \1/;
// A genuine human message is the fenced PAYLOAD, never this framing, so the
// startsWith + fence check can't misfire on real content. Shared with the
// user_message ingest so it can tell an envelope-that-stripped-to-empty (a
// scaffold to drop) apart from a legitimately-empty human message (keep).
function isMentionEnvelope(text: string): boolean {
  return text.startsWith("You were @-mentioned in ") && text.includes("BEGIN UNTRUSTED ");
}
export function stripMentionEnvelope(raw: string): string {
  const text = stripDateStamp(raw);
  if (!isMentionEnvelope(text)) {
    return text;
  }
  const m = MENTION_REQUEST_RE.exec(text);
  // Defensive: an envelope whose request fence we can't parse renders EMPTY
  // rather than dumping the whole scaffold as prose — an empty bubble beats the
  // wall. In practice the fence shape is fixed, so this fallback is unreachable.
  return m ? m[2].trim() : "";
}

// Tail window for the curated "last activity" line (#594). The glanceable line
// only ever surfaces the latest curated entry, so we cap the curation scan to
// the most recent N raw timeline entries — bounding the per-update cost on long
// (up to 500-entry) streams while still capturing the current turn's head.
const MAX_ACTIVITY_SCAN = 40;

// Idle bound for the client-side turn watchdog. This is an IDLE window, NOT a
// wall-clock cap: it RESETS on every event a running turn emits (timeline items,
// streaming tokens, usage, permission resolutions — see noteTurnActivity), so a
// long-but-healthy turn (heavy tool use, subagent fan-outs) never trips it, while
// a genuinely wedged turn — which emits nothing — still trips it at a known
// ceiling. Mirrors the server's AGENT_TURN_IDLE_TIMEOUT_MS (server/.../
// agent-turn-timeout.ts, 15 min): matching the authoritative server watchdog
// means the client never declares a FALSE timeout before the server — which
// resets on the same class of events — would itself reclaim a hung turn and emit
// a (better-classified) turn_failed. The prior 5-minute value was a wall-clock cap
// on the WHOLE turn regardless of health — the bug this fixes.
export const TURN_IDLE_TIMEOUT_MS = 15 * 60 * 1000;

// Bound on the PRE-turn wait: a dispatched prompt whose relay ack resolved but
// whose turn_started never arrives. The idle watchdog above is armed ONLY by
// turn_started, so this window used to be unbounded — the relay honors an
// explicit daemonId, and a prompt aimed at an offline daemon produces NO stream
// events, no error, nothing: the user saw a transient "sent" check and then
// silence forever. Mirrors BoardQueryDialog's PENDING_WATCHDOG_MS (same failure
// class, same 45s bound): past it, the optimistic bubble flips to "unknown"
// (delivery unconfirmed) and an inline row says so. Cleared by ANY stream event
// for the agent, by a roster "running" push, and by an ACCEPTED prompt verdict —
// see promptPendingUnconfirmedMessage for why that third one had to be added.
export const PROMPT_PENDING_WATCHDOG_MS = 45_000;

// Expiry establishes ONE thing: nothing reached THIS BROWSER in time. It does not
// establish that the daemon was silent, so the row may not say so — it used to, and
// production disproved it twice on turns that answered in under 10s. The word "failed"
// belongs to whoever observed the death (the daemon's 180s EPHEMERAL_TURN_TIMEOUT_MS
// forwards a real turn_failed), never to this timer's guess.
export function promptPendingUnconfirmedMessage(seconds: number): string {
  return (
    `No update from the agent after ${seconds}s. It may still be working — ` +
    `delivery is unconfirmed, not failed.`
  );
}

// Past this much silence the queue banner stops saying "agent working".
export const QUEUE_STALL_MS = 60 * 1000;

// ─── Stream types ─────────────────────────────────────────────────

export interface RetrySend {
  text: string;
  attachments?: Attachment[];
  pageRef?: string;
  clientMessageId?: string;
  workSessionId?: string;
  command?: AgentCommandInput;
}

// ponytail: seconds-resolution on purpose — utils/datetime's relativeTime
// bottoms out at "just now" under a minute, and "is anything happening RIGHT
// NOW" is the entire question the two lines below exist to answer.
function coarseAgeLabel(age: number): string {
  if (age < 60_000) return `${Math.floor(age / 1000)}s`;
  if (age < 3_600_000) return `${Math.floor(age / 60_000)}m`;
  return `${Math.floor(age / 3_600_000)}h`;
}

// The banner over the daemon's held rows; the component supplies `now` from its 1s tick.
export function queueWaitLabel(
  rows: readonly { paused?: true }[],
  lastActivityAt: number | null,
  now: number,
): string {
  const head = rows.length === 1 ? "Queued prompt" : `${rows.length} queued prompts`;
  // A Stop pauses the whole queue: one waiting row says it for all (the rows say "Paused").
  if (rows.some((row) => row.paused)) {
    return `${head} · paused after Stop · sends when you send a message or press Send now`;
  }
  const finishes = `${head} · sends when the agent finishes`;
  if (lastActivityAt === null) return finishes;
  const age = Math.max(0, now - lastActivityAt);
  return age < QUEUE_STALL_MS
    ? `${head} · agent working, last activity ${coarseAgeLabel(age)} ago`
    : finishes;
}

// Below this the stream is visibly moving and a counter would just be noise;
// above it the count-up IS the proof of life the indicator exists to give.
const THINKING_QUIET_MS = 5000;

// The DM "thinking" indicator's detail suffix. Pure so the copy is testable
// without mounting; the route supplies `now` from its 1s tick and
// `lastActivityAt` from lastTurnActivity() — a clock only an ARRIVING event
// writes, so this can never invent progress that didn't happen. Returns
// undefined when there is nothing honest to say (no event seen yet for this
// turn, or one arrived a moment ago).
export function thinkingAgeLabel(lastActivityAt: number | null, now: number): string | undefined {
  if (lastActivityAt == null) return undefined;
  const age = Math.max(0, now - lastActivityAt);
  if (age < THINKING_QUIET_MS) return undefined;
  return `${coarseAgeLabel(age)} since last update`;
}

export interface AgentControlSnapshot {
  model?: string | null;
  currentModeId?: string | null;
  availableModes?: AgentMode[] | null;
  thinkingOptionId?: string | null;
}

// A daemon "busy / turn still alive" reject (CYBORG-679): the agent already has
// a run in flight, so a freshly-dispatched prompt bounced. The daemon (agent-
// manager) throws "already has an active run"; message-router surfaces it as
// "busy with another run"; Pi's RPC rejects a mid-turn prompt with "already
// processing"/"streamingBehavior" (see the server's isAgentBusyError). This is
// DISTINCT from a genuine send failure: the previous turn is HEALTHY and will
// complete on its own, so a bounced prompt here must NOT be turned into a real
// turn_failed on the client (which would inject a false error card AND drain the
// real queue). Message-based — a busy reject signals "a turn is running on the
// daemon" regardless of the client's local turn status. Accepts either an Error
// (a rejected RPC) or a raw string (the server-broadcast turn_failed.error).
const AGENT_BUSY_REJECT_RE =
  /busy with another run|already has an active run|already processing|streamingBehavior/i;
export function isAgentBusyReject(err: unknown): boolean {
  let msg = "";
  if (err instanceof Error) msg = err.message;
  else if (typeof err === "string") msg = err;
  return AGENT_BUSY_REJECT_RE.test(msg);
}

// ─── Tool-call status: a terminal status is FINAL ─────────────────
//
// The four wire statuses (protocol/agent-types.ts): running | completed | failed |
// canceled. The last three are TERMINAL — a call that reported one is over, and no
// later row for that callId may move it back to `running`.
//
// WHY THIS EXISTS. handleTimeline's tool_call fold takes the LAST row per callId, so
// "later wins" was the whole rule. Claude's background-delegation tool (`Agent`)
// breaks it: it keeps emitting progress rows on a callId that already returned, so
// the persisted timeline reads `running → completed → running ×21` for ONE call.
// Measured on work session wsess_02803cec: 37 callIds, 32 correctly terminal, and
// the 5 stuck ones ALL the `Agent` tool — still badged RUNNING an hour after the
// session went `done`, across two daemon restarts. The emitters live in
// packages/server/src/server/agent/providers/claude/ (sidechain-tracker.ts:118-126
// re-creates its per-parent state on every subagent message and has no
// already-finished set; agent.ts:4694-4713 loses its `started` dedupe when
// handleToolResult deletes the cache entry at :4762). That directory is upstream
// Paseo and must not be modified, so the fold is where this can be fixed at all.
//
// PORTED VERBATIM from Paseo's own mobile client, which already got this right:
// packages/app/src/types/stream.ts:1060-1077 `mergeAgentToolCallStatus`, pinned by
// its stream.test.ts:888 "keeps terminal status when a stale running update arrives
// later". We cannot import across the fork boundary, so it is mirrored here rather
// than reinvented — same lattice, same precedence, so the two clients agree.
//
// It is a LATTICE, not a "keep the first terminal one": precedence, not arrival
// order, so `failed` outranks a `completed` that lands beside it and a stale
// `completed` tick cannot paper over a failure. `canceled` is terminal too; a guard
// that only knew completed/failed would leave the identical bug for every
// interrupted call. (It is NOT consulted across pages: prependTimeline folds an
// older page inside a throwaway state and splices the result in, so those rows never
// meet these entries.)
//
// ponytail: keyed on callId alone, with no turnId guard — upstream's projection has
// one (timeline-projection.ts's turnIdsAllowMerge) because a provider CAN reuse a
// callId in a new turn after a join, and a reused id would now stay pinned to the
// old terminal status. This fold already conflated those before this change (it has
// always matched on callId across the whole entries array) and StreamEntry carries
// no turnId to fix it with; real ids are per-tool-use (`toolu_…`) and every hydrate
// resets entries, so the exposure is narrow. Thread turnId onto StreamEntry and
// scope the match to the current turn_boundary if a reused callId ever shows up.
// Same narrow ceiling for capEntries: a card trimmed off the head (MAX_ENTRIES) has
// no `prev` left, so a late `running` row for it starts a fresh entry at `running`.
function mergeToolCallStatus(existing: string | undefined, incoming: string): string {
  if (existing === undefined) return incoming;
  if (existing === "failed" || incoming === "failed") return "failed";
  if (existing === "canceled") return "canceled";
  if (incoming === "canceled") return existing === "completed" ? "completed" : "canceled";
  if (existing === "completed" || incoming === "completed") return "completed";
  return "running";
}

function isTerminalToolCallStatus(status: string | undefined): boolean {
  return status === "completed" || status === "failed" || status === "canceled";
}

function openCallIds(
  entries: readonly StreamEntry[],
  keep: (e: Extract<StreamEntry, { kind: "tool_call" }>) => boolean,
): string[] {
  return entries.flatMap((e) =>
    e.kind === "tool_call" && !isTerminalToolCallStatus(e.status) && keep(e) ? [e.callId] : [],
  );
}

// Codex's plan approval, held past its turn; Claude's in-turn ExitPlanMode is not.
function isCarriedPlan(r: AgentPermissionRequest | undefined): boolean {
  return r?.kind === "plan" && r.name === "CodexPlanApproval";
}

// CYBORG-1516: a Codex question asked while the agent keeps working. The daemon holds it for the
// session (not the turn) until it is answered, so a clean turn end and the next turn keep it.
function outlivesTurn(r: AgentPermissionRequest | undefined): boolean {
  return isCarriedPlan(r) || (r?.kind === "question" && r.name === "request_user_input_async");
}

// Resolve every tool card that never reached a terminal status to `canceled`. PURE:
// returns the input array untouched (same identity) when there is nothing to do, so
// a caller can memoise on it.
//
// Runs over ALREADY-FOLDED entries — never per incoming row. Per row it would force a
// call's first `running` to `canceled`, and the lattice would then refuse that same
// call's later `completed`, turning correctly-finished cards into cancelled ones.
//
// `canceled`, not `failed`: the call stopped, nothing reported an error. Same verb the
// provider's own turn-end flush uses (mapClaudeCanceledToolCall) and the same honest
// reading pauseWorkSessionByAgent gives a session that "stopped WITHOUT finishing".
//
// `stillPending`: ids with an open permission (a Codex plan outlives its turn) wait on a human.
function resolveOrphanedToolCards(
  entries: readonly StreamEntry[],
  stillPending?: ReadonlyMap<string, unknown>,
  spare?: (entry: Extract<StreamEntry, { kind: "tool_call" }>) => boolean,
): {
  entries: readonly StreamEntry[];
  stuck: string[];
} {
  const stuck: string[] = [];
  const next = entries.slice();
  for (let i = 0; i < next.length; i += 1) {
    const entry = next[i];
    if (entry.kind !== "tool_call" || isTerminalToolCallStatus(entry.status)) continue;
    if (stillPending?.has(entry.callId) || spare?.(entry)) continue;
    stuck.push(`${entry.name}:${entry.callId}`);
    // An indexed rewrite, not a `.map()` spread — oxc/no-map-spread is an error in
    // this repo (see pg-sync.ts:1451 and the call sites restructured for it).
    next[i] = { ...entry, status: "canceled", error: null, swept: true };
  }
  return stuck.length > 0 ? { entries: next, stuck } : { entries, stuck };
}

function isTaskNotificationRow(event: AgentEvent): boolean {
  return event.type === "timeline" && isTaskNotificationItem(event.item);
}

// A restored send is often not lost work: the person retyped it, and that second send
// reached the timeline under its OWN client id, where the id match can never see it. A
// durable row with the same words stamped AT OR AFTER the restored one IS that delivery.
function deliveredAtOrAfter(
  entry: StreamEntry & { kind: "user_message" },
  items: Record<string, unknown>[],
): boolean {
  const sentAt = entry.ts;
  const words = entry.content.trim();
  if (sentAt === undefined || words === "") return false;
  return items.some((raw) => {
    // cast: a served timeline row, typed at this boundary like every reader here.
    const item = raw as unknown as AgentTimelineItem;
    if (item.type !== "user_message") return false;
    // One-sided: an identical row stamped BEFORE proves nothing (a person may say the
    // same thing twice), and a row with no server time proves no order at all.
    const at = parseIsoTs(item.ts);
    if (at === undefined || at < sentAt) return false;
    return stripDmPrefix(stripMentionEnvelope(item.text)).trim() === words;
  });
}

function optimisticRowsMissingFromSnapshot(
  entries: readonly StreamEntry[],
  items: Record<string, unknown>[],
): Array<StreamEntry & { kind: "user_message"; clientMessageId: string }> {
  const committedClientIds = new Set<string>();
  for (const raw of items) {
    const item = raw as unknown as AgentTimelineItem;
    if (item.type === "user_message" && item.clientMessageId) {
      committedClientIds.add(item.clientMessageId);
    }
  }
  return entries.filter(
    (entry): entry is StreamEntry & { kind: "user_message"; clientMessageId: string } => {
      if (entry.kind !== "user_message" || typeof entry.clientMessageId !== "string") return false;
      if (committedClientIds.has(entry.clientMessageId)) return false;
      // Only a RESTORED row reconciles by text. A live send is still waiting for its own
      // echo, and dropping that one by resemblance is how the newest prompt vanishes.
      if (!entry.rehydrated || !deliveredAtOrAfter(entry, items)) return true;
      void deleteAgentPromptOutbox(entry.clientMessageId);
      return false;
    },
  );
}

// Neither a mirror nor an owning-daemon page proves that an absent prompt was
// rejected: both can be partial. Only a still-pending row turns "unknown"; one its
// receipt or resend already settled keeps that verdict.
function settleRehydratedRows<T extends { rehydrated?: true; status?: string }>(rows: T[]): T[] {
  return rows.map((row) => {
    if (!row.rehydrated || row.status !== "pending") return row;
    return { ...row, status: "unknown" };
  });
}

// The two shapes a cut leaves of a message: its TAIL (a page that started inside it —
// deltas fold in order, so the page holds a suffix) or its HEAD (a live stream that
// stopped being applied, a page read before the last chunks landed — a prefix). `held`
// is what an entry shows; `full` is a longer text of the same message from elsewhere
// (the DM ledger row, the durable timeline, the text held before a re-read). True when
// `full` should replace `held`. An edit or a correction is neither, and is left alone.
export function isCutOf(held: string, full: string): boolean {
  return full.length > held.length && (full.endsWith(held) || full.startsWith(held));
}

// A seam shorter than this is coincidence, not the same chunk seen twice.
const MIN_STREAM_SEAM_CHARS = 4;

// What the screen's text becomes given the durable text of the same message: the
// durable text when the screen holds a cut of it (#3193), or durable + the screen's
// overhang when the tab came back mid-stream and the page lags the live chunks — the
// longest head of the screen text that tails the durable text is the seam (#3244).
// undefined when the two cannot be related, or nothing would change.
export function extendFromDurable(held: string, full: string): string | undefined {
  if (held === full) return undefined;
  if (isCutOf(held, full) || full.includes(held)) return full;
  for (let k = Math.min(held.length, full.length); k >= MIN_STREAM_SEAM_CHARS; k -= 1) {
    if (full.endsWith(held.slice(0, k))) return full + held.slice(k);
  }
  return undefined;
}

// The older half of a message joined in front of a body with ledger head text, when the
// rows carry no seq (an older daemon's page): the same rule as takeRowIntoLedger, with
// the page edge as the adjacency. undefined when the body has no ledger head.
function prependToLedgerHead(chunks: readonly TextChunk[], text: string): TextChunk[] | undefined {
  const ledger = ledgerChunk(chunks, "head")[0];
  if (!ledger) return undefined;
  const rest = chunks.filter((c) => c.ledger !== "head");
  if (!ledger.text.endsWith(text)) return [{ text }, ...rest];
  const left = ledger.text.slice(0, ledger.text.length - text.length);
  return [...(left ? [{ text: left, ledger: "head" as const }] : []), { text }, ...rest];
}

// The union of two bodies of the SAME message: the normal form of both sides' chunks,
// the durable side first so it wins a tie on a seq. undefined unless BOTH sides carry a
// seq to key on; a body with none is related by shape instead (isCutOf / extendFromDurable).
export function unionTextBodies(durable: TextBody, screen: TextBody): TextBody | undefined {
  const a = chunksOf(durable);
  const b = chunksOf(screen);
  if (!a.some((c) => c.seq !== undefined) || !b.some((c) => c.seq !== undefined)) return undefined;
  return bodyOfChunks([...a, ...b]);
}

// Each message's durable chunks in row order, keyed by provider message id — only rows
// that carry a seq (an older daemon's rows fold through foldAssistantText instead).
export function assistantChunksByMessage(
  items: ReadonlyArray<Record<string, unknown>>,
): Map<string, TextChunk[]> {
  const chunks = new Map<string, TextChunk[]>();
  for (const it of items) {
    if (it.type !== "assistant_message" || typeof it.text !== "string") continue;
    const id = typeof it.messageId === "string" ? it.messageId : "";
    if (!id || typeof it.seq !== "number") continue;
    const list = chunks.get(id) ?? [];
    list.push({ seq: it.seq, text: it.text });
    chunks.set(id, list);
  }
  return chunks;
}

// Contiguous runs of assistant chunk rows sharing one provider message id, folded into
// the message's text — the durable timeline's own word on what each message says.
export function foldAssistantText(
  items: ReadonlyArray<Record<string, unknown>>,
): Map<string, string> {
  const folded = new Map<string, string>();
  let run: { id: string; text: string } | null = null;
  const close = () => {
    if (run) folded.set(run.id, (folded.get(run.id) ?? "") + run.text);
    run = null;
  };
  for (const it of items) {
    const id = typeof it.messageId === "string" ? it.messageId : "";
    if (it.type === "assistant_message" && typeof it.text === "string" && id) {
      if (run && run.id === id) run.text += it.text;
      else {
        close();
        run = { id, text: it.text };
      }
    } else close();
  }
  close();
  return folded;
}

// A timeline item's ISO `ts` as epoch ms, or undefined when absent/unparseable. The one
// place the parse lives — tsFromItem (which falls back to Date.now() for a live entry)
// and firstAssistantTsByMessageId both read through it.
export function parseIsoTs(value: unknown): number | undefined {
  if (typeof value !== "string") return undefined;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? undefined : ms;
}

// The ms timestamp of each assistant message's FIRST durable row — the start time an
// inserted reply carries, as handleTimeline stamps a merged entry with its first chunk's
// ts. An unparseable ts is dropped, so the inserted entry lands at the end.
export function firstAssistantTsByMessageId(
  items: ReadonlyArray<Record<string, unknown>>,
): Map<string, number> {
  const first = new Map<string, number>();
  for (const it of items) {
    if (it.type !== "assistant_message") continue;
    const id = typeof it.messageId === "string" ? it.messageId : "";
    if (!id || first.has(id)) continue;
    const ms = parseIsoTs(it.ts);
    if (ms !== undefined) first.set(id, ms);
  }
  return first;
}

// Does the screen hold `entry`'s reply from its first chunk? The row just below its lowest
// is another entry's (or there is none: seq 1 starts the timeline) — the message edge the
// server snaps to. A bubble never spans a row the screen has not seen, so the rows up to
// the cut are held too.
function heldFromFirstChunk(entries: readonly StreamEntry[], entry: StreamEntry): boolean {
  const low = Math.min(...seqsOf(entry));
  return low <= 1 || entries.some((e) => e !== entry && seqsOf(e).includes(low - 1));
}

// A tool row's detail type — undefined when the row carries no detail at all (a
// task_notification row does not), which the reducer reads as "nothing typed".
function incomingTypeOf(item: { detail?: unknown }): string | undefined {
  const detail = item.detail as { type?: unknown } | undefined;
  return typeof detail?.type === "string" ? detail.type : undefined;
}

// The kept `unknown` detail takes only the fields it lacks from an older `unknown` row.
function fillUnknownDetail(
  kept: ToolCallDetail | undefined,
  older: ToolCallDetail | undefined,
): ToolCallDetail | undefined {
  if (kept?.type !== "unknown" || older?.type !== "unknown") return kept;
  const empty = (v: unknown) => v === null || v === undefined || v === "";
  return {
    type: "unknown",
    input: empty(kept.input) ? older.input : kept.input,
    output: empty(kept.output) ? older.output : kept.output,
  };
}

// A chunk continues a bubble across rows applied without drawing anything (an absorbed
// echo), never across a row the screen has not seen.
// A ledger row and its timeline copy are stamped seconds apart.
const LEDGER_TWIN_MS = 10_000;
// The ledger row's created_at (relay insert) and the timeline ts (daemon append) are measured
// 8-28 ms apart, the entry AFTER the row; an entry far after a row is another message.
const LEDGER_TWIN_AFTER_MS = 500;
const FAILURE_TWIN_MS = 60_000;
const inTwinWindow = (entryTs: number, rowTs: number): boolean =>
  entryTs - rowTs <= LEDGER_TWIN_AFTER_MS && rowTs - entryTs <= LEDGER_TWIN_MS;

function rowsBetweenApplied(applied: ReadonlySet<number>, from: number, to: number): boolean {
  if (to <= from) return false;
  for (let n = from + 1; n < to; n += 1) if (!applied.has(n)) return false;
  return true;
}

// Text entries on screen before a re-hydrate wipes them. Nothing is held across a
// rewind: it restarts the timeline on purpose.
function textHeldOnScreen(
  entries: readonly StreamEntry[],
  mode: "refresh" | "rewind" | undefined,
): Array<StreamEntry & { kind: "text" }> {
  if (mode === "rewind") return [];
  return entries.filter(
    (e): e is StreamEntry & { kind: "text" } => e.kind === "text" && e.messageId !== undefined,
  );
}

// A page is a WINDOW of rows. What the screen held of a message OUTSIDE that window —
// chunks below the page's first seq (the head above a page that opens inside a reply,
// #3193 F2/F4) and chunks above its last (the live overhang the page did not reach) —
// is kept, on the page's first / last entry of that message; inside the window the
// page wins, block by block, so a message the page shows as text/tool/text never
// receives the whole message on every block (#3248 C2). A held body with no seq is
// related by shape instead: kept only when the page's text is its cut.
function restoreHeldText(
  entries: readonly StreamEntry[],
  held: Array<StreamEntry & { kind: "text" }>,
  items: ReadonlyArray<Record<string, unknown>>,
): readonly StreamEntry[] {
  if (held.length === 0) return entries;
  let lo = Infinity;
  let hi = -Infinity;
  for (const raw of items) {
    const seq = raw.seq;
    if (typeof seq !== "number") continue;
    lo = Math.min(lo, seq);
    hi = Math.max(hi, seq);
  }
  let next = entries;
  for (const h of held) {
    const at = next.flatMap((e, i) =>
      e.kind === "text" && e.messageId === h.messageId ? [i] : [],
    );
    if (at.length === 0) continue;
    const chunks = chunksOf(h);
    const ledger = chunks.some((c) => c.ledger) ? restoreLedgerText(next, at, h) : undefined;
    if (ledger) {
      next = ledger;
      continue;
    }
    if (!chunks.some((c) => c.seq !== undefined) || lo === Infinity) {
      const first = next[at[0]] as StreamEntry & { kind: "text" };
      if (isCutOf(first.content, h.content)) {
        const restored = withLedgerText(first, h.content);
        next = next.map((e, i) => (i === at[0] ? restored : e));
      }
      continue;
    }
    const below = chunks.filter((c) => c.seq !== undefined && c.seq < lo);
    const above = chunks.filter((c) => c.seq === undefined || c.seq > hi);
    const apply = (index: number, extra: TextChunk[]): void => {
      if (extra.length === 0) return;
      const e = next[index] as StreamEntry & { kind: "text" };
      const union = unionTextBodies(e, bodyOfChunks(extra));
      if (union) next = next.map((x, i) => (i === index ? withTextBody(e, union) : x));
    };
    apply(at[0], below);
    apply(at[at.length - 1], above);
  }
  return next;
}

// A row is a sub-agent progress report when it is a sub_agent detail carrying the child's action log.
function isProgressRow(item: AgentTimelineItem): boolean {
  if (item.type !== "tool_call" || item.detail?.type !== "sub_agent") return false;
  return typeof item.detail.log === "string" && item.detail.log !== "";
}

// Only a running sub_agent row with an empty log is a launch; a result or terminal row never makes
// a progress-only card a boundary.
function isLaunchRow(item: AgentTimelineItem): boolean {
  return (
    item.type === "tool_call" &&
    item.status === "running" &&
    item.detail?.type === "sub_agent" &&
    item.detail.log === ""
  );
}

// The text entry before the run of passable rows (passableInReply) ending at `end` (exclusive),
// when it speaks `messageId` (a thinking entry, which has no id, for kind "thinking").
// Callers still check that every row between the two texts is applied.
function textBeforeProgressRun(
  entries: readonly StreamEntry[],
  end: number,
  kind: "text" | "thinking",
  messageId?: string,
): number {
  const named = kind === "text" && messageId !== undefined;
  let i = end - 1;
  if (!passableInReply(entries[i], named)) return -1;
  while (i > 0 && passableInReply(entries[i], named)) i -= 1;
  const base = entries[i];
  if (kind === "thinking") return base?.kind === "thinking" ? i : -1; // thinking carries no message id
  return messageId !== undefined && base?.kind === "text" && base.messageId === messageId ? i : -1;
}

// Neighbouring bubbles of one message with only applied rows between them are one.
function joinSplitBubbles(
  entries: readonly StreamEntry[],
  applied: ReadonlySet<number>,
): readonly StreamEntry[] {
  const out: StreamEntry[] = [];
  for (const e of entries) {
    const rb = seqRangeOf(e);
    const via =
      e.kind === "text" || e.kind === "thinking"
        ? textBeforeProgressRun(
            out,
            out.length,
            e.kind,
            e.kind === "text" ? e.messageId : undefined,
          )
        : -1;
    const viaRange = via >= 0 ? seqRangeOf(out[via]) : undefined;
    if (viaRange && rb && rowsBetweenApplied(applied, viaRange.max, rb.min)) {
      const base = out[via] as StreamEntry & { kind: "text" };
      out[via] = withTextBody(base, bodyOfChunks([...chunksOf(base), ...chunksOf(e as TextBody)]));
      continue;
    }
    const last = out.at(-1);
    const ra = last ? seqRangeOf(last) : undefined;
    const sameMessage =
      last?.kind === "text" && e.kind === "text"
        ? last.messageId === e.messageId
        : last?.kind === "thinking" && e.kind === "thinking";
    if (last && sameMessage && ra && rb && rowsBetweenApplied(applied, ra.max, rb.min)) {
      const body = bodyOfChunks([...chunksOf(last as TextBody), ...chunksOf(e as TextBody)]);
      out[out.length - 1] = withTextBody(last as StreamEntry & { kind: "text" }, body);
      continue;
    }
    out.push(e);
  }
  return out;
}

// Whether a row with `seq` passes an entry that carries no seq (splitPointForSeq).
function rowPassesUnsequenced(
  e: StreamEntry,
  item: AgentTimelineItem,
  seq: number,
  rowTs: number,
  newest: boolean,
  verdict?: PromptVerdict,
): boolean {
  if (e.kind === "user_message") return verdict?.(e.id) ?? (e.ts !== undefined && e.ts <= rowTs);
  if (e.kind === "turn_boundary") {
    // A divider with `afterSeq` is passed by the next turn's row; a plain one only by a prompt.
    const passes = newest && e.afterSeq !== undefined && seq > e.afterSeq;
    return item.type === "user_message" || passes;
  }
  if (e.kind === "error") return rowPassesCard(e, seq, rowTs);
  return e.ts !== undefined && e.ts <= rowTs;
}

// The two halves of a text/thinking entry around `seq`: chunks below it, chunks above
// it; a chunk without a seq stays with the chunk it followed.
function splitTextAt<E extends StreamEntry & { kind: "text" | "thinking" }>(
  e: E,
  seq: number,
): [E, E] {
  const chunks = chunksOf(e);
  const head: TextChunk[] = [];
  const tail: TextChunk[] = [];
  let side = head;
  for (const c of chunks) {
    if (c.seq !== undefined) side = c.seq < seq ? head : tail;
    side.push(c);
  }
  // Each half covers ONLY its own chunks: the whole entry's stamped seq belongs to the
  // tail, never to the head.
  const exact = (body: TextBody): E => {
    const { seq: _seq, ...rest } = e;
    return withTextBody(rest as E, body);
  };
  return [exact(bodyOfChunks(head)), exact(bodyOfChunks(tail))];
}

type TextEntry = StreamEntry & { kind: "text" | "thinking" };

// Two halves of one message joined: the id of the half already on screen survives.
function joinTextHalves(lo: TextEntry, hi: TextEntry, known: ReadonlySet<string>): TextEntry {
  const keep = known.has(lo.id) || !known.has(hi.id) ? lo : hi;
  const ts = (lo as { ts?: number }).ts ?? (hi as { ts?: number }).ts; // thinking entries carry none
  return {
    ...withTextBody(lo, bodyOfChunks([...chunksOf(lo), ...chunksOf(hi)])),
    id: keep.id,
    ...(ts === undefined ? {} : { ts }),
  };
}

function rowsJoin(lo: TextEntry, hi: TextEntry, applied: ReadonlySet<number>): boolean {
  const ra = seqRangeOf(lo);
  const rb = seqRangeOf(hi);
  return !!ra && !!rb && rowsBetweenApplied(applied, ra.max, rb.min);
}

// The seam halves of one message with passable rows on either side of it (passableInReply): the
// joined text keeps its place before the run. Thinking has no id, so only progress cards pass.
function coalesceAcrossProgress(
  prefix: readonly StreamEntry[],
  suffix: readonly StreamEntry[],
  known: ReadonlySet<string>,
  applied: ReadonlySet<number>,
): readonly StreamEntry[] | undefined {
  const [loIdx, hiIdx] = seamAround(prefix, suffix);
  const lo = prefix[loIdx];
  const hi = suffix[hiIdx];
  if (loIdx === prefix.length - 1 && hiIdx === 0) return undefined; // nothing at the seam
  if (lo?.kind !== "text" && lo?.kind !== "thinking") return undefined;
  if (hi?.kind !== lo.kind) return undefined;
  if (
    lo.kind === "text" &&
    (lo.messageId === undefined || lo.messageId !== (hi as TextEntry & { kind: "text" }).messageId)
  ) {
    return undefined;
  }
  if (!rowsJoin(lo, hi, applied)) return undefined;
  return [
    ...prefix.slice(0, loIdx),
    joinTextHalves(lo, hi, known),
    ...prefix.slice(loIdx + 1),
    ...suffix.slice(0, hiIdx),
    ...suffix.slice(hiIdx + 1),
  ];
}

// Re-join a prefix and the suffix that followed it when the prefix's last entry and the
// suffix's first are two halves of ONE message that now meet — same kind, compatible
// message id, every row between them applied. The joined entry keeps the id (and start time) of the
// half that was already on screen, so its key and toggle state survive the join.
function coalesceSeam(
  prefix: readonly StreamEntry[],
  suffix: readonly StreamEntry[],
  known: ReadonlySet<string>,
  applied: ReadonlySet<number>,
): readonly StreamEntry[] {
  const a = prefix.at(-1);
  const b = suffix[0];
  const joinedAcross = coalesceAcrossProgress(prefix, suffix, known, applied);
  if (joinedAcross) return joinedAcross;
  if (!a || !b || a.kind !== b.kind || (a.kind !== "text" && a.kind !== "thinking")) {
    return [...prefix, ...suffix];
  }
  const bb = b as typeof a;
  if (
    a.kind === "text" &&
    bb.kind === "text" &&
    a.messageId !== undefined &&
    bb.messageId !== undefined &&
    a.messageId !== bb.messageId
  ) {
    return [...prefix, ...suffix];
  }
  const ra = seqRangeOf(a);
  const rb = seqRangeOf(bb);
  if (!ra || !rb || !rowsBetweenApplied(applied, ra.max, rb.min)) return [...prefix, ...suffix];
  const keep = known.has(a.id) || !known.has(bb.id) ? a : bb;
  const joined = {
    ...withTextBody(
      a as StreamEntry & { kind: "text" },
      bodyOfChunks([...chunksOf(a), ...chunksOf(bb)]),
    ),
    id: keep.id,
    ...((a.ts ?? bb.ts) === undefined ? {} : { ts: a.ts ?? bb.ts }),
  };
  return [...prefix.slice(0, -1), joined, ...suffix.slice(1)];
}

// Text replaced by a shape rule (no seq to key on): the old chunk map would lie about it.
function withoutParts<E extends StreamEntry & { kind: "text" | "thinking" }>(
  e: E,
  content: string,
): E {
  const { parts: _parts, ...rest } = e;
  return { ...rest, content } as E;
}

// A text entry laid under the longer text of its message from elsewhere (the DM ledger,
// the text held before a re-read), which carries no row seqs: its rows stay as they are,
// and what the longer text has beyond them is kept as ledger text on the side it lies
// on, for the rows that spell it to take over as they arrive (#3238). Only called when
// `full` is a cut's completion (isCutOf).
function withLedgerText<E extends StreamEntry & { kind: "text" }>(e: E, full: string): E {
  const chunks = chunksOf(e);
  const body: TextChunk[] = full.endsWith(e.content)
    ? [{ text: full.slice(0, full.length - e.content.length), ledger: "head" }, ...chunks]
    : [...chunks, { text: full.slice(e.content.length), ledger: "tail" }];
  return withTextBody(e, bodyOfChunks(body));
}

// A held entry that carries ledger text, over the page's entry of the same rows: the
// page wins on the rows both hold, and the rows only the page has are taken in the way a
// merge takes them (takeRowIntoLedger) — below newest first, above oldest first — so a
// re-read and a reconcile of the same rows converge. undefined when no entry of the
// message on the page touches the held rows.
function restoreLedgerText(
  entries: readonly StreamEntry[],
  at: readonly number[],
  h: StreamEntry & { kind: "text" },
): StreamEntry[] | undefined {
  const held = seqRangeOf(h);
  if (!held) return undefined;
  const index = at.find((i) => {
    const r = seqRangeOf(entries[i]);
    return !!r && r.min <= held.max + 1 && r.max >= held.min - 1;
  });
  if (index === undefined) return undefined;
  const e = entries[index] as StreamEntry & { kind: "text" };
  const heldChunks = chunksOf(h);
  const heldSeqs = new Set(heldChunks.flatMap((c) => (c.seq === undefined ? [] : [c.seq])));
  const page = chunksOf(e).filter(
    (c): c is TextChunk & { seq: number } => c.seq !== undefined && !c.ledger,
  );
  let chunks: TextChunk[] = [...page.filter((c) => heldSeqs.has(c.seq)), ...heldChunks];
  const fresh = page.filter((c) => !heldSeqs.has(c.seq));
  const below = fresh.filter((c) => c.seq < held.min).toReversed();
  const above = fresh.filter((c) => c.seq >= held.min);
  for (const row of [...below, ...above]) {
    chunks = takeRowIntoLedger(chunks, row) ?? [...chunks, row];
  }
  const restored = withTextBody(e, bodyOfChunks(chunks));
  return entries.map((x, i) => (i === index ? restored : x));
}

// The live rows an unproven view keeps for replay once a page proves their life.
const PROVEN_LIVE_MAX_ROWS = 5_000;

function mergeOptimisticRows(
  entries: readonly StreamEntry[],
  optimisticRows: Array<StreamEntry & { kind: "user_message"; clientMessageId: string }>,
): readonly StreamEntry[] {
  let merged = entries;
  for (const optimistic of optimisticRows) {
    // A queued prompt waits for the turn on screen: it stays last, whatever its send time.
    const at =
      optimistic.ts === undefined || optimistic.queued
        ? -1
        : merged.findIndex((entry) => entry.ts !== undefined && entry.ts > optimistic.ts!);
    merged =
      at >= 0 ? [...merged.slice(0, at), optimistic, ...merged.slice(at)] : [...merged, optimistic];
  }
  return merged;
}

// Rows that landed last while the turn is over (and no page of the same read follows).
function landedLastAfterTurn(
  s: AgentStream,
  lastBefore: StreamEntry | undefined,
  touched: number,
  open: boolean,
): boolean {
  const last = s.entries.at(-1);
  if (open || touched === 0 || !last || last === lastBefore) return false;
  return last.kind !== "turn_boundary" && last.kind !== "user_message";
}

// ─── State Classes ────────────────────────────────────────────────

// A page proves the life its rows belong to; a rewind ends the life the frames named, and
// the next frame names the new one.
function adoptPageLife(s: AgentStream, life: string | undefined, mode: string | undefined): void {
  if (life !== undefined) {
    s.life = life;
    s.lifeUnproven = false;
    s.provenLive = undefined;
  }
  if (mode === "rewind") s.liveLife = undefined;
}

// What an agent page said about one work session (ws-unify B2).
export interface SessionFacts {
  agentId: string;
  tagged: boolean;
  agents?: readonly string[];
  // This agent's first row of the session: its history in the stream starts there.
  firstSeq?: number;
}

interface Projection {
  entries: readonly StreamEntry[];
  rev: number;
  settled: boolean;
  out: readonly StreamEntry[];
}

// What a hydrate page may say about itself (see hydrateFromTimeline).
interface HydrateMeta {
  hasOlder?: boolean;
  // olderProof(page): only then may the history count as fully loaded.
  olderProven?: boolean;
  olderCursor?: string | null;
  // A forward window from the read turn (newer-window.svelte.ts): the rows above its top are
  // paged in newer, so the page takes them off the screen on purpose.
  hasNewer?: boolean;
  maxSeq?: number;
  // "refresh": a normal re-read that outranks our own cached snapshot. "rewind":
  // a deliberate truncation, which is also the only mode that may DELETE rows this
  // page does not cover. One boolean used to mean both.
  mode?: "refresh" | "rewind";
  // "mirror": the relay's PG copy, served while the owning daemon was away.
  // "blind": unused here — the empty-page guard above already rejects it when
  // there's a populated view; kept for TimelineFetchClient type parity.
  source?: "mirror" | "blind";
  // "The work behind this stream is over" — see AgentStream.settled. Passed ONLY
  // by a surface that actually knows (SessionWorkView reads the work session's
  // own status). Omitting it leaves the flag untouched: a surface with no such
  // evidence (the agent DM page, which has no session row) neither asserts nor
  // clears it, so absence of evidence never becomes evidence either way.
  settled?: boolean;
  // These rows came off our own disk, not the server (coldPaintAgentTimeline).
  // Recorded on the stream as `hydratedFromCache` so the recovery retry and the
  // redundancy guard can tell an unverified paint from a confirmed one. Absent
  // (the normal case) means a server read, which CLEARS the mark.
  fromCache?: boolean;
  life?: string;
  // The page is not the whole history (knowledge/125); see AgentStream.historyIncomplete.
  incomplete?: boolean;
  // An empty page of a life the mirror retired the old one for (see lifeRetired).
  retired?: boolean;
  // The server's marks that it cut the page; see pageHoleIsServerCut.
  headPartial?: boolean;
  budgetCut?: boolean;
}

export class AgentStreamState {
  readonly streams = new TrackedStreamMap<AgentStream>();

  // A private instance (a page folded in isolation, the audit viewer) holds a copy of a
  // conversation under its real id: journalling or reporting it would describe a view
  // nobody sees, under the id of the one they do (U5).
  private readonly judge: WriteJudge;
  private readonly history: ConversationHistory;
  readonly outbox: PromptOutbox;
  // turn_without_visible_progress, once per turn; a private instance reports nothing (U5).
  readonly turnStalls: TurnProgressReports;
  // Throttles the transcript invariant reports; cleared with the conversations at sign-out.
  readonly invariants: TranscriptInvariantReporter;
  private readonly seating = new TurnPrompts(); // which prompts each live turn may answer
  // The module state clearAll resets with the streams (#3629).
  private readonly sync: SyncSession;

  constructor(
    opts: { journal?: boolean; sync?: SyncSession; invariants?: TranscriptInvariantReporter } = {},
  ) {
    this.sync = opts.sync ?? conversationSync;
    this.invariants =
      opts.invariants ?? new TranscriptInvariantReporter(undefined, (id) => this.reportSubject(id));
    this.judge = new WriteJudge(opts.journal !== false, (agentId) => ({
      ...this.reportSubject(agentId),
      ...this.entryReport(agentId),
    }));
    this.history = new ConversationHistory({
      streams: () => this.streams,
      ensure: (agentId) => this.ensure(agentId),
      judge: this.judge,
      generationOf: (agentId) => this.generationOf(agentId),
    });
    this.outbox = new PromptOutbox(this.history, this);
    this.turnStalls = new TurnProgressReports(
      opts.journal === false ? () => false : turnStallSender(this.invariants),
    );
  }

  // The prompt outbox (#3850): the store's public outbox API delegates to it.
  settlePromptVerdict(
    ...args: Parameters<PromptOutbox["settlePromptVerdict"]>
  ): ReturnType<PromptOutbox["settlePromptVerdict"]> {
    return this.outbox.settlePromptVerdict(...args);
  }

  getPromptPendingSince(
    ...args: Parameters<PromptOutbox["getPromptPendingSince"]>
  ): ReturnType<PromptOutbox["getPromptPendingSince"]> {
    return this.outbox.getPromptPendingSince(...args);
  }

  notePromptDispatched(
    ...args: Parameters<PromptOutbox["notePromptDispatched"]>
  ): ReturnType<PromptOutbox["notePromptDispatched"]> {
    return this.outbox.notePromptDispatched(...args);
  }

  addUserMessage(
    ...args: Parameters<PromptOutbox["addUserMessage"]>
  ): ReturnType<PromptOutbox["addUserMessage"]> {
    const id = this.outbox.addUserMessage(...args);
    // A bubble restored after the queue frame landed is labelled from that frame.
    if (args[7]) this.markListedQueued(typeof args[0] === "string" ? args[0] : args[0].agentId);
    return id;
  }

  retractOptimisticPrompt(
    ...args: Parameters<PromptOutbox["retractOptimisticPrompt"]>
  ): ReturnType<PromptOutbox["retractOptimisticPrompt"]> {
    return this.outbox.retractOptimisticPrompt(...args);
  }
  originalOfSessionCopy(
    ...args: Parameters<PromptOutbox["originalOfSessionCopy"]>
  ): ReturnType<PromptOutbox["originalOfSessionCopy"]> {
    return this.outbox.originalOfSessionCopy(...args);
  }

  setSendStatus(
    ...args: Parameters<PromptOutbox["setSendStatus"]>
  ): ReturnType<PromptOutbox["setSendStatus"]> {
    return this.outbox.setSendStatus(...args);
  }

  setPromptSourceMessageId(
    ...args: Parameters<PromptOutbox["setPromptSourceMessageId"]>
  ): ReturnType<PromptOutbox["setPromptSourceMessageId"]> {
    return this.outbox.setPromptSourceMessageId(...args);
  }

  markSent(...args: Parameters<PromptOutbox["markSent"]>): ReturnType<PromptOutbox["markSent"]> {
    return this.outbox.markSent(...args);
  }

  retryPrompt(
    ...args: Parameters<PromptOutbox["retryPrompt"]>
  ): ReturnType<PromptOutbox["retryPrompt"]> {
    return this.outbox.retryPrompt(...args);
  }

  // agentId → the agent's shared held queue, as the daemon last announced it (agent_prompt_queue).
  sharedQueues: Record<string, SharedHeldPrompt[]> = $state({});

  setSharedQueue(agentId: string, held: SharedHeldPrompt[]): void {
    this.sharedQueues = { ...this.sharedQueues, [agentId]: held };
    this.markListedQueued(agentId);
  }

  // A prompt the daemon lists is held, whether or not this client saw agent_prompt_queued (a
  // reload restores the bubble from the outbox without the flag, before or after the queue
  // frame). No-op for a bubble already marked or with no local entry.
  private markListedQueued(agentId: string): void {
    for (const item of this.getSharedQueue(agentId)) this.outbox.markQueued(agentId, item.promptId);
  }

  setQueuedText(...args: Parameters<PromptOutbox["setQueuedText"]>): void {
    this.outbox.setQueuedText(...args);
  }

  getSharedQueue(agentId: string): SharedHeldPrompt[] {
    return this.sharedQueues[agentId] ?? [];
  }

  // The agent whose shared queue lists one of these prompts (a DM row's client id is its prompt id).
  agentQueueing(promptIds: ReadonlySet<string>): string | undefined {
    for (const [agentId, held] of Object.entries(this.sharedQueues)) {
      if (held.some((item) => promptIds.has(item.promptId))) return agentId;
    }
    return undefined;
  }

  markQueued(
    ...args: Parameters<PromptOutbox["markQueued"]>
  ): ReturnType<PromptOutbox["markQueued"]> {
    return this.outbox.markQueued(...args);
  }

  dismissUnknownPrompt(
    ...args: Parameters<PromptOutbox["dismissUnknownPrompt"]>
  ): ReturnType<PromptOutbox["dismissUnknownPrompt"]> {
    return this.outbox.dismissUnknownPrompt(...args);
  }

  private record(agentId: string, writer: string, fields: Record<string, unknown>): void {
    this.judge.record(agentId, writer, fields);
  }

  // The client remains subscribed to several workspaces at once. Associate each
  // globally-unique agent id with its workspace so navigation changes the active
  // projection instead of destroying an in-flight conversation. A small LRU
  // bounds retained transcript memory; pending/running workspaces stay pinned.
  private readonly workspaceByAgent = new Map<string, string>();
  // agentId -> how it was last entered, and the conversations a notification asked to read.
  private readonly routeEntries = new Map<string, RouteEntry>();
  private readonly entryReads = new Set<string>();
  private readonly agentsByWorkspace = new Map<string, Set<string>>();
  private readonly workspaceRecency = new Map<string, number>();
  // Link parent+requestId -> subagentId so the daemon's REAL permission_resolved (which lands
  // on the parent id) clears the subagent card — not a "relay accepted" signal (Codex S2).
  private readonly subagentPermissionSource = new Map<string, string>();
  private static readonly SUBAGENT_PERMISSION_SOURCE_CAP = 500;
  // Set by app.svelte.ts to drop its answer-routing entry once the real resolution arrives.
  onSubagentPermissionResolved: ((subagentId: string, requestId: string) => void) | null = null;
  // Set by app.svelte.ts: a hydrate kept the rows on screen under a page that left a hole
  // between them; the committed rows strictly between `after` and `before` are missing.
  onTimelineHole:
    | ((agentId: string, hole: { after: number; before: number; cut?: boolean }) => void)
    | null = null;
  // Set by app.svelte.ts: the watchdog settled a turn the daemon ended unseen; read its rows back.
  onTurnEndUnseen: ((agentId: string) => void) | null = null;
  // Set by app.svelte.ts: this transcript is about to be wiped; what arrived before lands first.
  beforeTranscriptWipe: ((agentId: string) => void) | null = null;
  // Set by app.svelte.ts: a prompt the agent's computer refused while restarting. True
  // means it is held and will be re-sent; false (window spent) shows the error card.
  onPromptUndelivered: ((agentId: string, entryId: string) => boolean) | null = null;

  linkSubagentPermission(parentAgentId: string, requestId: string, subagentId: string): void {
    this.subagentPermissionSource.set(`${parentAgentId}::${requestId}`, subagentId);
    if (this.subagentPermissionSource.size <= AgentStreamState.SUBAGENT_PERMISSION_SOURCE_CAP) {
      return;
    }
    const oldest = this.subagentPermissionSource.keys().next();
    if (!oldest.done) this.subagentPermissionSource.delete(oldest.value);
  }

  linkedSubagent(parentAgentId: string, requestId: string): string | undefined {
    return this.subagentPermissionSource.get(`${parentAgentId}::${requestId}`);
  }

  // The parent's real permission_resolved arrived: clear the linked subagent card too and let
  // the caller drop its route. Returns true when a linked subagent card was resolved.
  private resolveLinkedSubagentPermission(parentAgentId: string, requestId: string): boolean {
    const linkKey = `${parentAgentId}::${requestId}`;
    const subagentId = this.subagentPermissionSource.get(linkKey);
    if (!subagentId) return false;
    this.subagentPermissionSource.delete(linkKey);
    this.resolvePendingLocally(subagentId, requestId);
    this.onSubagentPermissionResolved?.(subagentId, requestId);
    return true;
  }
  private workspaceClock = 0;
  private activeWorkspaceId: string | null = null;
  private static readonly RETAINED_WORKSPACES = 3;
  // Monotonic source for stable StreamEntry ids. Global (across agents) is fine —
  // ids only need to be unique within one agent's entry array; a fresh id per
  // newly-created entry, preserved through merges, guarantees that.
  private entrySeq = 0;
  // Per-agent memo for the curated "last activity" line (#594). Keyed on the
  // entries-array identity: `s.entries` is replaced (new reference) on every
  // mutation, so a cache hit means nothing changed → skip re-curation. This is
  // the throttle the AgentLastActivityCommitter provides in Paseo — here we
  // bound the work (cap the curation window) and reuse the last result instead
  // of re-walking the timeline on every streamed token.
  private lastActivityCache = new WeakMap<readonly StreamEntry[], CuratedActivity | null>();

  // Hard cap on a single agent's live stream entry array (RAM bound). A long
  // session (hours of streamed tokens + tool calls) otherwise grows `entries`
  // without limit — every append is an O(n) spread, and each StreamEntry pins
  // its text/detail in the renderer's keyed {#each}, so an uncapped stream is a
  // steady renderer-RAM leak (the "kills the Mac" path's cheaper sibling).
  // Mirrors ActivityState (cap 200): keep the most recent
  // MAX_ENTRIES, trimming to 80% on overflow so we don't reslice on every append
  // once the cap is hit. Reconnect/workspace-switch already resets to [] via
  // hydrate, so only live growth within one session matters here.
  private static readonly MAX_ENTRIES = VIEW_MAX_ENTRIES;
  // History read ON PURPOSE lands at the head, which is what this trim drops. ingestRow
  // exempts the read itself; nothing exempted the next LIVE row — measured 1562 entries /
  // 12 prompts, then 800 / 6 one row later, under the 10 just paged for. So a deliberate
  // read raises keepFloor and the trim honours it, to this ceiling: the bound on a
  // conversation that has been paged back through (a live-only stream still trims to 800).
  private static readonly MAX_ENTRIES_CEILING = 3000;

  // Trim s.entries to the cap in place (reassigns so runes observe it). A no-op
  // until the array exceeds MAX_ENTRIES. Trimming from the head drops the oldest
  // turns first — the visible "right now" tail is always preserved.
  capEntries(w: ConversationWrite, s: AgentStream): void {
    if (s.entries.length <= AgentStreamState.MAX_ENTRIES) return;
    const keep = Math.min(
      Math.max(Math.floor(AgentStreamState.MAX_ENTRIES * 0.8), s.keepFloor),
      AgentStreamState.MAX_ENTRIES_CEILING,
    );
    if (s.entries.length <= keep) return;
    const dropped = s.entries.length - keep;
    const agentId = this.agentOfStream.get(s);
    const trimmed = s.entries.slice(0, s.entries.length - keep);
    w.assign(s, s.entries.slice(-keep));
    if (!agentId) return;
    // Deliberate: journalled, never an alarm, and not the loss of a write it runs inside.
    const rows = screenRows(trimmed, seqsOf);
    this.judge.trimmed(agentId, rows);
    this.record(agentId, "cap", { dropped, keep, keepFloor: s.keepFloor, rows: rows.size });
  }

  // History just read on purpose must survive the next live row's trim.
  private raiseKeepFloor(s: AgentStream): void {
    this.reserveKeepFloor(s, s.entries.length);
  }

  private reserveForDeliberateRead(s: AgentStream, cap: boolean, incoming: number): void {
    if (!cap) this.reserveKeepFloor(s, s.entries.length + incoming);
  }

  // A live merge trims to the cap; a deliberate read keeps what it brought, like a prepend.
  private settleEntryCap(w: ConversationWrite, s: AgentStream, cap: boolean): void {
    if (cap) this.capEntries(w, s);
    else this.raiseKeepFloor(s);
  }

  private reserveKeepFloor(s: AgentStream, entries: number): void {
    s.keepFloor = Math.min(Math.max(s.keepFloor, entries), AgentStreamState.MAX_ENTRIES_CEILING);
  }

  // THE one way a durable row enters a stream — live, a reconcile page, a late frame.
  // A row applies once (by seq, #3248 C1) and lands at its seq: after the last entry
  // below it, splitting a text/thinking entry it falls inside (a tool the tab missed
  // in the middle of a reply, C2), through the same handleTimeline the live path uses,
  // then re-joined with what followed when the two halves of one message now meet.
  // `cap`: the entry cap trims the oldest — right for a live or reconciled tail, wrong for
  // a page the user scrolled up to (it would evict exactly what was just read).
  private ingestRow(
    w: ConversationWrite,
    agentId: string,
    row: AgentTimelineItem,
    seq: number,
    cap = true,
    turnId?: string,
  ): string[] {
    const s = this.ensure(agentId);
    if (s.applied.has(seq)) return [];
    s.applied.add(seq);
    const item = asDrawnPromptRow(row);
    if (!item) {
      this.touch(agentId);
      return [];
    }
    if (takeRowIntoLedgerText(w, s, item, seq)) {
      w.assign(s, joinSplitBubbles(s.entries, s.applied)); // a read-back row may close a gap
      this.touch(agentId);
      return [];
    }
    // CYBORG-1411: a leaked relive row leaves no entry AND no divider (seq stays applied).
    // Nor does the durable twin of a failure card the live turn_failed already drew.
    if (
      (item.type === "user_message" && isHiddenReliveRow(item)) ||
      this.pairsWithEventCard(w, s, item, { seq, ts: parseIsoTs(item.ts) })
    ) {
      this.touch(agentId);
      return [];
    }
    if (item.type === "user_message") {
      const text = stripDmPrefix(stripMentionEnvelope(item.text));
      if (this.absorbPromptEcho(w, s, agentId, item, text, seq, parseIsoTs(item.ts))) {
        w.assign(s, joinSplitBubbles(s.entries, s.applied));
        this.touch(agentId);
        return [];
      }
    }
    // A row that UPDATES an entity already on screen (a tool by callId, the compaction
    // in progress) is applied where that entity stands, on the whole list — never in
    // a prefix that hides it from the reducer (#3248 E1).
    if (updatesEntityOnScreen(s, item, seq)) {
      const prev = s.entries;
      this.handleTimeline(w, agentId, item, seq);
      stampCommittedSeq(w, s, seq, prev);
      this.repositionEntity(w, s, prev, item);
      w.assign(s, joinSplitBubbles(s.entries, s.applied)); // a late card row may close the hole between two halves
      this.touch(agentId);
      return [];
    }
    const at = this.splitPointForSeq(w, s, seq, item, this.seating.verdictFor(agentId, turnId));
    const suffix = s.entries.slice(at);
    w.assign(s, s.entries.slice(0, at));
    // Only a row landing at the TAIL belongs to the running turn: `openTurnSeq` is that
    // turn's end. A row placed mid-list (an older page, a hole read back) closes an OLDER
    // turn: its divider is plain and `openTurnSeq` is neither consumed nor advanced by it.
    const atTail = suffix.length === 0;
    const openTurnSeq = s.openTurnSeq;
    if (
      item.type === "user_message" &&
      s.entries.length > 0 &&
      s.entries.at(-1)?.kind !== "turn_boundary"
    ) {
      w.assign(s, [...s.entries, atTail ? this.closeTurn(s) : this.boundaryAt(undefined)]);
    }
    const prev = s.entries;
    const known = new Set([...prev, ...suffix].map((e) => e.id));
    this.handleTimeline(w, agentId, item, seq);
    if (!atTail) s.openTurnSeq = openTurnSeq;
    stampCommittedSeq(w, s, seq, prev);
    w.assign(s, coalesceSeam(s.entries, suffix, known, s.applied));
    if (cap) this.capEntries(w, s);
    this.touch(agentId);
    // The entries this row ADDED (a divider is scaffolding; an extended bubble keeps
    // its id and is not an insert).
    return s.entries.filter((e) => e.kind !== "turn_boundary" && !known.has(e.id)).map((e) => e.id);
  }

  // An entity sits where its FIRST row sat. A row older than the card's earliest moves
  // the card back to that row's place (#3248 G1a).
  private repositionEntity(
    w: ConversationWrite,
    s: AgentStream,
    prev: readonly StreamEntry[],
    item: AgentTimelineItem,
  ): void {
    const idx = s.entries.findIndex((e, i) => e !== prev[i]);
    if (idx < 0) return;
    const entity = s.entries[idx];
    const range = seqRangeOf(entity);
    const before = prev[idx] ? seqRangeOf(prev[idx]) : undefined;
    if (!range || !before || range.min >= before.min) return;
    w.assign(s, [...s.entries.slice(0, idx), ...s.entries.slice(idx + 1)]);
    const at = this.splitPointForSeq(w, s, range.min, item);
    w.assign(s, [...s.entries.slice(0, at), entity, ...s.entries.slice(at)]);
  }

  // Where a row with `seq` lands: after the last entry that lies entirely below it. An
  // entry it falls INSIDE (a fused bubble whose chunks span a hole this row fills) is
  // split there first. Past that anchor, entries without a seq are passed by time — a
  // chunk streamed before its row was committed, an optimistic prompt older than the
  // row — and a divider only by a prompt, which fences itself.
  private splitPointForSeq(
    w: ConversationWrite,
    s: AgentStream,
    seq: number,
    item: AgentTimelineItem,
    verdict?: PromptVerdict,
  ): number {
    let at = 0;
    for (let i = 0; i < s.entries.length; i += 1) {
      const e = s.entries[i];
      const range = seqRangeOf(e);
      if (!range) continue;
      const entity = e.kind !== "text" && e.kind !== "thinking";
      if (range.max < seq || (entity && range.min < seq)) {
        at = i + 1;
        continue;
      }
      if (range.min < seq && !entity) {
        const [head, tail] = splitTextAt(e, seq);
        w.assign(s, [
          ...s.entries.slice(0, i),
          head,
          { ...tail, id: this.nextEntryId() },
          ...s.entries.slice(i + 1),
        ]);
        return i + 1;
      }
      break;
    }
    const rowTs = parseIsoTs(item.ts) ?? Date.now();
    // A divider is passed by a row NEWER than every row on screen (the next turn, live or
    // read back into a tail with nothing after it); a row with a newer neighbour below is a
    // gap read back into a turn that ended, and stays above the divider.
    const newest = !s.entries.slice(at).some((e) => seqRangeOf(e));
    while (at < s.entries.length) {
      const e = s.entries[at];
      if (seqRangeOf(e) || !rowPassesUnsequenced(e, item, seq, rowTs, newest, verdict)) break;
      at += 1;
    }
    return at;
  }

  // A turn divider. `afterSeq` is the committed row the closed turn ends at (see
  // splitPointForSeq); it is a position, never a time, so no day pill and no seatByTs fence.
  private boundaryAt(afterSeq: number | undefined): StreamEntry {
    const id = this.nextEntryId();
    return afterSeq === undefined
      ? { id, kind: "turn_boundary" }
      : { id, kind: "turn_boundary", afterSeq };
  }

  // The divider that closes the turn at the tail of `s`: the newest committed row seq applied
  // since the last one (`openTurnSeq`, kept by handleTimeline), then reset.
  private closeTurn(s: AgentStream): StreamEntry {
    const afterSeq = s.openTurnSeq;
    s.openTurnSeq = undefined;
    return this.boundaryAt(afterSeq);
  }

  nextEntryId(): string {
    return `e${++this.entrySeq}`;
  }

  bindAgentWorkspace(agentId: string, workspaceId: string): void {
    const previous = this.workspaceByAgent.get(agentId);
    if (previous === workspaceId) {
      this.touchWorkspace(workspaceId);
      return;
    }
    if (previous) {
      const previousAgents = this.agentsByWorkspace.get(previous);
      previousAgents?.delete(agentId);
      if (previousAgents?.size === 0) this.agentsByWorkspace.delete(previous);
    }
    this.workspaceByAgent.set(agentId, workspaceId);
    const agents = this.agentsByWorkspace.get(workspaceId) ?? new Set<string>();
    agents.add(agentId);
    this.agentsByWorkspace.set(workspaceId, agents);
    this.touchWorkspace(workspaceId);
    this.evictInactiveWorkspaces();
  }

  workspaceForAgent(agentId: string): string | null {
    return this.workspaceByAgent.get(agentId) ?? null;
  }

  // L16: the agent and its workspace by name and id, its machine by id, for a report.
  reportSubject(agentId: string, daemonId?: string): ReportSubject {
    return {
      ...agentSubject(agentId, cyboState.list, daemonId),
      ...workspaceSubject(this.workspaceForAgent(agentId)),
    };
  }

  activateWorkspace(workspaceId: string): void {
    this.activeWorkspaceId = workspaceId;
    this.touchWorkspace(workspaceId);
    this.evictInactiveWorkspaces();
  }

  private touchWorkspace(workspaceId: string): void {
    this.workspaceRecency.set(workspaceId, ++this.workspaceClock);
  }

  private workspaceHasPendingWork(workspaceId: string): boolean {
    const agents = this.agentsByWorkspace.get(workspaceId);
    if (!agents) return false;
    for (const agentId of agents) {
      const stream = this.streams.get(agentId);
      // A conversation with a write open is pending work too: evicting it mid-write would
      // leave the workspace half-forgotten when its reset lease is refused (#3382 A-4a).
      if (this.history.isWriting(agentId)) return true;
      if (
        stream?.turnStatus === "running" ||
        stream?.pendingPermissions.size ||
        stream?.entries.some(
          (entry) =>
            entry.kind === "user_message" && (entry.status === "pending" || entry.queued === true),
        ) ||
        this.readBackOwed(agentId)
      ) {
        return true;
      }
    }
    return false;
  }

  // A reply still being read back: evicting it drops what the read exists to complete (T13).
  private readBackOwed(agentId: string): boolean {
    const recovery = this.transcriptRecovery(agentId);
    return (
      recovery === "reading" ||
      recovery === "pending" ||
      this.coverage.heldState(agentId) === "pending"
    );
  }

  private evictInactiveWorkspaces(): void {
    while (this.workspaceRecency.size > AgentStreamState.RETAINED_WORKSPACES) {
      const candidate = [...this.workspaceRecency.entries()]
        .filter(
          ([workspaceId]) =>
            workspaceId !== this.activeWorkspaceId && !this.workspaceHasPendingWork(workspaceId),
        )
        .sort((a, b) => a[1] - b[1])[0]?.[0];
      // Exceeding the soft cap temporarily is preferable to dropping user input.
      if (!candidate) return;
      // The live Set, not a copy: the check below relies on the late bind adding to this same object.
      const agents = this.agentsByWorkspace.get(candidate) ?? new Set<string>();
      // A copy: the flush below can bind a new agent to this workspace.
      const evicted = [...agents];
      for (const agentId of evicted) {
        this.beforeTranscriptWipe?.(agentId);
        this.forgetConversation(agentId, "evict");
      }
      if (agents.size > 0) {
        // It just received a frame: it stays for now, and the next bind evicts again.
        this.touch();
        return;
      }
      this.agentsByWorkspace.delete(candidate);
      this.workspaceRecency.delete(candidate);
      this.touch();
    }
  }

  // ─── Pending interactive tool-call survival across a client reload ──────
  //
  // A pending interactive tool call (a permission prompt, or an AskUserQuestion)
  // lives ONLY in `pendingPermissions`, fed exclusively by live
  // `permission_requested` events. It is NOT part of the persisted server
  // timeline — fetch_agent_timeline / list_agents / fetch_agent_state carry no
  // pending-permission payload on the cyborg wire — so a FULL PAGE RELOAD (most
  // notably the desktop auto-update restart) wipes the in-memory map while the
  // DAEMON stays blocked on canUseTool awaiting the answer. The tool call then
  // rehydrates from the timeline as a stuck "(running)" row with no answerable
  // card, and the only recovery was to restart the session.
  //
  // Mirror the map into localStorage so hydrateFromTimeline can restore the
  // answerable card after a reload; answering it then unblocks the still-waiting
  // daemon (a separate process that survives the client restart). A WS RECONNECT
  // keeps the live map intact (hydrate never clears it), so this only ever matters
  // for a cold load. TTL-bounded so an ancient ghost (daemon long gone) can't
  // resurrect forever.
  private static readonly PENDING_PERM_STORE_PREFIX = "cyborg7.pendingPerms.";
  private static readonly PENDING_PERM_TTL_MS = 24 * 60 * 60 * 1000;

  private pendingPermStoreKey(agentId: string): string {
    return `${AgentStreamState.PENDING_PERM_STORE_PREFIX}${agentId}`;
  }

  // OWNER-KEYED on top of the device-wide sweep (cache/local-owner.ts), because
  // this is the one persisted payload that comes back as an ACTIONABLE CONTROL:
  // an Allow/Deny card for a shell command, on a daemon still blocked awaiting
  // the answer. The sweep runs at login and is enough for data; a control that
  // executes wants a second, independent guard that fails CLOSED if the sweep
  // never ran (storage error, a build predating it, a hand-edited entry).
  //
  // The value is sealed with the hashed viewer segment (sealOwned), never the address, and read
  // back live off `authState.user` (set in connectToServer, nulled by clearLocal), so there is
  // no parallel owner binding to forget to wire. A value of another viewer, or in an older
  // format, is a miss.
  private persistPendingPermissions(agentId: string, s: AgentStream): void {
    if (typeof localStorage === "undefined") return;
    const key = this.pendingPermStoreKey(agentId);
    try {
      if (s.pendingPermissions.size === 0) {
        localStorage.removeItem(key);
        return;
      }
      // No owner ⇒ we cannot prove whose approval card this is, so we never write one.
      const sealed = sealOwned(
        authState.user?.email,
        JSON.stringify({ savedAt: Date.now(), requests: [...s.pendingPermissions.values()] }),
      );
      if (sealed !== undefined) localStorage.setItem(key, sealed);
    } catch {
      // intentional: best-effort mirror so a pending prompt survives a reload; not user-facing.
    }
  }

  private restorePendingPermissions(agentId: string, s: AgentStream): void {
    if (typeof localStorage === "undefined") return;
    // Never clobber a still-live map (a WS reconnect hydrate where the in-memory
    // pending permissions are current) — only a cold load starts empty.
    if (s.pendingPermissions.size > 0) return;
    const key = this.pendingPermStoreKey(agentId);
    try {
      // A card written by a DIFFERENT account, by a build that stored the raw email as owner, or
      // before any envelope existed, is a MISS, never a render. Left on disk rather than
      // deleted: deletion is the sweep's job. The daemon still holds the real prompt, so its
      // true owner sees the card again on their own next load.
      const opened = openOwned(authState.user?.email, localStorage.getItem(key));
      if (!opened) return;
      const parsed = JSON.parse(opened) as {
        savedAt?: number;
        requests?: AgentPermissionRequest[];
      };
      const stale =
        typeof parsed.savedAt === "number" &&
        Date.now() - parsed.savedAt > AgentStreamState.PENDING_PERM_TTL_MS;
      if (stale || !parsed.requests || parsed.requests.length === 0) {
        localStorage.removeItem(key);
        return;
      }
      const restored = new Map<string, AgentPermissionRequest>();
      for (const req of parsed.requests) {
        if (req && typeof req.id === "string") restored.set(req.id, req);
      }
      if (restored.size > 0) s.pendingPermissions = restored;
    } catch {
      // intentional: best-effort restore of a pending prompt across reload; not user-facing.
    }
  }

  private clearPersistedPendingPermissions(agentId: string): void {
    if (typeof localStorage === "undefined") return;
    try {
      localStorage.removeItem(this.pendingPermStoreKey(agentId));
    } catch {
      // intentional: best-effort cleanup of the persisted pending prompt; not user-facing.
    }
  }

  // CYBORG-1015 — prompt ids the SERVER has already shown us a user_message row for.
  //
  // Not scoped per agent on purpose: a promptId is a crypto.randomUUID() minted by this
  // client for exactly one press (AgentComposer.handleSendNowQueued), so it names one
  // prompt globally and two agents can never collide on one. Kept OUT of the transcript —
  // see the note at the recording site for the hydrate defect that stamping the entry
  // itself reintroduces.
  //
  // Bounded so a long-lived session cannot grow it without limit. It takes an id from
  // EVERY server user_message carrying one — not just send-now — and a hydrate replays a
  // whole page of them, so it does turn over. What makes eviction harmless is the WINDOW,
  // not the volume: an id only has to survive from the echo that recorded it to that same
  // prompt's verdict, so losing one early would need 500 others inside that gap.
  readonly serverEchoedPromptIds = new Set<string>();

  // A tool card's FIRST row time, by agent then callId (CYBORG-1312). A side table, not a StreamEntry field: the
  // view shape is pinned by the live-differential golden. peer-message's join guard reads it via cardTs().
  private readonly cardFirstTs = new Map<string, Map<string, number>>();
  // B4: per stream, one timer for the earliest deadline, and each spared call's own deadline (ms).
  private readonly longLivedTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly longLivedDeadlines = new Map<string, Map<string, number>>();
  private readonly deferredCleanSweeps = new Set<string>();
  cardTs(agentId: string, callId: string): number | undefined {
    return this.cardFirstTs.get(agentId)?.get(callId);
  }
  // The row time a card first reached a terminal status: with cardTs, the step's duration (work-segments.ts stepDuration).
  private readonly cardEndTs = new Map<string, Map<string, number>>();
  cardEndedAt(agentId: string, callId: string): number | undefined {
    return this.cardEndTs.get(agentId)?.get(callId);
  }
  private static readonly SERVER_ECHOED_PROMPT_ID_CAP = 500;

  private noteServerEchoedPrompt(clientMessageId: string | undefined): void {
    if (!clientMessageId) return;
    this.serverEchoedPromptIds.add(clientMessageId);
    if (this.serverEchoedPromptIds.size <= AgentStreamState.SERVER_ECHOED_PROMPT_ID_CAP) return;
    const oldest = this.serverEchoedPromptIds.values().next();
    if (!oldest.done) this.serverEchoedPromptIds.delete(oldest.value);
  }

  // clientMessageId -> the settled bubble it reconciled onto, so a SECOND echo of the same
  // turn (the PI/cybo runtime emits it twice, knowledge/114) collapses onto that bubble
  // instead of appending a duplicate. Bounded; a stale id is harmless (cmid is single-use).
  private readonly reconciledPromptEntryIds = new Map<string, string>();
  private static readonly RECONCILED_PROMPT_ENTRY_ID_CAP = 500;

  // The server has shown a row for this prompt id: a saved copy of it is already delivered.
  isPromptServerConfirmed(clientMessageId: string): boolean {
    return (
      this.serverEchoedPromptIds.has(clientMessageId) ||
      this.reconciledPromptEntryIds.has(clientMessageId)
    );
  }

  private noteReconciledPromptEntry(clientMessageId: string, entryId: string): void {
    this.reconciledPromptEntryIds.set(clientMessageId, entryId);
    if (this.reconciledPromptEntryIds.size <= AgentStreamState.RECONCILED_PROMPT_ENTRY_ID_CAP) {
      return;
    }
    const oldest = this.reconciledPromptEntryIds.keys().next();
    if (!oldest.done) this.reconciledPromptEntryIds.delete(oldest.value);
  }

  // A soft stop was sent and is awaiting its verdict (see settlePromptVerdict).
  readonly softStopPrompts = new Map<string, { agentId: string; note: string }>();
  // Agents whose soft stop came back "unavailable" — the composer reads this to offer the
  // hard stop rather than taking it silently.
  // $state: the composer reads this straight in its markup (the hard-stop dialog's
  // `open`), so a plain Set would never re-render.
  softStopRefusals = $state<Set<string>>(new Set());

  noteSoftStopDispatched(agentId: string, promptId: string, note: string): void {
    this.softStopPrompts.set(promptId, { agentId, note });
    this.clearSoftStopRefusal(agentId);
  }

  softStopWasRefused(agentId: string): boolean {
    return this.softStopRefusals.has(agentId);
  }

  clearSoftStopRefusal(agentId: string): void {
    if (!this.softStopRefusals.has(agentId)) return;
    const next = new Set(this.softStopRefusals);
    next.delete(agentId);
    this.softStopRefusals = next;
  }

  // ─── Soft cancel (CYBORG-884) ───────────────────────────────────────────
  //
  // Stop with running subagents does NOT interrupt: interrupt() kills every running
  // subagent (measured live 2026-08-24) and no provider primitive spares them. Instead
  // the live turn is STEERED with a stop instruction and the composer is freed here,
  // client-side — the parent winds itself down, its delegated work keeps running, and
  // its current tool call may still finish.
  //
  // The turn is therefore still alive on the daemon, which is why this flag exists: the
  // next thing the user sends must JOIN that turn (activeTurnBehavior "steer") instead of
  // bouncing off it with "already has an active run". Cleared by any real terminal event
  // for the agent (handleEvent), so it can never outlive the turn it describes.
  private readonly softCanceled = new Set<string>();

  isSoftCanceled(agentId: string): boolean {
    return this.softCanceled.has(agentId);
  }

  softCancelTurn(target: ConversationTarget, note: string): void {
    return this.history.within(target, "turn", (w) => {
      const agentId = w.agentId;
      const s = this.ensure(agentId);
      this.softCanceled.add(agentId);
      w.assign(s, [...s.entries, statusCard(this.nextEntryId(), note, s.entries)]);
      this.capEntries(w, s);
      s.turnStatus = "idle";
      this.finalizeTurn(w, agentId, s);
      this.clearTurnTimeout(agentId);
      this.touch(w.agentId);
    });
  }

  ensure(agentId: string): AgentStream {
    let s = this.streams.get(agentId);
    if (!s) {
      s = {
        entries: [],
        keepFloor: 0,
        applied: new Set(),
        turnStatus: "idle",
        usage: null,
        pendingPermissions: new Map(),
        currentModel: null,
        currentModeId: null,
        availableModes: [],
        thinkingOptionId: null,
        thinkingReported: false,
        controlVersions: { model: 0, mode: 0, thinking: 0 },
        runtimeInfo: null,
        hydrated: false,
        hydratedFromCache: false,
        hydratedFromMirror: false,
        lifeUnproven: false,
        historyIncomplete: false,
        timelineFetchFailed: false,
        timelineReadDenied: false,
        timelineGone: false,
        hasOlder: false,
        olderCursor: null,
        loadingOlder: false,
        olderLoadBackoffUntil: 0,
        olderLoadFailures: 0,
        recovery: "idle",
        recoveryCursor: null,
        recoveryFailures: 0,
        turnEndReadAgains: 0,
        headCut: null,
        hydrationNonce: 0,
        lastMaxSeq: null,
        settled: false,
        stickyCards: new Map(),
        stickyFiles: new Map(),
      };
      this.streams.set(agentId, s);
      this.agentOfStream.set(s, agentId);
    }
    return s;
  }

  // null: a ledger row stored with no files.
  private noteReplyFiles(s: AgentStream, messageId: string, files?: Attachment[] | null): void {
    if (!messageId || !files?.length || s.stickyFiles.has(messageId)) return;
    s.stickyFiles.set(messageId, files);
    this.touch(this.agentOfStream.get(s));
  }

  // The files of the reply `entries[idx]` ends: drawn once, under its last text entry.
  replyFiles(agentId: string, entries: readonly StreamEntry[], idx: number): Attachment[] {
    const entry = entries[idx];
    const files = this.streams.get(agentId)?.stickyFiles;
    if (!files?.size || entry?.kind !== "text" || !entry.messageId) return [];
    const ids = [entry.messageId, ...(entry.aliasIds ?? [])];
    const same = (e: StreamEntry) =>
      e.kind === "text" && !!e.messageId && ids.includes(e.messageId);
    if (entries.slice(idx + 1).some(same)) return [];
    return ids.map((id) => files.get(id)).find((f) => f !== undefined) ?? [];
  }

  // Re-runs the readers of one conversation; with no id (a store-wide change), of all of them.
  touch(agentId?: string): void {
    this.streams.notify(agentId);
    if (this.startWaiters.size > 0) this.releaseStartWaiters(agentId);
  }

  // The earliest time of the session's OWN rows (timeline, not ledger-built): where it starts.
  ownStartTs(agentId: string): number | undefined {
    let start: number | undefined;
    for (const e of this.getEntries(agentId)) {
      if ((e.kind !== "text" && e.kind !== "user_message") || e.ts === undefined) continue;
      if (this.ledgerEntryIds.has(e.id)) continue;
      if (start === undefined || e.ts < start) start = e.ts;
    }
    return start;
  }

  // A ledger merge that ran before the session had a start could not place the person's earlier
  // rows (End session, then the next message: the open beats the daemon's first append). It runs
  // again once the session's first own row lands.
  private readonly startWaiters = new Map<string, () => void>();
  whenSessionStarts(agentId: string, run: () => void): void {
    this.startWaiters.set(agentId, run);
  }
  private releaseStartWaiters(agentId: string | undefined): void {
    for (const [id, run] of this.startWaiters) {
      if (agentId !== undefined && id !== agentId) continue;
      if (this.ownStartTs(id) === undefined) continue;
      this.startWaiters.delete(id);
      queueMicrotask(run);
    }
  }

  // Every touch inside `run` lands once, when it ends (a batch of live frames).
  batchTouches(run: () => void): void {
    this.streams.batch(run);
  }

  private turnTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
  // agentId -> how many times its transcript was cleared or rewound. A read that started
  // before a clear must not land in the transcript built after it (#3242, #3248 B4).
  private generations = new Map<string, number>();
  // How many times clearAll() reset everything. Part of every generation, so a read begun
  // for an id with no stream yet is stale after a reset too (audit #3373 F3).
  private resetEpoch = 0;
  // Committed rows known missing between two on-screen rows that a read-back could not
  // bring back (it failed, or its budget ran out): named so the screen says so and the
  // next recovery trigger reads them again, instead of a silent hole (#3338 follow-up).
  // A conversation's coverage record: what is known missing and why its recovery may not
  // read idle. One record per agent, forgotten whole (forgetCoverage), so a reset cannot
  // leave a field behind. The generation and debt revisions stay outside: they are monotonic.
  private coverage = new CoverageBook();
  // agentId -> the daemon a read named when it opened the conversation (an ephemeral session
  // needs it): recovery, read-back, adoption and rewind reads route the same way.
  private routes = new Map<string, string>();
  // Which conversation a stream object is, for writers that only hold the stream (the cap).
  private agentOfStream = new WeakMap<AgentStream, string>();
  // agentId -> the retry armed for a failed older-page fetch (#3248 R4).
  private olderLoadRetryTimers = new Map<string, ReturnType<typeof setTimeout>>();
  // agentId -> the retry armed for a failed transcript-recovery page (#3248 C4).
  private recoveryRetryTimers = new Map<string, ReturnType<typeof setTimeout>>();

  private clearTurnTimeout(agentId: string): void {
    const t = this.turnTimeouts.get(agentId);
    if (t) {
      clearTimeout(t);
      this.turnTimeouts.delete(agentId);
    }
  }

  // A session projection sees only turn boundaries, so its watchdog asks the session's status.
  private projectionLiveness = new Map<string, () => boolean | undefined>();

  watchProjectionLiveness(key: string, live: () => boolean | undefined): () => void {
    this.projectionLiveness.set(key, live);
    return () => {
      if (this.projectionLiveness.get(key) === live) this.projectionLiveness.delete(key);
    };
  }

  /** Ends a projection's running turn that the session's status already ended. True when it did. */
  settleProjectedTurn(key: string, cause: "status" | "watchdog"): boolean {
    const s = this.streams.get(key);
    if (!isSessionStreamKey(key) || !s || s.turnStatus !== "running") return false;
    this.handleEvent(key, { type: "turn_completed", provider: "" }); // the handler reads no provider
    const workspaceId = this.workspaceForAgent(key);
    if (workspaceId) reportKpi("session_projection.turn_end_inferred", { cause, workspaceId });
    return true;
  }

  private startTurnTimeout(agentId: string, spared = 0): void {
    this.clearTurnTimeout(agentId);
    this.turnTimeouts.set(
      agentId,
      setTimeout(() => {
        const s = this.streams.get(agentId);
        if (!s || s.turnStatus !== "running") return;
        if (isSessionStreamKey(agentId)) {
          const live = this.projectionLiveness.get(agentId)?.();
          if (live) this.startTurnTimeout(agentId);
          else if (live === false) this.settleProjectedTurn(agentId, "watchdog");
          return; // undefined: no body to ask; the next mount settles it from the status
        }
        const facts = idleTurnFacts(
          agentId,
          s.entries,
          spared,
          (id) => daemonState.isKnownOffline(id),
          (id) => daemonState.isReconnecting(id, Date.now()),
        );
        if (this.spareIdleTurn(agentId, facts)) return;
        this.timeOutTurn(agentId, s, facts);
      }, TURN_IDLE_TIMEOUT_MS),
    );
  }

  private timeOutTurn(agentId: string, s: AgentStream, facts: IdleTurnFacts): void {
    // After its guard, the watchdog writes under its own lease; it never holds one.
    this.history.lease(agentId, "turn", (w) => {
      s.turnStatus = "error";
      w.assign(s, [
        ...s.entries,
        statusCard(this.nextEntryId(), "Turn timed out — no response from agent", s.entries),
      ]);
      this.capEntries(w, s);
      // A terminal path like any other: settle the turn's in-flight tool cards.
      this.finalizeTurn(w, agentId, s);
      this.touch(w.agentId);
      return { value: undefined };
    });
    // Transient only with redial proof (a drop this client saw, inside the reconnect window); all else is a fault.
    const reason = silenceReason(facts);
    reportUiFailure(
      "agents.turn_idle_timeout",
      new Error("turn timed out: no response from agent"),
      { ...this.reportSubject(agentId), reason },
      {
        code: "turn_idle_timeout",
        failureKind: reason === "daemon_reconnecting" ? "transient" : "fault",
      },
    );
  }

  // A silent window is not a dead agent while the daemon vouches for it (turn-liveness.ts).
  private spareIdleTurn(agentId: string, facts: IdleTurnFacts): boolean {
    const spared = facts.spared;
    const verdict = judgeIdleTurn(facts);
    if (verdict === "silent") return false;
    const workspaceId = this.workspaceForAgent(agentId);
    if (workspaceId) {
      reportKpi("agents.turn_watchdog_spared", { verdict, windows: spared + 1, workspaceId });
    }
    if (verdict === "alive") this.startTurnTimeout(agentId, spared + 1);
    else if (this.reconcileTurnWithRoster(agentId, facts.daemonLifecycle))
      this.onTurnEndUnseen?.(agentId);
    return true;
  }

  // Re-arm the idle watchdog because a RUNNING turn just showed proof of life
  // (any event it emits — mirrors the server's runAgentWithIdleTimeout, which
  // resets on every subscribed event). Pending-permission edge case: while a
  // permission request is outstanding the turn is waiting on the human, not hung,
  // so SUSPEND the watchdog (leave it cleared) and let the resolution re-arm it.
  // No-op unless the turn is running (a terminal turn has already cleared it).
  // When this client last saw a RUNNING turn emit anything for this agent. The
  // one signal that separates "hard at work" from "wedged" without consulting a
  // flag: it can only be set by an event actually arriving. Drives the queue
  // banner's copy and the backstop's delay. Plain Map (not $state) — its readers
  // are already re-evaluated by a 1s tick, so a signal would buy nothing.
  private turnActivityAt = new Map<string, number>();

  lastTurnActivity(agentId: string): number | null {
    return this.turnActivityAt.get(agentId) ?? null;
  }

  private noteTurnActivity(agentId: string): void {
    const s = this.streams.get(agentId);
    if (!s || s.turnStatus !== "running") return;
    this.turnActivityAt.set(agentId, Date.now());
    if (s.pendingPermissions.size > 0) {
      this.clearTurnTimeout(agentId);
      return;
    }
    this.startTurnTimeout(agentId);
  }

  /**
   * The daemon's roster push says this agent is RUNNING — the run was accepted,
   * so the pre-turn wait is over even though no stream event has arrived yet.
   *
   * This is what keeps a legitimately slow cold provider spawn (tens of seconds
   * before the first stream event) from tripping the watchdog: `agent_status`
   * lands the moment the run starts, and the route's own `isRunning` already
   * ORs the roster lifecycle, so the thinking indicator stays up. An idle status
   * ends only a wait whose prompt was already echoed: before the echo, the prompt
   * may have gone nowhere.
   */
  noteRosterRunning(agentId: string, lifecycle: string | null | undefined): void {
    if (lifecycle === "running") this.outbox.clearPromptPending(agentId);
    // Idle AFTER a slash command's echo: it ran out of band and settled with no turn.
    else if (lifecycle === "idle") this.outbox.settleIdleAfterEcho(agentId);
  }

  // The relay's not-run verdict: no turn is coming, so the pre-turn wait (and its watchdog) ends.
  noteNotRun(agentId: string): void {
    this.outbox.clearPromptPending(agentId);
  }

  // A clean turn_completed (or idle roster frame) keeps what outlivesTurn names: Codex emits its plan approval right
  // before it, and the daemon holds that (and an async question) past the turn until permission_resolved. Every other end (failed,
  // canceled, timeout, non-idle roster) may mean the daemon is gone, so it drops everything.
  private finalizeTurn(
    w: ConversationWrite,
    agentId: string,
    s: AgentStream,
    completed = false,
    turnEnd?: TurnEnd,
  ): void {
    const kept = completed ? [...s.pendingPermissions].filter(([, r]) => outlivesTurn(r)) : [];
    s.pendingPermissions = new Map(kept);
    if (kept.length > 0) this.persistPendingPermissions(agentId, s);
    else this.clearPersistedPendingPermissions(agentId);
    if (!completed) {
      this.forgetLongLived(agentId);
      this.settleInFlightToolCalls(w);
    } else if (turnEnd && this.rowsOwedAt(agentId, s, turnEnd)) {
      this.deferredCleanSweeps.add(agentId);
    } else {
      this.cleanEndSweep(w, agentId);
    }
  }

  // B4: a clean end leaves a long-lived call running until a deadline (or an earlier end/row/reset).
  private cleanEndSweep(w: ConversationTarget, agentId: string): void {
    this.settleInFlightToolCalls(w, (e) => outlivesCleanTurnEnd(e));
    const s = this.streams.get(agentId);
    const deadline = Date.now() + LONG_LIVED_SETTLE_MS;
    this.armLongLived(agentId, openCallIds(s?.entries ?? [], outlivesCleanTurnEnd).map((id) => [id, deadline])); // prettier-ignore
  }

  // A call keeps the deadline it got first: a later clean end never pushes it back (B4, audit M1).
  private armLongLived(agentId: string, calls: ReadonlyArray<readonly [string, number]>): void {
    const deadlines = this.longLivedDeadlines.get(agentId) ?? new Map<string, number>();
    const fresh = calls.filter(([callId]) => !deadlines.has(callId));
    if (fresh.length === 0) return;
    for (const [callId, at] of fresh) deadlines.set(callId, at);
    this.longLivedDeadlines.set(agentId, deadlines);
    this.timeLongLived(agentId, deadlines);
  }

  private timeLongLived(agentId: string, deadlines: ReadonlyMap<string, number>): void {
    clearTimeout(this.longLivedTimers.get(agentId));
    const wait = Math.max(0, Math.min(...deadlines.values()) - Date.now());
    this.longLivedTimers.set(
      agentId,
      setTimeout(() => this.settleLongLived(agentId), wait),
    );
  }

  // Sweeps only spared calls past their deadline; reports only if the stream has not reported yet.
  private settleLongLived(agentId: string): void {
    this.longLivedTimers.delete(agentId);
    const deadlines = this.longLivedDeadlines.get(agentId);
    if (!deadlines || !this.streams.has(agentId)) return this.forgetLongLived(agentId);
    const now = Date.now();
    const due = new Set([...deadlines].filter(([, at]) => at <= now).map(([callId]) => callId));
    for (const callId of due) deadlines.delete(callId);
    if (deadlines.size > 0) this.timeLongLived(agentId, deadlines);
    else this.longLivedDeadlines.delete(agentId);
    this.history.lease(agentId, "turn", (w) => {
      this.settleInFlightToolCalls(w, (e) => !due.has(e.callId));
      return { value: undefined };
    });
  }

  private forgetLongLived(agentId: string): void {
    clearTimeout(this.longLivedTimers.get(agentId));
    this.longLivedTimers.delete(agentId);
    this.longLivedDeadlines.delete(agentId);
    this.deferredCleanSweeps.delete(agentId);
  }

  private rowsOwedAt(agentId: string, s: AgentStream, end: TurnEnd): boolean {
    const held = s.life ?? s.liveLife;
    if (held !== undefined && held !== end.life) return false;
    return this.highestAppliedSeq(agentId) < end.lastSeq;
  }

  private runDeferredCleanSweep(agentId: string): void {
    if (!this.deferredCleanSweeps.delete(agentId) || !this.streams.has(agentId)) return;
    this.history.lease(agentId, "turn", (w) => {
      this.cleanEndSweep(w, agentId);
      return { value: undefined };
    });
  }

  /** The read-back of an owed turn end gave up: the deferred sweep runs once, reported (B4, M2). */
  giveUpTurnEnd(agentId: string): void {
    this.runDeferredCleanSweep(agentId);
  }

  // Drop carried plans, settling each row: resolveOrphanedToolCards skipped it while pending.
  private dropCarriedPlanRequests(w: ConversationWrite, agentId: string, s: AgentStream): void {
    const dropped = [...s.pendingPermissions].filter(([, r]) => isCarriedPlan(r));
    if (dropped.length === 0) return;
    for (const [id] of dropped) {
      s.pendingPermissions.delete(id);
      this.settlePlanRow(w, s, id, "canceled");
    }
    this.persistPendingPermissions(agentId, s);
  }

  // An offline daemon took its carried plans with it.
  dropCarriedPlans(agentId: string): void {
    const s = this.streams.get(agentId);
    if (!s || ![...s.pendingPermissions.values()].some(isCarriedPlan)) return;
    this.history.lease(agentId, "turn", (w) => {
      this.dropCarriedPlanRequests(w, agentId, s);
      this.touch(w.agentId);
      return { value: undefined };
    });
  }

  private dropSettledPlanRequests(agentId: string, s: AgentStream): void {
    const settled = [...s.pendingPermissions].filter(
      ([id, r]) =>
        isCarriedPlan(r) &&
        s.entries.some(
          (e) => e.kind === "tool_call" && e.callId === id && isTerminalToolCallStatus(e.status),
        ),
    );
    if (settled.length === 0) return;
    for (const [id] of settled) s.pendingPermissions.delete(id);
    this.persistPendingPermissions(agentId, s);
  }

  // `completed` when answered: a forced `canceled` would stick over the daemon's later row.
  private settlePlanRow(
    w: ConversationWrite,
    s: AgentStream,
    callId: string,
    status: "completed" | "canceled",
  ): void {
    const idx = s.entries.findIndex((e) => e.kind === "tool_call" && e.callId === callId);
    const row = s.entries[idx];
    if (row?.kind !== "tool_call" || isTerminalToolCallStatus(row.status)) return;
    const next = s.entries.slice();
    next[idx] = { ...row, status, error: null };
    w.assign(s, next);
  }

  // A TURN THAT DIES MUST NOT LEAVE CARDS IN FLIGHT.
  //
  // The status lattice (mergeToolCallStatus) makes a terminal status final, but it
  // cannot help a call that never reported one at all — and those read RUNNING
  // forever too. The provider is supposed to close them (Claude's
  // flushPendingToolCalls emits `canceled` for every started tool at turn end), but
  // it only knows about calls still in its toolUseCache: a background `Agent` whose
  // entry was already deleted by handleToolResult gets no terminal row, and a turn
  // that dies with the daemon emits nothing at all.
  //
  // finalizeTurn is the seam every LIVE terminal turn passes through —
  // turn_completed, turn_failed (a provider usage limit lands here), turn_canceled
  // (interrupt), the idle watchdog (startTurnTimeout, the "no response from agent"
  // path), and reconcileTurnWithRoster, which is the daemon-restart case seen by a
  // connected client. This is the surface with NO work session behind it — the agent
  // DM page — where `settled` can never be sourced, so getEntries' read-side
  // resolution has nothing to go on and the cards have to be rewritten in place.
  // A work session gets both, and they agree.
  //
  // Deliberately does NOT set `s.settled`: this rewrites the cards it can see and
  // stops there. Setting the flag would hand every LATER row on this stream to the
  // read-side resolution too, and the turn-end signals that reach here are not all
  // trustworthy — a spurious roster "idle" (app.svelte's agent_status handler calls
  // reconcileTurnWithRoster on every push) or a shared live agent's foreign
  // turn_completed mirrored in by SessionWorkView would then cancel genuinely live
  // rows. `settled` stays sourced from the work session's own status.
  //
  // A forced `canceled` carries `swept`: it is a guess, so the call's later real row
  // replaces it (handleTimeline). B4: a clean end (turn_completed, roster idle) spares a call
  // with its own lifecycle (outlivesCleanTurnEnd) until a deadline, and waits for owed rows;
  // safe even for a spurious "clean" end, since it only sweeps less. Unclean ends sweep all.
  //
  // The forced transition is REPORTED, never silent (knowledge/81), but ONCE PER
  // STREAM: /api/cyborg/client-log is rate-limited to 30 requests/60s per user and
  // every re-hydrate re-materialises these rows, so an unguarded beacon would spend
  // the shared budget on a defect we have already named — and could drop a genuine
  // window.onerror after a bad deploy.
  settleInFlightToolCalls(
    target: ConversationTarget,
    spare?: (entry: Extract<StreamEntry, { kind: "tool_call" }>) => boolean,
  ): void {
    return this.history.within(target, "turn", (w) => {
      const agentId = w.agentId;
      const s = this.streams.get(agentId);
      if (!s) return;
      const { entries, stuck } = resolveOrphanedToolCards(s.entries, s.pendingPermissions, spare);
      if (stuck.length === 0) return;
      w.assign(s, entries);
      this.touch(w.agentId);
      this.reportStuckToolCalls(agentId, stuck);
    });
  }

  private reportStuckToolCalls(agentId: string, stuck: string[]): void {
    if (stuck.length === 0 || this.reportedStuckToolCalls.has(agentId)) return;
    this.reportedStuckToolCalls.add(agentId);
    reportUiFailure(
      "AgentStreamState.settleInFlightToolCalls",
      // Stable text so every occurrence shares one fingerprint; the detail rides as properties.
      "turn ended with non-terminal tool calls — settled to canceled",
      { agentId, stuckCount: stuck.length, toolCallIds: stuck.join(",") },
      { failureKind: "recovered", code: "stuck_tool_calls" },
    );
  }

  // One report per stream per page load — see settleInFlightToolCalls' rate-limit note.
  private readonly reportedStuckToolCalls = new Set<string>();

  // Measures how often the sweep guessed wrong; once per stream, as above.
  private reportSweptOverride(agentId: string, toolCallId: string, status: string): void {
    if (this.reportedSweptOverrides.has(agentId)) return;
    this.reportedSweptOverrides.add(agentId);
    reportUiFailure(
      "AgentStreamState.sweptToolCallOverridden",
      "a swept tool call reported a later terminal row — the turn-end sweep was wrong",
      { agentId, toolCallId, status },
      { failureKind: "recovered", code: "swept_tool_call_overridden" },
    );
  }

  private readonly reportedSweptOverrides = new Set<string>();

  handleEvent(
    agentId: string,
    event: AgentEvent,
    committed?: { seq: number },
    turnEnd?: TurnEnd,
  ): void {
    this.history.lease(agentId, "live", (w) => {
      if (event.type === "turn_started") {
        const listed = new Set(this.getSharedQueue(agentId).map((item) => item.promptId));
        this.outbox.settleQueuedOnTurnStart(w, listed);
      }
      // A prompt row stays in place: its echo settles the queued bubble it belongs to.
      const promptRow = event.type === "timeline" && event.item.type === "user_message";
      const tail = promptRow ? [] : this.detachQueuedTail(w, agentId);
      this.applyEvent(w, agentId, event, committed, turnEnd);
      if (tail.length > 0) this.reattachQueuedTail(w, agentId, tail);
      return { value: undefined };
    });
  }

  // Queued prompts wait for the running turn: its rows are written above them, never below.
  private detachQueuedTail(w: ConversationWrite, agentId: string): StreamEntry[] {
    const s = this.streams.get(agentId);
    if (!s) return [];
    const at = queuedTailStart(s.entries);
    if (at === s.entries.length) return [];
    const tail = s.entries.slice(at);
    w.assign(s, s.entries.slice(0, at));
    return tail;
  }

  private reattachQueuedTail(w: ConversationWrite, agentId: string, tail: StreamEntry[]): void {
    const s = this.streams.get(agentId);
    if (!s) return;
    const present = new Set(s.entries.map((e) => e.id));
    w.assign(s, [...s.entries, ...tail.filter((e) => !present.has(e.id))]);
  }

  // oxlint-disable-next-line eslint/complexity -- event type dispatch
  private applyEvent(
    w: ConversationWrite,
    agentId: string,
    event: AgentEvent,
    committed?: { seq: number },
    turnEnd?: TurnEnd,
  ): void {
    // Task notifications alone do not prove the pending prompt reached the daemon. A prompt
    // echo proves delivery but lands before turn_started: the wait and its watchdog stay.
    const promptEcho = event.type === "timeline" && event.item.type === "user_message";
    if (!promptEcho && !isTaskNotificationRow(event)) this.outbox.clearPromptPending(agentId);
    if (
      event.type === "turn_started" ||
      (event.type === "timeline" && event.item.type === "assistant_message")
    )
      this.outbox.retractPromptPendingRow(w, undefined);
    switch (event.type) {
      case "image_rewrite": {
        const s = this.ensure(agentId);
        const entries = rewriteLiveOutputEntries(s.entries, event.from, event.to, event.messageId);
        const reply = event.messageId
          ? s.entries.filter(
              (e): e is StreamEntry & { kind: "text" } =>
                e.kind === "text" && e.messageId === event.messageId,
            )
          : [];
        if (entries) {
          w.assign(s, entries);
          this.touch(agentId);
        } else if (reply.length > 0 && !reply.some((e) => e.content.includes(event.to))) {
          // The patch's own reply is on screen but holds neither link (one holding `to` was
          // re-read or patched already): it stays a dead local link.
          this.invariants.report("output_rewrite_unmatched", { agentId });
        }
        break;
      }
      case "timeline": {
        // Committed seqs preserve rows during later hydration (#3244).
        if (committed) {
          const s = this.ensure(agentId);
          const anchored = anchorCards(s.entries, committed.seq);
          if (anchored) w.assign(s, anchored);
          const added = this.ingestRow(w, agentId, event.item, committed.seq, true, event.turnId);
          const reanchored = reanchorCards(s.entries);
          if (reanchored) w.assign(s, reanchored);
          this.checkLivePlacement(agentId, added, this.seating.verdictFor(agentId, event.turnId));
        } else this.handleTimeline(w, agentId, event.item);
        break;
      }
      case "turn_started": {
        const s = this.ensure(agentId);
        this.softCanceled.delete(agentId);
        s.turnStatus = "running";
        // A new turn must not inherit orphan resolution from the settled turn.
        s.settled = false;
        this.seating.noteStarted(agentId, event.turnId, s.entries);
        this.dropCarriedPlanRequests(w, agentId, s);
        this.startTurnTimeout(agentId);
        this.turnActivityAt.set(agentId, Date.now());
        this.touch(agentId);
        break;
      }
      case "turn_completed": {
        // The turn this described is over (see softCanceled).
        this.softCanceled.delete(agentId);
        const s = this.ensure(agentId);
        s.turnStatus = "idle";
        if (event.usage) s.usage = event.usage;
        w.assign(s, [...s.entries, this.closeTurn(s)]);
        this.capEntries(w, s);
        this.finalizeTurn(w, agentId, s, true, turnEnd);
        this.clearTurnTimeout(agentId);
        this.touch(agentId);
        break;
      }
      case "turn_failed": {
        // The turn this described is over (see softCanceled).
        this.softCanceled.delete(agentId);
        // CYBORG-679 — a "busy / turn still alive" reject is NOT this turn dying.
        // After the idle watchdog frees the composer on a WS outage (CYBORG-673),
        // the daemon turn can still be alive; a fresh Send bounces off it and the
        // server broadcasts turn_failed("Agent is busy with another run — …")
        // (message-router.ts's pre-turn-start reject). Treating THAT as a real
        // turn_failed would inject a false error card and flip the live turn's status
        // to "error" — corrupting a turn that is going to complete on its own.
        // The event carries no bubble/turn id to
        // pin the bounced prompt to, so no-op the destructive parts entirely.
        if (isAgentBusyReject(event.error)) break;
        const s = this.ensure(agentId);
        // Never reached the model: hold it as "Reconnecting…" for app.svelte.ts to re-send
        // (agent-unreachable.ts). Only the LAST entry — anything after it
        // means this refusal is not about it, and an answered prompt is never re-sent.
        this.letGoExpiredFailureCards();
        const pairSeq = this.exactFailureSeq(s, event.failureSeq);
        const drawnCard = this.failureTwinCard(s, event.error, pairSeq);
        if (drawnCard) this.restoredFailureCards.delete(drawnCard.id);
        // The card stands at its row's seq; with no numbered rows yet it waits for that row.
        const numbered = s.entries.some((e) => seqRangeOf(e));
        const exact: { seq?: number; pendingFailureSeq?: number } = {};
        if (pairSeq !== undefined) exact[numbered ? "seq" : "pendingFailureSeq"] = pairSeq;
        const lastEntry = s.entries.findLast((e) => e !== drawnCard);
        const heldPrompt =
          isNeverDeliveredFailure(event) && lastEntry?.kind === "user_message"
            ? lastEntry
            : undefined;
        if (heldPrompt?.clientMessageId && this.onPromptUndelivered?.(agentId, heldPrompt.id)) {
          s.turnStatus = "idle";
          w.assign(
            s,
            s.entries.flatMap((e) => {
              if (e === drawnCard) return [];
              return e === heldPrompt
                ? [{ ...heldPrompt, status: "pending" as const, statusReason: RECONNECTING_STATUS }]
                : [e];
            }),
          );
          this.finalizeTurn(w, agentId, s);
          this.clearTurnTimeout(agentId);
          this.touch(agentId);
          break;
        }
        s.turnStatus = "error";
        // A prompt into an ended session: its bubble fails with the code, so Retry is hidden.
        const endedPrompt =
          event.code === "session_ended" && lastEntry?.kind === "user_message"
            ? lastEntry
            : undefined;
        // Window spent: the bubble is failed so Retry and the card's Try again can re-send.
        // An ended session's sentence goes on the prompt's bubble and nowhere else: a second
        // error block saying the same thing under it read as two failures.
        const facts = {
          code: event.code,
          // The daemon's classification, when present, swaps the raw error for the remedy.
          reasonKind: isAgentUnreachableFailure(event)
            ? ("machine_restarting" as const)
            : (event.reasonKind ?? null),
          unavailableReason: event.unavailableReason ?? null,
          provider: event.provider,
          ...(event.diagnostic ? { diagnostic: event.diagnostic } : {}),
        };
        // A card a stored row drew keeps its id and place.
        const upgraded =
          drawnCard?.kind === "error"
            ? withoutAnchor({ ...drawnCard, ...facts, content: event.error }, exact)
            : undefined;
        const card = endedPrompt
          ? undefined
          : (upgraded ??
            withoutAnchor(statusCard(this.nextEntryId(), event.error, s.entries, facts), exact));
        // Exact pairing: the card IS that row, so the row never draws a second one.
        if (card && exact.seq !== undefined && card.seq === exact.seq) s.applied.add(exact.seq);
        else if (card && !drawnCard)
          this.eventFailureCards.set(card.id, { agentId, at: Date.now() });
        w.assign(s, [
          ...s.entries.flatMap((e): StreamEntry[] => {
            if (e === heldPrompt)
              return [{ ...heldPrompt, status: "failed" as const, statusReason: undefined }];
            if (e === endedPrompt) {
              return [
                {
                  ...endedPrompt,
                  status: "failed" as const,
                  statusReason: event.error,
                  statusCode: "session_ended",
                },
              ];
            }
            if (e === drawnCard) return card ? [card] : [];
            return [e];
          }),
          ...(card && !drawnCard ? [card] : []),
        ]);
        this.capEntries(w, s);
        this.finalizeTurn(w, agentId, s);
        this.clearTurnTimeout(agentId);
        this.touch(agentId);
        break;
      }
      case "turn_canceled": {
        // The turn this described is over (see softCanceled).
        this.softCanceled.delete(agentId);
        const s = this.ensure(agentId);
        s.turnStatus = "canceled";
        w.assign(s, [
          ...s.entries,
          statusCard(this.nextEntryId(), `Canceled: ${event.reason}`, s.entries),
        ]);
        this.capEntries(w, s);
        this.finalizeTurn(w, agentId, s);
        this.clearTurnTimeout(agentId);
        this.touch(agentId);
        break;
      }
      case "permission_requested": {
        const s = this.ensure(agentId);
        // Any new permission activity on the session retires the old expiry note.
        s.expiredHold = undefined;
        s.pendingPermissions.set(event.request.id, event.request);
        this.bumpPermissionStamp(agentId);
        this.persistPendingPermissions(agentId, s);
        this.touch(agentId);
        break;
      }
      case "permission_resolved": {
        const s = this.ensure(agentId);
        // A hold that dropped by expiry leaves a note where its card was (the card itself is gone).
        const closed = s.pendingPermissions.get(event.requestId);
        if (
          closed?.name === "peer_message_hold" &&
          event.resolution.behavior === "deny" &&
          /expired/i.test(event.resolution.message ?? "")
        ) {
          // The sender's name only (never the socket path/id); one hold names it, several do not.
          const holds = closed.metadata?.holds;
          const from =
            Array.isArray(holds) && holds.length === 1
              ? (holds[0] as { fromName?: string }).fromName
              : undefined;
          s.expiredHold = from ? { from } : {};
        }
        s.pendingPermissions.delete(event.requestId);
        this.bumpPermissionStamp(agentId);
        this.persistPendingPermissions(agentId, s);
        // If this was a surfaced subagent permission, the daemon just gave us its REAL
        // resolution on the parent id — clear the subagent card now (Codex S2).
        this.resolveLinkedSubagentPermission(agentId, event.requestId);
        this.touch(agentId);
        break;
      }
      case "usage_updated": {
        this.ensure(agentId).usage = event.usage;
        this.touch(agentId);
        break;
      }
      case "model_changed": {
        const s = this.ensure(agentId);
        s.runtimeInfo = event.runtimeInfo;
        s.currentModel = event.runtimeInfo.model ?? null;
        s.controlVersions.model += 1;
        if (event.runtimeInfo.thinkingOptionId !== undefined) {
          s.thinkingOptionId = event.runtimeInfo.thinkingOptionId ?? null;
          s.thinkingReported = true;
          s.controlVersions.thinking += 1;
        }
        if (event.runtimeInfo.modeId !== undefined) {
          s.currentModeId = event.runtimeInfo.modeId ?? null;
          s.controlVersions.mode += 1;
        }
        this.touch(agentId);
        break;
      }
      case "mode_changed": {
        const s = this.ensure(agentId);
        s.currentModeId = event.currentModeId;
        s.availableModes = event.availableModes;
        s.controlVersions.mode += 1;
        this.touch(agentId);
        break;
      }
      case "thinking_option_changed": {
        const s = this.ensure(agentId);
        s.thinkingOptionId = event.thinkingOptionId;
        s.thinkingReported = true;
        s.controlVersions.thinking += 1;
        this.touch(agentId);
        break;
      }
      case "thread_started": {
        const s = this.ensure(agentId);
        s.runtimeInfo = {
          ...s.runtimeInfo,
          sessionId: event.sessionId,
        } as typeof s.runtimeInfo;
        this.touch(agentId);
        break;
      }
      case "attention_required": {
        this.ensure(agentId);
        this.touch(agentId);
        break;
      }
    }
    // Idle watchdog: every event a RUNNING turn emits is proof of life → reset the
    // idle bound (noteTurnActivity). turn_started already armed it; the terminal
    // cases (completed / failed / canceled) already cleared it and must NOT re-arm.
    // Everything else — timeline items, streaming, usage, a permission request
    // (suspends the watchdog) or resolution (re-arms it) — bumps it.
    switch (event.type) {
      case "turn_started":
      case "turn_completed":
      case "turn_failed":
      case "turn_canceled":
        break;
      default:
        this.noteTurnActivity(agentId);
    }
  }

  // APPEND, exactly like the server. This used to GUESS whether a delta was a
  // re-send of text already shown — by string equality, by `existing.endsWith(
  // incoming)`, and by scanning for the longest tail/head overlap — and drop the
  // part it judged duplicate. Every one of those guesses is a content
  // coincidence, and a wrong guess DELETES the agent's words with nothing on
  // screen to show anything went missing.
  //
  // MEASURED, not reasoned: replayed over 65,113 real assistant deltas from one
  // daemon's `agent_timeline_rows`, the guesses fired 96 times and were WRONG
  // all 96 — never once was a genuine duplicate deduped. `endsWith` (78 hits)
  // swallowed any short delta that repeated the tail, so a provider streaming a
  // single character ("S", ".") lost it. The overlap scan (18 hits) ate whole
  // deltas out of repetitive markdown — a table's `|---` separator row arrives as
  // `|---` after the accumulation already ends in `|---`, and the column vanished,
  // taking the table's rendering with it.
  //
  // There is nothing to dedupe: the relay's own accumulator for the SAME frames is
  // a plain `existing.text += delta.text` (workspace-relay.ts mergeAgentStreamDelta)
  // and it writes byte-correct durable messages, and hydrateFromTimeline clears
  // `entries` before it replays, so no surface here ever sees a chunk twice. This
  // takes the tradeoff the surface already states in its own tests: a visible
  // duplicate is recoverable, a silently dropped real message is not.
  private mergeText(existing: string, incoming: string): string {
    return existing + incoming;
  }

  // A streaming text block's chunks always arrive back-to-back with nothing
  // interleaved — so only the entry IMMEDIATELY preceding this delta can be its
  // continuation. Once anything else has landed since (a thinking block, a
  // tool call, ...), the model has moved on to a genuinely separate content
  // block, and the next assistant_message must start a FRESH text entry rather
  // than glue onto the old one with no separator.
  //
  // This used to skip back PAST "thinking" entries looking for a text one to
  // merge into — which is exactly the "wall of text" bug: a reply that paused
  // for a thinking block then resumed (tool output "...exited 0." + thinking +
  // "Let me read...") got glued into one run-on paragraph ("...exited
  // 0.Let me read...") because the thinking block was transparently skipped.
  // Stopping at the true last entry instead makes that resumed text start a
  // new StreamEntry, which the renderer shows as its own block (fresh avatar +
  // top margin — isGroupedWithPrev only merges adjacent "text" entries), while
  // genuine same-block streaming (the last entry already IS "text") still
  // concatenates seamlessly through mergeText below.
  //
  // THE SECOND BOUNDARY — the provider's own message id. "Nothing in between"
  // was never sufficient: an agent that delivers SEVERAL messages back to back
  // (two progress pings then the result) emits them with nothing interleaved,
  // and they glued into ONE entry with no separator at all — measured against
  // relay-v0.0.408, a single `.message-markdown` reading "…dos.(2/3)…" while the
  // CHANNEL (which renders one persisted row per delivery) showed them as
  // separate bubbles. Same content, two surfaces, two answers.
  //
  // The boundary is in the data and always was: every provider that streams
  // stamps each delta with the id of the message it belongs to (claude/agent.ts
  // `messageId: state.id`, opencode `message.id`, acp resolveAssistantMessageId,
  // pi activeAssistantMessageId), so deltas of ONE message share an id and a NEW
  // message brings a new one. Upstream's own durable coalescer already splits on
  // exactly this (agent/timeline-projection.ts mergeAssistantChunks); the client
  // ingest simply never looked. Split only when BOTH ids are present and differ —
  // an absent id (a provider that doesn't stamp, an older row) still merges, so
  // no stream can fragment per delta.
  // The entry a chunk continues: the last one, when it is text of the same message AND
  // — when both sides know their rows — this chunk is the row right after the entry's
  // last (a chunk after a hole starts its own bubble until the hole is filled, #3248 C2).
  private findLastTextInTurn(
    s: AgentStream,
    entries: readonly StreamEntry[],
    messageId?: string,
    seq?: number,
  ): number {
    // A progress-only card, a peer's message row, a notice or a task finish line is not a boundary
    // of the message being spoken (passableInReply); a launched or plain tool card still splits (C2).
    const via = textBeforeProgressRun(entries, entries.length, "text", messageId);
    const idx = via >= 0 ? via : entries.length - 1;
    const last = entries[idx];
    if (!last || last.kind !== "text") return -1;
    if (messageId !== undefined && last.messageId !== undefined && last.messageId !== messageId) {
      return -1;
    }
    const range = seqRangeOf(last);
    if (seq !== undefined && range && !rowsBetweenApplied(s.applied, range.max, seq)) return -1;
    return idx;
  }

  // One compaction emits a RUN of timeline items, not two: the Claude provider pushes a
  // "loading" per SDK status heartbeat for as long as it compacts (providers/claude/
  // agent.ts, undeduped — the count is unbounded and scales with duration; a 967k-token
  // auto-compaction measured six rows) then one "compact_boundary" (status:"completed").
  // They coalesce into ONE card that transitions loading→done, mirroring tool_call's
  // in-place update; a row with a seq joins the card whose rows touch it, a live row
  // without one joins the compaction still loading in this turn. Two compactions never
  // merge. Whatever the count, the reply's rows sit above the run and seat after it.
  private applyCompaction(
    w: ConversationWrite,
    s: AgentStream,
    status: "loading" | "completed",
    seq?: number,
  ): void {
    const i = compactionCardFor(s, seq);
    if (i >= 0) {
      const e = s.entries[i] as StreamEntry & { kind: "compaction" };
      const top = highestSeq(e.seq, seq);
      const settled = e.status === "completed" || status === "completed" ? "completed" : "loading";
      w.assign(s, [
        ...s.entries.slice(0, i),
        {
          id: e.id,
          kind: "compaction",
          status: settled,
          rows: rowsWith(e, seq),
          ...(top === undefined ? {} : { seq: top }),
        },
        ...s.entries.slice(i + 1),
      ]);
      fuseCompactionRun(w, s);
      return;
    }
    w.assign(s, [
      ...s.entries,
      {
        id: this.nextEntryId(),
        kind: "compaction",
        status,
        ...(seq === undefined ? {} : { rows: [seq], seq }),
      },
    ]);
  }

  // Resolve an entry's creation time (ms) for CYBORG-588. A HYDRATED item carries the
  // persisted ISO `ts` (folded on at the serve points); a LIVE delta / older daemon
  // has none → stamp Date.now(). A malformed ts (NaN) also falls back to now.
  private tsFromItem(item: AgentTimelineItem): number {
    return parseIsoTs(item.ts) ?? Date.now();
  }

  // A stored failure row redraws its card for the live turn_failed to upgrade, unless that event drew it.
  private restoreFailureCard(
    w: ConversationWrite,
    s: AgentStream,
    item: AgentTimelineItem,
    row: { seq?: number; ts?: number },
  ): void {
    const failure = storedFailureOf(item);
    if (!failure?.error || isAgentBusyReject(failure.error)) return;
    // Deliberate: a seqless row with no id can't tell a replay from a second failure; it draws.
    if (this.pairsWithEventCard(w, s, item, row)) return;
    const id = this.nextEntryId();
    this.restoredFailureCards.set(id, { agentId: w.agentId, at: Date.now() });
    const reasonKind = storedFailureKind(failure.error, failure.diagnostic);
    const { code, provider, diagnostic } = failure;
    const facts = { code, provider, reasonKind, diagnostic };
    w.assign(s, [...s.entries, rowCard(id, failure.error, row, s.entries, facts)]);
  }

  // A failureSeq is trusted only when it is free or names a failure card: never another row's seq.
  // A free seq is safe: FailureRowLink (system-error-notice.ts) stamps only the row just before it.
  private exactFailureSeq(s: AgentStream, seq: number | undefined): number | undefined {
    if (seq === undefined || !Number.isInteger(seq)) return undefined;
    const own = s.entries.some((e) => isFailureCardFor(e, seq));
    return own || !s.applied.has(seq) ? seq : undefined;
  }

  // The card a turn_failed upgrades: its own row's, else a heuristic twin with no other seq.
  private failureTwinCard(
    s: AgentStream,
    error: string,
    exact: number | undefined,
  ): StreamEntry | undefined {
    const own = exact === undefined ? undefined : s.entries.find((e) => isFailureCardFor(e, exact));
    if (own) return own;
    const at = turnFailureCardIndex(s.entries, error, this.restoredFailureCards);
    // A row card with newer rows below it is history, not this event's twin.
    if (s.entries.slice(at + 1).some((e) => seqRangeOf(e))) return undefined;
    const card = s.entries[at];
    return exact !== undefined && card?.kind === "error" && card.seq !== undefined
      ? undefined
      : card;
  }

  // The row takes the event's card as its own (seq, time), so a re-read finds it applied.
  private pairsWithEventCard(
    w: ConversationWrite,
    s: AgentStream,
    item: AgentTimelineItem,
    row: { seq?: number; ts?: number },
  ): boolean {
    const error = storedFailureOf(item)?.error;
    if (!error) return false;
    this.letGoExpiredFailureCards();
    // The card whose event named this row's seq takes it exactly, whatever stands between.
    const pending =
      row.seq === undefined
        ? -1
        : s.entries.findIndex((e) => e.kind === "error" && e.pendingFailureSeq === row.seq);
    // A numbered row never takes, by guess, a card waiting for another seq.
    const waiting = new Set(
      s.entries.flatMap((e) => (pendingSeqOf(e) === undefined ? [] : [e.id])),
    );
    const guessable = {
      has: (id: string) =>
        this.eventFailureCards.has(id) && (row.seq === undefined || !waiting.has(id)),
    };
    const at = pending >= 0 ? pending : turnFailureCardIndex(s.entries, error, guessable);
    const card = s.entries[at];
    if (card?.kind !== "error") return false;
    // An older page's row at or below the card's anchor is another failure.
    if (
      pending < 0 &&
      w.label === "prepend" &&
      row.seq !== undefined &&
      row.seq <= (card.afterSeq ?? -1)
    ) {
      return false;
    }
    this.eventFailureCards.delete(card.id);
    if (row.seq === undefined) return true;
    const { afterSeq: _anchor, pendingFailureSeq: _pending, ...rest } = card;
    const owned = { ...rest, seq: row.seq, ts: row.ts ?? card.ts };
    // Moved to its seq when a numbered row sits below it, or a read-back would land out of order.
    const rowSeq = row.seq;
    const outOfOrder = s.entries.some((e, i) =>
      i > at ? seqRangeOf(e) : (seqRangeOf(e)?.max ?? -Infinity) > rowSeq,
    );
    if (!outOfOrder) {
      w.assign(
        s,
        s.entries.map((e) => (e === card ? owned : e)),
      );
      return true;
    }
    w.assign(
      s,
      s.entries.filter((e) => e !== card),
    );
    const to = this.splitPointForSeq(w, s, row.seq, item);
    w.assign(s, [...s.entries.slice(0, to), owned, ...s.entries.slice(to)]);
    return true;
  }

  private letGoExpiredFailureCards(): void {
    for (const waiting of [this.restoredFailureCards, this.eventFailureCards]) {
      for (const [id, card] of waiting) {
        if (Date.now() - card.at > FAILURE_TWIN_MS) waiting.delete(id);
      }
    }
  }

  private forgetFailureCardsOffScreen(agentId: string, s: AgentStream): void {
    const onScreen = new Set(s.entries.map((e) => e.id));
    for (const waiting of [this.restoredFailureCards, this.eventFailureCards]) {
      for (const [id, card] of waiting) {
        if (card.agentId === agentId && !onScreen.has(id)) waiting.delete(id);
      }
    }
  }

  // `seq`: the durable row this item is, when known (a committed frame, a hydrated row).
  // Index of the entry a ledger row drew for this message, if any.
  private ledgerEntryOf(s: AgentStream, messageId: string | undefined): number | undefined {
    if (!messageId) return undefined;
    const at = s.entries.findIndex(
      (e) => e.kind === "text" && e.messageId === messageId && this.ledgerEntryIds.has(e.id),
    );
    return at < 0 ? undefined : at;
  }

  // oxlint-disable-next-line eslint/complexity -- timeline item-type dispatch (mirrors handleEvent)
  private handleTimeline(
    w: ConversationWrite,
    agentId: string,
    row: AgentTimelineItem,
    seq?: number,
  ): void {
    const s = this.ensure(agentId);
    const item = asDrawnPromptRow(row);
    if (!item || (item !== row && finishedLineDrawn(s.entries, item))) return;
    if (seq !== undefined) s.openTurnSeq = Math.max(s.openTurnSeq ?? seq, seq);
    // Per-entry timestamp for the day separators + hover times (CYBORG-588). Only the
    // "message" entries (text / user_message) carry it; the merge path preserves the
    // FIRST chunk's ts so a streamed reply keeps its start time.
    const ts = this.tsFromItem(item);
    const clientStamped = parseIsoTs(item.ts) === undefined;
    switch (item.type) {
      case "assistant_message": {
        // A FAILED turn is not a reply. Upstream agent-manager fabricates one as
        // "[System Error] …" assistant text and we may not fix it at the producer
        // (fork rule — see system-error-text.ts); the `turn_failed` event right
        // behind this one draws the remedy card, so the failure stays visible and
        // dropping the bubble hides nothing. Checked BEFORE the merge branch too:
        // glued onto a preceding text entry the marker would no longer be at the
        // start, and it would corrupt a real reply's bubble.
        if (isSystemErrorText(item.text)) {
          // The durable row is all a reload has of the failure: it redraws the card.
          this.restoreFailureCard(w, s, item, { seq, ts: clientStamped ? undefined : ts });
          break;
        }
        // A command reply carries one daemon-minted id on both copies: the ledger row got here first.
        if (
          item.messageId &&
          s.entries.some(
            (e) =>
              e.kind === "text" &&
              e.messageId === item.messageId &&
              this.ledgerEntryIds.has(e.id) &&
              e.content.trim() === item.text.trim(),
          )
        ) {
          break;
        }
        // A ledger row drawn before its timeline is that message's entry: its uncommitted
        // deltas go there, never into a second entry of the same message (Fab's DM,
        // 2026-10-14). Committed rows reach it through takeRowIntoLedgerText, with its guards.
        const lastTextIdx =
          (seq === undefined ? this.ledgerEntryOf(s, item.messageId) : undefined) ??
          this.findLastTextInTurn(s, s.entries, item.messageId, seq);
        if (lastTextIdx >= 0) {
          const prev = s.entries[lastTextIdx] as StreamEntry & { kind: "text" };
          // Preserve the entry's id across the in-place token merge so its key
          // (and any toggle state) stays stable as it streams. Carry the
          // messageId through the merge too (fix/dm-view-renders-conversation-
          // not-one-session) — PREV WINS, never clobbered by a later delta:
          // this mirrors the server's own accumulator (workspace-relay.ts
          // mergeAgentStreamDelta, which never touches the pending message's
          // id once set at creation), so the client's merged entry's id stays
          // in lockstep with whatever id the persisted DM-ledger row actually
          // got. Getting this backwards (adopting the LATEST delta's id) would
          // stamp a streamed reply with a late delta's id, so the ledger row
          // for that reply matches nothing client-side and
          // mergeCrossSessionReply re-appends the whole merged text as a false
          // "unseen" duplicate. Adopt the delta's id only when prev has none
          // yet — the case some providers produce, stamping the id only once
          // the message is assembled.
          //
          // Two GENUINELY SEPARATE messages can no longer reach this branch at
          // all: findLastTextInTurn now refuses to merge across a differing
          // messageId, so each delivery keeps its own entry AND its own id, and
          // every ledger row has a client-side match. That was the "known gap"
          // this comment used to describe.
          // A ledger row drawn before its timeline is the whole message: that message's own
          // rows and deltas take that text over instead of repeating it (Fab's DM, 2026-10-14).
          const ledgerOfThis =
            this.ledgerEntryIds.has(prev.id) &&
            !!item.messageId &&
            (prev.messageId === item.messageId || !!prev.aliasIds?.includes(item.messageId));
          let taken: TextChunk[] | undefined;
          if (ledgerOfThis && seq !== undefined) {
            taken = takeRowIntoLedgerBody(chunksOf(prev), { seq, text: item.text });
          } else if (ledgerOfThis) {
            taken = takeDeltaIntoLedgerBody(chunksOf(prev), item.text, !prev.parts?.length);
          }
          const body = bodyOfChunks(
            taken ?? [
              ...chunksOf(prev),
              { ...(seq === undefined ? {} : { seq }), text: item.text },
            ],
          );
          w.assign(s, [
            ...s.entries.slice(0, lastTextIdx),
            {
              ...withTextBody(prev, body),
              // Keep the entry's original start time across the in-place merge (like id).
              ts: prev.ts ?? ts,
              ...((prev.messageId ?? item.messageId)
                ? { messageId: prev.messageId ?? item.messageId }
                : {}),
            },
            ...s.entries.slice(lastTextIdx + 1),
          ]);
        } else if (item.text === "") {
          // A coalesced run's continuation (coalesced-rows.ts) with no text of its own to join:
          // its head drew none (a failure card). An empty bubble says nothing.
          break;
        } else if (!item.messageId && this.foldIntoLedgerEntry(s, item.text, ts, clientStamped)) {
          break;
        } else if (this.spendLedgerTail(w, s, item.text)) {
          break;
        } else {
          const id = this.nextEntryId();
          if (clientStamped) this.clientStampedEntryIds.add(id);
          w.assign(s, [
            ...s.entries,
            {
              id,
              kind: "text",
              content: item.text,
              ts,
              ...(item.messageId ? { messageId: item.messageId } : {}),
              parts: [{ ...(seq === undefined ? {} : { seq }), len: item.text.length }],
              ...(seq === undefined ? {} : { seq }),
            },
          ]);
        }
        break;
      }
      case "reasoning": {
        // A background call's progress-only card is not a boundary of the thinking being streamed.
        const via = textBeforeProgressRun(s.entries, s.entries.length, "thinking");
        // Only a STAMPED thinking entry is reached past the run: unstamped chunks reconcile by tail order.
        const at = via >= 0 && seqRangeOf(s.entries[via]) ? via : s.entries.length - 1;
        const last = s.entries[at];
        const range = last ? seqRangeOf(last) : undefined;
        const continues =
          last?.kind === "thinking" &&
          (seq === undefined || !range || rowsBetweenApplied(s.applied, range.max, seq));
        const part: TextPart = { ...(seq === undefined ? {} : { seq }), len: item.text.length };
        if (last && last.kind === "thinking" && continues) {
          const body = bodyOfChunks([
            ...chunksOf(last),
            { ...(seq === undefined ? {} : { seq }), text: item.text },
          ]);
          w.assign(s, [
            ...s.entries.slice(0, at),
            withTextBody(last, body),
            ...s.entries.slice(at + 1),
          ]);
        } else {
          w.assign(s, [
            ...s.entries,
            {
              id: this.nextEntryId(),
              kind: "thinking",
              content: item.text,
              parts: [part],
              ...(seq === undefined ? {} : { seq }),
            },
          ]);
        }
        break;
      }
      case "tool_call": {
        const idx = toolCardIndexOf(s.entries, item);
        const prev = idx >= 0 ? (s.entries[idx] as StreamEntry & { kind: "tool_call" }) : undefined;
        // CLOBBER GUARD (the "sub-agent card went blank" bug). A LATER row for the
        // same callId can carry the bare `unknown` envelope while an earlier one
        // carried a typed detail: Claude lands the parent-side `Agent` tool_result
        // on the SAME callId the accumulated `Task`/sub_agent rows used. Taking it
        // verbatim throws the accumulated card away and the whole thing collapses
        // to ToolCallDetail's fallback text ("Tool call completed") — durably, on
        // the persisted timeline, so a reload can't recover it. Measured on the
        // owner's box: 47 of 205 sub_agent cards ended that way, one of them after
        // accumulating 41 actions (callId toolu_017va6…, 41 sub_agent rows then a
        // single Agent/unknown row).
        // `unknown` is by construction never richer than a typed detail, so keep
        // the typed one — and keep the `name` that goes with it, or the collapsed
        // row reads "Agent" above a sub_agent body. Status/error still advance, so
        // the card settles to completed/failed as it should. Provider-agnostic:
        // this is a property of the detail union, not of Claude.
        // A row OLDER than what the card shows (its earlier `running` arriving after the
        // terminal row: a late frame, an older page) may enrich an `unknown` card with
        // its typed detail, never replace a newer typed detail or output (#3248 G2).
        const detailAge = prev?.detailSeq ?? prev?.seq;
        const older = detailAge !== undefined && seq !== undefined && seq < detailAge;
        const incomingType = incomingTypeOf(item);
        // An older untyped row never replaces the card's detail, `unknown` included (item 8a).
        // The task's other frame (another callId) never trades the provider's text for a bare label.
        const otherFrame = prev !== undefined && prev.callId !== item.callId;
        const keepPrevDetail =
          prev?.detail != null &&
          ((otherFrame && prev.detail.type === "plain_text" && !!prev.detail.text) ||
            (prev.detail.type !== "unknown"
              ? incomingType === "unknown" || older
              : older && (incomingType === undefined || incomingType === "unknown")));
        const takesTypedDetail =
          !keepPrevDetail && incomingType !== undefined && incomingType !== "unknown";
        let detailSeq: number | undefined;
        if (keepPrevDetail) detailSeq = prev.detailSeq;
        else if (takesTypedDetail) detailSeq = seq;
        // A TERMINAL STATUS IS FINAL — the twin of keepPrevDetail, on the field the
        // comment above assumed could only ever advance ("Status/error still advance,
        // so the card settles to completed/failed as it should"). It doesn't, and a
        // late `running` row parked the card on RUNNING forever. mergeToolCallStatus
        // (top of file) is the lattice; see its doc for the measurement and for why
        // the emitter itself is out of reach. This is a READ-side fold, so it repairs
        // the rows ALREADY on disk — no migration, no backfill.
        // An ORPHAN — a call that never reported ANY terminal status — is NOT
        // resolved here: this runs per ROW, and forcing the first `running` row of a
        // call to `canceled` would make the lattice refuse that call's own later
        // `completed` (canceled outranks it), turning correctly-completed cards into
        // cancelled ones. Orphans are swept AFTER the whole page is folded — see
        // hydrateFromTimeline.
        // A swept `canceled` is the client's turn-end guess: the call's own later terminal row wins.
        const overrides = prev?.swept === true && isTerminalToolCallStatus(item.status);
        const status = overrides ? item.status : mergeToolCallStatus(prev?.status, item.status);
        if (overrides && item.status !== "canceled") {
          this.reportSweptOverride(agentId, `${item.name}:${item.callId}`, item.status);
        }
        const subagentId = subagentIdOfItem(item) ?? prev?.subagentId;
        const subagentLabel = prev?.subagentLabel ?? subagentLabelOfItem(item);
        // Reuse the existing entry's id when updating in place (status/detail
        // changes) so its key — and the expand/collapse toggle keyed on callId —
        // survives; a brand-new tool_call gets a fresh id.
        const cardTs = this.cardFirstTs.get(agentId) ?? new Map<string, number>();
        if (!cardTs.has(item.callId)) cardTs.set(item.callId, ts);
        this.cardFirstTs.set(agentId, cardTs);
        if (isTerminalToolCallStatus(status)) {
          // An end counts only from a LATER time than the start: a server-coalesced row repeats the
          // first row's ts on every seq (start == end = unknown), and carries the real end as `endedTs`.
          const start = cardTs.get(item.callId) ?? ts;
          const endedTs = parseIsoTs(item.endedTs);
          const end = endedTs !== undefined && endedTs > ts ? endedTs : ts;
          const ends = this.cardEndTs.get(agentId) ?? new Map<string, number>();
          if (!ends.has(item.callId) && end > start) ends.set(item.callId, end);
          this.cardEndTs.set(agentId, ends);
        }
        const taskId = prev?.taskId ?? finishedTaskIdOf(item);
        const updated = {
          id: prev ? prev.id : this.nextEntryId(),
          kind: "tool_call" as const,
          callId: prev?.callId ?? item.callId,
          ...(taskId ? { taskId } : {}),
          name: keepPrevDetail ? prev.name : item.name,
          status,
          detail: keepPrevDetail ? fillUnknownDetail(prev.detail, item.detail) : item.detail,
          ...(detailSeq === undefined ? {} : { detailSeq }),
          ...(subagentId ? { subagentId } : {}),
          ...(subagentLabel && subagentId ? { subagentLabel } : {}),
          ...((prev ? prev.progressOnly && !isLaunchRow(item) : isProgressRow(item))
            ? { progressOnly: true as const }
            : {}),
          ...(prev?.swept && !overrides ? { swept: true as const } : {}),
          // The error rides WITH the status that won: keeping a `failed` badge while
          // taking the late `running` row's null error would render a failed card
          // with nothing to read.
          error: status === item.status ? item.error : prev?.error,
          // Every row the card has consumed, and the highest of them — a late `running`
          // row after hydrate neither lowers the seq nor reopens the card (#3248 E1).
          rows: rowsWith(prev, seq),
          ...(highestSeq(prev?.seq, seq) === undefined ? {} : { seq: highestSeq(prev?.seq, seq) }),
        };
        if (idx >= 0) {
          w.assign(s, [...s.entries.slice(0, idx), updated, ...s.entries.slice(idx + 1)]);
        } else {
          w.assign(s, [...s.entries, updated]);
        }
        // The plan's own row reached a terminal status: its card is over too.
        if (isTerminalToolCallStatus(status) && s.pendingPermissions.has(item.callId)) {
          this.dropSettledPlanRequests(agentId, s);
        }
        break;
      }
      case "todo":
        w.assign(s, [...s.entries, { id: this.nextEntryId(), kind: "todo", items: item.items }]);
        break;
      case "error":
        if (isRuntimeExitRow(item.message)) {
          this.restoreFailureCard(w, s, item, { seq, ts: clientStamped ? undefined : ts });
        } else {
          const at = { seq, ts: clientStamped ? undefined : ts };
          w.assign(s, [...s.entries, rowCard(this.nextEntryId(), item.message, at, s.entries)]);
        }
        break;
      case "compaction":
        this.applyCompaction(w, s, item.status, seq);
        break;
      case "notification":
        // A served row is not validated: one with no text became a notice whose `.trim()` threw in
        // the next live DM's listener (client.listener_threw). Skipped, and said once per agent.
        if (typeof item.message !== "string") {
          reportNoticeWithoutText(agentId, seq);
          break;
        }
        // cyborg(upstream-sync 2026-10): stored history of upstream's "This chat uses OpenCode vN."
        // runtime notice is upstream jargon, not conversation; the daemon no longer emits it.
        if (/^This chat uses OpenCode v\d+\.$/.test(item.message)) break;
        w.assign(s, [
          ...s.entries,
          {
            id: this.nextEntryId(),
            kind: "notice",
            content: item.message,
            level: item.level,
            ...(ts !== undefined ? { ts } : {}),
          },
        ]);
        break;
      case "user_message": {
        // CYBORG-1411: stored relive rows from before the continue prompt was made internal.
        if (isHiddenReliveRow(item)) return;
        // stripMentionEnvelope first: a channel-mention row is a prompt scaffold
        // whose real request lives inside a fence — extract it, THEN drop any DM
        // prefix (a no-op on the extracted payload; the strip still runs for real
        // DM rows). The extracted request equals the clean human-ask row the same
        // turn also persists, so absorbPromptEcho below collapses the
        // twin (it crosses the one turn_boundary hydration synthesizes between
        // the pair) — the request shows once, without the BEGIN UNTRUSTED wall.
        const text = stripDmPrefix(stripMentionEnvelope(item.text));
        // An envelope whose request fence we couldn't parse (or that fenced an
        // empty ask) strips to "" — that's a prompt scaffold, not a human
        // message. Appending it makes a bodyless "You" bubble (avatar + name +
        // timestamp, no text) that the exact-string dedup below can't collapse
        // against the clean request row, so drop it. Scoped to the envelope case
        // so a legitimately-empty non-envelope message is still kept.
        if (text === "" && isMentionEnvelope(item.text)) return;
        // CYBORG-1015: remember that the SERVER has shown us a row for this prompt id,
        // before anything decides whether to render it. This is what tells an accepted
        // send-now's verdict that its words are already on screen when the provider's
        // stamped echo wins the race — the ONE case absorbPromptEcho cannot
        // answer, because in that order there is no optimistic entry for it to match.
        //
        // Recorded HERE and not on the entry itself: a hydrated row that carried a
        // clientMessageId would read as an unsent optimistic row to
        // optimisticRowsMissingFromSnapshot, and a later page that did not include it
        // would re-merge it beside the durable one. That is the exact defect round 3 of
        // CYBORG-1001 shipped. The id lives beside the transcript, never in it.
        this.noteServerEchoedPrompt(item.clientMessageId);
        if (item.clientMessageId) void deleteAgentPromptOutbox(item.clientMessageId);
        // parseIsoTs, not `ts`: tsFromItem falls back to Date.now(), so settling on a
        // live frame replaced the bubble's SEND time with the arrival time and its
        // rendered time became a fiction (CYBORG-1166). No server time, no change.
        if (this.absorbPromptEcho(w, s, agentId, item, text, seq, parseIsoTs(item.ts))) break;
        const entry = this.userMessageEntry(item, text, ts);
        if (item.clientMessageId) this.noteReconciledPromptEntry(item.clientMessageId, entry.id);
        w.assign(s, [...s.entries, entry]);
        break;
      }
    }
    this.capEntries(w, s);
    this.touch(agentId);
  }

  // The entry a durable user_message row becomes — the ONE construction the live
  // stream, a hydrated page and the reconcile share. Carries the timeline messageId
  // (the 'rewind to here' anchor, #649), the card a /pages prompt persisted (CYBORG-309,
  // re-attached on a cold reload with no optimistic echo to carry it), the daemon's
  // author stamp (an agent-authored turn input signs as that agent) and attachments.
  private userMessageEntry(
    item: Extract<AgentTimelineItem, { type: "user_message" }>,
    text: string,
    ts: number | undefined,
  ): StreamEntry & { kind: "user_message" } {
    return {
      id: this.nextEntryId(),
      kind: "user_message",
      content: text,
      ts,
      messageId: item.messageId,
      ...(item.card ? { card: item.card } : {}),
      ...(item.promptAuthor ? { promptAuthor: item.promptAuthor } : {}),
      ...(item.peerRelay ? { peerRelay: item.peerRelay } : {}),
      ...(item.wakeThreadId ? { wakeThreadId: item.wakeThreadId } : {}),
      ...(item.askReplyFrom ? { askReplyFrom: item.askReplyFrom } : {}),
      ...(item.attachments?.length ? { attachments: item.attachments } : {}),
      ...(item.sourceMessageId ? { sourceMessageId: item.sourceMessageId } : {}),
      ...(item.forwardedMessageId ? { forwardedMessageId: item.forwardedMessageId } : {}),
    };
  }

  // Merge a cybo→human DM the ledger recorded but THIS session's own timeline
  // never saw — sent via a DIFFERENT session (e.g. an ephemeral autonomous
  // self-wake calling cyborg7_send_message, message-router.ts's
  // invokeCyboForDefaultSession). fix/dm-view-renders-conversation-not-one-
  // session: a PROMOTED default session's page keeps AgentStreamView (typing,
  // streaming, tool calls, attachments, optimistic send/retry all stay working)
  // and merges in exactly the missing delta, instead of swapping to the
  // peer-scoped DM-thread view (reverted — see shouldRenderDefaultDmView's doc
  // comment for why).
  //
  // Idempotent by messageId: a no-op if an entry with this messageId is
  // ALREADY present ANYWHERE in the stream (not just the last entry) — the
  // dedup a naive handleEvent("timeline", {type:"assistant_message", ...})
  // call can't give you, since findLastTextInTurn only ever looks at the last
  // entry. This is also why a merge ALWAYS starts a fresh entry rather than
  // routing through handleTimeline's merge-continuation path: that path glues
  // onto the immediately-preceding "text" entry whenever there's no boundary
  // between them, which would corrupt an unrelated real reply's bubble by
  // concatenating a totally different message onto its tail.
  //
  // TRAILING turn_boundary (round 6, finding #1 BLOCKER): appending a bare
  // "text" entry here left THIS merged entry as the stream's new last entry —
  // findLastTextInTurn returns "the last entry, whenever it's text", so the
  // VERY NEXT live assistant_message delta (the session resuming a real turn,
  // or replying to something the CEO just typed) glued onto the DM this just
  // merged, under handleTimeline's "prev wins" messageId rule — silently
  // gluing a live reply onto a cross-session DM's bubble and stamping it with
  // the DM's ledger id. That messageId collision then hides the live reply's
  // OWN ledger row from every future loadedIds computation, so it re-appears
  // as a "missing" cross-session DM on every subsequent open — compounding.
  // A trailing turn_boundary (mirrors what turn_completed already inserts
  // after every OTHER complete turn) makes findLastTextInTurn return -1 for
  // whatever comes next, exactly as if this merged DM's own turn had genuinely
  // just completed — which, semantically, it did.
  //
  // `ts` = the DM ledger row's `createdAt` (ms). NOT optional in spirit: this was
  // the ONLY producer of a text/user_message entry that left `ts` unset, and
  // AgentStreamView gates BOTH the header time and the day-separator pill on
  // `entry.ts !== undefined` — so every merged cross-session DM rendered as a
  // bare name with no time beside it. That is 100% of scheduled (cron) replies,
  // since a channel-less autonomous fire's session is torn down and its DM row
  // can ONLY ever reach the stream through this merge. Left optional in the
  // signature so a wire frame that genuinely carries no time renders exactly as
  // before (no time) rather than being stamped with a lie like Date.now() — a
  // day-old cron message must never claim it arrived just now.
  //
  // `card` = the ledger row's structured card, passed straight through. Optional
  // for the same reason `ts` is: a row without one merges exactly as before.
  mergeCrossSessionReply(
    agentId: string,
    messageId: string,
    text: string,
    ts?: number,
    card?: MessageCard | null,
    attachments?: Attachment[] | null, // null: a row stored with none
  ): void {
    // Backstop (CYBORG-dm-reply-dup): fold a differing-id twin before history.open (no nesting).
    if (this.foldsIntoLiveReplyTwin(agentId, messageId, text, ts)) return;
    this.history.open(agentId, "ledger", (w) => {
      this.mergeLedgerRow(w, agentId, messageId, text, ts, card, attachments);
      w.judge({ messageId, chars: text.length });
      return { value: undefined };
    });
  }

  // Folds a row onto the live bubble drawn from the same stream under a different id. Narrow: only
  // a client-stamped live entry with its own id, so two persisted same-words replies stay two.
  private foldsIntoLiveReplyTwin(
    agentId: string,
    messageId: string,
    text: string,
    ts?: number,
  ): boolean {
    if (ts === undefined) return false;
    const s = this.streams.get(agentId);
    if (!s) return false;
    const onScreen = s.entries.some(
      (e) =>
        (e.kind === "text" || e.kind === "user_message") &&
        (e.messageId === messageId || (e.aliasIds?.includes(messageId) ?? false)),
    );
    if (onScreen) return false;
    const hasLiveTwin = s.entries.some(
      (e) =>
        e.kind === "text" &&
        e.messageId !== undefined &&
        e.messageId !== messageId &&
        this.clientStampedEntryIds.has(e.id) &&
        e.ts !== undefined &&
        inTwinWindow(e.ts, ts) &&
        e.content.trim() === text.trim(),
    );
    if (!hasLiveTwin) return false;
    return this.adoptLedgerTwin(agentId, "text", { id: messageId, text, createdAt: ts });
  }

  // Ledger-built entries (not timeline): never the twin of another ledger row; off-entry to keep the golden.
  private readonly ledgerEntryIds = new Set<string>();
  // The divider a ledger row added behind itself: the only one the tail skip may look past.
  private readonly ledgerDividerIds = new Set<string>();
  private ledgerDivider(): StreamEntry {
    const d = this.boundaryAt(undefined);
    this.ledgerDividerIds.add(d.id);
    return d;
  }
  isLedgerBuilt(entryId: string): boolean {
    return this.ledgerEntryIds.has(entryId);
  }
  // Failure cards still waiting for their twin: drawn by a stored row / by the live turn_failed.
  private readonly restoredFailureCards = new Map<string, { agentId: string; at: number }>();
  private readonly eventFailureCards = new Map<string, { agentId: string; at: number }>();
  // Ledger-built entries a timeline reply already folded into (one-to-one, the mirror of isLedgerTwin).
  private readonly claimedLedgerEntryIds = new Set<string>();

  // Entries whose ts is this client's Date.now() (a live item with no ts of its own): skewed vs the relay.
  private readonly clientStampedEntryIds = new Set<string>();
  // Per agent: a merge re-runs only that conversation's readers.
  private readonly persistedTimes = new SvelteMap<string, ReadonlyMap<string, number>>();
  readonly pageHoles = new PageHoles();

  private foldIntoLedgerEntry(
    s: AgentStream,
    text: string,
    ts: number | undefined,
    clientStamped: boolean,
  ): boolean {
    const at = this.ledgerEntryForTimelineReply(s, text, ts, clientStamped);
    if (at < 0) return false;
    this.claimedLedgerEntryIds.add(s.entries[at].id);
    return true;
  }

  // Broadcast-first order: the ledger row drew its own entry before the id-less timeline reply
  // arrived. Index of the nearest unclaimed ledger entry with the same words, else -1.
  private ledgerEntryForTimelineReply(
    s: AgentStream,
    text: string,
    ts: number | undefined,
    clientStamped: boolean,
  ): number {
    if (ts === undefined) return -1;
    let best = -1;
    let bestGap = Infinity;
    s.entries.forEach((e, i) => {
      if (e.kind !== "text" || e.ts === undefined || !this.ledgerEntryIds.has(e.id)) return;
      if (this.claimedLedgerEntryIds.has(e.id) || e.content.trim() !== text.trim()) return;
      // A client-stamped ts carries clock skew: a minute either way, the nearest still wins.
      if (!inTwinWindow(ts, e.ts) && !(clientStamped && Math.abs(ts - e.ts) <= 60_000)) return;
      const gap = Math.abs(ts - e.ts);
      if (gap < bestGap) {
        best = i;
        bestGap = gap;
      }
    });
    return best;
  }

  // Is this ledger row already on screen under ANOTHER id (the timeline's provider id)? Same
  // clientMsgId, or same words by the same author within LEDGER_TWIN_MS (reuses sameAuthor; an
  // unstamped bubble matches any author, since the timeline only stamps non-viewer turns).
  // When it is, the ledger id is recorded on that row (aliasIds) so an alert that points at the
  // ledger id still finds it on screen. Returns whether a twin exists.
  adoptLedgerTwin(
    agentId: string,
    kind: "text" | "user_message",
    row: { id: string; text: string; createdAt: number; clientMsgId?: string | null },
    author?: PromptAuthor,
  ): boolean {
    const s = this.streams.get(agentId);
    if (!s) return false;
    // One-to-one: an exact id/clientMsgId match wins, and the words+time fallback skips an entry
    // another ledger row already claimed (two identical "ok" 3 s apart are two messages).
    const exact = s.entries.find(
      (e) =>
        e.kind === kind &&
        (e.messageId === row.id ||
          (e.aliasIds?.includes(row.id) ?? false) ||
          // A timeline prompt's messageId IS the send's clientMsgId (promptId).
          (e.kind === "user_message" && !!row.clientMsgId && e.messageId === row.clientMsgId) ||
          (e.kind === "user_message" &&
            (e.sourceMessageId === row.id ||
              (!!row.clientMsgId && e.clientMessageId === row.clientMsgId)))),
    );
    // Nearest in time, like unidentifiedTextTwin.
    let near: StreamEntry | undefined;
    if (!exact) {
      for (const e of s.entries) {
        if (!this.isLedgerTwin(e, kind, row, author)) continue;
        if (!near || Math.abs(e.ts! - row.createdAt) < Math.abs(near.ts! - row.createdAt)) near = e;
      }
    }
    const twin = exact ?? near;
    if (!twin || !("messageId" in twin)) return false;
    if (twin.messageId !== row.id) this.aliasLedgerId(agentId, twin.id, row.id);
    return true;
  }

  // The words+time pairing both ledger merges share: same kind and words within LEDGER_TWIN_MS,
  // not a ledger-built entry, not already claimed by another ledger row, and (a stamped prompt)
  // the same author.
  private isLedgerTwin(
    e: StreamEntry,
    kind: "text" | "user_message",
    row: { id: string; text: string; createdAt: number },
    author?: PromptAuthor,
  ): boolean {
    if (e.kind !== kind || this.ledgerEntryIds.has(e.id)) return false;
    if (e.aliasIds?.some((id) => id !== row.id)) return false;
    if (e.ts === undefined || !inTwinWindow(e.ts, row.createdAt)) return false;
    if (e.content.trim() !== row.text.trim()) return false;
    if (e.kind === "user_message" && e.promptAuthor && author) {
      return sameAuthor(e.promptAuthor, author);
    }
    return true;
  }

  private aliasLedgerId(agentId: string, entryId: string, ledgerId: string): void {
    this.history.open(agentId, "ledger", (w) => {
      const s = w.ensure();
      const at = s.entries.findIndex((e) => e.id === entryId);
      const e = s.entries[at];
      if (
        e &&
        (e.kind === "text" || e.kind === "user_message") &&
        !e.aliasIds?.includes(ledgerId)
      ) {
        const next = { ...e, aliasIds: [...(e.aliasIds ?? []), ledgerId] };
        w.assign(s, [...s.entries.slice(0, at), next, ...s.entries.slice(at + 1)]);
      }
      this.touch(w.agentId);
      w.judge({ messageId: ledgerId, chars: 0 });
      return { value: undefined };
    });
  }

  // The viewer's own ledger message as the prompt bubble the timeline draws for a human turn
  // (user_message + promptAuthor), placed by time like a merged reply. Idempotent by messageId.
  mergeLedgerUserMessage(
    agentId: string,
    messageId: string,
    text: string,
    ts?: number,
    author?: PromptAuthor,
  ): void {
    this.history.open(agentId, "ledger", (w) => {
      const s = w.ensure();
      if (s.entries.some((e) => e.kind === "user_message" && e.messageId === messageId)) {
        w.judge({ messageId, chars: text.length });
        return { value: undefined };
      }
      const stickyTs = typeof ts === "number" && Number.isFinite(ts) && ts > 0 ? ts : undefined;
      const entry: StreamEntry = {
        id: this.nextEntryId(),
        kind: "user_message",
        content: text,
        messageId,
        ...(stickyTs !== undefined ? { ts: stickyTs } : {}),
        ...(author ? { promptAuthor: author } : {}),
      };
      this.ledgerEntryIds.add(entry.id);
      const at = insertIndexForTs(s, stickyTs);
      w.assign(
        s,
        at >= 0
          ? [...s.entries.slice(0, at), entry, ...s.entries.slice(at)]
          : [...s.entries, entry],
      );
      this.capEntries(w, s);
      this.touch(w.agentId);
      w.judge({ messageId, chars: text.length });
      return { value: undefined };
    });
  }

  // An entry with an id pairs by id or is another message, so only id-less entries qualify.
  // The two stamps come from different clocks: the entry's ts is the daemon's timeline append,
  // the row's is the relay's insert, and the append can land after the insert (live QA: a
  // "Goal paused." row at :30.153, its timeline row at :30.181). So the window is two-sided,
  // and the nearest same-words entry pairs (one-to-one via isLedgerTwin).
  private unidentifiedTextTwin(
    s: AgentStream,
    messageId: string,
    text: string,
    ts: number | undefined,
  ): number {
    if (ts === undefined) return -1;
    const row = { id: messageId, text, createdAt: ts };
    let best = -1;
    let bestGap = Infinity;
    s.entries.forEach((e, i) => {
      if (e.kind !== "text" || e.messageId !== undefined || e.ts === undefined) return;
      if (!this.isLedgerTwin(e, "text", row)) return;
      const gap = Math.abs(e.ts - ts);
      if (gap < bestGap) {
        best = i;
        bestGap = gap;
      }
    });
    // A live timeline reply with no ts is stamped by the CLIENT clock, which can sit seconds off the
    // relay's created_at. The newest text, id-less, same words, a minute at most away, is that
    // reply's twin whatever the skew (CYBORG-1408 "Goal cleared." drawn twice).
    const last = s.entries.findLastIndex((e) => e.kind === "text");
    const e = s.entries[last];
    if (
      best < 0 &&
      e?.kind === "text" &&
      e.messageId === undefined &&
      e.ts !== undefined &&
      this.clientStampedEntryIds.has(e.id)
    ) {
      if (
        Math.abs(e.ts - ts) <= 60_000 &&
        this.isLedgerTwin(e, "text", { ...row, createdAt: e.ts })
      ) {
        best = last;
      }
    }
    return best;
  }

  // notice entry id -> the ledger id it already stands for (one-to-one, so two restarts' notices
  // each take their own row and a re-delivered row stays a no-op).
  private readonly noticeLedgerIds = new Map<string, string>();

  // CYBORG-1411: a daemon notice (the relive "was restarted while working…") is written twice on
  // purpose, as the timeline's muted notice row (with `**` stripped) and as the DM venue post. A
  // cybo DM draws both stores in one stream, so the ledger copy of a notice already on screen is
  // not drawn again.
  private claimNoticeTwin(
    s: AgentStream,
    messageId: string,
    text: string,
    ts: number | undefined,
  ): boolean {
    // Same words AND time: two restarts read alike, so an older row must not claim a newer notice.
    if (ts === undefined) return false;
    const words = text.replaceAll("**", "").trim();
    const twin = s.entries.find((e) => {
      if (e.kind !== "notice" || e.content.trim() !== words) return false;
      if (e.ts === undefined || Math.abs(e.ts - ts) > LEDGER_TWIN_MS) return false;
      const claimed = this.noticeLedgerIds.get(e.id);
      return claimed === undefined || claimed === messageId;
    });
    if (!twin) return false;
    this.noticeLedgerIds.set(twin.id, messageId);
    return true;
  }

  private mergeLedgerRow(
    w: ConversationWrite,
    agentId: string,
    messageId: string,
    text: string,
    ts: number | undefined,
    card: MessageCard | null | undefined,
    attachments?: Attachment[] | null, // null: a row stored with none
  ): void {
    const s = w.ensure();
    this.noteReplyFiles(s, messageId, attachments);
    // Same refusal as the live path above. workspace-relay stops NEW fabricated
    // notices ever becoming a durable row, but rows written before that guard
    // shipped are still in PG and still re-merge here — for the very user who
    // reported this. One line, same predicate, and they stop rendering.
    if (isSystemErrorText(text)) return;
    const stickyTs = typeof ts === "number" && Number.isFinite(ts) && ts > 0 ? ts : undefined;
    // Remember it BEFORE the dedupe return: a card learned here has to outlive
    // this call whether or not this call is the one that draws it (AgentStream.
    // stickyCards). Keyed on a real id only — an empty messageId can't be
    // reinstated against anything, and the surface refuses to draw a card
    // without one anyway (AgentStreamView's rendersCard).
    if (card && messageId) {
      s.stickyCards.set(messageId, {
        card,
        text,
        ...(stickyTs !== undefined ? { ts: stickyTs } : {}),
      });
    }
    // A message that kept its id across a tool call is several entries; its ledger row is all of them.
    const parts = s.entries.flatMap((e, i) =>
      e.kind === "text" && (e.messageId === messageId || !!e.aliasIds?.includes(messageId))
        ? [i]
        : [],
    );
    const already = parts[0] ?? -1;
    if (already >= 0) {
      // Was a bare `return`. The row being present is NOT proof its card is:
      // every other producer of a text entry (handleTimeline's replay, a live
      // assistant delta) knows nothing about cards, so the row can be sitting
      // there stripped. Upgrade it in place rather than merging a duplicate.
      if (card) this.applyCardAt(w, s, already, card);
      // The row is the message in full; the entry may be its TAIL only — a page that
      // started inside the message (#3193 F1/F4). A suffix is the one shape a cut can
      // leave, so it is the one shape repaired; an edit or a correction is shorter or
      // different and is left alone. Several parts: the row cannot say which stretch a
      // missing piece belongs to, and the rows fill it by seq — never repaired (CYBORG-1444).
      const held = s.entries[already];
      const folded = this.foldFollowingBlocks(s.entries, already, text, parts.length);
      if (parts.length === 1 && held.kind === "text" && isCutOf(held.content, text)) {
        w.assign(s, this.absorbJoinedReplyBlocks(s.entries, already, text));
        this.markKeptTail(s, held.id);
        this.touch(agentId);
      }
      if (folded.size > 0) {
        w.assign(
          s,
          s.entries.filter((e) => !folded.has(e.id)),
        );
        this.touch(agentId);
      }
      return;
    }
    // A command reply has no provider id and a uuid ledger id: the id-less twin takes the ledger id.
    const twin = this.unidentifiedTextTwin(s, messageId, text, stickyTs);
    if (twin >= 0) {
      const held = s.entries[twin] as StreamEntry & { kind: "text" };
      w.assign(
        s,
        s.entries.map((e, i) =>
          i === twin ? { ...held, messageId, ...(card ? { card } : {}) } : e,
        ),
      );
      this.touch(agentId);
      return;
    }
    if (this.claimNoticeTwin(s, messageId, text, stickyTs)) return;
    const entry: StreamEntry = {
      id: this.nextEntryId(),
      kind: "text",
      content: text,
      messageId,
      // intentional: a missing/absurd created_at (older relay, NaN, epoch 0) is
      // dropped rather than rendered — "no time" beats a wrong time, and matches
      // how pre-ts history already renders.
      ...(stickyTs !== undefined ? { ts: stickyTs } : {}),
      ...(card ? { card } : {}),
    };
    this.ledgerEntryIds.add(entry.id);
    // BY TIME, NOT AT THE END. This was a bare `[...s.entries, entry]`, and that
    // append was the whole of the "my DM pane opens on a week-old conversation"
    // report: the pane is ONE surface stitched from TWO stores — the pinned
    // session's transcript (daemon SQLite, newest-tail, correctly ordered) and the
    // cross-session cybo→human DM ledger (relay PG, fetched by mergeMissingCyboDms
    // below). Appending the ledger block put rows that are DAYS OLDER than the
    // loaded turns underneath them, and since every one of those rows carries a real
    // `ts`, AgentStreamView drew day separators for them (:592-603) — so the bottom
    // of the transcript, which is where the pane auto-pins and where a human reads
    // "latest", was a stack of "Thursday / Friday" dividers over week-old text. A
    // re-hydrate after a workspace round-trip re-appended the same block under that
    // day's real turn every single time.
    //
    // The append-only comment on mergeMissingCyboDms used to claim this was
    // unbuildable because "no StreamEntry variant carries a timestamp". That has not
    // been true since CYBORG-588: `StreamEntry.ts` (:221) is folded from the served
    // ISO `ts` by tsFromItem (:1650) and is already what the day separators and the
    // hover times read. So the clamp is buildable, and this is it.
    //
    // The LIVE path does not regress and cannot: a delta merged from the "dm"
    // broadcast is stamped at receive time, so it is newer than everything loaded,
    // insertIndexForTs returns -1, and it appends exactly as before.
    //
    // TRAILING turn_boundary ONLY WHEN THIS LANDS LAST — same reasoning
    // restoreStickyCards spells out for the identical insert: the boundary exists to
    // stop the next live delta gluing onto this row (findLastTextInTurn only ever
    // looks at the LAST entry), so mid-list it protects nothing and would draw a
    // divider through the middle of somebody else's turn.
    const at = insertIndexForTs(s, stickyTs);
    w.assign(
      s,
      at >= 0
        ? [...s.entries.slice(0, at), entry, ...s.entries.slice(at)]
        : [...s.entries, entry, this.ledgerDivider()],
    );
    this.capEntries(w, s);
    this.checkOutOfOrder(agentId, s, entry.id, "ledger");
    this.touch(agentId);
  }

  private markKeptTail(s: AgentStream, id: string): void {
    const e = s.entries.find((x) => x.id === id);
    if (e?.kind === "text" && e.parts?.some((p) => p.ledger === "tail"))
      this.ledgerEntryIds.add(id);
  }

  // T-69 reverse order: this block is the unstreamed tail of the row's last entry; a hit spends it.
  private spendLedgerTail(w: ConversationWrite, s: AgentStream, text: string): boolean {
    let at = s.entries.length - 1;
    if (this.ledgerDividerIds.has(s.entries[at]?.id ?? "")) at -= 1;
    const e = s.entries[at];
    if (!text || e?.kind !== "text" || !e.parts?.some((p) => p.ledger === "tail")) return false;
    const body = chunksOf(e);
    if (ledgerChunk(body, "tail")[0]?.text !== text || body.every((c) => c.ledger)) return false;
    const spent = bodyOfChunks(body.map(({ ledger: _l, ...c }) => c));
    w.assign(
      s,
      s.entries.map((x, i) => (i === at ? withTextBody(e, spent) : x)),
    );
    return true;
  }

  // Ids of the text entries right after `at` that together spell exactly the rest of `full`; else none.
  private foldFollowingBlocks(
    entries: readonly StreamEntry[],
    at: number,
    full: string,
    partCount: number,
  ): Set<string> {
    const head = entries[at];
    const drop = new Set<string>();
    if (partCount !== 1 || head?.kind !== "text" || !full.startsWith(head.content)) return drop;
    const rest = full.slice(head.content.length);
    let used = 0;
    for (let i = at + 1; i < entries.length && used < rest.length && drop.size < 32; i += 1) {
      const e = entries[i];
      if (e.kind !== "text" || e.messageId === head.messageId || this.ledgerEntryIds.has(e.id))
        break;
      if (!rest.startsWith(e.content, used)) break;
      used += e.content.length;
      drop.add(e.id);
    }
    return used === rest.length ? drop : new Set();
  }

  // Repair the matched entry to the joined text and fold the contiguous live siblings the relay's
  // blank-line join absorbed, so the entry count equals the one row the relay persists (#3193 cut).
  private absorbJoinedReplyBlocks(
    entries: readonly StreamEntry[],
    at: number,
    full: string,
  ): StreamEntry[] {
    const head = entries[at];
    if (head?.kind !== "text") return [...entries];
    const drop = new Set<number>();
    let consumed = head.content;
    for (let i = at + 1; i < entries.length && consumed !== full; i += 1) {
      const e = entries[i];
      if (e.kind !== "text" || this.ledgerEntryIds.has(e.id)) break;
      const next = `${consumed}\n\n${e.content}`;
      if (full !== next && !full.startsWith(`${next}\n\n`)) break;
      drop.add(i);
      consumed = next;
    }
    return entries
      .map((e, i) => (i === at ? withLedgerText(head, full) : e))
      .filter((_, i) => !drop.has(i));
  }

  // The authoritative half of a card's DUAL settle (CYBORG-792): the server
  // re-rendered a card after a signed action resolved it and broadcast
  // `cyborg:message_card_updated`. app.svelte.ts's handler patches every store
  // that renders a Message (channel / dm / thread); a merged DM card lives in a
  // stream ENTRY instead, which that patch could never reach — so a card clicked
  // in AgentStreamView settled only optimistically and reverted to buttons on the
  // next broadcast-driven re-render. Same defect AnchorConversation.svelte:228
  // works around with its own subscription; fixed here at the ONE place every
  // caller already routes through instead of per-surface.
  //
  // messageId-keyed across ALL streams: the ledger id is globally unique and the
  // same card can be merged into more than one agent's stream, so this patches
  // wherever it landed rather than needing the caller to know the agentId.
  //
  // The settle is written to stickyCards too, not just to the entry: a settle that
  // only lives in `entries` is undone by the very next rebuild, which is how a
  // tapped card reverted to buttons. A `null` card is the server saying this row
  // has no card at all — FORGET it, so nothing reinstates the pre-settle version.
  // The registry is updated even for a stream whose entry was already clobbered
  // away, which is the case that most needs it.
  patchCardByMessageId(messageId: string, card: MessageCard | null): void {
    const targets = [...this.streams].filter(
      ([, s]) =>
        s.stickyCards.has(messageId) ||
        s.entries.some((e) => e.kind === "text" && e.messageId === messageId),
    );
    // All or none: a conversation with a write open refuses before any other is patched.
    for (const [agentId] of targets) {
      if (this.history.isWriting(agentId)) {
        throw new HistoryInvariantError(
          "write-open",
          `cards: a write to ${agentId} is already open`,
        );
      }
    }
    for (const [agentId, s] of targets) {
      // Each conversation holding the card is written under its own lease (#3382 A-4a).
      const patched = this.history.lease(agentId, "cards", (w) => ({
        value: this.patchCard(w, s, messageId, card),
      }));
      if (patched) this.touch(agentId);
    }
  }

  // A server-deleted message (an author's delete, a rewind cut elsewhere) leaves the views of its
  // own workspace live; a stream bound to another workspace never loses a row to it.
  dropDeletedMessage(messageId: string, workspaceId: string): void {
    const holds = (e: StreamEntry): boolean =>
      (e.kind === "text" && (e.messageId === messageId || e.aliasIds?.includes(messageId))) ||
      (e.kind === "user_message" &&
        (e.sourceMessageId === messageId ||
          e.messageId === messageId ||
          e.aliasIds?.includes(messageId) ||
          e.providerIds?.includes(messageId))) ||
      false;
    const targets = [...this.streams].filter(
      ([id, s]) => this.workspaceByAgent.get(id) === workspaceId && s.entries.some(holds),
    );
    for (const [agentId] of targets) {
      if (this.history.isWriting(agentId)) {
        throw new HistoryInvariantError(
          "write-open",
          `delete: a write to ${agentId} is already open`,
        );
      }
    }
    for (const [agentId, s] of targets) {
      // Judged and journaled as a deliberate loss: a delete is not a shrink to report.
      this.history.write(agentId, "live", undefined, (w) => {
        w.assign(
          s,
          s.entries.filter((e) => !holds(e)),
        );
        w.judge({ deliberate: "deleted" });
        return { value: undefined };
      });
      this.touch(agentId);
    }
  }

  private patchCard(
    w: ConversationWrite,
    s: AgentStream,
    messageId: string,
    card: MessageCard | null,
  ): boolean {
    const sticky = s.stickyCards.get(messageId);
    if (sticky) {
      if (card) s.stickyCards.set(messageId, { ...sticky, card });
      else s.stickyCards.delete(messageId);
    }
    const idx = s.entries.findIndex((e) => e.kind === "text" && e.messageId === messageId);
    if (idx < 0) return false;
    const entry = s.entries[idx];
    if (entry.kind !== "text") return false;
    w.assign(s, [...s.entries.slice(0, idx), { ...entry, card }, ...s.entries.slice(idx + 1)]);
    return true;
  }

  // Put `card` on the text entry at `idx`. Callers have already established the
  // index; no touch() — they own the notification.
  private applyCardAt(w: ConversationWrite, s: AgentStream, idx: number, card: MessageCard): void {
    const entry = s.entries[idx];
    if (entry?.kind !== "text" || entry.card === card) return;
    w.assign(s, [...s.entries.slice(0, idx), { ...entry, card }, ...s.entries.slice(idx + 1)]);
    this.touch(w.agentId);
  }

  // Reinstate every card this stream has been told about but that the entry list
  // no longer shows correctly. Called from the ONE write that rebuilds the list
  // (hydrateFromTimeline), which is the write that destroys them.
  //
  // WHY ON THE WRITE AND NOT IN getEntries, where the orphan-tool-card sweep
  // lives: that sweep only ever REWRITES rows, so a pure read-side fold is
  // invisible to everything else. This has to re-INSERT a row, and a row that
  // exists in the read view but not in `s.entries` is a row that
  // mergeCrossSessionReply's dedupe and mergeMissingCyboDms' loadedIds/floor both
  // fail to see — so the next merge appends it AGAIN and the card renders twice.
  // Cards must be restored where the list is written.
  //
  // Two shapes, because both actually happen:
  //  - row still there, card stripped  → re-attach (any rebuild that keeps the row)
  //  - row gone entirely               → re-insert (the offer-card shape: no
  //    agent_timeline_rows row exists, so the rebuild cannot reproduce it)
  private restoreStickyCards(w: ConversationWrite, s: AgentStream): void {
    if (s.stickyCards.size === 0) return;
    for (const [messageId, row] of s.stickyCards) {
      const idx = s.entries.findIndex((e) => e.kind === "text" && e.messageId === messageId);
      if (idx >= 0) {
        this.applyCardAt(w, s, idx, row.card);
        continue;
      }
      // Re-insert at its ledger time so a restored card lands where it was drawn
      // rather than jumping to the bottom of the transcript — the rebuilt rows
      // carry the persisted `ts` this compares against (CYBORG-588). SAME helper
      // mergeCrossSessionReply uses, deliberately: both paths re-insert the very
      // same merged DM row, so if they disagreed on its index a hydrate would move
      // a message that nobody touched.
      const at = insertIndexForTs(s, row.ts);
      const entry: StreamEntry = {
        id: this.nextEntryId(),
        kind: "text",
        content: row.text,
        messageId,
        ...(row.ts !== undefined ? { ts: row.ts } : {}),
        card: row.card,
      };
      // The trailing turn_boundary is only needed when this lands LAST — it is
      // there to stop the next live assistant delta gluing itself onto this row
      // (see mergeCrossSessionReply). Mid-list the row is never `entries.at(-1)`,
      // so findLastTextInTurn can't reach it, and a boundary there would draw a
      // divider through the middle of a turn.
      w.assign(
        s,
        at >= 0
          ? [...s.entries.slice(0, at), entry, ...s.entries.slice(at)]
          : [...s.entries, entry, this.boundaryAt(undefined)],
      );
    }
    this.touch(w.agentId);
  }

  // Record the committed high-water seq observed from a LIVE stream frame (the
  // top-level `seq` the daemon stamps on a committed cyborg:agent_stream). Keeps the
  // monotonic-read guard aware of turns that streamed in but were never re-fetched, so
  // a later stale fetch (mirror lag / reconnect race) can't clobber them.
  noteCommittedSeq(agentId: string, seq: number, life?: string): void {
    if (!Number.isFinite(seq)) return;
    const s = this.ensure(agentId);
    // A frame of another life restarts the seqs: the old life's high-water is not this one's.
    const newLife =
      typeof life === "string" && life && s.liveLife !== undefined && life !== s.liveLife;
    if (newLife || s.lastMaxSeq == null || seq > s.lastMaxSeq) s.lastMaxSeq = seq;
    if (typeof life === "string" && life) s.liveLife = life;
  }

  // The highest committed row seq this stream has seen, if any.
  committedHighWater(agentId: string): number | undefined {
    return this.streams.get(agentId)?.lastMaxSeq ?? undefined;
  }

  hydrateFromTimeline(
    agentId: string,
    items: Record<string, unknown>[],
    meta?: HydrateMeta,
  ): boolean {
    // Another life restarts seq: the old life's row sessions name nothing in it.
    if (meta?.mode === "rewind" || this.pageLifeReplaces(agentId, meta?.life)) {
      this.beforeTranscriptWipe?.(agentId);
      this.forgetLongLived(agentId);
      this.rowSessions.delete(agentId);
      for (const [id, facts] of this.sessionFacts) {
        if (facts.agentId === agentId) this.sessionFacts.delete(id);
      }
    }
    const verdict = this.lossyPageVerdict(agentId, items, meta);
    if (verdict) return this.takeLossyPage(agentId, items, meta, verdict);
    const proven = this.provenLiveRows(agentId, items, meta?.life);
    const shed = this.pageLifeReplaces(agentId, meta?.life) && this.provenShed(agentId);
    this.noteRowSessions(agentId, items);
    const accepted = this.history.create(agentId, "hydrate", (w) => ({
      value: this.hydratePage(w, agentId, markLegacyRelivePairs(items), meta),
    }));
    if (accepted && proven.length > 0) this.replayProvenRows(agentId, proven);
    // The establishing replace reset coverage: the rows shed past the budget are still owed.
    if (accepted && shed) this.noteDroppedDeltas(agentId);
    this.outbox.settleAnsweredPrompt(agentId);
    return accepted;
  }

  // The view came from a life-less mirror page and no page has named its life since.
  isLifeUnproven(agentId: string): boolean {
    return this.streams.get(agentId)?.lifeUnproven === true;
  }

  // A live row a frame named its life for, kept while the view's own life is unproven.
  noteLiveRow(agentId: string, seq: number, life: string, item: unknown): void {
    const s = this.streams.get(agentId);
    if (!s?.lifeUnproven || typeof item !== "object" || item === null) return; // null: no row
    // One life, one row budget: a row shed (an older life's, or past the cap) is owed a read-back.
    if (s.provenLive?.life !== life) {
      const shed = (s.provenLive?.rows.size ?? 0) > 0;
      if (shed) this.shedProvenRows(agentId, "life");
      s.provenLive = { life, rows: new Map(), shed: shed || s.provenLive?.shed };
    }
    if (s.provenLive.rows.size >= PROVEN_LIVE_MAX_ROWS) {
      s.provenLive.shed = true;
      return this.shedProvenRows(agentId, "cap");
    }
    s.provenLive.rows.set(seq, { ...(item as Record<string, unknown>), seq }); // cast: a timeline item
  }

  private provenShed(agentId: string): boolean {
    return this.streams.get(agentId)?.provenLive?.shed === true;
  }

  private shedProvenRows(agentId: string, reason: "life" | "cap"): void {
    this.record(agentId, "live", { outcome: "proven-rows-shed", reason });
    this.noteDroppedDeltas(agentId);
  }

  // The proven live rows of the life a page establishes over an unproven view, minus the page's.
  private provenLiveRows(
    agentId: string,
    items: Record<string, unknown>[],
    life: string | undefined,
  ): Record<string, unknown>[] {
    const s = this.streams.get(agentId);
    const rows = s?.provenLive?.life === life ? s?.provenLive?.rows : undefined;
    if (!s?.lifeUnproven || !rows) return [];
    const onPage = new Set(items.map((raw) => raw.seq));
    return [...rows.entries()]
      .filter(([seq]) => !onPage.has(seq))
      .toSorted(([x], [y]) => x - y)
      .map(([, row]) => row);
  }

  private replayProvenRows(agentId: string, rows: Record<string, unknown>[]): void {
    this.mergeDurableRows(agentId, rows);
    const top = rows.at(-1)?.seq;
    const s = this.streams.get(agentId);
    if (s && typeof top === "number") s.lastMaxSeq = Math.max(s.lastMaxSeq ?? top, top);
  }

  // undefined: the page may replace the view. A life-less mirror page over a view of a known life
  // is kept out whole (a rewind never reaches the mirror: its rows may be another life's); a page
  // naming the view's life that lacks rows on screen is merged.
  private lossyPageVerdict(
    agentId: string,
    items: Record<string, unknown>[],
    meta: HydrateMeta | undefined,
  ): { action: "merged" | "kept"; missing: number } | undefined {
    const s = this.streams.get(agentId);
    if (!s || !this.pageWouldReplace(agentId, s, items, meta)) return undefined;
    const held = s.life ?? s.liveLife;
    const lifeless = meta?.source === "mirror" && meta.life === undefined;
    const missing = committedRowsMissingFromPage(s.entries, items);
    if (lifeless && held !== undefined) return { action: "kept", missing };
    if (typeof meta?.maxSeq !== "number" || missing === 0) return undefined;
    if (lifeless) return { action: "kept", missing };
    return meta.life !== undefined && meta.life === held
      ? { action: "merged", missing }
      : undefined;
  }

  // The guard would accept the page as a same-life replace of a populated agent view.
  private pageWouldReplace(
    agentId: string,
    s: AgentStream,
    items: Record<string, unknown>[],
    meta: HydrateMeta | undefined,
  ): boolean {
    if (s.entries.length === 0 || isSessionStreamKey(agentId)) return false;
    const mode = this.pageLifeReplaces(agentId, meta?.life) ? "rewind" : meta?.mode;
    if (this.keepModeFor(s, mode, meta?.life) === "rewind") return false;
    return evaluateTimelineSnapshot({
      currentMaxSeq: s.lastMaxSeq,
      snapshotMaxSeq: meta?.maxSeq,
      mode,
      snapshotCount: items.length,
      currentCount: s.entries.length,
      retired: meta?.retired,
    }).accept;
  }

  private takeLossyPage(
    agentId: string,
    items: Record<string, unknown>[],
    meta: HydrateMeta | undefined,
    { action, missing }: { action: "merged" | "kept"; missing: number },
  ): false {
    if (action === "merged") this.mergeLossyPage(agentId, items, meta);
    const source = meta?.source === "mirror" ? "mirror" : "daemon";
    this.record(agentId, "hydrate", { outcome: action, source, missing, page: pageRange(items) });
    const workspaceId = this.workspaceForAgent(agentId) ?? workspaceState.current?.id;
    if (workspaceId) {
      reportKpi("agents.transcript.page_merged", {
        source,
        action,
        mode: meta?.mode ?? "none",
        missing: Math.min(missing, 1_000_000),
        workspaceId,
      });
    }
    this.touch(agentId);
    return false;
  }

  // Adds what the page has, takes nothing; not the view (uncached, cursor only if none).
  private mergeLossyPage(
    agentId: string,
    items: Record<string, unknown>[],
    meta: HydrateMeta | undefined,
  ): void {
    this.mergeDurableRows(agentId, items);
    const s = this.ensure(agentId);
    if (typeof meta?.maxSeq === "number") {
      s.lastMaxSeq = Math.max(s.lastMaxSeq ?? meta.maxSeq, meta.maxSeq);
    }
    if (meta?.settled !== undefined) s.settled = meta.settled;
    if (s.life === undefined && meta?.life !== undefined) s.life = meta.life;
    if (!s.hydrated) {
      s.hydrated = true;
      notePageProvenance(s, meta);
      this.adoptPageCursor(s, meta, false);
      s.hydrationNonce += 1;
    }
  }

  // Nothing across a rewind; never the "unconfirmed" row, a claim the page itself settles.
  private cardsAPageMayKeep(
    agentId: string,
    s: AgentStream,
    keep: "refresh" | "rewind" | undefined,
  ): readonly StreamEntry[] {
    if (keep === "rewind") return [];
    const unconfirmed = this.outbox.promptPendingErrorRows.get(agentId)?.errorEntryId;
    return s.entries.filter((e) => e.id !== unconfirmed);
  }

  private hydratePage(
    w: ConversationWrite,
    agentId: string,
    items: Record<string, unknown>[],
    meta: HydrateMeta | undefined,
  ): boolean {
    const s = w.stream;
    // Monotonic-read guard (pure decision in timeline-monotonic-guard.ts): reject a
    // stale snapshot that would move the timeline BACKWARD and delete on-screen
    // messages (the 2026-07-19 regression). Keep the fresher view. A rewind (force)
    // legitimately shrinks the timeline and is accepted; an older daemon that sends no
    // maxSeq keeps the legacy replace behavior.
    // A page from another durable life is a rewind made elsewhere: it replaces, like ours.
    const mode = this.pageLifeReplaces(agentId, meta?.life) ? "rewind" : meta?.mode;
    const decision = evaluateTimelineSnapshot({
      currentMaxSeq: s.lastMaxSeq,
      snapshotMaxSeq: meta?.maxSeq,
      mode,
      // An empty page must not wipe a populated view, whatever the mode — the counts
      // are what let the guard tell "this session is new" from "the read returned
      // nothing". See the empty rule in timeline-monotonic-guard.ts.
      snapshotCount: items.length,
      currentCount: s.entries.length,
      retired: meta?.retired,
    });
    if (!decision.accept) {
      this.noteHydrate(w, "hydrate:rejected", mode, meta, items);
      this.noteRefusedPage(agentId, s, decision.refused, mode, meta);
      // Lifecycle is independent of the fetched row window. Pagination is not:
      // keep its cursor and completeness with the accepted rows, otherwise a
      // rejected response can disable older history or skip an unloaded interval.
      if (meta?.settled !== undefined) s.settled = meta.settled;
      this.touch(agentId);
      return false;
    }
    // A reconnect can beat the daemon's background timeline append. Keep local
    // prompts until this exact client id appears in the durable page; replacing
    // the array before that point made the newest prompt disappear mid-turn.
    const optimisticRows = optimisticRowsMissingFromSnapshot(s.entries, items);
    // Rows this page does NOT cover. A page is a WINDOW, not the whole transcript, and
    // replacing the list with it deleted everything above the window's floor — the
    // "my earlier messages disappeared" class. Retention is one-sided and strictly
    // BELOW that floor, which is what keeps it from being a blind union: it cannot
    // duplicate a streamed delta (live rows carry no seq), cannot bridge a hole
    // (contiguity is required), and cannot resurrect a rewound row (a rewind restarts
    // at seq 1, so there is no floor above it to retain under).
    if (mode === "rewind") this.bumpGeneration(agentId);
    const keep = this.keepModeFor(s, mode, meta?.life);
    const retained = this.rowsKeptUnder(agentId, s, items, keep, meta);
    const heldAbove = seqlessHeldAbove(s.entries, items, keep, optimisticRows);
    const heldText = textHeldOnScreen(s.entries, keep);
    const onScreen = this.cardsAPageMayKeep(agentId, s, keep);
    // BEFORE the replay: the fold below reads it on every tool_call row, so an
    // orphaned card is resolved as its row is applied rather than patched up after.
    if (meta?.settled !== undefined) s.settled = meta.settled;
    if (meta?.hasNewer) this.trimAboveForwardWindow(agentId, s, items);
    w.assign(s, []);
    s.openTurnSeq = undefined;
    // A fresh replay is not the depth an earlier page had read.
    s.keepFloor = 0;
    s.applied = new Set();
    // Replay through the SAME merge logic as the live stream (handleTimeline):
    // the daemon persists a reply as many delta items (one per chunk), so a naive
    // 1:1 mapping rendered the text split across dozens of lines and produced
    // several "thinking" blocks per turn. handleTimeline merges consecutive text
    // (mergeText) and reasoning, and dedups tool_calls / the echoed prompt, so
    // reloaded history matches what was shown live.
    this.replayHydratedPage(w, s, agentId, items, retained);
    w.assign(s, restoreHeldText(s.entries, heldText, items));
    w.assign(s, mergeOptimisticRows(s.entries, settleRehydratedRows(optimisticRows)));
    for (const card of cardsKeptByPage(onScreen, s.entries)) w.assign(s, seatCard(s.entries, card));
    this.spliceRetainedRows(w, s, retained);
    this.spliceHeldAbove(w, s, heldAbove);
    rebuildApplied(s);
    this.forgetFailureCardsOffScreen(agentId, s);
    // NO ORPHAN SWEEP HERE — it lives in getEntries, the single read. A sweep on this
    // ONE write path missed every entry that arrives through prependTimeline, which is
    // how the orphans on this session actually got in. See getEntries.
    //
    // Cards, unlike orphaned tool cards, MUST be restored here: the replay above just
    // dropped every row the timeline has no source for, and an offer card is exactly
    // such a row. See restoreStickyCards for why this can't live on the read.
    this.restoreStickyCards(w, s);
    s.hydrated = true;
    // Assigned, never OR-ed: a server hydrate landing on a cache-painted stream is
    // exactly the event that makes the history verified, so it must clear the mark.
    // Only reached on an ACCEPTED snapshot — a page the monotonic guard rejected
    // confirmed nothing, so it leaves the stream as unverified as it found it.
    notePageProvenance(s, meta);
    // Read off the flag set just above, so this adds no branch here.
    rearmTimelineGone(s, s.hydratedFromCache);
    // Signal a fresh (re-)hydration to any viewer watching this stream. entries was
    // just reset to the server's latest page, so the short-page auto-loader must
    // re-arm even when the reseeded window is identical in length to the prior one.
    s.hydrationNonce += 1;
    // Re-attach any pending interactive tool call (permission / AskUserQuestion)
    // mirrored to localStorage before the reload. The timeline itself carries no
    // pending-permission payload, so without this a prompt that was awaiting input
    // at reload time rehydrates as a stuck "(running)" row with no answerable card.
    // No-op on a live WS-reconnect hydrate (the in-memory map is still populated).
    this.restorePendingPermissions(agentId, s);
    this.dropSettledPlanRequests(agentId, s);
    this.adoptPageCursor(s, meta, heldAbove.length > 0);
    // Update the high-water mark from the accepted snapshot (rewind resets to the new
    // epoch's smaller max; otherwise it only rises). Same pure decision as the guard.
    s.lastMaxSeq = decision.nextMaxSeq;
    adoptPageLife(s, meta?.life, mode);
    // `keep` is the mode that decided what stayed: a rewind, or an unproven paint read as one.
    this.noteHydrate(w, "hydrate", keep, meta, items, retained.length);
    this.checkPromptBubbles(agentId, s, "hydrate");
    this.touch(agentId);
    return true;
  }

  // The screen's rows above a forward window are paged back in newer: not this hydrate's loss.
  private trimAboveForwardWindow(
    agentId: string,
    s: AgentStream,
    items: Record<string, unknown>[],
  ): void {
    const top = pageRange(items).maxSeq;
    if (top === undefined) return;
    const above = [...screenRows(s.entries, seqsOf)].filter((seq) => seq > top);
    this.judge.trimmed(agentId, new Set(above));
  }

  // A refused EMPTY page is an invariant report (a stale one is the guard's routine).
  private noteRefusedPage(
    agentId: string,
    s: AgentStream,
    refused: "empty_page" | "stale" | undefined,
    mode: "refresh" | "rewind" | undefined,
    meta: { source?: string } | undefined,
  ): void {
    if (refused !== "empty_page") return;
    this.invariants.report(
      "empty_page_refused",
      {
        agentId,
        workspaceId: workspaceState.current?.id,
        readSource: meta?.source ?? "daemon",
        mode,
        onScreen: s.entries.length,
      },
      Date.now(),
      "",
      // A blind read is the relay answering for a daemon that is away: empty by design.
      meta?.source === "blind" ? "expected" : undefined,
    );
  }

  // After a write that rebuilt or merged the transcript: are any prompt bubbles provably
  // the same send twice, or an authorless copy of a signed one?
  private checkPromptBubbles(agentId: string, s: AgentStream, writer: string): void {
    const duplicates = duplicatePromptBubbles(s.entries, this.reconciledPromptEntryIds);
    if (duplicates > 0) {
      this.invariants.report("duplicate_prompt_bubble", { agentId, writer, duplicates });
    }
    const twins = unattributedTwinBubbles(s.entries);
    if (twins > 0) {
      this.invariants.report("unattributed_user_bubble", { agentId, writer, twins });
    }
  }

  // A live committed row seated above a prompt that has no row seq: the screen reads the
  // reply above the question it answers (the session body of 2026-10-07).
  private checkLivePlacement(agentId: string, added: readonly string[], v?: PromptVerdict): void {
    if (added.length === 0) return;
    const prompts = promptsBelowLiveRow(this.ensure(agentId).entries, new Set(added), v);
    if (prompts > 0) {
      this.invariants.report("live_row_above_newer_prompt", {
        agentId,
        writer: "live",
        prompts,
      });
    }
  }

  // Only for inserts placed by time (the ledger merge, #2532). A seq-ordered merge is
  // not judged: a live delta is stamped at receipt, so its time is not the row's.
  private checkOutOfOrder(agentId: string, s: AgentStream, id: string, writer: string): void {
    const rows = outOfOrderRows(s.entries, new Set([id]));
    if (rows > 0) this.invariants.report("out_of_order_insert", { agentId, writer, rows });
  }

  private noteHydrate(
    w: ConversationWrite,
    writer: JudgedWriter,
    mode: "refresh" | "rewind" | undefined,
    meta: { source?: string; life?: string; fromCache?: boolean } | undefined,
    items: Record<string, unknown>[],
    kept?: number,
  ): void {
    const page = pageRange(items);
    const before = w.before;
    w.judge(
      {
        mode,
        source: meta?.source,
        life: meta?.life,
        fromCache: meta?.fromCache === true,
        page,
        kept,
        deliberate: deliberateDrop(mode, before, page.minSeq),
      },
      writer,
    );
  }

  heldLife(agentId: string): string | null {
    const s = this.streams.get(agentId);
    return s?.life ?? s?.liveLife ?? null; // null: the wire's "holds no life"
  }

  // True when a page names a durable life other than the one on screen. A page with no
  // life (an older daemon, the relay's mirror) never differs: unknown is not "another".
  lifeDiffers(agentId: string, life: string | undefined): boolean {
    const s = this.streams.get(agentId);
    const held = s?.life ?? s?.liveLife;
    return life !== undefined && held !== undefined && life !== held;
  }

  // A page that names a life replaces a view of another life, or one a life-less mirror page drew
  // (its proven live rows are replayed after, see provenLiveRows).
  pageLifeReplaces(agentId: string, life: string | undefined): boolean {
    if (this.lifeDiffers(agentId, life)) return true;
    const s = this.streams.get(agentId);
    return life !== undefined && s?.lifeUnproven === true;
  }

  // Another life, or a named one over an incomplete view that holds none (knowledge/125:
  // a view painted from an unverified mirror page, whose cursor the mirror then reset).
  lifeReplaces(agentId: string, life: string | undefined): boolean {
    if (this.lifeDiffers(agentId, life)) return true;
    const s = this.streams.get(agentId);
    if (life === undefined) return false;
    // A view a life-less mirror page drew proves no life, so a reset naming one replaces it.
    return s?.lifeUnproven === true || (s?.historyIncomplete === true && s.life === undefined);
  }

  // What a page keeps of the screen below it (nothing across a rewind); a hole between the
  // two is reported for read-back, the rows it would have dropped stay.
  // What a page may keep of the screen: nothing across a rewind, and nothing of a cache paint
  // that names no life (written before lives were cached) under a page that names one — it
  // cannot prove it is that life, so neither its rows nor its text survive the page.
  private keepModeFor(
    s: AgentStream,
    mode: "refresh" | "rewind" | undefined,
    pageLife: string | undefined,
  ): "refresh" | "rewind" | undefined {
    const unproven = s.hydratedFromCache && s.life === undefined && pageLife !== undefined;
    return unproven ? "rewind" : mode;
  }

  private rowsKeptUnder(
    agentId: string,
    s: AgentStream,
    items: Record<string, unknown>[],
    mode: "refresh" | "rewind" | undefined,
    marks: PageCutMarks | undefined,
  ): readonly StreamEntry[] {
    if (mode === "rewind") return [];
    // A copy far under the page is only a preview: scroll-up reads what lies below it, no read-back.
    const preview = s.hydratedFromCache || s.hydratedFromMirror;
    const below = rowsBelowPageFloor(s.entries, items);
    const holeRows = below.hole ? below.hole.before - below.hole.after - 1 : 0;
    if (preview && holeRows > PREVIEW_HOLE_KEEP_MAX_ROWS) return [];
    // Once this hydrate is done: the read-back continues from the page's own cursor.
    const hole = below.hole;
    // A rewind or clear before it runs makes this hole another life's: it is dropped.
    const generation = this.generationOf(agentId);
    if (hole) {
      this.openPageHole(agentId, hole, generation, marks);
    }
    return below.rows;
  }

  // A repeat echo a kept prompt already folded in is applied, as an absorbed echo is, never redrawn.
  private replayHydratedPage(
    w: ConversationWrite,
    s: AgentStream,
    agentId: string,
    items: readonly Record<string, unknown>[],
    retained: readonly StreamEntry[],
  ): void {
    const folded = seqsFoldedInto(retained);
    for (const raw of items) {
      const item = raw as unknown as AgentTimelineItem;
      const seq = raw.seq;
      if (item.type === "user_message" && typeof seq === "number" && folded.has(seq))
        s.applied.add(seq);
      else this.replayHydratedRow(w, s, agentId, item, seq);
    }
  }

  /** Put back the rows a narrower page did not cover, above it, bridged like a prepend. */
  // A prompt row starts a turn unless it is an echo — asked BEFORE its divider exists, or
  // the divider would read as a closed turn.
  private replayHydratedRow(
    w: ConversationWrite,
    s: AgentStream,
    agentId: string,
    row: AgentTimelineItem,
    rawSeq: unknown,
  ): void {
    const seq = typeof rawSeq === "number" ? rawSeq : undefined;
    if (seq !== undefined) s.applied.add(seq);
    const item = asDrawnPromptRow(row);
    if (!item) return;
    // CYBORG-1411: before closeTurn, so a hidden row never leaves an orphan divider.
    if (item.type === "user_message" && isHiddenReliveRow(item)) return;
    if (item.type === "user_message") {
      const text = stripDmPrefix(stripMentionEnvelope(item.text));
      if (this.absorbPromptEcho(w, s, agentId, item, text, seq, parseIsoTs(item.ts))) return;
      if (s.entries.length > 0) {
        w.assign(s, [...s.entries, this.closeTurn(s)]);
      }
    }
    const prev = s.entries;
    this.handleTimeline(w, agentId, item, seq);
    if (seq !== undefined) stampCommittedSeq(w, s, seq, prev);
  }

  private spliceHeldAbove(
    w: ConversationWrite,
    s: AgentStream,
    held: readonly StreamEntry[],
  ): void {
    if (held.length === 0) return;
    w.assign(s, joinAbove(held, s.entries));
    this.capEntries(w, s);
  }

  // The page's cursor points just above the page; with rows kept above it, the screen's top is
  // still where the stream's own cursor points, so a load-older never re-reads kept rows (#4401).
  private adoptPageCursor(s: AgentStream, meta: HydrateMeta | undefined, keptAbove: boolean): void {
    if (!meta || keptAbove) return;
    s.hasOlder = meta.hasOlder ?? false;
    s.olderStated = meta.olderProven === true;
    s.olderCursor = meta.olderCursor ?? null;
  }

  private spliceRetainedRows(
    w: ConversationWrite,
    s: AgentStream,
    retained: readonly StreamEntry[],
  ): void {
    if (retained.length === 0) return;
    // Same bridge rule prependTimeline uses, so two dividers can never meet.
    const headIsBoundary = s.entries[0]?.kind === "turn_boundary";
    const tailIsBoundary = retained[retained.length - 1]?.kind === "turn_boundary";
    const bridge =
      s.entries.length > 0 && !headIsBoundary && !tailIsBoundary
        ? [this.boundaryAt(undefined)]
        : [];
    w.assign(s, [...retained, ...bridge, ...s.entries]);
    this.capEntries(w, s);
  }

  // Prepend an OLDER page of timeline history (scroll-up lazy-load). Rows that carry a
  // seq enter through ingestRow — the same fold as live and the reconcile, so a page
  // split anywhere reads as one hydrate would (#3248 G1–G3); a page without one is
  // folded in isolation and spliced at the front (prependFoldedPage). Neither touches
  // this stream's turn status / usage.
  prependTimeline(agentId: string, items: Record<string, unknown>[], meta: OlderPageMeta): void {
    this.noteRowSessions(agentId, items);
    this.history.open(agentId, "prepend", (w) => {
      this.prependPage(w, agentId, markLegacyRelivePairs(items), meta);
      w.judge({ page: pageRange(items), hasOlder: meta.hasOlder, source: meta.source });
      return { value: undefined };
    });
  }

  private prependPage(
    w: ConversationWrite,
    agentId: string,
    items: Record<string, unknown>[],
    meta: OlderPageMeta,
  ): void {
    const s = w.ensure();
    // CYBORG-1056: a "blind" page is a routing guess, not end-of-history — keep
    // today's hasOlder/cursor instead of adopting this page's false hasOlder:false.
    if (meta.source === "blind") {
      this.touch(agentId);
      return;
    }
    s.hasOlder = meta.hasOlder;
    s.olderStated = meta.olderProven === true;
    s.olderCursor = meta.olderCursor;
    if (items.length === 0) {
      this.touch(agentId);
      return;
    }
    // Rows that know their seq take the ONE ingest every path uses (#3248 G1–G3): each
    // lands at its seq, an entity's earlier rows update the card the screen has (its
    // position moves to its first row), the halves of a message re-join, and a prompt
    // fences its turn — so a page split at any boundary folds as one hydrate would.
    if (items.every((raw) => typeof raw.seq === "number")) {
      // Cap-evicted rows may come back through this page, and no row's cap may trim the page:
      rebuildApplied(s);
      this.reserveKeepFloor(s, s.entries.length + 2 * items.length); // a row adds ≤ 2 entries
      const rows = items.toSorted((x, y) => (x.seq as number) - (y.seq as number));
      // Newest first, so the rows under a reply repaired from the ledger take over its
      // head row by row before the oldest-first ingest could land them beside it (#3238).
      for (const raw of rows.toReversed()) {
        const seq = raw.seq as number;
        if (s.applied.has(seq)) continue;
        // cast: a served timeline row, typed at this boundary like every reader here.
        if (takeRowIntoLedgerText(w, s, raw as unknown as AgentTimelineItem, seq)) {
          s.applied.add(seq);
        }
      }
      for (const raw of rows) {
        this.ingestRow(w, agentId, raw as unknown as AgentTimelineItem, raw.seq as number, false);
      }
      this.foldTrailingCopies(w, s);
      this.touch(agentId);
      return;
    }
    this.prependFoldedPage(w, s, agentId, items);
    rebuildApplied(s);
    this.raiseKeepFloor(s);
    this.touch(agentId);
  }

  // Rows without a seq (an older daemon, the session-work shape): fold the page in
  // isolation and splice it at the front, joined to the head when it is the same
  // message, with a divider between two turns.
  private prependFoldedPage(
    w: ConversationWrite,
    s: AgentStream,
    agentId: string,
    items: Record<string, unknown>[],
  ): void {
    const temp = new AgentStreamState({ journal: false, invariants: this.invariants });
    temp.hydrateFromTimeline(agentId, items);
    // Our own copy: the pop below must not reach into temp's view.
    const olderEntries = [...temp.getEntries(agentId)];
    for (const e of olderEntries) e.id = this.nextEntryId();
    if (olderEntries.length > 0) {
      // A page edge is not a message edge: the older page's last text and the current
      // head's first text can be two halves of ONE reply (#3193 F2). Same id ⇒ one entry,
      // older half first, no divider between them.
      const olderTail = olderEntries[olderEntries.length - 1];
      const head = s.entries[0];
      const joined =
        olderTail?.kind === "text" &&
        head?.kind === "text" &&
        !!olderTail.messageId &&
        olderTail.messageId === head.messageId;
      if (joined) {
        olderEntries.pop();
        // A head repaired from the ledger may already spell the older half (#3238).
        const body =
          unionTextBodies(olderTail, head) ??
          bodyOfChunks(
            prependToLedgerHead(chunksOf(head), olderTail.content) ?? [
              ...chunksOf(olderTail),
              ...chunksOf(head),
            ],
          );
        w.assign(s, [
          { ...withTextBody(head, body), ts: olderTail.ts ?? head.ts },
          ...s.entries.slice(1),
        ]);
      }
      const contiguous = rowsFollowEachOther(olderTail, head);
      // Bridge the old tail and the existing head with a turn boundary — but only
      // if one isn't already there. After capEntries() trims from the head, the
      // current head's first entry can itself be a turn_boundary; an unconditional
      // bridge would then render two consecutive dividers.
      const headIsBoundary = s.entries[0]?.kind === "turn_boundary";
      const olderTailIsBoundary = olderEntries[olderEntries.length - 1]?.kind === "turn_boundary";
      const bridge =
        s.entries.length > 0 && !joined && !contiguous && !headIsBoundary && !olderTailIsBoundary
          ? [this.boundaryAt(undefined)]
          : [];
      w.assign(s, [...olderEntries, ...bridge, ...s.entries]);
    }
  }

  adoptPagination(
    agentId: string,
    hasOlder: boolean,
    olderCursor: string | null,
    proven = false,
  ): void {
    const s = this.streams.get(agentId);
    if (!s) return;
    s.hasOlder = hasOlder;
    s.olderStated = proven;
    s.olderCursor = olderCursor;
    this.touch(agentId);
  }

  setLoadingOlder(agentId: string, loading: boolean): void {
    const s = this.streams.get(agentId);
    if (s) {
      s.loadingOlder = loading;
      this.touch(agentId);
    }
  }

  // Stop scroll-up pagination for this agent after a fetch FAILURE. Clears
  // hasOlder/olderCursor so the auto-trigger (maybeLoadOlder on scroll) can't
  // immediately re-fire and spin the request + "Couldn't load older history" toast
  // in an infinite loop when the error is permanent (e.g. the owning daemon restarted
  // and reports "Agent not found in workspace"). A later hydrateFromTimeline reseeds
  // hasOlder from the server meta, so reopening/reloading the chat re-enables it.
  stopOlderLoad(agentId: string): void {
    const s = this.streams.get(agentId);
    if (s) {
      s.hasOlder = false;
      s.olderCursor = null;
      this.touch(agentId);
    }
  }

  // A paging FAILURE is not the end of history. stopOlderLoad above is the audit
  // viewer's deliberate halt; a dropped request must not clear hasOlder, or the
  // conversation renders as fully loaded with 47k rows still on disk (#3244). The
  // auto-trigger is held off by a growing backoff instead, so a permanent error
  // still cannot loop the fetch and its toast.
  // The socket's own reconnect envelope (1s doubling to a 30s cap, full jitter so
  // several tabs scrolling the same dead session do not retry in lockstep).
  // Returns true for the FIRST failure of a run — the one the user is told about.
  noteOlderLoadFailed(
    agentId: string,
    now = Date.now(),
    rand: () => number = Math.random,
  ): boolean {
    const s = this.streams.get(agentId);
    if (!s) return false;
    s.olderLoadBackoffUntil = now + reconnectBackoffMs(s.olderLoadFailures, rand);
    s.olderLoadFailures += 1;
    this.touch(agentId);
    return s.olderLoadFailures === 1;
  }

  noteOlderLoadSucceeded(agentId: string): void {
    const s = this.streams.get(agentId);
    if (!s) return;
    s.olderLoadFailures = 0;
    s.olderLoadBackoffUntil = 0;
    this.cancelOlderLoadRetry(agentId);
    this.touch(agentId);
  }

  // The entry's `ts` is the persisted row's time, not this client's Date.now() for a live
  // delta or a ts-less history row (the unread divider compares it to the read cursor).
  hasServerTs(entryId: string): boolean {
    return !this.clientStampedEntryIds.has(entryId);
  }

  // The time the unread divider may compare: persisted, or none.
  persistedTs(agentId: string, entryId: string, ts: number | undefined): number | undefined {
    const known = this.persistedTimes.get(agentId)?.get(entryId);
    return known ?? (this.hasServerTs(entryId) ? ts : undefined);
  }

  private openPageHole(
    agentId: string,
    hole: PageHole,
    generation: number,
    marks?: PageCutMarks,
  ): void {
    this.pageHoles.open(agentId, hole);
    const read = pageHoleIsServerCut(marks) ? { ...hole, cut: true } : hole;
    queueMicrotask(() => {
      if (this.generationOf(agentId) === generation) this.onTimelineHole?.(agentId, read);
    });
  }

  olderLoadHeldOff(agentId: string, now = Date.now()): boolean {
    return (this.streams.get(agentId)?.olderLoadBackoffUntil ?? 0) > now;
  }

  // How many older-page fetches have failed in a row; the auto-loader's guard keys on
  // it so a retry re-measures the pane instead of waiting for a scroll (#3248 R4).
  olderLoadFailures(agentId: string): number {
    return this.streams.get(agentId)?.olderLoadFailures ?? 0;
  }

  // A failed older read whose retry is armed (armOlderLoadRetry): the history it was
  // reading for is still coming, not out of reach.
  olderLoadRetrying(agentId: string): boolean {
    const failures = this.olderLoadFailures(agentId);
    return failures > 0 && failures <= AgentStreamState.MAX_OLDER_LOAD_RETRIES;
  }

  // A failed older page retries on its own when the backoff elapses — a short pane has
  // no scroll to fire the loader again — bounded so a permanent error ends in a held-off
  // loader with hasOlder still true, not a fetch every 30s forever. Replaces an earlier
  // retry; cancelled by clear()/clearAll() and by a success.
  static readonly MAX_OLDER_LOAD_RETRIES = 5;
  armOlderLoadRetry(agentId: string, retry: () => void, now = Date.now()): boolean {
    const s = this.streams.get(agentId);
    if (!s || s.olderLoadFailures > AgentStreamState.MAX_OLDER_LOAD_RETRIES) return false;
    this.cancelOlderLoadRetry(agentId);
    const delay = Math.max(0, s.olderLoadBackoffUntil - now);
    const timer = setTimeout(() => {
      this.olderLoadRetryTimers.delete(agentId);
      if (this.streams.get(agentId) === s) retry();
    }, delay);
    this.olderLoadRetryTimers.set(agentId, timer);
    return true;
  }

  // A reply on screen is not known to be whole — being completed, failed or stalled — so
  // it is shown as such, never presented as whole (#3248 B3, E4).
  isTranscriptRecoveryPending(agentId: string): boolean {
    return this.transcriptRecovery(agentId) !== "idle";
  }

  transcriptRecovery(agentId: string): TranscriptRecovery {
    return this.streams.get(agentId)?.recovery ?? "idle";
  }

  lostRange(agentId: string): { after: number; before: number } | undefined {
    return this.coverage.lostRange(agentId);
  }

  // Whether anything besides a head cut (a lost range, an overflow, a debt) keeps recovery open.
  hasRecoveryHolds(agentId: string): boolean {
    return this.coverage.heldState(agentId) !== undefined;
  }

  // Record what a read-back of `attempted` left missing (`after`..`before`). A recorded hole
  // inside the attempted span is REPLACED by what is still missing, so a retry that made
  // progress shrinks it; one outside it is widened to cover both.
  noteLostRange(
    agentId: string,
    after: number,
    before: number,
    attempted: { after: number; before: number },
  ): void {
    this.pageHoles.settle(agentId, attempted);
    if (!this.streams.has(agentId)) return;
    const held = this.lostRange(agentId);
    const retried = held && held.after >= attempted.after && held.before <= attempted.before;
    const next =
      held && !retried
        ? { after: Math.min(held.after, after), before: Math.max(held.before, before) }
        : { after, before };
    if (next.before - next.after <= 1) return this.dropLostRange(agentId);
    this.coverage.setLostRange(agentId, next);
    this.holdRecovery(agentId, "hole", "stalled");
  }

  copyTailSeq(agentId: string): number | undefined {
    return this.streams.get(agentId)?.copyTailSeq;
  }

  // Rows that merged in over a copy hide its hole from the page: read back what the page left
  // between the copy's tail and its floor, when the copy is still on screen under it.
  noteHoleOverCopy(
    agentId: string,
    copyTail: number | undefined,
    items: ReadonlyArray<Record<string, unknown>>,
    marks?: PageCutMarks,
  ): void {
    const floor = oldestSeq(items);
    if (copyTail === undefined || floor === undefined || this.pageHoles.of(agentId)) return;
    const rows = floor - copyTail - 1;
    const stream = this.streams.get(agentId);
    if (rows <= 0 || !stream?.entries.some((e) => e.seq !== undefined && e.seq <= copyTail)) return;
    // Past one read's worth a copy is only a preview, as under the page: scroll-up reads the rest.
    if (rows > PREVIEW_HOLE_KEEP_MAX_ROWS) return this.dropRowsUnder(agentId, floor);
    const hole = { after: copyTail, before: floor };
    this.openPageHole(agentId, hole, this.generationOf(agentId), marks);
  }

  private dropRowsUnder(agentId: string, floor: number): void {
    this.history.write(agentId, "merge", 0, (w) => {
      const at = w.stream.entries.findIndex((e) => e.seq !== undefined && e.seq >= floor);
      if (at > 0) w.assign(w.stream, w.stream.entries.slice(at));
      rebuildApplied(w.stream);
      return { value: undefined };
    });
  }

  // The open's page over a loaded stream: merged, and what it left under it is read back.
  mergeOpenPage(
    agentId: string,
    items: ReadonlyArray<Record<string, unknown>>,
    lifecycle: string | null | undefined,
  ): void {
    const held = this.highestHeldSeq(agentId);
    this.mergeDurableRows(agentId, items, lifecycle);
    const floor = oldestSeq(items);
    if (held === undefined || floor === undefined || floor <= held + 1) return;
    const hole = { after: held, before: floor };
    if (floor - held - 1 <= HOLE_KEEP_MAX_ROWS) {
      this.openPageHole(agentId, hole, this.generationOf(agentId));
    }
  }

  // A read-back that brought `after`..`before` back clears what that span had recorded.
  noteRangeRecovered(agentId: string, after: number, before: number): void {
    this.pageHoles.settle(agentId, { after, before });
    const held = this.lostRange(agentId);
    if (!held || held.after < after || held.before > before) return;
    this.dropLostRange(agentId);
  }

  // A rewind or clear: every hold (a hole and whatever else) was the old transcript's, and
  // so was the state it caused.
  private forgetCoverage(agentId: string): void {
    this.pageHoles.forget(agentId);
    this.persistedTimes.delete(agentId);
    if (this.coverage.forget(agentId)) this.setTranscriptRecovery(agentId, "idle");
  }

  private dropLostRange(agentId: string): void {
    this.coverage.setLostRange(agentId, undefined);
    this.releaseRecovery(agentId, "hole");
  }

  // A reason the recovery may not be idle. It moves an idle (or hold-made) state to what the
  // holds ask for; a pass's own state is left to that pass, which meets the hold when it
  // reaches idle.
  holdRecovery(agentId: string, reason: RecoveryHold, state: "stalled" | "pending"): void {
    if (!this.streams.has(agentId)) return;
    this.coverage.hold(agentId, reason, state);
    this.followHolds(agentId);
  }

  releaseRecovery(agentId: string, reason: RecoveryHold): void {
    if (!this.coverage.release(agentId, reason)) return;
    this.followHolds(agentId);
  }

  noteDroppedDeltas(agentId: string): void {
    // New debt needs a tail read BEGUN after it: each drop is a new revision of the debt.
    this.coverage.bumpDebt(agentId);
    this.holdRecovery(agentId, "deltaDebt", "pending");
  }

  // The revision a read captures before it starts, to arm the payment of THAT debt only.
  deltaDebtRevision(agentId: string): number {
    return this.coverage.debtRevision(agentId);
  }

  // The TAIL (not an older page) was read once the turn was over, by a read begun at debt
  // `revision`: its committed rows hold what the deltas dropped up to then previewed.
  noteTailRead(agentId: string, afterTurn: boolean, revision: number): void {
    if (!afterTurn || !this.hasDeltaDebt(agentId)) return;
    if (revision === this.deltaDebtRevision(agentId)) this.coverage.armTailRead(agentId, revision);
  }

  hasDeltaDebt(agentId: string): boolean {
    return this.isRecoveryHeld(agentId, "deltaDebt");
  }

  // A recovery pass reached coverage. It settles dropped deltas only if the tail was read
  // after the turn since they were dropped; a refusal or a failed read never lands here.
  noteTailCovered(agentId: string): void {
    const armed = this.coverage.takeTailRead(agentId);
    if (armed === this.deltaDebtRevision(agentId)) this.releaseRecovery(agentId, "deltaDebt");
  }

  // A turn's end named its last row and life (knowledge/124 T1). Another life's end is
  // stale and rejected; an end past what the view applied is owed until a read covers it.
  noteTurnEnd(agentId: string, end: TurnEnd | undefined): "owed" | "covered" | "rejected" | "none" {
    const s = this.streams.get(agentId);
    if (!end || !s) return "none";
    const held = s.life ?? s.liveLife;
    if (held !== undefined && held !== end.life) {
      this.record(agentId, "turnEnd", {
        outcome: "rejected",
        lastSeq: end.lastSeq,
        life: end.life,
      });
      return "rejected";
    }
    if (this.highestAppliedSeq(agentId) >= end.lastSeq) return "covered";
    const owed = Math.max(this.coverage.turnEndSeq(agentId) ?? 0, end.lastSeq);
    if (owed !== this.coverage.turnEndSeq(agentId)) s.turnEndReadAgains = 0;
    this.coverage.setTurnEndSeq(agentId, owed);
    this.record(agentId, "turnEnd", { outcome: "owed", lastSeq: owed });
    this.holdRecovery(agentId, "turnEnd", "pending");
    return "owed";
  }

  turnEndOwed(agentId: string): boolean {
    return this.isRecoveryHeld(agentId, "turnEnd");
  }

  turnEndReadAgains(agentId: string): number {
    return this.streams.get(agentId)?.turnEndReadAgains ?? 0;
  }

  // After a read reached the tail: true when no turn end is owed any more. False when the
  // answer stopped short of it (it came from behind the turn's end, a lagging mirror).
  settleTurnEnd(agentId: string): boolean {
    const owed = this.coverage.turnEndSeq(agentId);
    if (owed === undefined) return true;
    if (this.reachedSeq(agentId) < owed) return false;
    this.coverage.setTurnEndSeq(agentId, undefined);
    this.releaseRecovery(agentId, "turnEnd");
    this.runDeferredCleanSweep(agentId);
    return true;
  }

  private noteShapeRows(
    agentId: string,
    items: ReadonlyArray<Record<string, unknown>>,
    byShape: (raw: Record<string, unknown>) => boolean,
  ): void {
    for (const raw of items) {
      if (typeof raw.seq === "number" && byShape(raw)) this.coverage.noteShapeSeq(agentId, raw.seq);
    }
  }

  // Rows between the highest row held (by seq or by shape) and the turn end owed; 0 when none.
  turnEndGap(agentId: string): number {
    const owed = this.coverage.turnEndSeq(agentId);
    return owed === undefined ? 0 : Math.max(0, owed - this.reachedSeq(agentId));
  }

  private reachedSeq(agentId: string): number {
    return Math.max(this.highestAppliedSeq(agentId), this.coverage.shapeSeq(agentId) ?? 0);
  }

  private highestAppliedSeq(agentId: string): number {
    let top = 0;
    for (const seq of this.streams.get(agentId)?.applied ?? []) if (seq > top) top = seq;
    return top;
  }

  // How the person entered this conversation (#3244). A notification announced a reply, so
  // the next load reads even a loaded stream; the shrink report names the entry.
  noteRouteEntry(agentId: string, entry: RouteEntry): void {
    this.routeEntries.set(agentId, entry);
    if (entry.source === "notification") this.entryReads.add(agentId);
    else this.entryReads.delete(agentId);
  }

  // Whether this load owes the read a notification asked for; asked once.
  takeEntryRead(agentId: string): boolean {
    return this.entryReads.delete(agentId);
  }

  private entryReport(agentId: string): Record<string, string> {
    const entry = this.routeEntries.get(agentId);
    if (!entry) return {};
    return entry.messageId
      ? { entrySource: entry.source, notificationMessageId: entry.messageId }
      : { entrySource: entry.source };
  }

  isRecoveryHeld(agentId: string, reason: RecoveryHold): boolean {
    return this.coverage.isHeld(agentId, reason);
  }

  private followHolds(agentId: string): void {
    const current = this.transcriptRecovery(agentId);
    if (current === "idle" || this.coverage.fromHolds(agentId)) {
      this.setTranscriptRecovery(agentId, "idle");
    }
  }

  // What "idle" means while holds remain: the strongest state they ask for. An open head
  // cut is one of them (#3239): no path — a settled delta, a whole tail, a refusal — may
  // call a cut head whole, and it is evaluated with the others, never instead of them.
  private heldState(agentId: string): TranscriptRecovery {
    const held = this.coverage.heldState(agentId);
    const cut = this.headCutOpen(agentId);
    if (!cut && !held) {
      this.coverage.setFromHolds(agentId, false);
      return "idle";
    }
    this.coverage.setFromHolds(agentId, true);
    return cut ? "stalled" : (held ?? "pending");
  }

  transcriptRecoveryCursor(agentId: string): string | null {
    return this.streams.get(agentId)?.recoveryCursor ?? null;
  }

  // A page opened inside a reply at `seq` (undefined: an older daemon's page, no seq).
  // The lowest open cut is kept: reading back from it covers the ones above.
  markHeadCut(agentId: string, seq: number | undefined, cursor: string | null): void {
    const s = this.streams.get(agentId);
    if (!s) return;
    const at = seq === undefined || Number.isNaN(seq) ? Number.POSITIVE_INFINITY : seq;
    if (s.headCut && s.headCut.seq <= at && this.headCutOpen(agentId)) return;
    s.headCut = { seq: at, cursor, nonce: s.hydrationNonce };
    this.followHolds(agentId);
  }

  // A page whose head is whole was read from `from` (undefined: no seq) down: it covers a
  // cut at or above that row.
  resolveHeadCut(agentId: string, from: number | undefined): void {
    const s = this.streams.get(agentId);
    if (!s?.headCut) return;
    // An empty page (NaN) covers nothing; a page starting above the cut does not reach it.
    if (from !== undefined && !(from <= s.headCut.seq)) return;
    s.headCut = null;
    this.followHolds(agentId);
  }

  // A rewind or clear: the cut was the old transcript's, and so was the recovery it drove.
  // Its row and cursor mean nothing in the next life, which may reuse the seq.
  private forgetHeadCut(agentId: string): void {
    const s = this.streams.get(agentId);
    if (!s?.headCut) return;
    s.headCut = null;
    if (s.recovery !== "idle") this.setTranscriptRecovery(agentId, "idle");
  }

  // The cursor to read an open cut back from, or null (none open, or nothing older).
  headCutCursor(agentId: string): string | null {
    // null: the cursor contract is `string | null` (TimelineFetchClient.olderCursor).
    return this.headCutOpen(agentId) ? (this.streams.get(agentId)?.headCut?.cursor ?? null) : null;
  }

  // An open cut: marked, still on screen, and the screen does not already hold the
  // reply from its first chunk (a refresh page cut at a row whose prefix the client
  // kept is uncertainty, not loss).
  headCutOpen(agentId: string): boolean {
    const s = this.streams.get(agentId);
    const cut = s?.headCut;
    if (!s || !cut) return false;
    if (!Number.isFinite(cut.seq)) return cut.nonce === s.hydrationNonce;
    const holder = s.entries.find((e) => seqsOf(e).includes(cut.seq));
    return holder !== undefined && !heldFromFirstChunk(s.entries, holder);
  }

  // Move the recovery machine. `idle` is reached only by evidence of coverage and drops
  // every residual retry; `pending` records confirmed progress (its cursor); `failed`
  // and `stalled` keep the cursor they had so an explicit or armed retry resumes there.
  setTranscriptRecovery(agentId: string, state: TranscriptRecovery, cursor?: string | null): void {
    const s = this.streams.get(agentId);
    if (!s) return;
    if (state === "idle") {
      s.recoveryFailures = 0;
      s.recoveryCursor = null;
      this.cancelRecoveryRetry(agentId);
    } else {
      // A pass moving the recovery owns the state it sets.
      this.coverage.setFromHolds(agentId, false);
      if (state === "pending") s.recoveryFailures = 0;
    }
    if (cursor !== undefined) s.recoveryCursor = cursor;
    // Coverage of the tail is not coverage of what a hold names (a hole below it…).
    const next = state === "idle" ? this.heldState(agentId) : state;
    if (s.recovery === next) return;
    s.recovery = next;
    this.touch(agentId);
  }

  // A failed recovery page retries from where it stood, with the socket's backoff, a
  // bounded number of times; past that the recovery stays visibly pending for the next
  // trigger (a turn's end, the foreground). Cancelled by clear()/clearAll(). Returns
  // whether a retry was armed.
  armRecoveryRetry(
    agentId: string,
    maxRetries: number,
    retry: () => void,
    counter: "recoveryFailures" | "turnEndReadAgains" = "recoveryFailures",
  ): boolean {
    const s = this.streams.get(agentId);
    if (!s) return false;
    s[counter] += 1;
    if (s[counter] > maxRetries) return false;
    this.cancelRecoveryRetry(agentId);
    const timer = setTimeout(
      () => {
        this.recoveryRetryTimers.delete(agentId);
        if (this.streams.get(agentId) === s) retry();
      },
      reconnectBackoffMs(s[counter] - 1, Math.random),
    );
    this.recoveryRetryTimers.set(agentId, timer);
    return true;
  }

  private cancelRecoveryRetry(agentId: string): void {
    const timer = this.recoveryRetryTimers.get(agentId);
    if (timer === undefined) return;
    clearTimeout(timer);
    this.recoveryRetryTimers.delete(agentId);
  }

  cancelOlderLoadRetry(agentId: string): void {
    const timer = this.olderLoadRetryTimers.get(agentId);
    if (timer === undefined) return;
    clearTimeout(timer);
    this.olderLoadRetryTimers.delete(agentId);
  }

  hasOlder(agentId: string): boolean {
    return this.streams.get(agentId)?.hasOlder ?? false;
  }

  olderCursor(agentId: string): string | null {
    return this.streams.get(agentId)?.olderCursor ?? null;
  }

  // The newest durable row the screen holds, read off the entries themselves (not the
  // `applied` cache, which is rebuilt lazily). undefined when nothing on screen carries
  // a seq (an older daemon, or an empty stream): the caller then cannot ask for a delta.
  // Also what a page must meet to land contiguous (the reconnect page-back).
  // Whether every durable row from..to has been applied to this stream.
  appliedAll(agentId: string, from: number, to: number): boolean {
    const applied = this.streams.get(agentId)?.applied;
    if (!applied) return false;
    for (let seq = from; seq <= to; seq += 1) if (!applied.has(seq)) return false;
    return true;
  }

  // `agentRowsOnly`: skip prompts, whose seq must not hide the unread agent rows below it.
  highestHeldSeq(agentId: string, agentRowsOnly = false): number | undefined {
    const s = this.streams.get(agentId);
    if (!s) return undefined;
    let top: number | undefined;
    for (const e of s.entries) {
      if (agentRowsOnly && e.kind === "user_message") continue;
      for (const seq of seqsOf(e)) if (top === undefined || seq > top) top = seq;
    }
    return top;
  }

  isLoadingOlder(agentId: string): boolean {
    return this.streams.get(agentId)?.loadingOlder ?? false;
  }

  // Monotonic per-stream (re-)hydration counter. Changes whenever the stream is
  // rebuilt from the server timeline, so a viewer's short-page auto-loader can
  // re-arm on a reconnect re-seed. 0 for an unhydrated / unknown stream.
  hydrationNonce(agentId: string): number {
    return this.streams.get(agentId)?.hydrationNonce ?? 0;
  }

  generationOf(agentId: string): number {
    return (this.generations.get(agentId) ?? 0) + this.resetEpoch;
  }

  // A new route makes every read begun on the old one stale, the relay-resolved one included:
  // its cursors are that daemon's. Kept apart from the stream, so a cold open registers its
  // route before its first await, and a later open outranks an earlier one's late answer.
  noteRoute(agentId: string, daemonId: string | undefined): void {
    if (!daemonId || this.routes.get(agentId) === daemonId) return;
    this.invalidateReads(agentId);
    this.routes.set(agentId, daemonId);
  }

  routeOf(agentId: string): string | undefined {
    return this.routes.get(agentId);
  }

  // Every read of the conversation begun before now is stale, without forgetting what it
  // is known to miss: a rewind invalidates first and forgets once its page lands.
  invalidateReads(agentId: string): void {
    this.generations.set(agentId, (this.generations.get(agentId) ?? 0) + 1);
  }

  private bumpGeneration(agentId: string): void {
    this.generations.set(agentId, (this.generations.get(agentId) ?? 0) + 1);
    // A hole of a transcript that was rewound or cleared is not the new one's.
    this.forgetCoverage(agentId);
    this.forgetHeadCut(agentId);
  }

  // The one way a conversation's view and everything recorded about it go: the stream, its
  // coverage (generation, holes, holds, head cut), its timers and its workspace binding.
  // "clear" is an agent that is gone; "evict" one dropped from memory to be read back later.
  // A reset lease (#3382 A-4a): refused while a write to this conversation is open.
  private forgetConversation(agentId: string, reason: "clear" | "evict"): void {
    this.history.lease(agentId, "reset", () => {
      this.dropConversation(agentId, reason);
      return { value: undefined };
    });
  }

  private dropConversation(agentId: string, reason: "clear" | "evict"): void {
    this.record(agentId, reason, {});
    this.routes.delete(agentId);
    this.rowSessions.delete(agentId);
    this.routeEntries.delete(agentId);
    this.entryReads.delete(agentId);
    // No adoption may place frames or retry into the stream that replaces this one (U3).
    // Adoption state is the live instance's; a private copy never touches it.
    if (this.judge.journaled) forgetLifeAdoption(agentId);
    this.judge.close(agentId);
    this.bumpGeneration(agentId);
    this.forgetCoverage(agentId);
    this.forgetHeadCut(agentId);
    this.clearTurnTimeout(agentId);
    this.cancelOlderLoadRetry(agentId);
    this.cancelRecoveryRetry(agentId);
    this.outbox.forget(agentId);
    this.turnActivityAt.delete(agentId);
    this.streams.delete(agentId);
    this.forgetLongLived(agentId);
    this.cardFirstTs.delete(agentId);
    this.cardEndTs.delete(agentId);
    const workspaceId = this.workspaceByAgent.get(agentId);
    if (workspaceId) {
      this.workspaceByAgent.delete(agentId);
      const agents = this.agentsByWorkspace.get(workspaceId);
      agents?.delete(agentId);
      if (agents?.size === 0) {
        this.agentsByWorkspace.delete(workspaceId);
        if (workspaceId !== this.activeWorkspaceId) this.workspaceRecency.delete(workspaceId);
      }
    }
  }

  clear(agentId: string): void {
    this.seating.clear(agentId);
    this.beforeTranscriptWipe?.(agentId);
    this.forgetConversation(agentId, "clear");
    newerWindows.close(agentId);
    this.clearPersistedPendingPermissions(agentId);
    this.touch(agentId);
  }

  // Resolve a turn the client still believes is running against a FRESH roster
  // snapshot that says the agent is settled. The terminal turn events
  // (turn_completed/failed/canceled) are the only things that clear `turnStatus`,
  // and they are NOT replayed for a socket that was down when they fired — the
  // resumed live stream starts at "now" and the reconnect timeline catch-up
  // (hydrateFromTimeline) deliberately rebuilds entries only. So a completion
  // missed across a reconnect left turnStatus "running" forever: the composer
  // stayed in Queue mode (isTurnActive), and only an app restart (fresh in-memory
  // stream map) recovered. The idle watchdog would eventually declare a FALSE
  // "Turn timed out" after 15 idle minutes — long after the user gave up.
  //
  // Called with each agent's lifecycle from a freshly-fetched roster (server
  // truth) on reconnect. Mirrors turn_completed's semantics: settle the turn
  // silently (the re-hydrated entries already show how it actually ended — no
  // synthetic error row). No-op unless the stream says "running" and the
  // roster disagrees; a roster that still says "running" is proof of life, not
  // staleness, and is left alone.
  reconcileTurnWithRoster(agentId: string, lifecycle: string | null | undefined): boolean {
    const s = this.streams.get(agentId);
    if (!s || s.turnStatus !== "running") return false;
    if (lifecycle == null || lifecycle === "running") return false;
    // One lease for the settle (#3382 A-4a).
    this.history.lease(agentId, "turn", (w) => {
      s.turnStatus = "idle";
      // An idle roster is a clean end like turn_completed (its frame beats turn_completed live).
      this.finalizeTurn(w, agentId, s, lifecycle === "idle");
      this.clearTurnTimeout(agentId);
      this.touch(w.agentId);
      return { value: undefined };
    });
    return true;
  }

  // Optimistically drop a pending interactive tool call the user just answered,
  // WITHOUT waiting for the server's permission_resolved echo. The live path also
  // clears on that echo (a harmless double-delete), but a RESTORED (post-reload)
  // card may have no echo coming — e.g. the daemon already resolved it while the
  // client was gone — so its answer/Dismiss must clear the card locally or it
  // becomes a fresh stuck row. Idempotent.
  resolvePendingLocally(agentId: string, requestId: string): void {
    const s = this.streams.get(agentId);
    if (!s?.pendingPermissions.has(requestId)) return;
    // The answer re-arms the watchdog, whose callback writes: under a lease (#3382 A-4a).
    this.history.lease(agentId, "turn", (w) => {
      const carried = isCarriedPlan(s.pendingPermissions.get(requestId));
      s.pendingPermissions.delete(requestId);
      this.bumpPermissionStamp(agentId);
      this.persistPendingPermissions(agentId, s);
      // A carried plan's row outlived its turn's settle; a restored card may get no echo.
      if (carried) this.settlePlanRow(w, s, requestId, "completed");
      // Answering the last outstanding permission un-suspends the watchdog (the
      // turn resumes) — re-arm without waiting for the server's permission_resolved
      // echo, which a restored/post-reload card may never receive.
      this.noteTurnActivity(agentId);
      this.touch(w.agentId);
      return { value: undefined };
    });
  }

  // Sign-out: drops every conversation and the in-memory queue; refused while a write is open.
  clearAll(): void {
    newerWindows.clearAll();
    this.history.resetAll("clearAll", () => {
      this.dropEveryConversation();
      return { value: undefined };
    });
  }

  private dropEveryConversation(): void {
    this.startWaiters.clear();
    this.seating.clear();
    this.turnStalls.clear();
    this.invariants.clear();
    this.projectionLiveness.clear();
    this.sharedQueues = {};
    this.routeEntries.clear();
    this.entryReads.clear();
    this.rowSessions.clear();
    this.sessionFacts.clear();
    this.projections.clear();
    this.ledgerEntryIds.clear();
    this.ledgerDividerIds.clear();
    this.claimedLedgerEntryIds.clear();
    this.clientStampedEntryIds.clear();
    this.persistedTimes.clear();
    this.pageHoles.clear();
    this.noticeLedgerIds.clear();
    this.restoredFailureCards.clear();
    this.eventFailureCards.clear();
    // Every stream is gone, and with them every read, run, adoption and merge of theirs.
    this.sync.reset();
    this.routes.clear();
    // Nor may a hole of a transcript that no longer exists be read into the next one.
    this.coverage.clear();
    // Nor may a head cut of a transcript that no longer exists stall the next one.
    for (const s of this.streams.values()) s.headCut = null;
    for (const id of this.turnTimeouts.keys()) {
      this.clearTurnTimeout(id);
    }
    for (const id of this.olderLoadRetryTimers.keys()) {
      this.cancelOlderLoadRetry(id);
    }
    for (const id of this.recoveryRetryTimers.keys()) {
      this.cancelRecoveryRetry(id);
    }
    this.resetEpoch += 1;
    this.outbox.reset();
    for (const id of this.streams.keys()) this.forgetLongLived(id);
    this.streams.clear();
    this.touch();
    this.workspaceByAgent.clear();
    this.agentsByWorkspace.clear();
    this.workspaceRecency.clear();
    // The reconciled-bubble ledger is keyed by the streams just dropped, so it goes too
    // (knowledge/114). Bounded regardless; clearing keeps it from pointing at gone rows.
    this.reconciledPromptEntryIds.clear();
    this.softCanceled.clear();
    this.softStopPrompts.clear();
    this.softStopRefusals = new Set();
    this.serverEchoedPromptIds.clear();
    this.subagentPermissionSource.clear();
    this.turnActivityAt.clear();
    this.reportedStuckToolCalls.clear();
    this.reportedSweptOverrides.clear();
    this.activeWorkspaceId = null;
  }

  // THE single read every surface goes through — AgentStreamView's `entries`
  // ($derived, :176), SessionWorkView's publish to the Progress rail, the activity
  // curator, board-query. Nothing reads `s.entries` directly.
  //
  // Which is why the orphan resolution lives HERE and not on a write path. It was in
  // hydrateFromTimeline, and that was wrong for a reason no test caught: entries also
  // enter through prependTimeline (scroll-up / AgentStreamView's short-page
  // autoloader), which folds an older page in a THROWAWAY AgentStreamState —
  // `temp.hydrateFromTimeline(agentId, items)` with no meta, so temp's `settled` is
  // false — and splices the result straight into `s.entries`. Every orphan on an
  // older page therefore walked in unresolved, which is exactly what the live
  // measurement kept showing: the initial page had none, so the sweep found nothing
  // and reported CANCELED 0, while five older-page cards sat there reading RUNNING.
  // Resolving on READ covers every way an entry can arrive — hydrate, prepend, live
  // frame, and whatever is added next — instead of one guard per write site.
  //
  // Cheap: an unsettled stream (the overwhelming majority, and every live one)
  // returns `s.entries` untouched, same array identity. A settled stream memoises on
  // the source array, so repeated renders of unchanged entries cost one WeakMap hit
  // and the identity `$derived` needs stays stable.
  getEntries(agentId: string): readonly StreamEntry[] {
    const s = this.streams.get(agentId);
    if (!s) return [];
    if (!s.settled) return s.entries;
    const cached = this.resolvedEntries.get(s.entries);
    if (cached) return cached;
    // A long-lived call still young enough to finish is left running (B4, C2): not memoised then.
    const firstTs = this.cardFirstTs.get(agentId);
    const young = (e: Extract<StreamEntry, { kind: "tool_call" }>) => outlivesCleanTurnEnd(e) && Date.now() - (firstTs?.get(e.callId) ?? 0) < LONG_LIVED_SETTLE_MS; // prettier-ignore
    const spared = openCallIds(s.entries, young);
    if (spared.length > 0) {
      // Its deadline counts from the card's first row; settleLongLived sweeps it then (audit m1).
      this.armLongLived(agentId, spared.map((id) => [id, (firstTs?.get(id) ?? 0) + LONG_LIVED_SETTLE_MS])); // prettier-ignore
      return resolveOrphanedToolCards(s.entries, undefined, young).entries;
    }
    const { entries, stuck } = resolveOrphanedToolCards(s.entries);
    // Memoise BOTH outcomes against the source array: a settled stream with nothing
    // to resolve caches itself, so it never rescans on re-render either.
    this.resolvedEntries.set(s.entries, entries);
    this.reportStuckToolCalls(agentId, stuck);
    return entries;
  }

  // agentId-free, keyed on the entries array itself, so it self-evicts when the
  // stream re-hydrates or a frame replaces the array.
  private readonly resolvedEntries = new WeakMap<readonly StreamEntry[], readonly StreamEntry[]>();

  // ── Work-session projection (ws-unify B2, session-projection.ts) ──
  // agentId → seq → the work session its row is stamped with (null: none), from pages and frames.
  private rowSessions = new Map<string, Map<number, string | null>>();
  private rowSessionsRev = 0;
  // sessionId → what a page asked for it said: rows tagged, and which agents hold it.
  private sessionFacts = new Map<string, SessionFacts>();
  private readonly projections = new Map<string, Projection>();

  private noteRowSessions(agentId: string, items: ReadonlyArray<Record<string, unknown>>): void {
    for (const raw of items) {
      // A malformed row is the write's own failure to raise, never this bookkeeping's.
      if (typeof raw?.seq !== "number" || !("workSessionId" in raw)) continue;
      const tag = raw.workSessionId;
      // null: the wire's "row of no session".
      this.noteRowSession(agentId, raw.seq, typeof tag === "string" ? tag : null);
    }
  }

  // A live frame's committed row, before handleEvent touches the store.
  noteRowSession(agentId: string, seq: number, sessionId: string | null): void {
    let rows = this.rowSessions.get(agentId);
    if (!rows) this.rowSessions.set(agentId, (rows = new Map()));
    if (rows.get(seq) === sessionId) return;
    rows.set(seq, sessionId);
    this.rowSessionsRev += 1;
  }

  // A page read for a work session: its facts, and its rows' tags unless it is another life.
  noteSessionPage(
    sessionId: string | undefined,
    agentId: string,
    page: {
      items: ReadonlyArray<Record<string, unknown>>;
      life?: string;
      workSessionTags?: boolean;
      workSessionAgents?: string[];
      workSessionFirstSeq?: number;
    },
  ): void {
    if (!sessionId) return;
    if (!this.lifeDiffers(agentId, page.life)) this.noteRowSessions(agentId, page.items);
    this.sessionFacts.set(sessionId, {
      agentId,
      tagged: page.workSessionTags === true,
      ...(page.workSessionAgents ? { agents: page.workSessionAgents } : {}),
      ...(page.workSessionFirstSeq === undefined ? {} : { firstSeq: page.workSessionFirstSeq }),
    });
    this.touch();
  }

  // The session one entry of `agentId`'s stream proves: a string, null for none, undefined unknown.
  entrySessionOf(agentId: string, e: StreamEntry): string | null | undefined {
    const rows = this.rowSessions.get(agentId);
    return entrySession(
      e,
      (seq) => rows?.get(seq),
      (id) => this.ledgerEntryIds.has(id),
    );
  }

  sessionFactsFor(sessionId: string, agentId: string): SessionFacts | undefined {
    const facts = this.sessionFacts.get(sessionId);
    return facts?.agentId === agentId ? facts : undefined;
  }

  // The entries of `sessionId` in `agentId`'s stream: THE read of a projected work session.
  // Memoised on the stream's array and the tags, so an unchanged projection keeps its identity.
  getSessionEntries(agentId: string, sessionId: string, settled = false): readonly StreamEntry[] {
    const entries = this.getEntries(agentId);
    const key = `${agentId}\u0000${sessionId}`;
    const held = this.projections.get(key);
    if (
      held &&
      held.entries === entries &&
      held.rev === this.rowSessionsRev &&
      held.settled === settled
    ) {
      return held.out;
    }
    const rows = this.rowSessions.get(agentId);
    const projected = projectSessionEntries(
      entries,
      sessionId,
      (seq) => rows?.get(seq),
      (id) => this.ledgerEntryIds.has(id),
    );
    const out = settled ? resolveOrphanedToolCards(projected).entries : projected;
    const same = held && held.out.length === out.length && held.out.every((e, i) => e === out[i]);
    this.projections.set(key, {
      entries,
      rev: this.rowSessionsRev,
      settled,
      out: same ? held.out : out,
    });
    return same ? held.out : out;
  }

  // "Is this conversation already showing that message?" — the same predicate
  // mergeCrossSessionReply dedupes on and mergeMissingCyboDms builds `loadedIds`
  // from, named once so a caller can ask it WITHOUT merging. The repair path on
  // the agent DM route asks it to compare what the client was told about against
  // what it actually painted; a `false` there is a dropped reply.
  hasMessage(agentId: string, messageId: string): boolean {
    const s = this.streams.get(agentId);
    if (!s) return false;
    return s.entries.some(
      (e) =>
        (e.kind === "text" || e.kind === "user_message") &&
        (e.messageId === messageId || !!e.aliasIds?.includes(messageId)),
    );
  }

  // Curated, glanceable "what is this agent doing right now" line (#594):
  // `[Shell] pnpm test`, `[Assistant] Done — 42 passing`, `[Error] …`. Derived
  // from the live timeline via the pure curator, so it advances as the stream
  // does. Reactive: reading `s.entries` inside a `$derived`/`$effect` (the agent
  // row) re-runs when the stream mutates. Only the last `MAX_ACTIVITY_SCAN`
  // entries are curated — the head is all the "right now" line needs, and it
  // keeps the per-update cost bounded on long timelines. Returns null when there
  // is no activity yet (renderer falls back to the generic status line).
  getLastActivity(agentId: string): CuratedActivity | null {
    // The settled view, as every reader sees it: a canceled card never reads as running (U6).
    const entries = this.getEntries(agentId);
    if (!entries || entries.length === 0) {
      return null;
    }
    const cached = this.lastActivityCache.get(entries);
    if (cached !== undefined) {
      return cached;
    }
    // what the conversation draws: an answered share card is gone here too
    const curated = curateLastActivity(renderedStreamEntries(entries), {
      maxItems: MAX_ACTIVITY_SCAN,
    });
    this.lastActivityCache.set(entries, curated);
    return curated;
  }

  // Whether this agent's stream was already rebuilt from the server timeline in
  // its current instance — the mount-time fetch guards on this (not entry count)
  // so a live event arriving first can't suppress the history hydrate.
  isHydrated(agentId: string): boolean {
    return this.streams.get(agentId)?.hydrated ?? false;
  }

  // "Something is painted, but only from our own disk." See `hydratedFromCache`.
  isCachePainted(agentId: string): boolean {
    return this.streams.get(agentId)?.hydratedFromCache ?? false;
  }

  // "The history on screen has been confirmed by a server read." The predicate the
  // RECOVERY paths want — never `isHydrated`, which a cache paint also satisfies.
  isServerHydrated(agentId: string): boolean {
    const s = this.streams.get(agentId);
    return (s?.hydrated ?? false) && !(s?.hydratedFromCache ?? false);
  }

  // "Painted from the relay's PG mirror while the owning daemon was away." The mirror
  // lags the daemon and is pruned, so this is verified against a copy, not the source:
  // the daemon coming back online re-reads it (#3193 F3).
  isMirrorHydrated(agentId: string): boolean {
    return this.streams.get(agentId)?.hydratedFromMirror ?? false;
  }

  // Positive proof that nothing older exists: a server read (not cache or mirror) that stated
  // `hasOlder: false`, with no incomplete marker. An unhydrated or silent stream proves nothing.
  isHistoryFullyLoaded(agentId: string): boolean {
    const s = this.streams.get(agentId);
    return (
      !!s &&
      this.isServerHydrated(agentId) &&
      !s.hydratedFromMirror &&
      s.olderStated === true &&
      !s.hasOlder &&
      !s.historyIncomplete
    );
  }

  isHistoryIncomplete(agentId: string): boolean {
    return this.streams.get(agentId)?.historyIncomplete ?? false;
  }

  // An older page read off the mirror said the history below it is not whole.
  noteHistoryIncomplete(agentId: string): void {
    const s = this.streams.get(agentId);
    if (!s || s.historyIncomplete) return;
    s.historyIncomplete = true;
    this.touch(agentId);
  }

  // Extend text entries from the durable timeline's rows without replacing anything
  // else: an entry that shows a cut of what the rows hold for its message id takes the
  // rows' text (#3193 F10 — a live stream that stopped being applied). Entries the rows
  // do not cover (a newer turn, a message without id) and entries that differ (an edit)
  // are untouched, so this is safe while another turn streams. Returns how many changed.
  private repairTextFromRows(
    w: ConversationWrite,
    items: ReadonlyArray<Record<string, unknown>>,
  ): number {
    const agentId = w.agentId;
    const s = this.streams.get(agentId);
    if (!s) return 0;
    const chunks = assistantChunksByMessage(items);
    const folded = foldAssistantText(items);
    let repaired = 0;
    // A message split by tool rows is several entries; when they already spell its folded
    // text, there is nothing to repair (each part grew into the whole reply: CYBORG-1444).
    const joined = new Map<string, string>();
    for (const e of s.entries) {
      if (e.kind === "text" && e.messageId) {
        joined.set(e.messageId, (joined.get(e.messageId) ?? "") + e.content);
      }
    }
    const next = s.entries.map((e) => {
      // An entry that knows its rows is completed row by row (ingestRow); this is the
      // shape rule for a body with no seq at all (an older daemon, a DM ledger row).
      if (e.kind !== "text" || !e.messageId || seqRangeOf(e)) return e;
      const durable = chunks.get(e.messageId);
      const full = folded.get(e.messageId);
      if (full !== undefined && joined.get(e.messageId) === full) return e;
      // Rows that spell a cut of the screen's text (a ledger row that landed first) are
      // recorded under it: the rest is ledger text the next rows take over (#3238).
      if (durable && durable.length > 0 && full !== undefined && isCutOf(full, e.content)) {
        repaired += 1;
        return withLedgerText(withTextBody(e, bodyOfChunks(durable)), e.content);
      }
      const content = full === undefined ? undefined : extendFromDurable(e.content, full);
      if (content === undefined) return e;
      repaired += 1;
      // The rows now spell this entry's head: record them, so they are not applied
      // again. What the screen had past them (a ledger row's rest, a live overhang) is
      // held as the tail the next rows take over as they spell it (#3238).
      if (durable && durable.length > 0 && content.startsWith(full ?? "")) {
        const overhang = content.slice((full ?? "").length);
        const tail: TextChunk[] = overhang ? [{ text: overhang, ledger: "tail" }] : [];
        return withTextBody(e, bodyOfChunks([...durable, ...tail]));
      }
      return withoutParts(e, content);
    });
    if (repaired > 0) {
      w.assign(s, next);
      this.touch(w.agentId);
    }
    return repaired;
  }

  // MERGE the durable timeline into what is on screen (#115): every row with a seq goes
  // through ingestRow — applied once, at its seq, filling a hole or extending a cut or
  // adding a turn the suspended tab never applied — touching nothing else (#3193 F10).
  // Rows below the screen's first seq are the older-history domain (loadOlder), taken
  // only while they chain onto the screen's head (the reply a page opened inside of),
  // newest first. Rows without a seq (an older daemon) are related by shape and prompt
  // identity instead. Returns how many rows added or extended something visible.
  mergeDurableRows(
    agentId: string,
    items: ReadonlyArray<Record<string, unknown>>,
    lifecycle?: string | null,
    // Rows read back on purpose (a hole's read-back): history the cap must not trim off
    // the head, like a scroll-up's prepend. `more`: the next page of a chain continues it.
    opts?: { deliberate?: boolean; more?: boolean },
  ): number {
    this.noteRowSessions(agentId, items);
    const added = this.history.write(agentId, "merge", 0, (w) => ({
      value: this.mergeRows(w, agentId, markLegacyRelivePairs(items), lifecycle, opts),
    }));
    this.outbox.settleAnsweredPrompt(agentId);
    return added;
  }

  // A row read back names the time a live reply's entry only guessed; the entry keeps its own.
  private notePersistedTimes(
    agentId: string,
    s: AgentStream,
    items: ReadonlyArray<Record<string, unknown>>,
  ): void {
    const tsById = firstAssistantTsByMessageId(items);
    let known: Map<string, number> | undefined;
    const held = this.persistedTimes.get(agentId);
    for (const e of s.entries) {
      const ts = e.kind === "text" && e.messageId ? tsById.get(e.messageId) : undefined;
      if (ts === undefined || !this.clientStampedEntryIds.has(e.id) || held?.get(e.id) === ts)
        continue;
      known ??= new Map(held);
      known.set(e.id, ts);
    }
    if (known) this.persistedTimes.set(agentId, known);
  }

  private mergeRows(
    w: ConversationWrite,
    agentId: string,
    items: ReadonlyArray<Record<string, unknown>>,
    lifecycle: string | null | undefined,
    opts: { deliberate?: boolean; more?: boolean } | undefined,
  ): number {
    const s = w.stream;
    const cap = !opts?.deliberate;
    this.repairTextFromRows(w, items);
    this.notePersistedTimes(agentId, s, items);
    rebuildApplied(s);
    const running = this.isTurnActive(agentId, lifecycle);
    // A message the screen shows as a body with no seq (an older daemon's live stream)
    // is related by shape above and never by row: its rows would land beside it.
    const shapeOnly = new Set(
      s.entries.flatMap((e) =>
        e.kind === "text" && e.messageId && !seqRangeOf(e) ? [e.messageId] : [],
      ),
    );
    const byShape = (raw: Record<string, unknown>): boolean =>
      raw.type === "assistant_message" &&
      typeof raw.messageId === "string" &&
      shapeOnly.has(raw.messageId);
    const merged = unmergedOverHeld(
      items
        .filter(
          (raw): raw is Record<string, unknown> & { seq: number } => typeof raw.seq === "number",
        )
        .filter((raw) => !byShape(raw)),
      s.applied,
    );
    const withSeq = merged.rows.toSorted((x, y) => x.seq - y.seq);
    // The seqs a merged row could not place are read back row by row; a read that fails reports.
    for (const hole of merged.holes) this.openPageHole(agentId, hole, this.generationOf(agentId), { budgetCut: true }); // prettier-ignore
    const floor = s.applied.size > 0 ? Math.min(...s.applied) : undefined;
    const below = floor === undefined ? [] : withSeq.filter((raw) => raw.seq < floor).toReversed();
    const above = floor === undefined ? withSeq : withSeq.filter((raw) => raw.seq >= floor);
    const touched = new Set<string>();
    // The live paths inside ingestRow trim too: room for the whole read is made first.
    this.reserveForDeliberateRead(s, cap, withSeq.length);
    for (const raw of below) {
      if (!s.applied.has(raw.seq + 1) && !headChainsTo(s, raw.seq)) break;
      for (const id of this.ingestRow(
        w,
        agentId,
        raw as unknown as AgentTimelineItem,
        raw.seq,
        cap,
      )) {
        touched.add(id);
      }
    }
    const lastBefore = s.entries.at(-1);
    for (const raw of above) {
      for (const id of this.ingestRow(
        w,
        agentId,
        raw as unknown as AgentTimelineItem,
        raw.seq,
        cap,
      )) {
        touched.add(id);
      }
    }
    // A reply that landed last while the turn is over closes its turn, as turn_completed
    // would have; mid-turn the live continuation still merges onto it (#115).
    if (landedLastAfterTurn(s, lastBefore, touched.size, running || opts?.more === true)) {
      w.assign(s, [...s.entries, this.closeTurn(s)]);
    }
    this.noteShapeRows(agentId, items, byShape);
    this.foldTrailingCopies(w, s);
    let changed = touched.size;
    changed += this.mergeLegacyRows(
      w,
      s,
      agentId,
      items.filter((raw) => typeof raw.seq !== "number"),
      running,
    );
    if (changed > 0) {
      this.settleEntryCap(w, s, cap);
      this.checkPromptBubbles(agentId, s, "merge");
      this.touch(agentId);
    }
    w.judge({ page: pageRange(items), changed });
    return changed;
  }

  // Rows an older daemon serves without a seq: a whole assistant message the screen
  // lacks is inserted by time (keyed by messageId); prompts by identity.
  private mergeLegacyRows(
    w: ConversationWrite,
    s: AgentStream,
    agentId: string,
    items: ReadonlyArray<Record<string, unknown>>,
    running: boolean,
  ): number {
    if (items.length === 0) return 0;
    const folded = foldAssistantText(items);
    const tsById = firstAssistantTsByMessageId(items);
    let inserted = 0;
    for (const [messageId, text] of folded) {
      if (isSystemErrorText(text)) continue;
      if (s.entries.some((e) => e.kind === "text" && e.messageId === messageId)) continue;
      const ts = tsById.get(messageId);
      const entry: StreamEntry = {
        id: this.nextEntryId(),
        kind: "text",
        content: text,
        messageId,
        ...(ts !== undefined ? { ts } : {}),
      };
      const at = insertIndexForTs(s, ts);
      if (at >= 0) {
        w.assign(s, [...s.entries.slice(0, at), entry, ...s.entries.slice(at)]);
      } else if (running) {
        w.assign(s, [...s.entries, entry]);
      } else {
        w.assign(s, [...s.entries, entry, this.boundaryAt(undefined)]);
      }
      inserted += 1;
    }
    return inserted + this.insertMissingUserInputs(w, s, agentId, items);
  }

  // The other half of the conversation (#3244): a prompt sent from another device while
  // this tab slept is a durable user_message row with no entry. Built by the same rule a
  // hydrated prompt is — never replayed through handleTimeline, whose last-turn echo
  // dedup folds two prompts that read alike, and whose cap shifts the list under an
  // index (#3248 R1, R3). Seated by time, fenced like hydrate fences a turn.
  private insertMissingUserInputs(
    w: ConversationWrite,
    s: AgentStream,
    agentId: string,
    items: ReadonlyArray<Record<string, unknown>>,
  ): number {
    let inserted = 0;
    for (const raw of items) {
      if (raw.type !== "user_message" || typeof raw.text !== "string") continue;
      // cast: the type guard above proves a user_message row with a string text.
      const item = asDrawnPromptRow(raw as unknown as AgentTimelineItem);
      if (item?.type !== "user_message" || isHiddenReliveRow(raw)) continue;
      const seq = typeof raw.seq === "number" ? raw.seq : undefined;
      const text = stripDmPrefix(stripMentionEnvelope(item.text));
      if (text === "" && isMentionEnvelope(item.text)) continue;
      if (this.absorbPromptEcho(w, s, agentId, item, text, seq, parseIsoTs(item.ts))) continue;
      const entry = { ...this.userMessageEntry(item, text, this.tsFromItem(item)), seq };
      if (item.clientMessageId) {
        this.noteReconciledPromptEntry(item.clientMessageId, entry.id);
        // Confirmed: a saved copy left in the outbox would be restored beside this row.
        void deleteAgentPromptOutbox(item.clientMessageId);
      }
      this.noteServerEchoedPrompt(item.clientMessageId);
      const found = insertIndexForTs(s, entry.ts);
      const at = found >= 0 ? found : s.entries.length;
      const fenced = at > 0 && s.entries[at - 1]?.kind !== "turn_boundary";
      const boundary = fenced ? [this.boundaryAt(undefined)] : [];
      w.assign(s, [...s.entries.slice(0, at), ...boundary, entry, ...s.entries.slice(at)]);
      this.checkOutOfOrder(agentId, s, entry.id, "user_input");
      inserted += 1;
    }
    return inserted;
  }

  // ONE rule, every path: a copy of a prompt on screen (confirmed send, repeat echo, the
  // provider's identity-less copy) settles onto it and adds its seq. Two sends stay two.
  private absorbPromptEcho(
    w: ConversationWrite,
    s: AgentStream,
    agentId: string,
    item: Extract<AgentTimelineItem, { type: "user_message" }>,
    text: string,
    seq: number | undefined,
    ts?: number,
  ): boolean {
    const target = this.promptEchoTarget(s, agentId, item, text, seq);
    if (!target) return false;
    const { at } = target;
    const row = s.entries[at] as StreamEntry & { kind: "user_message" };
    const optimistic = target.optimistic || wordsSettleThisSend(row, item, text, seq);
    // Identity, never status: a durable row of this send landed before this one.
    const confirmed = [...this.reconciledPromptEntryIds.values()].includes(row.id);
    // Recorded from the BUBBLE's id as well as the echo's, so the settle above does not
    // close the door on the twin emission that does carry a client id.
    for (const id of new Set([
      item.clientMessageId,
      optimistic ? row.clientMessageId : undefined,
    ])) {
      if (!id) continue;
      this.noteServerEchoedPrompt(id);
      void deleteAgentPromptOutbox(id);
      this.noteReconciledPromptEntry(id, row.id);
    }
    const { queued: _queued, ...ran } = row;
    const { clientMessageId: _clientMessageId, status: _status, ...settled } = ran;
    const next: StreamEntry & { kind: "user_message" } = {
      ...(optimistic ? settled : ran),
      ...(optimistic ? settledTextOf(settledWordsOf(row, text), ts) : {}),
      ...(row.messageId === undefined && item.messageId !== undefined
        ? { messageId: item.messageId }
        : {}),
      // The ack may have stamped it first; a durable row from an older daemon has none.
      ...(row.sourceMessageId === undefined && item.sourceMessageId
        ? { sourceMessageId: item.sourceMessageId }
        : {}),
      ...(item.forwardedMessageId ? { forwardedMessageId: item.forwardedMessageId } : {}),
      ...providerIdsAfter(row, item, confirmed),
      ...(item.promptAuthor ? { promptAuthor: item.promptAuthor } : {}),
      ...(item.attachments?.length ? { attachments: item.attachments } : {}),
      ...(seq === undefined ? {} : { seq: row.seq ?? seq, rows: rowsWith(row, seq) }),
    };
    w.assign(s, [...s.entries.slice(0, at), next, ...s.entries.slice(at + 1)]);
    return true;
  }

  // The bubble a prompt row belongs to: by seq, this tab's send (optimistic bubble or the
  // ledger), provider message id — then by words within the row's own turn.
  private promptEchoTarget(
    s: AgentStream,
    agentId: string,
    item: Extract<AgentTimelineItem, { type: "user_message" }>,
    text: string,
    seq: number | undefined,
  ): { at: number; optimistic: boolean } | undefined {
    const isPrompt = (e: StreamEntry): e is StreamEntry & { kind: "user_message" } =>
      e.kind === "user_message";
    if (seq !== undefined) {
      const at = s.entries.findIndex((e) => isPrompt(e) && seqsOf(e).includes(seq));
      if (at >= 0) return { at, optimistic: false };
    }
    if (item.clientMessageId) {
      const at = s.entries.findIndex(
        (e) => isPrompt(e) && e.clientMessageId === item.clientMessageId,
      );
      if (at >= 0) return { at, optimistic: true };
      const settledId = this.reconciledPromptEntryIds.get(item.clientMessageId);
      const ledger = settledId ? s.entries.findIndex((e) => e.id === settledId) : -1;
      if (ledger >= 0) return { at: ledger, optimistic: false };
    }
    if (item.messageId !== undefined) {
      const id = item.messageId;
      const at = s.entries.findIndex(
        (e) => isPrompt(e) && (e.messageId === id || e.providerIds?.includes(id) === true),
      );
      if (at >= 0) return { at, optimistic: false };
    }
    const at = this.sameTurnPromptWithWords(s, agentId, item, text, seq);
    return at >= 0 ? { at, optimistic: false } : undefined;
  }

  // The prompt of the row's own turn reading as `text`, unless identity separates them:
  // two different client ids; another device's send meeting a send; echoIsDistinct.
  private sameTurnPromptWithWords(
    s: AgentStream,
    agentId: string,
    item: Extract<AgentTimelineItem, { type: "user_message" }>,
    text: string,
    seq: number | undefined,
  ): number {
    const settledId = item.clientMessageId
      ? this.reconciledPromptEntryIds.get(item.clientMessageId)
      : undefined;
    const settledIds = new Set(this.reconciledPromptEntryIds.values());
    const isSend = (e: StreamEntry & { kind: "user_message" }): boolean =>
      e.clientMessageId !== undefined || settledIds.has(e.id);
    const foreignSend = item.clientMessageId !== undefined && settledId === undefined;
    const incoming = {
      promptAuthor: item.promptAuthor,
      ts: parseIsoTs(item.ts),
      messageId: item.messageId,
    };
    const conflicts = (e: StreamEntry & { kind: "user_message" }): boolean =>
      (settledId !== undefined && settledId !== e.id) ||
      (e.clientMessageId !== undefined &&
        item.clientMessageId !== undefined &&
        e.clientMessageId !== item.clientMessageId) ||
      (foreignSend && isSend(e)) ||
      echoIsDistinct(e, incoming, isSend(e), item.clientMessageId !== undefined);
    const reads = (e: StreamEntry & { kind: "user_message" }): boolean =>
      e.content === text ||
      // `e.content !== ""`: attaching a page with no note is a valid send, and
      // `"".startsWith` is true of every string, so an empty page-only bubble read as a
      // match for any prompt at all and swallowed it whole.
      (e.content !== "" && e.card !== undefined && e.card !== null && text.startsWith(e.content)) ||
      (!!e.attachments?.length && echoIsAttachmentSuperset(e.content, text)) ||
      // The same rule with the sides swapped, for a bubble rebuilt from a durable row: that
      // row is the daemon's rendering (words PLUS its `[image]` markers) and carries no
      // `attachments` to key off, while the provider's copy of the turn is the words alone.
      (text !== "" && echoIsAttachmentSuperset(text, e.content));
    // A send never steps over a closed turn; only an identity-less echo does.
    let nearest = -1;
    for (const i of turnWindow(s, seq, item.clientMessageId === undefined)) {
      const e = s.entries[i];
      if (e.kind !== "user_message" || !reads(e) || conflicts(e)) continue;
      if (nearest < 0) nearest = i;
      // An UNSETTLED send of this tab's is an outstanding obligation, and the oldest one
      // is owed the first row. Nearest-first bound both rows of two identical pending
      // sends to the second bubble and left the first spinning (CYBORG-1166).
      if (e.clientMessageId !== undefined && e.status !== undefined) nearest = i;
    }
    return nearest;
  }

  // An identity-less prompt right after a send, every row between them on screen and no
  // evidence of its own (echoIsDistinct), is its copy: folded, the reply it split re-joined.
  private foldTrailingCopies(w: ConversationWrite, s: AgentStream): void {
    const settledIds = new Set(this.reconciledPromptEntryIds.values());
    let changed = false;
    let send = -1;
    for (let i = 0; i < s.entries.length; i += 1) {
      const e = s.entries[i];
      if (e.kind !== "user_message") continue;
      const prev =
        send >= 0 ? (s.entries[send] as StreamEntry & { kind: "user_message" }) : undefined;
      const a = prev ? seqRangeOf(prev) : undefined;
      const b = seqRangeOf(e);
      const isCopy =
        prev !== undefined &&
        a !== undefined &&
        b !== undefined &&
        e.clientMessageId === undefined &&
        !settledIds.has(e.id) &&
        e.content === prev.content &&
        !echoIsDistinct(prev, e, settledIds.has(prev.id) || prev.clientMessageId !== undefined) &&
        rowsBetweenApplied(s.applied, a.max, b.min);
      if (!isCopy || !prev) {
        send = i;
        continue;
      }
      const fenced = s.entries[i - 1]?.kind === "turn_boundary";
      const merged = {
        ...prev,
        ...foldedProviderIds(prev, e, settledIds.has(prev.id)),
        rows: [...new Set([...seqsOf(prev), ...seqsOf(e)])].sort((x, y) => x - y),
      };
      w.assign(s, [
        ...s.entries.slice(0, send),
        merged,
        ...s.entries.slice(send + 1, fenced ? i - 1 : i),
        ...s.entries.slice(i + 1),
      ]);
      changed = true;
      i = send;
    }
    if (changed) w.assign(s, joinSplitBubbles(s.entries, s.applied));
  }

  // Record a failed timeline hydration and report whether THIS was the first one —
  // i.e. whether the user still needs to be told. See AgentStream.timelineFetchFailed
  // for why a repeating toast is possible at all. Returns true exactly once per
  // stream until a successful hydrate clears the mark.
  //
  // Deliberately does NOT `touch()`: this flips no rendered state (it gates a toast,
  // not a view), and re-cloning the streams map on every failed retry would push a
  // reactive invalidation through every subscribed surface for nothing.
  noteTimelineFetchFailed(agentId: string): boolean {
    const s = this.ensure(agentId);
    if (s.timelineFetchFailed) return false;
    s.timelineFetchFailed = true;
    return true;
  }

  // Record that the relay REFUSED this stream's timeline, and report whether this was
  // the first time — i.e. whether the person still needs to be told. See
  // AgentStream.timelineReadDenied. Same no-touch() rule as its sibling above: it gates
  // a fetch and a one-shot notice, not a rendered surface.
  noteTimelineReadDenied(agentId: string): boolean {
    const s = this.ensure(agentId);
    if (s.timelineReadDenied) return false;
    s.timelineReadDenied = true;
    return true;
  }

  /** Has the relay already refused this stream's timeline? Then do not ask again. */
  isTimelineReadDenied(agentId: string): boolean {
    return this.streams.get(agentId)?.timelineReadDenied ?? false;
  }

  // Record that the catch-up read answered not_found (AgentStream.timelineGone). Only an
  // existing stream is marked: ensure() would resurrect one a racing clear() deleted.
  noteTimelineGone(agentId: string): void {
    const s = this.streams.get(agentId);
    if (s) s.timelineGone = true;
  }

  /** Did a catch-up read already find this conversation gone? Then catch-up skips it. */
  isTimelineGone(agentId: string): boolean {
    return this.streams.get(agentId)?.timelineGone ?? false;
  }

  getTurnStatus(agentId: string): TurnStatus {
    return this.streams.get(agentId)?.turnStatus ?? "idle";
  }

  // "Is this agent mid-turn right now" — the shared predicate for the surfaces
  // that show or act on a SINGLE agent's live state: the composer's send-vs-queue
  // decision (AgentComposer), the chat sidebar's status word + row dot
  // (ChannelSidebar), the Agents pane and mobile DMs status pills, and the daemon
  // SessionList dot.
  //
  // NOT universal, deliberately: HomePane's "N running" counter and its
  // lifecycleColor/lifecycleLabel helpers still read raw `lifecycle`. Those two
  // helpers are keyed by a lifecycle STRING (no agentId in hand) and shared across
  // rows, so switching them is a refactor rather than a one-line adoption — the
  // known, accepted gap, not an oversight. The composer's Cancel button is also
  // deliberately NOT on this predicate (see mayBeRunning there).
  //
  // CYBORG-673: this used to be written inline at each call site as
  // `turnStatus === "running" || agent.lifecycle === "running"`, and that OR is
  // the "stuck in Queued forever" bug. The two signals are cleared by DIFFERENT
  // paths: the client idle watchdog (startTurnTimeout) declares a wedged turn
  // dead by setting turnStatus = "error", but it CANNOT touch `lifecycle` — that
  // field only ever comes from the daemon's roster snapshot (listAgents) or a
  // turn event. A daemon whose provider never emits a terminal event keeps
  // reporting "running" indefinitely, so the OR stayed true forever: the composer
  // never left queue mode, every further message parked in a queue that only
  // flushes on a turn_completed that is never coming, and no timer could re-arm
  // (noteTurnActivity returns early once turnStatus isn't "running").
  //
  // So a TERMINAL turnStatus (error / canceled) — an explicit, local "this turn is
  // over" verdict — WINS over the roster snapshot instead of being OR-ed with it.
  // `lifecycle` still decides the ambiguous case (turnStatus "idle"), which is why
  // it was OR-ed in the first place: the ephemeral stream map can be missing or
  // stale after a workspace switch / navigating away while the agent really is
  // generating, and dropping STOP + QUEUE there would be wrong.
  //
  // `settled` is an EXTERNAL terminal verdict for that same ambiguous case, supplied
  // by callers that hold a SECOND server truth about the same turn — today the work
  // session's own status (isSettledWorkSession: done / paused). It exists because the
  // roster lifecycle is the one input nothing local can clear: it is a snapshot the
  // daemon reports, and a page RELOAD rebuilds it verbatim, so a session whose turn
  // had already finished kept rendering "LIVE · <agent> working" with the composer
  // stuck in Queue mode until the daemon's next status report landed. Ranked BELOW a
  // live "running" turnStatus on purpose: a stale settled row must never blank out a
  // turn this client is watching frames from right now.
  // Whether the daemon will hold a send now behind the running turn. A joining provider takes it
  // into that turn instead, unless prompts already wait (they go first): server join-on-arrival.ts.
  sendWouldQueue(agentId: string): boolean {
    const live = workspaceState.agents.find((a) => a.agentId === agentId);
    if (!this.isTurnActive(agentId, live?.lifecycle)) return false;
    if (this.getSharedQueue(agentId).length > 0) return true;
    // An unknown provider is drawn as a direct send; if the daemon holds it, it moves in one update.
    return !!live?.provider && !JOINS_RUNNING_TURN.has(live.provider);
  }

  isTurnActive(agentId: string, lifecycle?: string | null, settled?: boolean): boolean {
    const status = this.getTurnStatus(agentId);
    if (status === "running") return true;
    if (status === "error" || status === "canceled") return false;
    if (settled) return false;
    return lifecycle === "running";
  }

  getUsage(agentId: string): AgentUsage | null {
    return this.streams.get(agentId)?.usage ?? null;
  }

  /** The last peer hold on this session dropped by expiry and no newer hold replaced it. */
  hasExpiredHold(agentId: string): boolean {
    return !!this.streams.get(agentId)?.expiredHold;
  }

  /** Raw sender name of the expired hold (resolve it to a display name before showing), when it was named. */
  expiredHoldFrom(agentId: string): string | undefined {
    return this.streams.get(agentId)?.expiredHold?.from;
  }

  /** The user closed the expiry note. */
  dismissExpiredHold(agentId: string): void {
    const s = this.streams.get(agentId);
    if (!s?.expiredHold) return;
    s.expiredHold = undefined;
    this.touch(agentId);
  }

  getPendingPermissions(agentId: string): AgentPermissionRequest[] {
    return Array.from(this.streams.get(agentId)?.pendingPermissions.values() ?? []);
  }

  // CYBORG-1516: counts live permission changes, so a daemon read that raced one is dropped.
  private readonly permissionStamps = new Map<string, number>();
  private bumpPermissionStamp(agentId: string): void {
    this.permissionStamps.set(agentId, this.permissionStamp(agentId) + 1);
  }
  permissionStamp(agentId: string): number {
    return this.permissionStamps.get(agentId) ?? 0;
  }

  // The hosting daemon's whole list as of `since`. False: a live change landed meanwhile.
  replacePendingPermissions(
    agentId: string,
    since: number,
    requests: readonly AgentPermissionRequest[],
  ): boolean {
    if (this.permissionStamp(agentId) !== since) return false;
    const s = this.ensure(agentId);
    s.pendingPermissions = new Map(requests.map((r) => [r.id, r]));
    this.persistPendingPermissions(agentId, s);
    this.touch(agentId);
    return true;
  }

  getModel(agentId: string): string | null {
    return this.streams.get(agentId)?.currentModel ?? null;
  }

  // Optimistically reflect a model switch right away. The server's model_changed
  // event doesn't reliably reach cloud guests, so without this the selector stays
  // stale (shows the old/binding model) even though the switch took effect.
  setModel(agentId: string, model: string | null): void {
    const s = this.ensure(agentId);
    s.currentModel = model;
    s.controlVersions.model += 1;
    this.touch(agentId);
  }

  // Same optimistic reflection for thinking/mode switches — their
  // thinking_option_changed / mode_changed events share model_changed's
  // cloud-guest unreliability, so without this the pills stay stale after a user
  // switch (the reported "effort stays on the old value" / bypass-not-updating).
  setThinkingOption(agentId: string, thinkingOptionId: string | null): void {
    const s = this.ensure(agentId);
    s.thinkingOptionId = thinkingOptionId;
    s.thinkingReported = true;
    s.controlVersions.thinking += 1;
    this.touch(agentId);
  }

  setModeId(agentId: string, modeId: string | null): void {
    const s = this.ensure(agentId);
    s.currentModeId = modeId;
    s.controlVersions.mode += 1;
    this.touch(agentId);
  }

  /** Capture the per-field clock before requesting an authoritative roster snapshot. */
  controlSnapshotToken(agentId: string): AgentControlVersions {
    return { ...this.ensure(agentId).controlVersions };
  }

  /**
   * Apply a roster snapshot after reconnect without rolling back a newer local
   * selection or stream event. Each field has its own clock, so changing Model
   * while the request is in flight does not prevent a fresh Mode snapshot from
   * being accepted.
   */
  reconcileControlsFromSnapshot(
    agentId: string,
    snapshot: AgentControlSnapshot,
    requestedAt: AgentControlVersions,
  ): void {
    const s = this.ensure(agentId);
    let changed = false;
    if (snapshot.model !== undefined && s.controlVersions.model === requestedAt.model) {
      s.currentModel = snapshot.model;
      changed = true;
    }
    if (s.controlVersions.mode === requestedAt.mode) {
      if (snapshot.currentModeId !== undefined) {
        s.currentModeId = snapshot.currentModeId;
        changed = true;
      }
      if (snapshot.availableModes !== undefined) {
        s.availableModes = snapshot.availableModes ?? [];
        changed = true;
      }
    }
    if (
      snapshot.thinkingOptionId !== undefined &&
      s.controlVersions.thinking === requestedAt.thinking
    ) {
      s.thinkingOptionId = snapshot.thinkingOptionId;
      s.thinkingReported = true;
      changed = true;
    }
    if (changed) this.touch(agentId);
  }

  // Seed mode/thinking from the agent's INITIAL snapshot (roster / agent state),
  // not just from the *_changed stream events. Non-ACP providers (Claude, Codex,
  // Pi, OpenCode) never emit mode_changed/thinking_option_changed, so without
  // this the composer falls back to the generic default/bypass mode list and a
  // blank thinking value. Fills gaps only — a live event already received is
  // never clobbered.
  hydrateFromSnapshot(
    agentId: string,
    snapshot: {
      availableModes?: AgentMode[] | null;
      currentModeId?: string | null;
      thinkingOptionId?: string | null;
    },
  ): void {
    const s = this.ensure(agentId);
    let changed = false;
    if (snapshot.thinkingOptionId !== undefined && !s.thinkingReported) {
      s.thinkingReported = true;
      changed = true;
    }
    if (
      s.availableModes.length === 0 &&
      snapshot.availableModes &&
      snapshot.availableModes.length > 0
    ) {
      s.availableModes = snapshot.availableModes;
      changed = true;
    }
    if (s.currentModeId == null && snapshot.currentModeId != null) {
      s.currentModeId = snapshot.currentModeId;
      changed = true;
    }
    if (s.thinkingOptionId == null && snapshot.thinkingOptionId != null) {
      s.thinkingOptionId = snapshot.thinkingOptionId;
      changed = true;
    }
    if (changed) this.touch(agentId);
  }

  getModeId(agentId: string): string | null {
    return this.streams.get(agentId)?.currentModeId ?? null;
  }

  getAvailableModes(agentId: string): AgentMode[] {
    return this.streams.get(agentId)?.availableModes ?? [];
  }

  getThinkingOptionId(agentId: string): string | null {
    return this.streams.get(agentId)?.thinkingOptionId ?? null;
  }

  hasThinkingOption(agentId: string): boolean {
    return this.streams.get(agentId)?.thinkingReported ?? false;
  }

  getRuntimeInfo(agentId: string): AgentRuntimeInfo | null {
    return this.streams.get(agentId)?.runtimeInfo ?? null;
  }
}

// ─── Agent attention (#591: derived finished/error "needs attention") ─────────
//
// Holds the per-agent derived attention reason for the agents-LIST badge, kept
// separate from AgentStreamState so the change is additive (no refactor of the
// shared stream structures; #594 edits the same file in parallel). Two inputs
// feed it:
//   1. The list snapshot (cyborg:list_agents) — authoritative for agents the
//      client isn't actively streaming; seeded via seedFromSnapshot on each
//      listAgents.
//   2. Live turn events — for an agent already streaming, a turn that completes
//      ("finished") or fails ("error") raises attention without a refetch,
//      mirroring the daemon's edge rule (noteTurnEvent).
// The flag is CLEARED when the agent is viewed (clearForView), the issue's
// falsifiable DONE. A viewed-this-session guard (`cleared`) stops an in-flight
// snapshot from resurrecting a badge the user just dismissed.
export class AttentionState {
  // agentId → the reason a badge should show. Absent = no attention badge.
  private reasons: Map<string, AgentAttentionReason> = $state(new Map());
  // agentIds the user opened this session — suppresses snapshot re-raise until a
  // genuinely new edge (a fresh turn) occurs.
  private cleared: Set<string> = new Set();

  private raise(agentId: string, reason: AgentAttentionReason): void {
    // Only the rendered reasons matter; "permission" has its own surface.
    if (reason !== "finished" && reason !== "error") return;
    if (this.reasons.get(agentId) === reason) return;
    // `reasons` is a Svelte 5 reactive Map proxy ($state(new Map())): a `.set()`
    // here surgically triggers only the effects reading THIS key — no manual
    // reassignment (the old `touch()`) which would re-run every observer.
    this.reasons.set(agentId, reason);
  }

  // Seed/refresh from a list snapshot row's attention projection. Adopts a fresh
  // server-set flag (unless the user already cleared it this session), and
  // clears when the server reports it no longer requires attention (e.g. another
  // device viewed it).
  seedFromSnapshot(agentId: string, attention: AgentAttention | null | undefined): void {
    const reason = reconcileAttentionFromSnapshot({
      snapshot: attention,
      locallyCleared: this.cleared.has(agentId),
    });
    if (reason) {
      this.raise(agentId, reason);
    } else {
      // `.delete()` on the reactive Map is itself reactive (and a no-op when the
      // key is absent), so no guard / reassignment needed.
      this.reasons.delete(agentId);
    }
  }

  // Bulk seed from a full agents list (each row may carry `attention`).
  seedFromAgents(agents: Array<{ agentId: string; attention?: AgentAttention | null }>): void {
    for (const a of agents) this.seedFromSnapshot(a.agentId, a.attention);
  }

  // Live turn edge for an agent already streaming. A new turn STARTING means the
  // agent is active again — drop any stale "finished/error" and un-suppress so a
  // future completion/failure re-raises (the user is clearly back working on it).
  noteTurnEvent(agentId: string, eventType: AgentTurnEventType): void {
    if (eventType === "turn_started") {
      this.cleared.delete(agentId);
      this.reasons.delete(agentId);
      return;
    }
    const reason = attentionReasonForTurnEvent(eventType);
    if (reason) this.raise(agentId, reason);
  }

  // Viewing the agent clears its attention badge (the falsifiable DONE). Marks it
  // cleared-this-session so an in-flight snapshot can't immediately re-raise it.
  clearForView(agentId: string): void {
    this.cleared.add(agentId);
    this.reasons.delete(agentId);
  }

  getReason(agentId: string): AgentAttentionReason | null {
    return this.reasons.get(agentId) ?? null;
  }

  // The badge to render for a row (label/tone/description), or null.
  badgeFor(agentId: string): AttentionBadge | null {
    const reason = this.reasons.get(agentId) ?? null;
    if (reason === "error") {
      const failure = agentStreamState.getEntries(agentId).findLast((e) => e.kind === "error");
      if (failure && isUsageLimitFailure(failure.content)) return PAUSED_BADGE;
    }
    return badgeForReason(reason);
  }

  requiresAttention(agentId: string): boolean {
    return this.reasons.has(agentId);
  }

  // Drop everything (workspace switch / disconnect).
  clearAll(): void {
    this.reasons.clear();
    this.cleared.clear();
  }
}

export class ProviderState {
  // Monotonic authorization-context generation. Async probes capture it and
  // must not mutate this store after clear() advances the boundary.
  generation = 0;
  list: ProviderInfo[] = $state([]);
  loading = $state(false);
  // True when the LAST list_providers rejected and left us with nothing.
  // CYBORG-686 §7: without this, a failed fetch (no daemon access, relay blip)
  // renders the exact same "No providers detected" as a genuinely empty
  // machine — a member without access hits it every single time. "We couldn't
  // check" and "there's nothing there" are different sentences.
  loadError = $state(false);
  // Per-daemon catalogs. In multi-daemon workspaces the un-targeted
  // cyborg:list_providers lands on an arbitrary daemon, so the flat `list` can
  // be ANOTHER daemon's catalog — looking up an agent's provider there misses
  // and the model menu silently degrades to a static label. Surfaces that know
  // their daemon must read through forDaemon() instead.
  byDaemon: Record<string, ProviderInfo[]> = $state({});
  // Per-daemon in-flight flags: several composers can mount at once (and for
  // DIFFERENT daemons) — a single global `loading` would either dedupe wrongly
  // or serialize unrelated fetches.
  loadingDaemons: Record<string, boolean> = $state({});
  // Per-daemon "the last probe aimed at THIS daemon rejected", for exactly the
  // reason loadingDaemons above exists: `loadError` is ONE boolean shared by
  // every machine, so a rejected probe against a teammate's daemon B set it and
  // every consumer asking about the user's own daemon A — whose byDaemon
  // catalog is fresh and positive — then read "we couldn't check". A global
  // error cannot answer a per-daemon question. `loadError` stays for the
  // surfaces that genuinely ask the global one (the agent-new picker).
  loadErrorDaemons: Record<string, boolean> = $state({});
  // The fetch each daemon has in flight, and the one re-fetch `fresh` callers share
  // after it (app.svelte.ts fetchProviders). Not reactive: nothing renders from them.
  fetchInFlight = new Map<string, Promise<void>>();
  freshFollowUp = new Map<string, Promise<void>>();

  get available(): ProviderInfo[] {
    return this.list.filter((p) => p.available);
  }

  /** Catalog for a specific daemon, falling back to the last global fetch. */
  forDaemon(daemonId: string | null | undefined): ProviderInfo[] {
    if (daemonId && this.byDaemon[daemonId]) return this.byDaemon[daemonId];
    return this.list;
  }

  /** Catalog proven to come from this daemon; never falls back across machines. */
  forDaemonExact(daemonId: string | null | undefined): ProviderInfo[] | undefined {
    return daemonId ? this.byDaemon[daemonId] : undefined;
  }

  /**
   * Did the last list_providers aimed at THIS daemon reject? False for a daemon
   * nobody has probed — "never asked" is not "asked and failed"; the absence of
   * a byDaemon catalog is what makes that case unverified.
   */
  loadFailedFor(daemonId: string | null | undefined): boolean {
    return !!daemonId && this.loadErrorDaemons[daemonId] === true;
  }

  /** Drop catalogs and transient probe state at workspace/account boundaries. */
  clear(): void {
    this.generation += 1;
    this.list = [];
    this.loading = false;
    this.loadError = false;
    this.byDaemon = {};
    this.loadingDaemons = {};
    this.loadErrorDaemons = {};
    this.fetchInFlight = new Map();
    this.freshFollowUp = new Map();
  }
}

export class CyboState {
  list: Cybo[] = $state([]);
  loading = $state(false);
  // True ONLY when the roster failed to load AND there is nothing to show
  // (list is empty) — that's the one state with no valid surface and no
  // auto-recovery otherwise. NEVER true while `list.length > 0`: a warm
  // roster that fails a background refetch just keeps showing what it has
  // (adversarial review caught a false "Couldn't load agents" banner sitting
  // ON TOP of an already-loaded roster on the old People page — that violated this exact
  // invariant). retryCybosOnEmpty (app.svelte.ts) is the SOLE setter-to-true,
  // gated on the list already being empty; every site that repopulates
  // cyboState.list on success clears this alongside.
  loadError = $state(false);
}

// ─── Daemon state (global selector + per-workspace daemon list) ──────
//
// Makes daemons first-class: the full workspace daemon list, per-user access
// grants, and a single globally-selected daemon id. Live online/offline status
// is merged from the `daemon_status` WS handler so dots stay realtime without
// re-fetching. All getters fall back gracefully so single-daemon users keep the
// default behavior (the one daemon is auto-loaded).

export class DaemonState {
  list: Daemon[] = $state([]);
  readonly masked = new MaskedDaemons();
  access: DaemonAccessEntry[] = $state([]);
  // False until a grants read has landed (and after a failed one): `access` is then a guess, and
  // the approval gate fails open on it (the relay stays the gate).
  accessKnown = $state(false);
  loading = $state(false);
  // The daemon id THIS CLIENT is connected to (fetchDaemonInfo(), the daemon's own `serverId`);
  // null on the cloud relay or before the fetch resolves. resolveViewerDaemonId (daemon-select.ts)
  // reads it as the one non-inferred "which machine am I at" signal for the Remote/Local badge, NOT
  // as the target of a NEW action (effectiveId()/selectedId, which prefers a reachable grant).
  connectedDaemonId: string | null = $state(null);
  // Explicit user/route selection. `null` means "use the auto-loaded default".
  // Backed by a private rune + per-workspace localStorage so the choice is
  // STICKY across reloads/navigation (issue #34). Assigning `selectedId` from
  // anywhere persists it; `load()` restores + validates it against the current
  // (present + accessible) daemon list.
  //
  // BLAST RADIUS of the global daemon selector (issue #38) — what `selectedId`
  // re-scopes:
  //   1. NEW SESSIONS: the create-agent dialog seeds its target
  //      daemon from `effectiveId()` (selectedId → fallbacks). Selecting a daemon
  //      sets where the next agent/cybo spawns.
  //   2. DETAIL VIEW: the Agents pane's Daemon sub-tab shows the selected daemon's
  //      DaemonDetail (providers, access, cybos, slash default).
  //   3. AGENTS LIST: the Agents pane scopes the "Your agents" cybo list to the
  //      EFFECTIVE daemon (`selectedId ?? effectiveId()`), not the whole workspace —
  //      so a teammate's machine shows THEIR agents, and the default view shows only
  //      the user's own daemon's cybos (falling back to the user's own cybos when no
  //      daemon is usable). The sessions list still scopes only on an EXPLICIT
  //      selection; Logs stay unscoped.
  private _selectedId = $state<string | null>(null);
  // The user's configured default daemon for slash commands (null = unset).
  defaultSlashDaemonId: string | null = $state(null);
  // The user's preferred model for channel AI commands (null = auto-resolve).
  slashCommandModel: { provider: string; model: string } | null = $state(null);
  // Realtime overrides keyed by daemon id, fed by the daemon_status handler.
  private liveStatus = $state<Record<string, "online" | "offline">>({});
  private readonly drops = new DaemonDrops(); // when THIS client saw each daemon drop: redial proof

  get selectedId(): string | null {
    return this._selectedId;
  }
  set selectedId(id: string | null) {
    this._selectedId = id;
    if (id) this.writeStoredSelection(id);
    else this.clearStoredSelection();
  }

  // list_daemons `workerBillingLive`: false = connecting a workspace machine
  // skips checkout (the server allows it). undefined = not sent (older relay)
  // → the license check decides, as before.
  workerBillingLive = $state<boolean | undefined>(undefined);

  setWorkerBillingLive(live: boolean | undefined): void {
    this.workerBillingLive = live;
  }

  // `access` undefined = the grants read failed: keep what we had and mark it unknown.
  load(daemons: DaemonWireRow[], access: DaemonAccessEntry[] | undefined, now = Date.now()): void {
    this.refreshList(daemons, now);
    if (access) this.access = access;
    this.accessKnown = access !== undefined;
    this.reconcileSelection();
  }

  // Refresh just the per-user access grants (e.g. after a daemon-access request is
  // approved) without touching the daemon list / selection.
  setAccess(access: DaemonAccessEntry[]): void {
    this.access = access;
    this.accessKnown = true;
  }

  // Rows only, folded to the nested shape (normalizeDaemonRow): never access or the selection.
  refreshList(daemons: DaemonWireRow[], now = Date.now()): void {
    const listed = this.masked.split(daemons.map(normalizeDaemonRow), daemons === this.list);
    this.list = this.drops.noteRows(this.list, listed, this.liveStatus, now);
  }

  // Restore the persisted per-workspace selection, then keep it only if the
  // daemon is still PRESENT and ACCESSIBLE. A removed daemon or a revoked grant
  // drops the stale pointer (issues #33, #34); an offline-but-accessible daemon
  // stays selected so the detail view can surface its offline state (#33).
  private reconcileSelection(): void {
    const candidate = this.readStoredSelection() ?? this._selectedId;
    if (candidate && this.isSelectable(candidate)) {
      this._selectedId = candidate;
      this.writeStoredSelection(candidate);
    } else {
      this._selectedId = null;
      this.clearStoredSelection();
    }
  }

  // Present in the list AND accessible to the current user (online not required).
  private isSelectable(daemonId: string): boolean {
    const daemon = this.byId(daemonId);
    return !!daemon && this.hasAccess(daemon, authState.user?.id);
  }

  private selectionStorageKey(): string | null {
    const wsId = workspaceState.current?.id;
    return wsId ? `cyborg.daemon.selected.${wsId}` : null;
  }
  private readStoredSelection(): string | null {
    const key = this.selectionStorageKey();
    if (!key) return null;
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }
  private writeStoredSelection(daemonId: string): void {
    const key = this.selectionStorageKey();
    if (!key) return;
    try {
      localStorage.setItem(key, daemonId);
    } catch {
      // intentional: best-effort daemon-selection persistence; not user-facing.
    }
  }
  private clearStoredSelection(): void {
    const key = this.selectionStorageKey();
    if (!key) return;
    try {
      localStorage.removeItem(key);
    } catch {
      // intentional: best-effort daemon-selection cleanup; not user-facing.
    }
  }

  setDefaultSlashDaemon(daemonId: string | null): void {
    this.defaultSlashDaemonId = daemonId;
  }

  setConnectedDaemonId(daemonId: string | null): void {
    this.connectedDaemonId = daemonId;
  }

  setSlashCommandModel(model: { provider: string; model: string } | null): void {
    this.slashCommandModel = model;
  }

  setStatus(daemonId: string, status: "online" | "offline", now = Date.now()): void {
    this.drops.noteStatus(daemonId, status, this.isOnline(daemonId), now);
    this.liveStatus = { ...this.liveStatus, [daemonId]: status };
  }

  clear(): void {
    this.list = [];
    this.masked.clear();
    this.access = [];
    this.accessKnown = false;
    this.workerBillingLive = undefined;
    this.selectedId = null;
    this.defaultSlashDaemonId = null;
    this.slashCommandModel = null;
    this.liveStatus = {};
    this.drops.clear();
  }

  byId(daemonId: string): Daemon | undefined {
    return this.list.find((d) => d.id === daemonId) ?? this.masked.find(daemonId);
  }

  // Offline by a live status or the loaded list; a daemon neither knows is NOT offline.
  isKnownOffline(daemonId: string): boolean {
    const live = this.liveStatus[daemonId];
    if (live) return live === "offline";
    const row = this.byId(daemonId);
    return row !== undefined && row.status !== "online";
  }

  // Known offline AND seen dropping by this client inside the reconnect window: it is redialing.
  isReconnecting(daemonId: string, now: number): boolean {
    return this.drops.within(daemonId, now) && this.isKnownOffline(daemonId);
  }

  isOnline(daemonId: string): boolean {
    const live = this.liveStatus[daemonId];
    if (live) return live === "online";
    return this.byId(daemonId)?.status === "online";
  }

  get online(): Daemon[] {
    return this.list.filter((d) => this.isOnline(d.id));
  }

  // Online daemons the current user may run code on (owner OR daemon_access
  // grant) — what capability/provider rows must enumerate instead of `online`,
  // so foreign daemons are never offered as setup/spawn targets. $derived (not
  // a getter): capability rows read this per render row, memoization keeps the
  // filter from re-running on every read.
  accessibleOnline: Daemon[] = $derived(
    accessibleOnlineDaemons(
      this.list,
      (d) => this.isOnline(d.id),
      (d) => this.hasAccess(d, authState.user?.id),
    ),
  );

  // Human-readable machine location, e.g. "macbook · darwin/arm64".
  locationLabel(meta?: DaemonMeta | null): string | null {
    if (!meta) return null;
    const host = meta.host?.trim();
    const os = [meta.platform, meta.arch].filter(Boolean).join("/");
    if (host && os) return `${host} · ${os}`;
    return host || os || null;
  }

  // Users with access to a daemon: its owner is always implicitly included.
  accessUserIds(daemonId: string): string[] {
    const owner = this.byId(daemonId)?.ownerId;
    const ids = new Set<string>(owner ? [owner] : []);
    for (const a of this.access) {
      if (a.daemonId === daemonId) ids.add(a.userId);
    }
    return [...ids];
  }

  // The raw scopes a non-owner user holds on a daemon (#705), as stored in the
  // access list. Returns null if there is no grant ROW for them (no access).
  // The owner is handled by the caller (owner = implicit admin, not a row). A
  // present row with a missing/empty scopes field is normalized to ['admin']
  // (legacy total-access fail-safe) by the matrix via normalizeScopes.
  accessScopesFor(daemonId: string, userId: string): string[] | null {
    const entry = this.access.find((a) => a.daemonId === daemonId && a.userId === userId);
    return entry ? (entry.scopes ?? null) : null;
  }

  // Worker tier: the member-default scopes a Worker daemon grants everyone, or
  // null (not a worker / empty default). Filtered to chat/spawn defensively — a
  // workspace-wide DEFAULT must never yield terminal/admin, whatever the wire says.
  workerDefaultScopes(daemonId: string): string[] | null {
    const raw = this.byId(daemonId)?.worker?.defaultScopes;
    if (!raw?.length) return null; // null: mirrors accessScopesFor's no-grant contract
    const scoped = raw.filter((s) => s === "chat" || s === "spawn");
    return scoped.length > 0 ? scoped : null; // null: empty default = nothing granted
  }

  // What a user can DO on a daemon: their explicit grant row wins; otherwise the
  // Worker default (if any). Kept separate from raw accessScopesFor so the access
  // matrix can still tell an explicit grant from the default. The `blocked`
  // sentinel (explicit Off) yields effective ∅ — never the default, never admin.
  effectiveScopesFor(daemonId: string, userId: string): string[] | null {
    const explicit = this.accessScopesFor(daemonId, userId);
    if (explicit?.includes("blocked")) return [];
    return explicit ?? this.workerDefaultScopes(daemonId);
  }

  // Public access-matrix check by id (owner OR daemon_access grant) — mirrors
  // the server gate (pg-sync.canUserAccessDaemon). Drives selectors that must
  // only offer daemons the user may run code on (e.g. the slash-daemon config).
  canAccess(daemonId: string, currentUserId: string | undefined): boolean {
    const daemon = this.byId(daemonId);
    return !!daemon && this.hasAccess(daemon, currentUserId);
  }

  // Owner, or a spawn/admin grant (a chat-only grant is not enough): mirrors the server's
  // canUserSpawnOnDaemon, the bar for managing someone else's agent.
  canSpawn(daemonId: string, currentUserId: string | undefined): boolean {
    return this.holdsScope(daemonId, currentUserId, ["spawn"]);
  }

  // CYBORG-1434: may the viewer approve a tool call on this machine? Owner, or admin / spawn /
  // terminal: the twin of the server's scopesRefuseApproval (approval-drive-authority.ts).
  canDrive(daemonId: string, currentUserId: string | undefined): boolean {
    return this.holdsScope(daemonId, currentUserId, ["spawn", "terminal"]);
  }

  private holdsScope(
    daemonId: string,
    currentUserId: string | undefined,
    wanted: readonly string[],
  ): boolean {
    const daemon = this.byId(daemonId);
    if (!daemon || !currentUserId) return false;
    const viewer = this.viewerFor(currentUserId);
    if (isMe(daemon.ownerId, viewer)) return true;
    // The grant row may sit under either of the viewer's ids. A row with empty scopes is NO access
    // (the server's effectiveScopes), not the legacy admin.
    const row = this.access.find((a) => a.daemonId === daemonId && isMe(a.userId, viewer));
    let scopes = this.workerDefaultScopes(daemonId) ?? [];
    if (row) scopes = row.scopes ?? [];
    return scopes.includes("admin") || wanted.some((w) => scopes.includes(w));
  }

  private hasAccess(daemon: Daemon, currentUserId: string | undefined): boolean {
    if (!currentUserId) return false;
    // isMe, not `=== currentUserId`: BOTH fields are dual-namespace — `daemons.owner_id`
    // is daemon-LOCAL on a self-claim (workspace-relay syncDaemonToPg), cloud on a relay
    // claim; `daemon_access.user_id` has a relay writer AND dispatcher.ts's. The raw
    // compare denied the OWNER their own machine — the conversion
    // routes/workspace/+page.svelte already made for its twin.
    const viewer = this.viewerFor(currentUserId);
    if (isMe(daemon.ownerId, viewer)) return true;
    // An explicit per-user row wins over the Worker default — including the
    // `blocked` sentinel (explicit Off, effective scopes ∅).
    const row = this.access.find((a) => a.daemonId === daemon.id && isMe(a.userId, viewer));
    if (row) return !(row.scopes ?? []).includes("blocked");
    // Worker tier: a Worker with default scopes is usable by EVERY member —
    // no per-user grant row required.
    return !!daemon.worker?.defaultScopes?.length;
  }

  // The full viewer identity behind a bare `currentUserId` argument. Every caller of
  // the access/selection methods passes `authState.user?.id`, and a bare id makes isMe
  // degrade to the compare it replaces — so resolve it back here, at the one seam they
  // all route through, instead of editing ~30 call sites. STRICTLY NON-WIDENING: an id
  // that is not the signed-in viewer's own falls through unchanged.
  private viewerFor(currentUserId: string | undefined): ViewerIdentity {
    const me = authState.user;
    return me && isMe(currentUserId, me) ? me : currentUserId;
  }

  // The daemon a new session should target by default — never a foreign one.
  // Priority lives in pickDefaultDaemon (pure + unit-tested in daemon-select.ts).
  loadedId(currentUserId: string | undefined): string | null {
    return pickDefaultDaemon(
      this.list,
      // The resolved identity, not the bare id — pickDefaultDaemon's own owner arms go
      // through isMe and a bare id would leave them exactly as broken as before.
      this.viewerFor(currentUserId),
      (d) => this.isOnline(d.id),
      (d) => this.hasAccess(d, currentUserId),
    );
  }

  // The effective selected daemon: an explicit selection wins, else the default.
  effectiveId(currentUserId: string | undefined): string | null {
    // Only honor the explicit selection if it's still usable — online AND
    // accessible. A selected daemon that went offline or had its access revoked
    // must NOT be returned, or launch defaults would target an unreachable
    // daemon and fail; fall back to the auto-select default instead.
    const sel = this.selectedId ? this.byId(this.selectedId) : undefined;
    if (sel && this.isOnline(sel.id) && this.hasAccess(sel, currentUserId)) return sel.id;
    return this.loadedId(currentUserId);
  }

  // ── No-daemon / cloud-only + selected-daemon health (issues #32, #33) ──

  // True when the workspace has zero daemons at all (cloud-only / none connected).
  get isEmpty(): boolean {
    return this.list.length === 0;
  }

  // Any daemon the user can actually launch on right now (online + accessible).
  // Drives the new-session guard + the sidebar empty-state CTA (#32).
  hasUsableDaemon(currentUserId: string | undefined): boolean {
    return this.list.some((d) => this.isOnline(d.id) && this.hasAccess(d, currentUserId));
  }

  // The EXPLICITLY selected daemon is present + accessible but currently OFFLINE.
  // The detail view shows an offline state and blocks new sessions rather than
  // silently auto-switching (#33).
  get selectionOffline(): boolean {
    const id = this._selectedId;
    return !!id && !!this.byId(id) && !this.isOnline(id);
  }

  // One-line "why this daemon is loaded" hint for the detail view (#33).
  loadedReason(currentUserId: string | undefined): string {
    const id = this.effectiveId(currentUserId);
    if (!id) {
      return this.isEmpty ? "No daemons in this workspace" : "No daemon you can use right now";
    }
    const daemon = this.byId(id);
    if (!daemon) return "";
    if (this._selectedId === id) return "Selected by you";
    if (isMe(daemon.ownerId, this.viewerFor(currentUserId))) return "Your online daemon";
    return "The only online daemon you can access";
  }
}

// ─── Activity state (user-facing notification inbox) ─────────────

export type ActivityEventType =
  | "mention"
  | "dm_received"
  | "task_assigned"
  // A task the human is assigned to moved to a new status/state. Emitted by the
  // relay task handlers via taskActivityEvents; task_review_requested has no
  // emitter yet (kept for the render label).
  | "task_status_changed"
  | "task_review_requested"
  | "thread_reply"
  | "reaction"
  | "permission_request"
  // #705: a non-owner asked a daemon owner for access → the owner sees an
  // approvable inbox row (mirrors permission_request, with Approve/Deny actions).
  | "daemon_access_request"
  // The owner approved/denied a request → the requester sees a resolution row.
  // The relay persists this as an activity_events row (emitActivityEvent), so it
  // MUST have a client case or getUnreadActivityCount counts a row the feed can't
  // render as itself — the ghost-badge half of Bug C.
  | "daemon_access_request_resolved"
  | "agent_error"
  // CYBORG-304: a newly @-mentioned page member. Emitted by the relay/daemon's
  // page-mention notify (sourceType "page", sourceId = pageId) — see
  // emitPageActivity / page-mention-activity.ts.
  | "page_mention"
  // Phase 4 (E16/E17, PR #1515): a tool-approval card was posted for this user
  // to act on. Emitted by approval-card-daemon.ts's insertActivityEvent /
  // the relay's cyborg:approval_card_notify handler (sourceType "message",
  // sourceId = the card's message id) with the SAME channelId/dmPeerId the
  // card itself carries, so activity-registry.ts's approvalRequestedClick can
  // rebuild the exact deep link (buildApprovalDeepLinkUrl's client-side
  // equivalent, ?m=<messageId>) instead of only opening the channel/DM at
  // latest (PR #1600).
  | "approval_requested"
  // A non-owner asked a private page's owner for access → the owner sees an
  // approvable inbox row (structural clone of daemon_access_request, minus
  // scopes — a page grant is per-user READ access, all-or-nothing, no scope set).
  | "page_access_request"
  // The owner approved/denied the request → the REQUESTER sees a resolution
  // row. Access is per-user (page_grants — see schema.ts), not
  // binary/workspace-wide: approving Alice's request grants ONLY Alice and
  // never touches Bob's still-pending request for the same page — no
  // auto-resolve fan-out to other requesters.
  | "page_access_request_resolved"
  // CYBORG-839: a work session a HUMAN started moved to a status its initiator
  // is waiting on. Emitted ONLY to that initiator by the relay's
  // cyborg:work_session_changed handler (sourceType "work_session", sourceId =
  // the session id), never for an autonomous/watcher session. One type per
  // transition rather than a single catch-all so "needs your approval" — the one
  // that BLOCKS the human — carries its own copy and lands in the Permissions
  // tab, while started/finished stay passive notices.
  | "work_session_started"
  | "work_session_waiting_approval"
  | "work_session_done"
  // The server emits a FOURTH transition (work-session-notify.ts): the turn ended
  // badly (failed / timed out / the daemon died under it) and the session is
  // parked. Missing here it fell through normalizeEventType's `default: "mention"`
  // and rendered as "<Agent> mentioned you" whose click had no channel to open
  // ("This mention has no channel to open.") — the agent never mentioned anyone,
  // it stopped.
  | "work_session_failed"
  // Cyborg7 Connect: an outsider the host invited accepted and took their seat in
  // the shared channel. Emitted by the relay's cyborg:accept_external_invite to the
  // INVITER (falling back to owners+admins when that account is gone) — see
  // connect-external.ts's EXTERNAL_ACCEPT_EVENT_TYPE / externalAcceptRecipients.
  // sourceType is "connection" and sourceId the connection's composite key
  // `<workspaceId>:<userId>` (workspace_externals has no id column of its own), NOT
  // a message id — which is why this type's registry entry must never build a `?m=`
  // deep link out of it. Missing here it fell through normalizeEventType's
  // `default: "mention"` and the host got "<Accepter> mentioned you" for a message
  // nobody ever sent.
  | "external_invite_accepted"
  // Cyborg7 Connect, the OTHER end of the same connection's life: the partner's seat
  // was revoked by something that happened inside THEIR company (their employer
  // offboarded them, cancelled their invitation, or deleted the whole organization),
  // so nothing on the host's side announced it. Emitted by the relay's offboarding
  // paths — see connect-external.ts's EXTERNAL_REVOKE_EVENT_TYPE — to the same
  // recipients the acceptance goes to, with the same "connection" sourceType and the
  // same composite `<workspaceId>:<userId>` sourceId, so the same "never build a `?m=`
  // out of it" rule applies. A host-initiated revoke emits NOTHING: the host who
  // pressed the button is not news to themselves.
  | "external_connection_revoked"
  // CYBORG-1074: Slack stopped syncing (the app was removed, its token revoked, or Slack
  // refused a send). Emitted by the relay's slack-connection-health.ts to owners, admins and
  // whoever connected Slack; sourceType "integration", sourceId the installation id.
  | "slack_connection_lost"
  // 0280: this person's own Slack grant stopped working mid-post; their messages now reach
  // Slack through the app until they reconnect. sourceType "integration", sourceId their id.
  | "slack_post_as_me_stopped"
  // CYBORG-1087: the owner/admin decided the invite request this person sent. Two types,
  // not one with a status field, so the approve/decline copy lives in the registry — and
  // so neither falls through normalizeEventType's `default: "mention"` and renders as
  // "<Admin> mentioned you" for a message nobody sent.
  | "invite_request_approved"
  | "invite_request_declined"
  // A scheduled run finished, and cannot do its job properly until the person who
  // scheduled it answers something. Emitted by the relay's cyborg:dm_broadcast
  // case when the agent DECLARED it (cyborg7_send_message's `needsReply`, honored
  // only on a schedule fire) — never inferred from what the message says. A cron
  // run that reports and needs nothing raises none of these, which is the only
  // reason the alert is worth reading: the owner's most frequent schedule fires
  // every five minutes.
  | "schedule_needs_input"
  // CYBORG-1231: agent transfer (sourceType "cybo", sourceId = the first cybo id; previewText is
  // the body line). moved: to the target machine's owner; handed_off: to the new owner;
  // needs_home: to the owner of an agent that lost its machine.
  | "cybo_moved"
  | "cybo_handed_off"
  | "cybo_needs_home"
  // An agent transfer request (sourceType "cybo_transfer", sourceId the request id): to the
  // recipient while it waits; resolved: the sender's outcome (accepted/declined/expired/failed).
  | "cybo_transfer_request"
  | "cybo_transfer_resolved"
  // knowledge/135: an agent tagged in a page finished or failed (to the person who asked;
  // sourceType "page", sourceId the pageId, actorId the cybo).
  | "page_agent_done"
  | "page_agent_failed"
  // Alerts catalog: every kind the server emits has ONE entry here and in
  // ACTIVITY_REGISTRY (activity-catalog-exhaustive.test.ts scans the server source).
  // Someone asked to join the workspace / an invite request (→ owners+admins), and the
  // decision on a join request (→ the requester). sourceId = the request id.
  | "workspace_join_request"
  | "workspace_join_request_decided"
  // A member asked an owner/admin to invite someone (→ owners+admins). sourceId = request id.
  | "workspace_invite_request"
  // Worker tier: a machine was disabled (→ managers), its first connect finished (→ the
  // inviter), or it was handed to a new manager (→ the heir). sourceId = daemon id (invite
  // id for first connect).
  | "worker_daemon_disabled"
  | "worker_first_connect"
  | "worker_daemon_transferred"
  // The user lost access to a machine (→ that user). sourceId = daemon id.
  | "daemon_access_revoked"
  // An admin removed the user's machine from the workspace (→ its owner). sourceId = daemon id.
  | "workspace_machine_removed"
  // An ops notice (daemon health, schedule adopted, boot recovery, quiet worker). Not an
  // error. Click: detail.url when the server gave one, else nothing opens.
  | "system_notice"
  // A mirrored standing order was recorded. Informational.
  | "order_mirrored"
  // An agent DM'd you while you were not on that DM. actorId = the cybo id, sourceId = the
  // message id; opens /dm/<cybo>?m=<message> like dm_received.
  | "agent_dm_received"
  // CYBORG-1408, daemon-written (stored as `action.<kind>`, see activityKindOf): a /goal the
  // person set was met, or a background subagent of their session failed. To the session's
  // initiator; actorId = the session (agent) id, so the row names and opens it.
  | "goal_completed"
  | "subagent_failed"
  // CYBORG-1408 W4 (record-action.ts). Your access changed (→ the grantee; sourceId = the
  // shared session's agent id for cockpit, the daemon id for machine access).
  | "cockpit_access_granted"
  | "cockpit_access_revoked"
  | "daemon_access_granted"
  | "daemon_access_changed"
  // Someone else opened a terminal / started an agent on your machine (→ the owner, at most
  // once per person per machine per hour; sourceId = daemon id).
  | "daemon_used_by_other"
  // Your terminal died under you (PtyHost link lost / daemon restart). Daemon-reported, so
  // the relay namespaces it `action.` (record-action.ts DAEMON_ACTIVITY_PREFIX); sourceId =
  // the terminal id.
  | "action.terminal_lost"
  // Someone else deleted / edited your agent (→ the owner; sourceId = cybo id).
  | "agent_deleted_by_other"
  | "agent_edited_by_other"
  // CYBORG-1408: a member who can drive the machine is told a private session has a pending
  // Codex plan (content-free: sourceId = the request id, actorName "<Owner>'s Codex").
  | "plan_pending"
  // Your webhook could not run its agent (→ its creator, once per webhook per hour;
  // sourceId = webhook id).
  | "webhook_fire_skipped"
  // Your machine has been offline for a while after an unexpected cut (→ its owner;
  // sourceId = daemon id).
  | "daemon_offline"
  // CYBORG-1408 W2, daemon-written too (`action.<kind>`): session and security outcomes, to the
  // session's owner (agent_refused_contained: to the refused invoker). sourceId and actorId are the
  // agent (session) id; for agent_refused_contained the cybo id.
  | "dm_reply_not_delivered"
  | "session_resumed"
  | "session_resume_skipped"
  | "session_archived_tasks_stopped"
  | "session_close_held"
  | "agent_refused_contained"
  // To the agent's maker: a teammate it could not answer on its current AI (sourceId = cybo id).
  | "agent_teammate_unanswered"
  | "session_contained";

// Every member of the union above, as a runtime lookup for normalizeEventType
// (which maps a raw server event_type string onto the union). It was a 20-label
// `switch` fallthrough until external_invite_accepted made it a 21st and put the
// method over the linter's complexity cap — but the Record is the better shape
// anyway: `Record<ActivityEventType, true>` makes TypeScript REFUSE to compile a
// new union member that isn't listed here, where the switch silently let one fall
// to `default: "mention"` (exactly how work_session_failed, and then this type,
// each shipped rendering as "<Actor> mentioned you"). Same trick
// activity-registry.test.ts's ALL_ACTIVITY_EVENT_TYPES uses.
const ACTIVITY_EVENT_TYPES: Record<ActivityEventType, true> = {
  mention: true,
  dm_received: true,
  task_assigned: true,
  task_status_changed: true,
  task_review_requested: true,
  thread_reply: true,
  reaction: true,
  permission_request: true,
  daemon_access_request: true,
  daemon_access_request_resolved: true,
  agent_error: true,
  page_mention: true,
  approval_requested: true,
  page_access_request: true,
  page_access_request_resolved: true,
  work_session_started: true,
  work_session_waiting_approval: true,
  work_session_done: true,
  work_session_failed: true,
  external_invite_accepted: true,
  external_connection_revoked: true,
  slack_connection_lost: true,
  slack_post_as_me_stopped: true,
  invite_request_approved: true,
  invite_request_declined: true,
  schedule_needs_input: true,
  cybo_moved: true,
  cybo_handed_off: true,
  cybo_needs_home: true,
  cybo_transfer_request: true,
  cybo_transfer_resolved: true,
  page_agent_done: true,
  page_agent_failed: true,
  workspace_join_request: true,
  workspace_join_request_decided: true,
  workspace_invite_request: true,
  worker_daemon_disabled: true,
  worker_first_connect: true,
  worker_daemon_transferred: true,
  daemon_access_revoked: true,
  workspace_machine_removed: true,
  system_notice: true,
  order_mirrored: true,
  agent_dm_received: true,
  goal_completed: true,
  subagent_failed: true,
  cockpit_access_granted: true,
  cockpit_access_revoked: true,
  daemon_access_granted: true,
  daemon_access_changed: true,
  daemon_used_by_other: true,
  "action.terminal_lost": true,
  agent_deleted_by_other: true,
  agent_edited_by_other: true,
  plan_pending: true,
  webhook_fire_skipped: true,
  daemon_offline: true,
  dm_reply_not_delivered: true,
  session_resumed: true,
  session_resume_skipped: true,
  session_archived_tasks_stopped: true,
  session_close_held: true,
  agent_refused_contained: true,
  agent_teammate_unanswered: true,
  session_contained: true,
};

// A daemon's Activity rows are stored as `action.<kind>` (the server's DAEMON_ACTIVITY_PREFIX) so a
// daemon can never name a relay kind such as approval_requested. Only these kinds are read back
// from that namespace; any other `action.*` stays unknown.
const DAEMON_ACTION_KINDS: ReadonlySet<string> = new Set<ActivityEventType>([
  "goal_completed",
  "subagent_failed",
  "dm_reply_not_delivered",
  "session_resumed",
  "session_resume_skipped",
  "session_archived_tasks_stopped",
  "session_close_held",
  "agent_refused_contained",
  "agent_teammate_unanswered",
  "session_contained",
  // A terminal opened on the machine itself (no relay hop): the daemon twin tells the owner.
  "daemon_used_by_other",
  // CYBORG-1415: a daemon's ops notice (expired schedule slot, boot recovery), mirrored to PG.
  "system_notice",
]);

/** The client kind for a server event_type: `action.goal_completed` → `goal_completed`. */
export function activityKindOf(t: string): string {
  const kind = t.startsWith("action.") ? t.slice("action.".length) : undefined;
  // Retired: rows stored before it was dropped read as a plain notice, never "mentioned you".
  if (kind === "session_close_capped") return "system_notice";
  return kind && DAEMON_ACTION_KINDS.has(kind) ? kind : t;
}

// The work-session subset of the union above — the one place "which event types
// belong to a work session" is spelled, shared by the live push, the read-on-open
// clear and the pointer surfaces' unread dot so they can never drift apart.
export type WorkSessionActivityEventType =
  | "work_session_started"
  | "work_session_waiting_approval"
  | "work_session_done"
  | "work_session_failed";

export const WORK_SESSION_ACTIVITY_EVENT_TYPES: readonly WorkSessionActivityEventType[] = [
  "work_session_started",
  "work_session_waiting_approval",
  "work_session_done",
  "work_session_failed",
];

export function isWorkSessionActivityEventType(
  eventType: string,
): eventType is WorkSessionActivityEventType {
  return (WORK_SESSION_ACTIVITY_EVENT_TYPES as readonly string[]).includes(eventType);
}

// Args for client.markActivityRead: the stored id when the row has one, else (a live row the
// client made itself) source + type for the server to resolve. A DM row's source is its message.
export function activityReadArgs(
  item: ActivityItem,
): [eventId?: string, by?: { sourceId: string; eventType: string }] {
  if (item.serverId) return [item.serverId];
  const sourceId = item.eventType === "dm_received" ? item.messageId : item.sourceId;
  // An agent's DM is stored as `agent_dm_received` (dm_received is human-authored only).
  const eventType =
    item.eventType === "dm_received" && item.actorType === "agent"
      ? "agent_dm_received"
      : item.eventType;
  return sourceId ? [undefined, { sourceId, eventType }] : [item.id];
}

// Every Args tuple that reads this card server-side: its own row, then the other unread rows
// folded into it (task:/page:/mention: share one card id).
export function activityReadArgsAll(
  item: ActivityItem,
): Array<ReturnType<typeof activityReadArgs>> {
  return [activityReadArgs(item), ...(item.foldedServerIds ?? []).map((id) => [id] as [string])];
}

export interface ActivityItem {
  id: string;
  // Raw activity_events.id from the server seed (a UUID). The client `id` above
  // is a synthetic dedupe key (`mention:<src>`, `activity:<uuid>`, …) that does
  // NOT match the PG row, so the per-row read endpoint must clear by THIS id.
  // Undefined for live client-only items (permission_request/agent_error) that
  // were never persisted server-side.
  serverId?: string;
  // Other UNREAD server rows folded into this card (several rows share one card id: task:/page:/
  // mention:). The card is unread while any is; reading it must read them all.
  foldedServerIds?: string[];
  // Stable source id (e.g. the originating message id) for items that can be
  // rebuilt from history, so reload/reconnect dedupes and read-state persists.
  sourceId?: string;
  // knowledge/117: the message a `dm_received` row is about — sourceId there is
  // the per-peer `dm:<peer>` dedup key, so the deep link needs the id apart.
  messageId?: string;
  // CYBORG-1404: permission_request only — the request id the inline Allow/Deny
  // answers (resolved against agentStreamState's pending set, actorId = agent).
  permissionRequestId?: string;
  // permission_request only: the card was answered/dropped. A re-used request id then raises a fresh row.
  // approval_requested too: a share card that was answered or expired (it is gone from the DM).
  permissionSettled?: boolean;
  // approval_requested only: the allowed share's post, which the settled row opens.
  posted?: { channelId: string; messageId: string };
  // permission_request only: how it ended, when that is more than "Resolved" (a hold's "Expired — not delivered").
  permissionOutcome?: string;
  eventType: ActivityEventType;
  actorName: string;
  actorId: string | null;
  // Server dm_peer_id on a seeded DM-scoped row (what the server's DM-open clear keys on).
  dmPeerId?: string | null;
  actorType: "human" | "agent" | "system";
  preview: string;
  channelId: string | null;
  channelName: string | null;
  isRead: boolean;
  createdAt: string;
  // Page-mention tap target (CYBORG-304): the mentioned page's project id.
  // Present on a LIVE push (activity_events has no project_id column); absent
  // on a reload seed — ActivityPane falls back to a fetchPage lookup then.
  projectId?: string | null;
  // CYBORG-1231: cybo_moved / cybo_handed_off only.
  detail?: CyboTransferDetail;
  // system_notice only: the server's detail.url, the one place the row opens.
  noticeUrl?: string;
  // "dm" on a DM-anchored work_session_* row: sourceId is then the cybo id (the DM peer), not
  // a session id. Unset on every other row.
  sourceType?: "dm";
  // The message this alert quoted was deleted: the preview is the tombstone wording and a click
  // opens nothing (the message is gone).
  deleted?: boolean;
  // An agent row raised as its cybo (actorId = cybo id): the session that acted, which keys
  // the viewer's alias for it (activityActorName).
  actorSessionId?: string;
  // task_assigned: the tasks this row covers (one, or one turn's batch), as the server read them.
  tasks?: TaskAlertCard[];
  // The daemon that raised the row when the machine itself is the actor (machine-avatar.ts).
  machineId?: string;
}

// What the relay writes into an alert's preview when its message is deleted (pg-sync
// DELETED_MESSAGE_PREVIEW), the same wording ChatMessage shows for a tombstone.
export const DELETED_ACTIVITY_PREVIEW = "(message deleted)";

// The server keeps the stored (sealed) preview and flags a deleted source; the client tombstones it.
function servedPreview(s: {
  source_type?: string | null;
  preview_text: string | null;
  source_deleted?: number | boolean;
}): {
  preview: string;
  deleted?: true;
} {
  const gone =
    !!s.source_deleted ||
    (s.source_type === "message" && s.preview_text === DELETED_ACTIVITY_PREVIEW);
  return gone
    ? { preview: DELETED_ACTIVITY_PREVIEW, deleted: true }
    : { preview: s.preview_text ?? "" };
}

export interface MentionInput {
  sourceId: string;
  actorName: string;
  actorId: string | null;
  actorType: "human" | "agent" | "system";
  preview: string;
  channelId: string | null;
  channelName: string | null;
  createdAt: string;
}

// One server-side activity_events row (the relay's cyborg:fetch_activity items),
// snake-cased as it comes off the wire. Used to seed the feed authoritatively.
export interface ServerActivityItem {
  id: string;
  event_type: string;
  // 'message' | 'task' | 'daemon' (free text). Task rows carry no channel/dm
  // scope, so source_type is what keys them as `task:<sourceId>` (read-on-open).
  // Optional: older/seed rows that predate the field default to a message item.
  source_type?: string | null;
  source_id: string | null;
  channel_id: string | null;
  dm_peer_id?: string | null;
  preview_text: string | null;
  /** The quoted message was deleted (server keeps the stored preview untouched). */
  source_deleted?: number | boolean;
  actor_id: string | null;
  actor_name: string | null;
  is_read: number | boolean;
  created_at: number;
  // CYBORG-1231: cybo_moved / cybo_handed_off only. Agent rows may carry `actorSessionId`.
  detail?: CyboTransferDetail | NoticeDetail | null;
  // task_assigned rows: their tasks, read for this viewer.
  tasks?: TaskAlertCard[];
}

// The session an agent row was raised for (detail.actorSessionId), when the relay stamped one.
function detailSessionId(d: unknown): { actorSessionId?: string } {
  const id = (d as { actorSessionId?: unknown } | null | undefined)?.actorSessionId;
  return typeof id === "string" && id ? { actorSessionId: id } : {};
}

// CYBORG-1516: the relay stamps an answered (or ended) permission request on its row, because
// `is_read` alone is also what opening the alert sets (server permission-alert-settled-pg.ts).
function permissionSettledDetail(
  s: ServerActivityItem,
): Pick<ActivityItem, "permissionSettled" | "posted"> {
  // cast: wire JSON
  const d = s.detail as { permissionResolved?: unknown; postedChannelId?: unknown; postedMessageId?: unknown } | null | undefined; // prettier-ignore
  if (d?.permissionResolved !== true) return {};
  if (s.event_type === "permission_request") return { permissionSettled: true };
  if (s.event_type !== "approval_requested") return {};
  const { postedChannelId: channelId, postedMessageId: messageId } = d;
  return typeof channelId === "string" && typeof messageId === "string"
    ? { permissionSettled: true, posted: { channelId, messageId } }
    : { permissionSettled: true };
}

// A transfer row's detail carries `cyboNames`; a system_notice's carries only an optional url.
function splitDetail(d: CyboTransferDetail | NoticeDetail | null | undefined): {
  detail?: CyboTransferDetail;
  noticeUrl?: string;
} {
  if (!d) return {};
  if ("cyboNames" in d) return { detail: d };
  return typeof d.url === "string" && d.url ? { noticeUrl: d.url } : {};
}

// The machine that raised the row, as an optional field (absent on every other row).
function machineIdOf(row: Parameters<typeof activityMachineId>[0]): { machineId?: string } {
  const machineId = activityMachineId(row);
  return machineId ? { machineId } : {};
}

// The name a transfer row leads with: handed_off = who gave the agents, needs_home = the agents.
function transferActorName(
  type: string,
  detail: CyboTransferDetail | undefined,
  fallback: string,
): string {
  if (type === "cybo_handed_off" && detail?.fromUserName) return detail.fromUserName;
  if (type === "cybo_needs_home" && detail) {
    return detail.count > 1 ? `${detail.count} agents` : (detail.cyboNames[0] ?? fallback);
  }
  return fallback;
}

let activitySeq = 0;

const ACTIVITY_READ_BASE = "cyborg7_activity_read";

export class ActivityState {
  items: ActivityItem[] = $state([]);
  // The workspace whose server feed was read last, and whether it read. Until it names the
  // current workspace, `items` may still be the previous workspace's (seedFromServer replaces).
  feed: { workspaceId: string; ok: boolean } | null = $state(null);
  maxItems = 200;
  // True once appendOlder loaded rows the user scrolled to; set by seedFromServer back to false.
  private _pagedOlder = false;
  // ONE trim rule for every live push: never drop rows while older pages are held or still
  // reachable (hasMore), or a push would delete what appendOlder just loaded.
  private capLive(next: ActivityItem[]): ActivityItem[] {
    if (this._pagedOlder || this._hasMore || next.length <= this.maxItems) return next;
    return next.slice(-Math.floor(this.maxItems * 0.8));
  }
  // Live rows sort on the server's clock: browser time + the MEDIAN offset of the recent
  // activity_new frames, so one late (replayed) or other-clock (daemon) row can't move it.
  private clockOffsetMs = 0;
  private offsetSamples: number[] = [];
  noteServerTime(createdAt: number | string | undefined): string | undefined {
    const ms = typeof createdAt === "string" ? Date.parse(createdAt) : (createdAt ?? NaN);
    if (!Number.isFinite(ms)) return undefined;
    this.offsetSamples = [...this.offsetSamples.slice(-8), ms - Date.now()];
    const sorted = this.offsetSamples.toSorted((a, b) => a - b);
    this.clockOffsetMs = sorted[Math.floor(sorted.length / 2)];
    return new Date(ms).toISOString();
  }
  private serverNow(): string {
    return new Date(Date.now() + this.clockOffsetMs).toISOString();
  }
  private liveServerAt: string | undefined;
  stampLive<T>(createdAt: number | string | undefined, route: () => T): T {
    this.liveServerAt = this.noteServerTime(createdAt);
    try {
      return route();
    } finally {
      this.liveServerAt = undefined;
    }
  }
  // items stay oldest→newest (ActivityPane reverses them): a late older row goes in at its time.
  private pushItem(pushed: ActivityItem): void {
    const item = this.liveServerAt ? { ...pushed, createdAt: this.liveServerAt } : pushed;
    this.items = this.capLive(this.inTimeOrder(this.items, item));
  }
  private inTimeOrder(items: ActivityItem[], item: ActivityItem): ActivityItem[] {
    const at = Date.parse(item.createdAt);
    let i = items.length;
    while (i > 0 && Date.parse(items[i - 1].createdAt) > at) i--;
    return [...items.slice(0, i), item, ...items.slice(i)];
  }
  private readIds: Set<string> = new Set();
  private _boundUserId: string | null = null;
  // P2 Item 9 (server-authoritative badge): the relay returns an authoritative
  // unread total alongside the activity items (it counts ALL unread
  // activity_events, not just the page we fetched). The rail badge reads this so
  // it never under-counts when there are more unread events than fetched items.
  // Null = not seeded yet → fall back to counting unread items locally. Kept in
  // lockstep with the local read/add paths (markRead etc. decrement; a fresh
  // live mention increments).
  private _serverUnread: number | null = $state(null);
  // Cross-workspace Alerts unread (the one dock/taskbar/app badge), kept as its distance from
  // this workspace's count so live adds and reads move both together. Null = the server sent none.
  private _totalOffset: number | null = $state(null);

  // null while unbound — see UserStatusState.statusKey (core/state.svelte.ts).
  private get readKey(): string | null {
    return this._boundUserId ? `${ACTIVITY_READ_BASE}_${this._boundUserId}` : null;
  }

  bindUser(userId: string): void {
    this._boundUserId = userId;
    this.readIds = new Set();
    const key = this.readKey;
    if (!key) return;
    try {
      const raw = localStorage.getItem(key);
      if (raw) this.readIds = new Set(JSON.parse(raw));
    } catch {
      // intentional: best-effort restore of read activity ids; falls back to empty.
    }
  }

  get unreadCount(): number {
    if (this._serverUnread !== null) return Math.max(0, this._serverUnread);
    return this.items.filter((i) => !i.isRead).length;
  }

  // The rail/tab badge skips unread rows of a channel archived for me (the row stays in the Alerts
  // list, and counts again once the channel resurfaces). Only rows held locally can be named, and
  // the result never exceeds unreadCount, so the badge stays within the pane (#4147).
  private get hiddenUnread(): number {
    // With the relay's split, only the open workspace's rows: a row of another workspace held here
    // (a live mention) is already in that workspace's split.
    const own = this._hiddenSplit ? new Set(workspaceState.channels.map((c) => c.id)) : undefined;
    let n = 0;
    for (const i of this.items) {
      if (i.isRead || !i.channelId || !isArchivedForMe(i.channelId)) continue;
      if (!own || own.has(i.channelId)) n++;
    }
    return n;
  }

  get badgeCount(): number {
    const base =
      this._totalOffset === null
        ? this.unreadCount
        : Math.max(0, this.unreadCount + this._totalOffset);
    return Math.max(0, base - this.hiddenUnread - this.hiddenElsewhere);
  }

  // The relay's split of the total's hidden rows; undefined until a relay that knows it sends one.
  private _hiddenSplit: HiddenUnreadByWorkspace | undefined = $state(undefined);
  get knowsHiddenSplit(): boolean {
    return this._hiddenSplit !== undefined;
  }
  // The open workspace is left to hiddenUnread above, so no row is taken off twice.
  private get hiddenElsewhere(): number {
    return hiddenElsewhere(this._hiddenSplit, workspaceState.current?.id);
  }
  setHiddenSplit(hidden: unknown): void {
    this._hiddenSplit = readHiddenSplit(hidden) ?? this._hiddenSplit;
  }

  setTotalUnread(total: number | undefined, hidden?: unknown): void {
    if (typeof total === "number") this._totalOffset = total - this.unreadCount;
    this.setHiddenSplit(hidden);
  }

  // The badge's unread outside this workspace, and the server's split of it (first-page seeds only).
  get otherWorkspacesUnread(): number {
    return Math.max(0, this._totalOffset ?? 0);
  }
  private _unreadByWorkspace: Record<string, number> | undefined = $state(undefined);
  get unreadByWorkspace(): Readonly<Record<string, number>> | undefined {
    return this._unreadByWorkspace;
  }
  setUnreadByWorkspace(split: Record<string, number> | undefined): void {
    if (split) this._unreadByWorkspace = split;
  }
  applyWorkspaceUnread(workspaceId: string, unread: number): void {
    if (this._unreadByWorkspace) {
      this._unreadByWorkspace = { ...this._unreadByWorkspace, [workspaceId]: unread };
    }
  }

  // Telemetry only (lib/state/activity-telemetry.ts): the server's total as held now, undefined until sent.
  get serverTotalUnread(): number | undefined {
    const offset = this._totalOffset;
    if (offset === null) return undefined;
    return Math.max(0, this.unreadCount + offset);
  }

  push(
    eventType: ActivityEventType,
    actorName: string,
    actorId: string | null,
    actorType: "human" | "agent" | "system",
    preview: string,
    channelId: string | null = null,
    channelName: string | null = null,
    messageId: string | null = null,
    permissionRequestId: string | null = null,
  ): void {
    // Unique across sessions: `readIds` is persisted per user, so a per-session
    // counter alone re-minted an id a previous session had already read and the
    // new row was born read (no dot, no badge).
    // A permission card re-raised under the same id (hold added/shrunk, steer) updates its row in
    // place: no new unread, no badge bump.
    if (eventType === "permission_request" && permissionRequestId) {
      const existing = this.items.find(
        (i) =>
          i.eventType === "permission_request" &&
          !i.permissionSettled &&
          i.actorId === actorId &&
          i.permissionRequestId === permissionRequestId,
      );
      if (existing) {
        existing.preview = preview;
        return;
      }
    }
    const id = `activity-${Date.now()}-${++activitySeq}`;
    const item: ActivityItem = {
      id,
      // Stamp DM items with a stable per-peer key so the on-load server seed
      // (serverItemToActivity uses the same `dm:<peer>` key) can reconcile read-
      // state via seedReadFromServer after a reload. Other live pushes have no
      // stable source id.
      sourceId: eventType === "dm_received" && actorId ? `dm:${actorId}` : undefined,
      messageId: messageId ?? undefined,
      permissionRequestId: permissionRequestId ?? undefined,
      eventType,
      actorName,
      actorId,
      actorType,
      preview,
      channelId,
      channelName,
      isRead: this.readIds.has(id),
      createdAt: this.serverNow(),
    };
    this.pushItem(item);
    // A live push (dm_received, agent permission/error) that lands unread bumps
    // the server-authoritative badge so the rail count stays in sync.
    if (!item.isRead && this._serverUnread !== null) this._serverUnread += 1;
  }

  // A DM from an AGENT the viewer has no openable surface for — see
  // agent-dm-surface.ts for the whole mechanism. A plain provider session
  // (Claude/Codex harness, no cybo identity) can DM a human, and that DM badges
  // under the raw agent id; Direct Messages is built from the cybo roster and the
  // Sessions list is `filterMine`-scoped, so nothing renders it and the badge has
  // no door. This row IS the door: `dm_received` resolves through
  // activity-registry.ts's dmClick to `/workspace/<ws>/dm/<peerId>`, whose
  // `dmPeer` read scope persists the clear through the SAME `dm_reads` cursor
  // every other DM uses (ActivityPane applyReadScope -> client.markDmRead).
  //
  // A SEPARATE METHOD, not `push("dm_received", ...)`, for one reason: this row is
  // written from TWO doors — the live `dm_broadcast` and the workspace-open
  // backfill that rebuilds it from the reseeded badge — and `push`'s
  // `activity-<n>` id would stack a duplicate every time the second one runs.
  // Keyed by (peer, message) so those two collapse onto one row, while a LATER
  // DM from the same agent still gets its own (a per-peer key would be worse than
  // a duplicate: markReadByDmPeer persists the id into `readIds`, so the next
  // unread DM from that agent would be born read).
  //
  // actorType "agent" so the row resolves an agent face/name; actorId is the
  // CANONICAL peer id — the same key the badge was incremented under and the one
  // markReadByDmPeer matches on, so clicking the row clears the badge it came from.
  pushAgentDm(
    peerId: string,
    messageId: string,
    actorName: string,
    preview: string,
    createdAt: string,
    actorSessionId?: string,
  ): void {
    const id = `dm:${peerId}:${messageId}`;
    if (this.items.some((i) => i.id === id) || this.hasDmRow(peerId, messageId)) return;
    const item: ActivityItem = {
      id,
      // The stable per-peer key the server seed / seedReadFromServer reconcile on
      // (serverItemToActivity uses the same `dm:<peer>` shape) — identical to what
      // push() already stamps for a dm_received row.
      sourceId: `dm:${peerId}`,
      messageId,
      eventType: "dm_received",
      actorName,
      actorId: peerId,
      ...(actorSessionId ? { actorSessionId } : {}),
      actorType: "agent",
      preview,
      channelId: null,
      channelName: null,
      isRead: this.readIds.has(id),
      createdAt,
    };
    this.pushItem(item);
    if (!item.isRead && this._serverUnread !== null) this._serverUnread += 1;
  }

  // #705: add a daemon-access-request inbox row (owner view), keyed by the request
  // id so it dedupes against the server activity_new seed (same `activity:`/source
  // scheme — sourceId carries the request id the Approve/Deny actions resolve by).
  // Idempotent: a re-push for the same request id is ignored.
  pushDaemonAccessRequest(
    requestId: string,
    requesterName: string,
    requesterId: string | null,
    preview: string,
  ): void {
    const id = `daemon_access_request:${requestId}`;
    if (this.items.some((i) => i.id === id)) return;
    const item: ActivityItem = {
      id,
      sourceId: requestId,
      eventType: "daemon_access_request",
      actorName: requesterName,
      actorId: requesterId,
      actorType: "human",
      preview,
      channelId: null,
      channelName: null,
      isRead: this.readIds.has(id),
      createdAt: this.serverNow(),
    };
    this.pushItem(item);
    if (!item.isRead && this._serverUnread !== null) this._serverUnread += 1;
  }

  // Page-access-request inbox row (owner view), structural clone of
  // pushDaemonAccessRequest above (minus scopes — a page grant is per-user,
  // all-or-nothing), keyed by the request id so it dedupes against the server
  // activity_new seed.
  // `projectId` is the tap target (ActivityPane routes via it, falling back to a
  // fetchPage lookup when absent — same pattern pushPageMention uses).
  // Idempotent: a re-push for the same request id is ignored.
  pushPageAccessRequest(
    requestId: string,
    projectId: string | null,
    requesterName: string,
    requesterId: string | null,
    preview: string,
  ): void {
    const id = `page_access_request:${requestId}`;
    if (this.items.some((i) => i.id === id)) return;
    const item: ActivityItem = {
      id,
      sourceId: requestId,
      eventType: "page_access_request",
      actorName: requesterName,
      actorId: requesterId,
      actorType: "human",
      preview,
      channelId: null,
      channelName: null,
      projectId,
      isRead: this.readIds.has(id),
      createdAt: this.serverNow(),
    };
    this.pushItem(item);
    if (!item.isRead && this._serverUnread !== null) this._serverUnread += 1;
  }

  // Page-access-request RESOLVED row (requester view) — sibling of
  // pushPageAccessRequest above, but keyed by pageId, not requestId: unlike the
  // pending row (which needs a live pageAccessRequestsState lookup for its
  // pageId), a resolved row is self-contained — the server stamps its sourceId
  // with the pageId directly (see relay-standalone.ts / dispatcher.ts's resolve
  // handlers), since there's no pending-card state left to look up. Keying the
  // dedup id off pageId too (not requestId) means a second full request→resolve
  // cycle for the SAME page from the SAME requester within one session collapses
  // onto the same row instead of stacking a duplicate — an accepted trade (see
  // "deny invites immediate re-ask", a known accepted gap) rather than adding a
  // second field just to keep the two ids apart. Idempotent: a re-push for the
  // same pageId is ignored.
  pushPageAccessResolved(
    pageId: string,
    projectId: string | null,
    resolverName: string,
    resolverId: string | null,
    preview: string,
    serverId?: string,
  ): void {
    const id = `page_access_request_resolved:${pageId}`;
    if (this.foldLiveRow(id, serverId, { preview, actorName: resolverName, actorId: resolverId })) {
      return;
    }
    const item: ActivityItem = {
      id,
      serverId,
      sourceId: pageId,
      eventType: "page_access_request_resolved",
      actorName: resolverName,
      actorId: resolverId,
      actorType: "human",
      preview,
      channelId: null,
      channelName: null,
      projectId,
      isRead: !serverId && this.readIds.has(id),
      createdAt: this.serverNow(),
    };
    this.pushItem(item);
    if (!item.isRead && this._serverUnread !== null) this._serverUnread += 1;
  }

  // Work-session status notification (CYBORG-839), keyed by `activity:<rowId>` —
  // the SAME id activityRowId() falls back to for a `work_session` source row, so
  // the live push and a later reload seed collapse onto one feed row. Keyed by the
  // ROW, not the session: a session legitimately produces several notices
  // (started → needs approval → finished) and each is its own alert with its own
  // read state. sourceId carries the session id so handleClick deep-links to the
  // session home and the pointer surfaces can look their unread up by session.
  // Idempotent: a re-push for the same activity row id is ignored.
  pushWorkSessionStatus(
    activityId: string,
    sessionId: string,
    eventType: WorkSessionActivityEventType,
    agentName: string,
    cyboId: string | null,
    preview: string,
    sourceType?: "dm",
    actorSessionId?: string,
  ): void {
    const id = `activity:${activityId}`;
    if (this.items.some((i) => i.id === id)) return;
    const item: ActivityItem = {
      id,
      serverId: activityId,
      sourceId: sessionId,
      ...(sourceType ? { sourceType } : {}),
      eventType,
      actorName: agentName,
      actorId: cyboId,
      ...(actorSessionId ? { actorSessionId } : {}),
      // The actor is a cybo — ActivityPane resolves its face through
      // resolveAgentAvatar(item.actorId, cyboState.list, …).
      actorType: "agent",
      preview,
      channelId: null,
      channelName: null,
      isRead: this.readIds.has(id),
      createdAt: this.serverNow(),
    };
    this.pushItem(item);
    if (!item.isRead && this._serverUnread !== null) this._serverUnread += 1;
  }

  // Connect notice (host view), for BOTH ends of a connection's life: the outsider
  // they invited accepted and took their seat, or that seat was revoked by something
  // inside the partner's own company. One method, not two near-copies — the two rows
  // differ only in `eventType` (the copy, accent and tap target hang off the registry)
  // and a second copy of this body is a second place to forget the dedupe rule below.
  // Keyed by `activity:<rowId>` — the SAME id activityRowId()
  // already falls back to for a "connection" source row — so the live push and a
  // later reload seed collapse onto one feed row (same trick as
  // pushWorkSessionStatus above, and the reason activityRowId needs no branch for
  // these types). Keyed by the ROW, not by the connection: sourceId is the composite
  // `<workspaceId>:<userId>`, so keying on it would let a second invite→accept→revoke
  // cycle for the same partner silently overwrite the first notice.
  // channelId/channelName ride along so the row names the channel and the click can
  // open it; both are legitimately null when the channel was deleted between invite
  // and accept (or before the revoke), and the row is still worth showing then.
  // Idempotent: a re-push for the same activity row id is ignored.
  // CYBORG-1087: the requester's alert when an owner/admin decides their invite request.
  // Keyed `activity:<rowId>` like the two pushes above, so this live row and a later reload
  // seed collapse onto one. sourceId carries the request id; the tap target is Settings › Members.
  // Idempotent: a re-push for the same activity row id is ignored.
  pushInviteRequestDecision(
    eventType: "invite_request_approved" | "invite_request_declined",
    activityId: string,
    requestId: string,
    deciderName: string,
    deciderId: string | null,
    preview: string,
  ): void {
    const id = `activity:${activityId}`;
    if (this.items.some((i) => i.id === id)) return;
    const item: ActivityItem = {
      id,
      serverId: activityId,
      sourceId: requestId,
      eventType,
      actorName: deciderName,
      actorId: deciderId,
      actorType: "human",
      preview,
      channelId: null,
      channelName: null,
      isRead: this.readIds.has(id),
      createdAt: this.serverNow(),
    };
    this.pushItem(item);
    if (!item.isRead && this._serverUnread !== null) this._serverUnread += 1;
  }

  pushExternalConnectionActivity(
    eventType: "external_invite_accepted" | "external_connection_revoked",
    activityId: string,
    sourceId: string,
    partnerName: string,
    partnerId: string | null,
    preview: string,
    channelId: string | null,
    channelName: string | null,
  ): void {
    const id = `activity:${activityId}`;
    if (this.items.some((i) => i.id === id)) return;
    const item: ActivityItem = {
      id,
      serverId: activityId,
      sourceId,
      eventType,
      actorName: partnerName,
      actorId: partnerId,
      actorType: "human",
      preview,
      channelId,
      channelName,
      isRead: this.readIds.has(id),
      createdAt: this.serverNow(),
    };
    this.pushItem(item);
    if (!item.isRead && this._serverUnread !== null) this._serverUnread += 1;
  }

  // CYBORG-1231: cybo_moved / cybo_handed_off. Same live-bell shape as the Slack row above;
  // sourceId is the cybo id (Open <cybo>, Move to another machine).
  pushCyboTransferActivity(
    eventType: "cybo_moved" | "cybo_handed_off" | "cybo_needs_home",
    activityId: string,
    cyboId: string,
    actorName: string,
    actorId: string | null,
    preview: string,
    detail?: CyboTransferDetail,
  ): void {
    const id = `activity:${activityId}`;
    if (this.items.some((i) => i.id === id)) return;
    const item: ActivityItem = {
      id,
      serverId: activityId,
      sourceId: cyboId,
      eventType,
      detail,
      actorName: transferActorName(eventType, detail, actorName),
      actorId,
      actorType: "human",
      preview,
      channelId: null,
      channelName: null,
      isRead: this.readIds.has(id),
      createdAt: this.serverNow(),
    };
    this.pushItem(item);
    if (!item.isRead && this._serverUnread !== null) this._serverUnread += 1;
  }

  // Alerts catalog live receive: ANY catalog kind with no bespoke push above. Keyed
  // `activity:<rowId>`, the id the reload seed gives these rows (serverItemToActivity's
  // fallback), so a live row and a later seed collapse onto one. Idempotent on that id.
  private hasDmRow(peerId: string | null, messageId: string | undefined): boolean {
    return (
      !!peerId &&
      !!messageId &&
      this.items.some(
        (i) =>
          (i.eventType === "dm_received" || i.eventType === "agent_dm_received") &&
          i.actorId === peerId &&
          i.messageId === messageId,
      )
    );
  }

  // A row the agent stream already pushed in memory takes the stored server id (one row, persisted reads).
  private adoptStreamRow(
    eventType: ActivityEventType,
    e: { id: string; sourceId: string; actorId: string | null; dmPeerId?: string | null },
    permissionRequestId: string | undefined,
  ): boolean {
    // permission_request is persisted server-side too (sourceId = request id): merge with the
    // in-memory row push() made from the agent stream instead of adding a second one.
    if (permissionRequestId) {
      const mem = this.items.find(
        (i) =>
          i.eventType === "permission_request" &&
          i.actorId === e.actorId &&
          i.permissionRequestId === permissionRequestId,
      );
      if (mem) {
        // Adopt the stored row id so marking it read persists (and reaches other tabs).
        if (!mem.serverId)
          this.items = this.items.map((i) => (i === mem ? { ...i, serverId: e.id } : i));
        return true;
      }
    }
    // agent_error is persisted server-side too (`agent-err-<agentId>-<turnId>`): adopt the id onto
    // the in-memory row push() made from the turn_failed stream event (same agent, last ~60s) so
    // it is ONE row and marking it read persists across a reload.
    if (eventType === "agent_error") {
      // The row may predate the first offset sample: within 60 s on either clock.
      const cutoff = Math.min(Date.now(), Date.now() + this.clockOffsetMs) - 60_000;
      const mem = this.items.find(
        (i) =>
          i.eventType === "agent_error" &&
          !i.serverId &&
          i.actorId === e.actorId &&
          Date.parse(i.createdAt) >= cutoff,
      );
      if (mem) {
        this.items = this.items.map((i) =>
          i === mem
            ? {
                ...i,
                serverId: e.id,
                sourceId: e.sourceId,
                dmPeerId: e.dmPeerId ?? i.dmPeerId ?? null, // null: wire null
              }
            : i,
        );
        return true;
      }
    }
    return false;
  }

  // A notice reworded in place: same row, new words, the server's read state when the frame has one.
  private rewordNotice(
    eventType: ActivityEventType,
    item: ActivityItem,
    e: { previewText: string | null; isRead?: boolean },
  ): void {
    if (eventType !== "system_notice") return;
    if (e.previewText) item.preview = e.previewText;
    if (e.isRead) this.markRead(item.id);
    if (e.isRead === false && item.isRead) this.reopen(item.id);
  }

  private reopen(id: string): void {
    this.items = this.items.map((i) => (i.id === id ? { ...i, isRead: false } : i));
    this.readIds.delete(id);
    if (this._serverUnread !== null) this._serverUnread += 1;
    this.persistRead();
  }

  pushCatalogActivity(
    eventType: ActivityEventType,
    e: {
      id: string;
      sourceId: string;
      messageId?: string | null;
      channelId: string | null;
      channelName: string | null;
      dmPeerId?: string | null;
      actorName: string | null;
      actorId: string | null;
      previewText: string | null;
      detail?: CyboTransferDetail | NoticeDetail;
      actorSessionId?: string | null;
      isRead?: boolean;
    },
  ): void {
    const id = `activity:${e.id}`;
    const had = this.items.find((i) => i.id === id);
    if (had) return this.rewordNotice(eventType, had, e);
    const messageId =
      eventType === "agent_dm_received" || eventType === "schedule_needs_input"
        ? (e.messageId ?? e.sourceId)
        : undefined;
    // The same DM also reaches handleIncomingAgentDm -> pushAgentDm (orphan peers): one row per
    // (peer, message), whichever door arrives first.
    if (eventType === "agent_dm_received" && this.hasDmRow(e.actorId, messageId)) return;
    const permissionRequestId = eventType === "permission_request" ? e.sourceId : undefined;
    if (this.adoptStreamRow(eventType, e, permissionRequestId)) return;
    const split = splitDetail(e.detail);
    const item: ActivityItem = {
      id,
      serverId: e.id,
      sourceId: e.sourceId,
      dmPeerId: e.dmPeerId ?? null, // null: wire null
      messageId,
      permissionRequestId,
      eventType,
      ...split,
      ...machineIdOf({
        eventType,
        sourceId: e.sourceId,
        actorId: e.actorId,
        actorName: e.actorName,
        detail: e.detail,
      }),
      actorName: e.actorName ?? "Someone",
      actorId: e.actorId,
      ...(e.actorSessionId ? { actorSessionId: e.actorSessionId } : {}),
      actorType:
        eventType === "agent_dm_received" || eventType === "agent_error" ? "agent" : "human",
      preview: e.previewText ?? "",
      channelId: e.channelId,
      channelName: e.channelName,
      isRead: e.isRead === true || this.readIds.has(id),
      createdAt: this.serverNow(),
    };
    this.pushItem(item);
    if (!item.isRead && this._serverUnread !== null) this._serverUnread += 1;
  }

  // CYBORG-1074: "Slack stopped syncing". Keyed by `activity:<rowId>`, the id the reload
  // seed gives an "integration" row, so the live push and a reload collapse onto one row.
  pushSlackConnectionLost(
    activityId: string,
    installationId: string,
    preview: string,
    eventType: "slack_connection_lost" | "slack_post_as_me_stopped" = "slack_connection_lost",
  ): void {
    const id = `activity:${activityId}`;
    if (this.items.some((i) => i.id === id)) return;
    const item: ActivityItem = {
      id,
      serverId: activityId,
      sourceId: installationId,
      eventType,
      actorName: "Slack",
      actorId: null,
      actorType: "system",
      preview,
      channelId: null,
      channelName: null,
      isRead: this.readIds.has(id),
      createdAt: this.serverNow(),
    };
    this.pushItem(item);
    if (!item.isRead && this._serverUnread !== null) this._serverUnread += 1;
  }

  // A live push for a card id we already hold. True when the card exists (the caller stops). If the
  // push is a server row the card does not yet account for, the card is unread again and carries
  // it, and the badge counts it: the server counts every row, however many cards share an id.
  private foldLiveRow(
    id: string,
    serverId: string | undefined,
    live: Pick<ActivityItem, "preview" | "actorName" | "actorId" | "tasks">,
  ): boolean {
    const card = this.items.find((i) => i.id === id);
    if (!card) return false;
    if (serverId && card.serverId === serverId && this.rearmed(card, live)) return true;
    if (!serverId || card.serverId === serverId || card.foldedServerIds?.includes(serverId)) {
      return true;
    }
    // The card shows its newest row (ties: the later arrival), unread; a newer row moves it to that
    // time (no trim: the card already held its place). An older row only folds in.
    const at = this.liveServerAt ?? this.serverNow();
    const newest = Date.parse(at) >= Date.parse(card.createdAt) ? { ...live, createdAt: at } : {};
    const folded = card.isRead
      ? { ...card, ...newest, isRead: false, serverId, foldedServerIds: undefined }
      : { ...card, ...newest, foldedServerIds: [...(card.foldedServerIds ?? []), serverId] };
    this.items = this.inTimeOrder(
      this.items.filter((i) => i !== card),
      folded,
    );
    this.readIds.delete(id);
    this.handUnread.delete(id);
    if (this._serverUnread !== null) this._serverUnread += 1;
    this.persistRead();
    return true;
  }

  // The server re-armed this card's own row (someone else edited the task): a newer push of the
  // same row shows its latest values, newest and unread again. An older or equal push is a repeat.
  private rearmed(
    card: ActivityItem,
    live: Pick<ActivityItem, "preview" | "actorName" | "actorId" | "tasks">,
  ): boolean {
    const at = this.liveServerAt;
    if (!at || Date.parse(at) <= Date.parse(card.createdAt)) return false;
    const fresh = { ...card, ...live, createdAt: at, isRead: false };
    this.items = this.inTimeOrder(
      this.items.filter((i) => i !== card),
      fresh,
    );
    if (card.isRead && this._serverUnread !== null) this._serverUnread += 1;
    this.readIds.delete(card.id);
    this.handUnread.delete(card.id);
    this.persistRead();
    return true;
  }

  // Task notification (assignment / status change), keyed by taskCardId so it
  // dedupes against the on-load server seed (serverItemToActivity uses the same
  // id) and the read-on-open clear (markReadByTask). Idempotent: a re-push for the
  // same card is ignored. sourceId carries the taskId so handleClick deep-links.
  pushTask(
    taskId: string,
    eventType: "task_assigned" | "task_status_changed",
    actorName: string,
    actorId: string | null,
    preview: string,
    serverId?: string,
    tasks?: TaskAlertCard[],
  ): void {
    const id = taskCardId(eventType, taskId);
    const live = { preview, actorName, actorId, ...(tasks ? { tasks } : {}) };
    if (this.foldLiveRow(id, serverId, live)) return;
    const item: ActivityItem = {
      id,
      serverId,
      sourceId: taskId,
      eventType,
      actorName,
      actorId,
      actorType: "human",
      ...(tasks ? { tasks } : {}),
      preview,
      channelId: null,
      channelName: null,
      isRead: !serverId && this.readIds.has(id),
      createdAt: this.serverNow(),
    };
    this.pushItem(item);
    if (!item.isRead && this._serverUnread !== null) this._serverUnread += 1;
  }

  // Page-mention notification (CYBORG-304), keyed by `page:<pageId>` so it
  // dedupes against the on-load server seed (serverItemToActivity uses the same
  // id via activityRowId) and a re-push for the same page is ignored. sourceId
  // carries the pageId and projectId the tap target navigates to (ActivityPane
  // falls back to a fetchPage lookup when projectId is absent — e.g. after a
  // reload seed, which has no project_id column to restore it from).
  pushPageMention(
    pageId: string,
    projectId: string,
    actorName: string,
    actorId: string | null,
    preview: string,
    serverId?: string,
  ): void {
    const id = `page:${pageId}`;
    if (this.foldLiveRow(id, serverId, { preview, actorName, actorId })) return;
    const item: ActivityItem = {
      id,
      serverId,
      sourceId: pageId,
      eventType: "page_mention",
      actorName,
      actorId,
      actorType: "human",
      preview,
      channelId: null,
      channelName: null,
      projectId,
      isRead: !serverId && this.readIds.has(id),
      createdAt: this.serverNow(),
    };
    this.pushItem(item);
    if (!item.isRead && this._serverUnread !== null) this._serverUnread += 1;
  }

  // Live push for a thread reply (Mattermost CRT parity, knowledge/17): the
  // root author — now an implicit follower per maintainThreadOnReply — gets a
  // feed row the moment someone else replies to their post. Keyed by the SAME
  // `mention:<sourceId>` scheme the server seed uses for thread_reply rows
  // (see activityRowId/serverItemToActivity below), so this live push dedupes
  // against a reload's server seed instead of double-counting. Idempotent: a
  // re-push for the same sourceId (the reply message id) is ignored — mirrors
  // pushTask/pushDaemonAccessRequest.
  pushThreadReply(
    sourceId: string,
    actorName: string,
    actorId: string | null,
    preview: string,
    channelId: string | null,
    channelName: string | null,
    dmPeerId: string | null = null,
    serverId?: string,
  ): void {
    const id = `mention:${sourceId}`;
    if (this.foldLiveRow(id, serverId, { preview, actorName, actorId })) return;
    const item: ActivityItem = {
      id,
      serverId,
      sourceId,
      dmPeerId,
      eventType: "thread_reply",
      actorName,
      actorId,
      actorType: "human",
      preview,
      channelId,
      channelName,
      isRead: !serverId && this.readIds.has(id),
      createdAt: this.serverNow(),
    };
    this.pushItem(item);
    if (!item.isRead && this._serverUnread !== null) this._serverUnread += 1;
  }

  // Add (or backfill) an @mention keyed by its source message id. Idempotent —
  // safe to call from both the live broadcast and the on-load history backfill.
  addMention(opts: MentionInput): void {
    this.addMentions([opts]);
  }

  // Batch variant: one array copy + sort + reactivity update for the whole set
  // (the on-load backfill can add many at once). Dedupes against existing items
  // and within the batch.
  addMentions(optsList: MentionInput[]): void {
    const fresh: ActivityItem[] = [];
    const pending = new Set<string>();
    for (const opts of optsList) {
      const id = `mention:${opts.sourceId}`;
      if (pending.has(id) || this.items.some((i) => i.id === id)) continue;
      pending.add(id);
      fresh.push({
        id,
        sourceId: opts.sourceId,
        eventType: "mention",
        actorName: opts.actorName,
        actorId: opts.actorId,
        actorType: opts.actorType,
        preview: opts.preview,
        channelId: opts.channelId,
        channelName: opts.channelName,
        isRead: this.readIds.has(id),
        createdAt: opts.createdAt,
      });
    }
    if (fresh.length === 0) return;
    // ISO timestamps are lexicographically ordered; ActivityPane reverses for
    // newest-first display.
    const next = [...this.items, ...fresh].sort((a, b) => {
      if (a.createdAt < b.createdAt) return -1;
      if (a.createdAt > b.createdAt) return 1;
      return 0;
    });
    this.items = this.capLive(next);
    // Keep the server-authoritative badge in lockstep: a freshly added (live)
    // mention that isn't already read bumps the count. Seeded items already
    // counted toward the baseline (seedFromServer sets it from the server total),
    // so this only fires for the live path, never for the on-load seed.
    if (this._serverUnread !== null) {
      const newUnread = fresh.filter((i) => !i.isRead).length;
      if (newUnread > 0) this._serverUnread += newUnread;
    }
  }

  // P2 Item 9 (server-authoritative): seed the Activity feed AND the rail-badge
  // unread baseline from the relay's cyborg:fetch_activity response — the real
  // activity_events, not a reconstruction from the last ~200 workspace messages.
  // The server `unread` is the authoritative total (counts every unread row, not
  // just this page), so the badge no longer under-counts on a fresh session.
  // Items are mapped to the same `mention:<sourceId>` id scheme as addMentions so
  // the live mention handler dedupes against them (no double-count). thread_reply
  // rows reuse the mention id scheme too (both keyed by the source message id).
  seedFromServer(
    serverItems: ServerActivityItem[],
    unread: number,
    totalUnread?: number,
    unreadByWorkspace?: Record<string, number>,
    hiddenUnreadByWorkspace?: unknown,
  ): void {
    // Absent from a relay that predates the split: the doors then fall back to the workspace list.
    this._unreadByWorkspace = unreadByWorkspace;
    const channelNames = new Map(workspaceState.channels.map((c) => [c.id, c.name]));
    const fresh: ActivityItem[] = [];
    const pending = new Map<string, number>();
    for (const s of serverItems) {
      const item = this.serverItemToActivity(s, channelNames);
      // Dedupe within the batch only — we REPLACE the feed below, so a stale
      // entry from a previously-selected workspace must not suppress it.
      const at = pending.get(item.id);
      if (at !== undefined) {
        fresh[at] = this.foldDuplicate(fresh[at], item);
        continue;
      }
      pending.set(item.id, fresh.length);
      fresh.push(item);
    }
    // REPLACE, don't append: the server query is `WHERE workspace_id = ?`, so this
    // set is the authoritative feed for the CURRENT workspace. Appending (the old
    // behavior) accumulated the previous workspace's items on every switch — the
    // "Activity shows items from another workspace" bug. Replacing scopes the feed
    // to the current workspace and clears it atomically (no flicker, read-state in
    // `readIds` is untouched so per-item read status persists).
    const next = fresh.sort((a, b) => {
      if (a.createdAt < b.createdAt) return -1;
      if (a.createdAt > b.createdAt) return 1;
      return 0;
    });
    this.items = next.length > this.maxItems ? next.slice(-this.maxItems) : next;
    this._pagedOlder = false;
    this.handUnread.clear();
    this._cursorMs = next.length > 0 ? Date.parse(this.items[0].createdAt) : undefined;
    this._unreadCursorMs = undefined;
    this._hasMoreUnread = true;
    // Authoritative baseline from the server (NOT a count of seeded items, which
    // is capped by the fetch limit). The rail badge reads unreadCount → this.
    // The rows seeded here carry the server's own is_read: a total below them (a count query that
    // disagrees with the list) must not read 0 over unread dots. Rows not paged in keep it larger.
    this._serverUnread = Math.max(0, unread, next.filter((i) => !i.isRead).length);
    this.setTotalUnread(totalUnread, hiddenUnreadByWorkspace);
  }

  // Paging (D9): the seed holds only the newest page; `hasMore` is true while older rows may exist.
  private _hasMore = $state(false);
  loadingMore = $state(false);
  get hasMore(): boolean {
    return this._hasMore;
  }
  setHasMore(more: boolean): void {
    this._hasMore = more;
  }
  // Cursors for the next page. Tracked apart from items[0]: the Unread-only pull merges rows far
  // older than the main feed's frontier, and the main cursor must not jump over the read rows
  // between. Undefined (no seed yet) falls back to the oldest row held. The relay's `before` is
  // strict, so rows sharing the cursor's millisecond are refetched and dropped by the id dedupe.
  private _cursorMs: number | undefined;
  private _unreadCursorMs: number | undefined;
  private _hasMoreUnread = $state(true);
  get hasMoreUnread(): boolean {
    return this._hasMoreUnread;
  }
  setHasMoreUnread(more: boolean): void {
    this._hasMoreUnread = more;
  }
  get oldestCreatedAtMs(): number | undefined {
    return (
      this._cursorMs ?? (this.items.length > 0 ? Date.parse(this.items[0].createdAt) : undefined)
    );
  }
  get oldestUnreadCursorMs(): number | undefined {
    return this._unreadCursorMs ?? this.oldestCreatedAtMs;
  }
  // Leaving "Unread only": the rows it pulled from beyond the main frontier sit past a gap of read
  // rows. Drop them (the main paging refetches that span in order) and forget the unread cursor.
  dropUnreadGap(): void {
    const cursor = this._cursorMs;
    this._unreadCursorMs = undefined;
    this._hasMoreUnread = true;
    if (cursor === undefined) return;
    const kept = this.items.filter((i) => Date.parse(i.createdAt) >= cursor);
    if (kept.length !== this.items.length) this.items = kept;
  }

  // Add an older page below the held rows. NOT capped at maxItems: the user scrolled to them. The
  // unread baseline is the server's total for the whole feed, so it is left alone.
  // `unreadOnly` pages the server's unread-only set (its own cursor + hasMore).
  // Returns how many rows were new (0 = nothing older, the caller stops paging).
  appendOlder(serverItems: ServerActivityItem[], pageSize: number, unreadOnly = false): number {
    const channelNames = new Map(workspaceState.channels.map((c) => [c.id, c.name]));
    const have = new Map(this.items.map((i) => [i.id, i]));
    const older: ActivityItem[] = [];
    let folded = 0;
    let oldest = Infinity;
    for (const s of serverItems) {
      const item = this.serverItemToActivity(s, channelNames);
      oldest = Math.min(oldest, Date.parse(item.createdAt));
      const prev = have.get(item.id);
      if (prev) {
        // A further row of a card we hold (same id): fold it in; a refetched row folds to itself.
        const next = this.foldDuplicate(prev, item);
        if (next !== prev) {
          have.set(item.id, next);
          folded++;
        }
        continue;
      }
      have.set(item.id, item);
      older.push(item);
    }
    if (folded > 0) this.items = this.items.map((i) => have.get(i.id) ?? i);
    if (older.length > 0) {
      this._pagedOlder = true;
      this.items = [...older.map((i) => have.get(i.id) ?? i), ...this.items].sort(
        (a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt),
      );
    }
    const more = serverItems.length >= pageSize && older.length + folded > 0;
    if (unreadOnly) {
      this._hasMoreUnread = more;
      if (Number.isFinite(oldest)) this._unreadCursorMs = oldest;
    } else {
      this._hasMore = more;
      if (Number.isFinite(oldest)) this._cursorMs = oldest;
    }
    return older.length + folded;
  }

  // Several server rows can share one card id (task:/page:/mention:). The card is unread while ANY
  // of them is, and carries the other unread row ids so reading it reads them all server-side.
  private foldDuplicate(kept: ActivityItem, dup: ActivityItem): ActivityItem {
    if (
      dup.serverId === kept.serverId ||
      (dup.serverId && kept.foldedServerIds?.includes(dup.serverId))
    ) {
      return kept;
    }
    const addsUnread = !dup.isRead && !!dup.serverId;
    const dupNewer = dup.createdAt > kept.createdAt;
    if (!addsUnread && !dupNewer) return kept;
    // The card shows its newest row (preview, time) and is unread while any row is.
    const base = dupNewer ? dup : kept;
    if (kept.isRead && !addsUnread) return { ...base };
    if (kept.isRead)
      return { ...base, isRead: false, serverId: dup.serverId, foldedServerIds: undefined };
    const folded = addsUnread
      ? [...(kept.foldedServerIds ?? []), dup.serverId!]
      : kept.foldedServerIds;
    return { ...base, isRead: false, serverId: kept.serverId, foldedServerIds: folded };
  }

  // D11: messages were deleted. Their alert rows stop quoting them and count as read. Matches a
  // row by the message id it was keyed on (sourceId, or messageId on a DM row).
  tombstoneMessages(messageIds: Iterable<string>): void {
    const ids = new Set(messageIds);
    const hit = (i: ActivityItem): boolean =>
      !i.deleted &&
      i.sourceType !== "dm" &&
      ((!!i.sourceId && ids.has(i.sourceId)) || (!!i.messageId && ids.has(i.messageId)));
    if (!this.items.some(hit)) return;
    let cleared = 0;
    this.items = this.items.map((i) => {
      if (!hit(i)) return i;
      if (!i.isRead) cleared++;
      this.readIds.add(i.id);
      return {
        ...i,
        preview: DELETED_ACTIVITY_PREVIEW,
        deleted: true,
        isRead: true,
        foldedServerIds: undefined,
      };
    });
    if (cleared > 0 && this._serverUnread !== null) {
      this._serverUnread = Math.max(0, this._serverUnread - cleared);
    }
    this.persistRead();
  }

  // Map one server activity_events row to a client ActivityItem. mention /
  // Stable activity-row id by event type. Extracted from serverItemToActivity to
  // keep it under the cyclomatic-complexity cap (the daemon_access_request branch
  // pushed it to 21): mention/thread_reply dedupe by source id, daemon_access_request
  // (#705) by request id so the live push dedupes against the seed, else the row id.
  private activityRowId(s: ServerActivityItem, sourceId: string | null): string {
    if (sourceId && (s.event_type === "mention" || s.event_type === "thread_reply")) {
      return `mention:${sourceId}`;
    }
    if (sourceId && s.event_type === "daemon_access_request") {
      return `daemon_access_request:${sourceId}`;
    }
    // page_access_request/_resolved also carry source_type "page" (same as
    // page_mention below), so these MUST be checked before the generic
    // source_type === "page" branch or they'd fall into it and get keyed
    // `page:<sourceId>` — which the live push never uses (it mints
    // `page_access_request:<id>` / `page_access_request_resolved:<id>`), so the
    // seeded row would never dedupe against the live one: markRead on the live
    // id wouldn't match the seeded row, and the row would render unread again
    // (and re-count in the badge) on every reload. Mirrors the
    // daemon_access_request branch above; sourceId is the request id for the
    // pending event, the pageId for the resolved one (see pushPageAccessRequest
    // / pushPageAccessResolved's own docstrings for why they differ).
    if (sourceId && s.event_type === "page_access_request") {
      return `page_access_request:${sourceId}`;
    }
    if (sourceId && s.event_type === "page_access_request_resolved") {
      return `page_access_request_resolved:${sourceId}`;
    }
    // Task rows key by taskCardId (sourceType="task", sourceId=taskId) so the
    // live activity_new push (pushTask) and the read-on-open clear dedupe against
    // the seed by the same id: one "assigned you" card and one "updated" card per task.
    if (sourceId && s.source_type === "task") {
      return taskCardId(s.event_type, sourceId);
    }
    // Page-mention rows key by page:<pageId> (sourceType="page", sourceId=pageId,
    // CYBORG-304), mirroring task's scheme — pushPageMention's live push uses the
    // same id so a reload seed dedupes against it.
    if (sourceId && s.source_type === "page") {
      return `page:${sourceId}`;
    }
    // Connect-acceptance rows (source_type "connection") deliberately take the
    // fallback below: pushExternalInviteAccepted mints `activity:<rowId>` too, so
    // the live push and this seed already collapse onto one row. Do NOT add a
    // `source_type === "connection"` branch keyed off sourceId — sourceId there is
    // the composite `<workspaceId>:<userId>`, so the seeded id would stop matching
    // the live one and the row would re-render unread (and re-count in the badge)
    // on every reload, exactly the failure the page_access_request branch above
    // documents.
    return `activity:${s.id}`;
  }

  // thread_reply rows reuse the `mention:<sourceId>` id scheme (keyed by the
  // source message id) so the live mention handler dedupes against the seed;
  // other event types fall back to the row id.
  private serverItemToActivity(
    s: ServerActivityItem,
    channelNames: Map<string, string>,
  ): ActivityItem {
    const dmPeer = s.dm_peer_id ?? null;
    // DM rows reconcile by peer: stamp the same `dm:<peer>` key that push() uses
    // so seedReadFromServer clears all of a peer's items on reload. Non-DM rows
    // keep the message source id. (Inert if the relay seed never returns
    // dm_peer_id for dm_received rows — harmless; the live fix in handleClick
    // does not depend on it.)
    const sourceId =
      s.event_type === "dm_received" && dmPeer ? `dm:${dmPeer}` : (s.source_id ?? null);
    const id = this.activityRowId(s, sourceId);
    // The server row is the truth: a local readIds entry the server never accepted must not hide it.
    const isRead = s.is_read === 1 || s.is_read === true;
    return {
      id,
      // Carry the real PG row id so the per-row read endpoint matches (the
      // synthetic `id` never does → badge stuck until "Mark all read").
      serverId: s.id,
      sourceId: sourceId ?? undefined,
      // The relay stamps a dm_received row's source_id with the DM's id (#2366);
      // keep it apart from the per-peer dedup key above so dmClick can `?m=` it.
      // schedule_needs_input is the same shape — its source_id is the DM the
      // scheduled run wrote — so it lands ON the question, not at the end of the
      // conversation, which for a run that fires daily is not the same place.
      messageId: this.activityMessageId(s),
      // A persisted permission row (source_id = request id) carries the id the in-memory
      // card dedups on. Read is not resolved: permissionSettledDetail says that.
      permissionRequestId:
        s.event_type === "permission_request" ? (s.source_id ?? undefined) : undefined,
      eventType: this.normalizeEventType(s.event_type),
      // Resolve a human name: server-provided name first, then the member map
      // (repairs older rows persisted before the server populated actor_name),
      // never the raw actor_id UUID.
      actorName: this.activityActorName(s),
      ...splitDetail(s.detail),
      ...detailSessionId(s.detail),
      ...(s.tasks ? { tasks: s.tasks } : {}),
      ...permissionSettledDetail(s),
      ...machineIdOf({
        eventType: activityKindOf(s.event_type),
        sourceId: s.source_id,
        actorId: s.actor_id,
        actorName: s.actor_name,
        detail: s.detail,
      }),
      ...(s.source_type === "dm" && isWorkSessionActivityEventType(s.event_type)
        ? { sourceType: "dm" as const }
        : {}),
      // DM rows carry no channel; the peer (actor) drives markReadByDmPeer.
      actorId: s.actor_id ?? s.dm_peer_id ?? null,
      dmPeerId: dmPeer,
      // The server row doesn't store actor type; default to human (agents
      // surface their own activity types). Avatars degrade to initials.
      actorType: "human",
      ...servedPreview(s),
      channelId: s.channel_id ?? null,
      channelName: s.channel_id ? (channelNames.get(s.channel_id) ?? null) : null,
      isRead,
      createdAt: new Date(s.created_at).toISOString(),
    };
  }

  private activityMessageId(s: ServerActivityItem): string | undefined {
    const jumpsToMessage =
      s.event_type === "dm_received" ||
      s.event_type === "schedule_needs_input" ||
      s.event_type === "agent_dm_received";
    return jumpsToMessage && s.source_id ? s.source_id : undefined;
  }

  private activityActorName(s: ServerActivityItem): string {
    const named = s.actor_name ?? (s.actor_id ? authState.getMemberName(s.actor_id) : null);
    return transferActorName(s.event_type, splitDetail(s.detail).detail, named ?? "Someone");
  }

  // Map a server event_type string onto the client union, defaulting unknown
  // values to "mention" so a new server type never breaks the feed render.
  // Object.hasOwn, not `t in`, so a prototype key ("toString", "constructor")
  // coming off the wire can't be waved through as a valid event type.
  private normalizeEventType(raw: string): ActivityEventType {
    const t = activityKindOf(raw);
    return Object.hasOwn(ACTIVITY_EVENT_TYPES, t) ? (t as ActivityEventType) : "mention";
  }

  markRead(id: string): void {
    const card = this.items.find((i) => i.id === id && !i.isRead);
    const wasUnread = !!card;
    // Idempotent: a repeat call must not mint a new `items` array (effect_update_depth_exceeded
    // for any caller that remounts/re-runs on the feed changing, e.g. AlertPermissionAnswer).
    if (!wasUnread && this.readIds.has(id)) return;
    if (wasUnread) {
      this.items = this.items.map((i) =>
        i.id === id ? { ...i, isRead: true, foldedServerIds: undefined } : i,
      );
    }
    this.readIds.add(id);
    if (card && this._serverUnread !== null) {
      this._serverUnread = Math.max(
        0,
        this._serverUnread - 1 - (card.foldedServerIds?.length ?? 0),
      );
    }
    this.persistRead();
  }

  // Manual "mark as unread" toggle (Alerts card affordance). The mirror of
  // markRead: drop the id from the persisted readIds so it stays unread across a
  // remount/reload, and bump the badge back. Local half only: the caller persists it
  // with client.markActivityUnread, or a reconnect re-seed resurrects the read state.
  markUnread(id: string): void {
    const wasRead = this.items.some((i) => i.id === id && i.isRead);
    if (!wasRead) return;
    this.handUnread.add(id);
    this.items = this.items.map((i) => (i.id === id ? { ...i, isRead: false } : i));
    this.readIds.delete(id);
    if (this._serverUnread !== null) this._serverUnread += 1;
    this.persistRead();
  }

  // Read a card: locally now, then server-side (its own row plus the rows folded into it). A read
  // the server refused puts back exactly the rows that failed; the server's count then wins.
  async markCardRead(
    item: ActivityItem,
    send: (...args: ReturnType<typeof activityReadArgs>) => Promise<{
      unread: number;
      totalUnread?: number;
      hiddenUnreadByWorkspace?: unknown;
    }>,
  ): Promise<number> {
    this.markRead(item.id);
    const res = await Promise.allSettled(activityReadArgsAll(item).map((a) => send(...a)));
    const folded = item.foldedServerIds ?? [];
    this.markUnreadRows(
      item.id,
      item.serverId,
      res[0].status === "rejected",
      folded.filter((_, n) => res[n + 1].status === "rejected"),
    );
    // Parallel reads answer with the count at THEIR moment: the lowest is the freshest.
    const acks = res.flatMap((r) => (r.status === "fulfilled" ? [r.value] : []));
    const unreads = acks.flatMap((a) => (typeof a.unread === "number" ? [a.unread] : []));
    const totals = acks.filter((a) => typeof a.totalUnread === "number");
    if (unreads.length > 0) this.setServerUnread(Math.min(...unreads));
    // The ack with the lowest total is the freshest: its split belongs with it.
    const freshest = totals.toSorted((a, b) => (a.totalUnread ?? 0) - (b.totalUnread ?? 0))[0];
    if (freshest) this.setTotalUnread(freshest.totalUnread, freshest.hiddenUnreadByWorkspace);
    // The rows the server acked as read (the card's own plus its folded ones).
    return acks.length;
  }

  // The rows of a card that the server did not read: the card is unread again, carries them, and
  // the badge counts exactly them.
  private markUnreadRows(
    id: string,
    ownId: string | undefined,
    ownFailed: boolean,
    foldedFailed: string[],
  ): void {
    const failed = [...(ownFailed && ownId ? [ownId] : []), ...foldedFailed];
    let count = (ownFailed && !ownId ? 1 : 0) + failed.length;
    if (count === 0) return;
    this.items = this.items.map((i) => {
      if (i.id !== id) return i;
      // A live row may have landed during the read (the card is unread again): keep its rows too.
      const live = i.isRead ? [] : [i.serverId ?? "", ...(i.foldedServerIds ?? [])].filter(Boolean);
      count -= failed.filter((x) => live.includes(x)).length;
      const rows = [...new Set([...live, ...failed])];
      // A card whose own row has no id keeps reading by source; the failed rows ride along.
      if (ownFailed && !ownId && !i.serverId) {
        return { ...i, isRead: false, foldedServerIds: rows.length ? rows : undefined };
      }
      const [own, ...rest] = rows;
      return {
        ...i,
        isRead: false,
        serverId: own,
        foldedServerIds: rest.length ? rest : undefined,
      };
    });
    this.readIds.delete(id);
    this.handUnread.delete(id);
    if (this._serverUnread !== null) this._serverUnread += count;
    this.persistRead();
  }

  // Removed from a private channel / channel deleted: its alert rows can no longer be opened.
  // Drops them from the feed and the unread badge now (a reseed would also omit them server-side).
  dropChannel(channelId: string): void {
    const gone = this.items.filter((i) => i.channelId === channelId);
    if (gone.length === 0) return;
    this.items = this.items.filter((i) => i.channelId !== channelId);
    const unread = gone.filter((i) => !i.isRead).length;
    if (unread > 0 && this._serverUnread !== null) {
      this._serverUnread = Math.max(0, this._serverUnread - unread);
    }
  }

  markAllRead(): void {
    this.items = this.items.map((i) => ({ ...i, isRead: true, foldedServerIds: undefined }));
    for (const item of this.items) this.readIds.add(item.id);
    // Mark-all clears the whole inbox — the authoritative count goes to zero.
    if (this._serverUnread !== null) this._serverUnread = 0;
    this.persistRead();
  }

  // Exactly the rows the server flipped. A card reads once none of its rows is left unread, and a
  // row in `heldBefore` (held when the mark-all was sent or received) that the ack omits was
  // already read. Client-only cards read with the rest; the caller applies the server's count.
  markRowsRead(ids: Iterable<string>, heldBefore?: ReadonlySet<string>): void {
    const done = new Set(ids);
    this.items = this.items.map((i) => {
      if (i.isRead) return i;
      if (!i.serverId) return { ...i, isRead: true, foldedServerIds: undefined };
      const left = [i.serverId, ...(i.foldedServerIds ?? [])].filter(
        (x) => !done.has(x) && !heldBefore?.has(x),
      );
      if (left.length === 0) return { ...i, isRead: true, foldedServerIds: undefined };
      const [own, ...rest] = left;
      return { ...i, serverId: own, foldedServerIds: rest.length ? rest : undefined };
    });
    for (const item of this.items) if (item.isRead) this.readIds.add(item.id);
    this.persistRead();
  }

  // Cards the user marked unread by hand (local only: the server rows behind them are read). A card
  // leaves the set when it is seeded or unread for any other reason (a live row).
  private handUnread = new Set<string>();

  // The server rows behind hand-marked-unread cards that are still unread: the only rows a
  // broadcast mark-all may clear without naming them.
  handUnreadServerIds(): Set<string> {
    const held = new Set<string>();
    for (const i of this.items) {
      if (i.isRead || !this.handUnread.has(i.id)) continue;
      if (i.serverId) held.add(i.serverId);
      for (const f of i.foldedServerIds ?? []) held.add(f);
    }
    return held;
  }

  // Every server row id the feed holds, read or not (a snapshot for markRowsRead).
  heldServerIds(): Set<string> {
    const held = new Set<string>();
    for (const i of this.items) {
      if (i.serverId) held.add(i.serverId);
      for (const f of i.foldedServerIds ?? []) held.add(f);
    }
    return held;
  }

  // P2 Item 9: reading a channel clears its activity items (mentions / thread-
  // replies). Reconciles the local feed when the server broadcasts that a read
  // also cleared activity (cross-device), and on the local read path.
  markReadByChannel(channelId: string): void {
    let cleared = 0;
    const next = this.items.map((i) => {
      // thread_reply rows clear when the THREAD opens (markReadByThread), not on a channel read.
      if (i.channelId === channelId && !i.isRead && i.eventType !== "thread_reply") {
        cleared++;
        this.readIds.add(i.id);
        return { ...i, isRead: true, foldedServerIds: undefined };
      }
      return i;
    });
    // Only reassign the $state array when something actually changed. An
    // unconditional `this.items = this.items.map(...)` mints a NEW array
    // reference on every call — and because this runs synchronously inside
    // selectChannel (itself inside the channel page's $effect), that read +
    // write of `items` retriggered the effect forever
    // (effect_update_depth_exceeded → messages never rendered).
    if (cleared > 0) {
      this.items = next;
      if (this._serverUnread !== null) {
        this._serverUnread = Math.max(0, this._serverUnread - cleared);
      }
      this.persistRead();
    }
  }

  // DM counterpart: DM activity items carry no channelId, so match on the peer
  // (the item's actor — the other party in the DM).
  markReadByDmPeer(peerId: string): void {
    let cleared = 0;
    const next = this.items.map((i) => {
      // Mirror the server's DM-open clear exactly: DM rows (dm_peer_id = peer) plus
      // agent_error / agent_dm_received by actor. NOT invite / daemon-access requests.
      if (
        i.channelId === null &&
        !i.isRead &&
        (i.dmPeerId === peerId ||
          (i.actorId === peerId &&
            (i.eventType === "dm_received" ||
              i.eventType === "agent_error" ||
              i.eventType === "agent_dm_received")))
      ) {
        cleared++;
        this.readIds.add(i.id);
        return { ...i, isRead: true, foldedServerIds: undefined };
      }
      return i;
    });
    // Guard the reassignment (same effect-loop hazard as markReadByChannel).
    if (cleared > 0) {
      this.items = next;
      if (this._serverUnread !== null) {
        this._serverUnread = Math.max(0, this._serverUnread - cleared);
      }
      this.persistRead();
    }
  }

  // A share card was answered or expired (message_card_updated): its Alerts row resolves, stops
  // counting, and opens the post it allowed (or the agent's DM).
  settleApprovalCard(
    messageId: string,
    posted?: { channelId: string | null; messageId: string },
  ): void {
    const card = (i: ActivityItem) =>
      i.eventType === "approval_requested" &&
      (i.sourceId === messageId || i.messageId === messageId);
    if (!this.items.some((i) => card(i) && !i.permissionSettled)) return;
    const to = posted?.channelId ? { posted: { channelId: posted.channelId, messageId: posted.messageId } } : {}; // prettier-ignore
    this.items = this.items.map((i) => (card(i) ? { ...i, permissionSettled: true, ...to } : i));
    this.markMatching(card);
  }

  // Clear every unread row `pred` selects; returns how many (guarded reassignment, as above).
  private markMatching(pred: (i: ActivityItem) => boolean): number {
    let cleared = 0;
    const next = this.items.map((i) => {
      if (!i.isRead && pred(i)) {
        cleared++;
        this.readIds.add(i.id);
        return { ...i, isRead: true, foldedServerIds: undefined };
      }
      return i;
    });
    if (cleared > 0) {
      this.items = next;
      if (this._serverUnread !== null) {
        this._serverUnread = Math.max(0, this._serverUnread - cleared);
      }
      this.persistRead();
    }
    return cleared;
  }

  // activity_read_changed shapes from another tab/device.
  markReadByServerId(eventId: string): number {
    return this.markRowIds(new Set([eventId]), (i) => i.id === eventId);
  }
  // Rows read elsewhere. A card clears once every row it holds is read; a folded card with rows
  // left stays unread on those. `byCard` matches whole cards (a key that names the card itself).
  private markRowIds(ids: Set<string>, byCard: (i: ActivityItem) => boolean): number {
    const rows = (i: ActivityItem): string[] => [i.serverId ?? "", ...(i.foldedServerIds ?? [])];
    this.items = this.items.map((i) => {
      if (i.isRead || !i.serverId || byCard(i)) return i;
      const left = rows(i).filter((x) => !ids.has(x));
      if (left.length === 0 || left.length === rows(i).length) return i;
      const [own, ...rest] = left;
      return { ...i, serverId: own, foldedServerIds: rest.length ? rest : undefined };
    });
    return this.markMatching(
      (i) => byCard(i) || (!!i.serverId && rows(i).every((x) => ids.has(x))),
    );
  }
  // A row marked by (type, source) on another tab: a live DM row's source is its message id.
  markReadBySource(eventType: string, sourceId: string): number {
    return this.markMatching(
      (i) =>
        i.eventType === eventType &&
        (i.sourceId === sourceId || (i.eventType === "dm_received" && i.messageId === sourceId)),
    );
  }
  markReadByDaemonRequest(requestId: string): number {
    return this.markMatching(
      (i) => i.eventType === "daemon_access_request" && i.sourceId === requestId,
    );
  }
  // A thread_reply row's sourceId is the REPLY id and carries no root, so the caller names the
  // replies it knows for that thread.
  markReadByThread(replyIds: Iterable<string>): number {
    const ids = new Set(replyIds);
    return this.markMatching(
      (i) => i.eventType === "thread_reply" && !!i.sourceId && ids.has(i.sourceId),
    );
  }
  // Exactly the rows the server cleared for a thread: by server id, row id, or the
  // `mention:<sourceId>` key a seeded thread_reply row uses.
  // `sourceIds` = the reply message ids: a LIVE thread_reply row has no serverId, only its
  // `mention:<replyId>` id / sourceId.
  markReadByEventIds(eventIds: Iterable<string>, sourceIds: Iterable<string> = []): number {
    const ids = new Set(eventIds);
    const srcs = new Set(sourceIds);
    return this.markRowIds(
      ids,
      (i) =>
        ids.has(i.id) ||
        (!!i.sourceId && ids.has(`mention:${i.sourceId}`)) ||
        (i.eventType === "thread_reply" && !!i.sourceId && srcs.has(i.sourceId)),
    );
  }
  // The server's count is the truth after a read it just applied.
  setServerUnread(unread: number): void {
    this._serverUnread = Math.max(0, unread);
  }

  // Task counterpart: a task's activity rows (task_assigned / task_status_changed)
  // are keyed by taskCardId. Opening the task detail clears them — every card of
  // that task, a batch row listing it included — at once, mirroring
  // markReadByChannel/markReadByDmPeer (guarded reassignment to avoid the
  // effect-loop hazard; idempotent for the duplicate-clear path).
  markReadByTask(taskId: string): void {
    let cleared = 0;
    const next = this.items.map((i) => {
      if (isTaskCardFor(i, taskId) && !i.isRead) {
        cleared++;
        this.readIds.add(i.id);
        return { ...i, isRead: true, foldedServerIds: undefined };
      }
      return i;
    });
    if (cleared > 0) {
      this.items = next;
      if (this._serverUnread !== null) {
        this._serverUnread = Math.max(0, this._serverUnread - cleared);
      }
      this.persistRead();
    }
  }

  // A task was deleted (tasks_changed "deleted", e.g. Undo of an agent's board setup): a row covering
  // only it leaves Alerts (the relay deleted it too, pg-sync deleteTask); a batch row drops its card.
  removeTask(taskId: string): void {
    if (!this.items.some((i) => isTaskCardFor(i, taskId))) return;
    let cleared = 0; // an unread row that leaves stops counting, as on the relay
    this.items = this.items.flatMap((i) => {
      if (!isTaskCardFor(i, taskId)) return [i];
      const rest = i.tasks?.filter((t) => t.id !== taskId) ?? [];
      if (rest.length > 0) return [{ ...i, tasks: rest, sourceId: rest[0].id }];
      if (!i.isRead) cleared++;
      return [];
    });
    if (cleared > 0 && this._serverUnread !== null) {
      this._serverUnread = Math.max(0, this._serverUnread - cleared);
    }
  }

  // Work-session counterpart (CYBORG-839): every work_session_* row for this
  // session (their sourceId is the session id). Opening the session home clears
  // them — the "unread" the pointer card's dot reads is exactly this set, so the
  // dot goes out the moment the user walks through the door.
  //
  // Returns the SERVER row ids it cleared so the caller can persist them
  // cross-device via the generic client.markActivityRead(workspaceId, serverId).
  // There is no per-session read RPC (unlike markTaskRead / markRead) and a
  // session produces at most a handful of rows, so reusing the per-row endpoint
  // beats adding a relay endpoint for it.
  markReadBySession(sessionId: string): string[] {
    const clearedServerIds: string[] = [];
    const next = this.items.map((i) => {
      if (i.sourceId === sessionId && isWorkSessionActivityEventType(i.eventType) && !i.isRead) {
        clearedServerIds.push(i.serverId ?? i.id);
        this.readIds.add(i.id);
        return { ...i, isRead: true, foldedServerIds: undefined };
      }
      return i;
    });
    // Guard the reassignment (same effect-loop hazard as markReadByChannel) — this
    // runs from a route $effect, so an unconditional write would re-trigger it.
    if (clearedServerIds.length > 0) {
      this.items = next;
      if (this._serverUnread !== null) {
        this._serverUnread = Math.max(0, this._serverUnread - clearedServerIds.length);
      }
      this.persistRead();
    }
    return clearedServerIds;
  }

  // Does this work session have an unread notice? Drives the unread dot on the
  // session pointer surfaces (SessionPointerCard / SessionChip) — the SAME feed
  // rows the Alerts pane and the rail badge count, so a session can never look
  // read in one place and unread in another.
  hasUnreadForSession(sessionId: string): boolean {
    return this.items.some(
      (i) => i.sourceId === sessionId && !i.isRead && isWorkSessionActivityEventType(i.eventType),
    );
  }

  // P2 Item 9: hydrate read-state from the server's activity_events on load, so a
  // conversation read on another device is reflected here (not just the local
  // localStorage readIds). Server items are keyed by their source message id;
  // mention items here use `mention:<sourceId>`, so reconcile on sourceId.
  seedReadFromServer(serverItems: Array<{ sourceId: string | null; isRead: boolean }>): void {
    const readSources = new Set<string>();
    for (const s of serverItems) {
      if (s.isRead && s.sourceId) readSources.add(s.sourceId);
    }
    if (readSources.size === 0) return;
    let cleared = 0;
    const next = this.items.map((i) => {
      if (!i.isRead && i.sourceId && readSources.has(i.sourceId)) {
        cleared++;
        this.readIds.add(i.id);
        return { ...i, isRead: true, foldedServerIds: undefined };
      }
      return i;
    });
    // Guard the reassignment (same effect-loop hazard as markReadByChannel).
    if (cleared > 0) {
      this.items = next;
      if (this._serverUnread !== null) {
        this._serverUnread = Math.max(0, this._serverUnread - cleared);
      }
      this.persistRead();
    }
  }

  clear(): void {
    this.handUnread.clear();
    this.items = [];
    this._pagedOlder = false;
    this._cursorMs = undefined;
    this._unreadCursorMs = undefined;
    this._hasMoreUnread = true;
    this._serverUnread = null;
    this._totalOffset = null;
    this._hiddenSplit = undefined;
    this._unreadByWorkspace = undefined;
  }

  clearLocal(): void {
    this._boundUserId = null;
    this.feed = null;
    this.handUnread.clear();
    this.items = [];
    this._pagedOlder = false;
    this._cursorMs = undefined;
    this._unreadCursorMs = undefined;
    this._hasMoreUnread = true;
    this.readIds = new Set();
    this._serverUnread = null;
    this._totalOffset = null;
    this._hiddenSplit = undefined;
    this._unreadByWorkspace = undefined;
  }

  private persistRead(): void {
    const key = this.readKey;
    if (!key) return; // unbound (signed out) ⇒ no owner to write under.
    try {
      const ids = [...this.readIds].slice(-500);
      localStorage.setItem(key, JSON.stringify(ids));
    } catch {
      // intentional: best-effort persistence of read activity ids; not user-facing.
    }
  }
}

// ─── Session state ───────────────────────────────────────────────

export interface RecentSession {
  providerId: string;
  providerLabel: string;
  providerHandleId: string;
  cwd: string;
  title: string | null;
  firstPromptPreview: string | null;
  lastPromptPreview: string | null;
  lastActivityAt: string;
}

import type { ArchivedSession } from "../../ws-client-types.js";
export type { ArchivedSession };

// Optional config overrides applied when RESUMING an archived session (#593).
// All optional — omitting them resumes on the archived model/mode/thinking.
export interface ResumeOverrides {
  model?: string;
  modeId?: string;
  thinkingOptionId?: string | null;
}

// Page size for the archived-sessions list. Keeps the panes from rendering an
// unbounded archive; older pages load on demand via "Show more" (server-side
// keyset pagination, not a client DOM slice).
export const ARCHIVED_SESSIONS_PAGE_SIZE = 30;

export class SessionState {
  list: ArchivedSession[] = $state([]);
  loading = $state(false);
  fetched = $state(false);
  // Keyset pagination: opaque cursor for the NEXT (older) page, or null when the
  // loaded list is the tail. Drives the "Show more" affordance so the panes never
  // render the full unbounded archive at once.
  nextCursor = $state<string | null>(null);
  loadingMore = $state(false);

  clear(): void {
    this.list = [];
    this.loading = false;
    this.fetched = false;
    this.nextCursor = null;
    this.loadingMore = false;
  }
}

// ─── Daemon access requests (#705 REQUEST → NOTIFY → APPROVE) ──────
//
// Client mirror of the wire row (DaemonAccessRequestView in core/client.ts —
// Dates already serialized to epoch-ms). Defined locally so this module stays
// decoupled from the transport layer; the shape is kept in lockstep.
export interface DaemonAccessRequest {
  id: string;
  workspaceId: string;
  daemonId: string;
  requesterId: string;
  requesterName: string | null;
  scopes: Array<"chat" | "spawn" | "terminal" | "admin">;
  status: "pending" | "approved" | "denied";
  resolvedBy: string | null;
  resolvedAt: number | null;
  createdAt: number;
}

// The owner inbox (pending requests for daemons I own) + my own outbox (requests
// I sent). Seeded on workspace open via client.fetchDaemonAccessRequests and kept
// live via the daemon_access_request_changed push (upsert created/resolved). The
// REQUEST affordance reads `hasPendingOutgoing` to disable a re-request; the owner
// surfaces read `pendingForDaemon` / `byId`.
export class DaemonAccessRequestsState {
  // All requests the server returns for the caller (owner inbox + own outbox),
  // keyed by id so the live push upserts in place.
  requests: DaemonAccessRequest[] = $state([]);

  load(requests: DaemonAccessRequest[]): void {
    this.requests = requests;
  }

  // Upsert one row (live push). Replaces an existing row with the same id,
  // appends otherwise — so a resolve (pending → approved/denied) updates in place.
  upsert(request: DaemonAccessRequest): void {
    const idx = this.requests.findIndex((r) => r.id === request.id);
    if (idx === -1) {
      this.requests = [...this.requests, request];
    } else {
      const next = this.requests.slice();
      next[idx] = request;
      this.requests = next;
    }
  }

  byId(id: string): DaemonAccessRequest | undefined {
    return this.requests.find((r) => r.id === id);
  }

  // Pending requests for one daemon (owner view, surfaced atop its Access section).
  pendingForDaemon(daemonId: string): DaemonAccessRequest[] {
    return this.requests.filter((r) => r.daemonId === daemonId && r.status === "pending");
  }

  // Pending requests awaiting THIS owner's action: the server returns the caller's
  // owner-inbox + own-outbox, so a pending request whose requester is NOT me is an
  // INCOMING request on a daemon I own. Drives the mobile owner notification (the
  // Projects banner count + the Daemons-page approve/deny inbox) so the owner sees
  // requests without an Activity tab. #705.
  pendingIncoming(ownerUserId: string | undefined): DaemonAccessRequest[] {
    if (!ownerUserId) return [];
    return this.requests.filter((r) => r.status === "pending" && r.requesterId !== ownerUserId);
  }

  // Does the given user already have a PENDING outgoing request for this daemon?
  // Drives the disabled "Requested" state on the request affordance.
  hasPendingOutgoing(daemonId: string, userId: string | undefined): boolean {
    if (!userId) return false;
    return this.requests.some(
      (r) => r.daemonId === daemonId && r.requesterId === userId && r.status === "pending",
    );
  }

  clear(): void {
    this.requests = [];
  }
}

// ─── Worker invites (Worker tier) ─────────────────────────────────
//
// Client mirror of WorkerInviteView (core/client.ts, wire-normalized).
// `code`/`setupCommand` exist only on the MINT response's row — never on a
// fetched or pushed one — so holders of this state must not expect them.
export interface WorkerInvite {
  id: string;
  workspaceId: string;
  code?: string;
  setupCommand?: string;
  label?: string | null;
  createdBy: string;
  createdAt: number;
  expiresAt?: number;
  status: "pending" | "connected" | "canceled";
  redeemedAt?: number | null;
  daemonId: string | null;
  connectedAt: number | null;
  memberDefaultRole?: "viewer" | "operator" | null;
}

// Seeded on workspace open off the list_daemons response
// (pendingWorkerInvites) and kept live via the worker_invite_changed push —
// structural clone of DaemonAccessRequestsState above.
export class WorkerInvitesState {
  invites: WorkerInvite[] = $state([]);

  load(invites: WorkerInvite[]): void {
    this.invites = invites;
  }

  // Upsert one row (live push / local echo). Replaces an existing row with the
  // same id, appends otherwise — so a connect/cancel updates in place.
  upsert(invite: WorkerInvite): void {
    const idx = this.invites.findIndex((i) => i.id === invite.id);
    if (idx === -1) {
      this.invites = [...this.invites, invite];
    } else {
      const next = this.invites.slice();
      next[idx] = invite;
      this.invites = next;
    }
  }

  byId(id: string): WorkerInvite | undefined {
    return this.invites.find((i) => i.id === id);
  }

  // Codes waiting for a machine to redeem them (the "Pending Workers" rows).
  get pending(): WorkerInvite[] {
    return this.invites.filter((i) => i.status === "pending");
  }

  clear(): void {
    this.invites = [];
  }
}

// ─── Page access requests (REQUEST → NOTIFY → APPROVE, structural clone of
// Daemon access requests above, minus scopes — a page grant is per-user,
// all-or-nothing) ──────
//
// Client mirror of the wire row (PageAccessRequestView in core/client.ts —
// Dates already serialized to epoch-ms). Defined locally so this module stays
// decoupled from the transport layer; the shape is kept in lockstep.
export interface PageAccessRequest {
  id: string;
  workspaceId: string;
  pageId: string;
  requesterId: string;
  requesterName: string | null;
  status: "pending" | "approved" | "denied";
  resolvedBy: string | null;
  resolvedAt: number | null;
  createdAt: number;
}

// The owner inbox (pending requests for pages I own) + my own outbox (requests
// I sent). Seeded on workspace open via client.fetchPageAccessRequests and kept
// live via the page_access_request_changed push (upsert created/resolved) —
// mirrors daemonAccessRequestsState exactly. Without the seed, an owner who
// reloads (or was offline) before the live push landed would never see the
// pending row at all — the request would be invisible until another push
// happened to arrive, defeating the point of the feature. Re-asking after a
// reload is still idempotent server-side either way (createPageAccessRequest
// returns the existing pending row rather than stacking).
export class PageAccessRequestsState {
  // All requests the server returns for the caller (owner inbox + own outbox),
  // keyed by id so the live push upserts in place.
  requests: PageAccessRequest[] = $state([]);

  load(requests: PageAccessRequest[]): void {
    this.requests = requests;
  }

  // Upsert one row (live push). Replaces an existing row with the same id,
  // appends otherwise — so a resolve (pending → approved/denied) updates in place.
  upsert(request: PageAccessRequest): void {
    const idx = this.requests.findIndex((r) => r.id === request.id);
    if (idx === -1) {
      this.requests = [...this.requests, request];
    } else {
      const next = this.requests.slice();
      next[idx] = request;
      this.requests = next;
    }
  }

  byId(id: string): PageAccessRequest | undefined {
    return this.requests.find((r) => r.id === id);
  }

  // Does the given user already have a PENDING outgoing request for this page?
  // Drives the disabled "Requested" state on PageCard's request affordance.
  hasPendingOutgoing(pageId: string, userId: string | undefined): boolean {
    if (!userId) return false;
    return this.requests.some(
      (r) => r.pageId === pageId && r.requesterId === userId && r.status === "pending",
    );
  }

  clear(): void {
    this.requests = [];
  }
}

// ─── Singleton instances ──────────────────────────────────────────

export const agentStreamState = new AgentStreamState();

// A read-only audit copy: it reports through the live store's throttle, so reopening never re-sends.
export function newAuditStream(): AgentStreamState {
  return new AgentStreamState({ journal: false, invariants: agentStreamState.invariants });
}
export const attentionState = new AttentionState();
export const providerState = new ProviderState();
export const cyboState = new CyboState();
export const daemonState = new DaemonState();
export const activityState = new ActivityState();
export const sessionState = new SessionState();
export const daemonAccessRequestsState = new DaemonAccessRequestsState();
export const pageAccessRequestsState = new PageAccessRequestsState();
export const workerInvitesState = new WorkerInvitesState();

// A logout resets every store above through its own clear (#3629).
accountSync.adopt("agentStreams", agentStreamState, (s) => s.clearAll());
accountSync.adopt("attention", attentionState, (s) => s.clearAll());
accountSync.adopt("providers", providerState, (s) => s.clear());
accountSync.adopt("cybos", cyboState, (s) => {
  s.list = [];
  s.loading = false;
  s.loadError = false;
});
accountSync.adopt("daemons", daemonState, (s) => s.clear());
accountSync.adopt("activity", activityState, (s) => s.clearLocal());
accountSync.adopt("archivedSessions", sessionState, (s) => s.clear());
accountSync.adopt("daemonAccessRequests", daemonAccessRequestsState, (s) => s.clear());
accountSync.adopt("pageAccessRequests", pageAccessRequestsState, (s) => s.clear());
accountSync.adopt("workerInvites", workerInvitesState, (s) => s.clear());

// ─── Agent-specific actions ───────────────────────────────────────

export async function sendAgentPrompt(
  client: {
    sendAgentPrompt(workspaceId: string, agentId: string, prompt: string): Promise<void>;
  },
  agentId: string,
  prompt: string,
): Promise<void> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  agentStreamState.addUserMessage(agentId, prompt);
  try {
    await client.sendAgentPrompt(workspaceState.current.id, agentId, prompt);
  } catch (err) {
    const msg = err instanceof Error ? err.message : "Failed to send";
    agentStreamState.handleEvent(agentId, {
      type: "turn_failed",
      provider: "unknown",
      error: msg,
    } as AgentEvent);
    throw err;
  }
}

export function respondToPermission(
  client: {
    respondToPermission(
      workspaceId: string,
      agentId: string,
      permissionRequestId: string,
      response: AgentPermissionResponse,
    ): void;
  },
  agentId: string,
  permissionRequestId: string,
  response: AgentPermissionResponse,
): void {
  if (!workspaceState.current) return;
  client.respondToPermission(workspaceState.current.id, agentId, permissionRequestId, response);
  // Clear the card immediately. The server normally echoes permission_resolved
  // (which also clears it), but a card RESTORED after a reload may get no echo if
  // the daemon already moved on — so don't leave a dead prompt on screen.
  agentStreamState.resolvePendingLocally(agentId, permissionRequestId);
}

export function cancelAgent(
  client: { cancelAgent(workspaceId: string, agentId: string): void },
  agentId: string,
): void {
  if (!workspaceState.current) return;
  client.cancelAgent(workspaceState.current.id, agentId);
}

export async function setAgentModel(
  client: {
    setAgentModel(workspaceId: string, agentId: string, modelId: string | null): Promise<void>;
  },
  agentId: string,
  modelId: string | null,
): Promise<void> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  await client.setAgentModel(workspaceState.current.id, agentId, modelId);
}

export async function setAgentMode(
  client: {
    setAgentMode(workspaceId: string, agentId: string, modeId: string): Promise<void>;
  },
  agentId: string,
  modeId: string,
): Promise<void> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  await client.setAgentMode(workspaceState.current.id, agentId, modeId);
}

export async function setAgentThinking(
  client: {
    setAgentThinking(
      workspaceId: string,
      agentId: string,
      thinkingOptionId: string | null,
    ): Promise<void>;
  },
  agentId: string,
  thinkingOptionId: string | null,
): Promise<void> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  await client.setAgentThinking(workspaceState.current.id, agentId, thinkingOptionId);
}

export interface TimelineFetchClient {
  fetchAgentTimeline(
    workspaceId: string,
    agentId: string,
    opts?: {
      cursor?: string;
      limit?: number;
      direction?: "older" | "newer";
      daemonId?: string;
      lastPrompts?: number;
      workSessionId?: string;
      around?: string;
      untilTs?: number;
      pagesNewer?: boolean;
      // The life on screen (null: none); a server in another life answers a reset page.
      life?: string | null;
    },
  ): Promise<{
    items: Record<string, unknown>[];
    nextCursor: string | null;
    hasMore: boolean;
    olderCursor: string | null;
    hasOlder: boolean;
    // The wire stated `hasOlder` (olderProof reads it; the field above defaults to false).
    hasOlderStated?: boolean;
    // Committed high-water seq for the monotonic-read guard (undefined from an older daemon).
    maxSeq?: number;
    // A forward page (newer-window.svelte.ts); undefined from an older daemon.
    hasNewer?: boolean;
    workSessionTags?: boolean;
    workSessionAgents?: string[];
    workSessionFirstSeq?: number;
    // The server answered `lastPrompts` (CYBORG-1180): the page already holds them.
    recentPrompts?: number;
    // "mirror": the relay's PG copy, served while the owning daemon was away.
    // "blind": the relay's owner-unresolved guess answered not_found (CYBORG-1056).
    source?: "mirror" | "blind";
    headPartial?: boolean;
    budgetCut?: boolean;
    life?: string;
    // A mirror page's coverage (knowledge/125); absent from daemons and older relays.
    verified?: boolean;
    coverage?: { unguaranteedBelow: number };
    reset?: boolean;
  }>;
}

// Asked by every cursor reader once its read is current: whether the page is another life
// than the one on screen (a rewind made elsewhere, or a mirror reset naming one), which it
// must not merge. A page that is not the whole history marks the view either way.
function pageTakesAnotherLife(
  agentId: string,
  page: {
    life?: string;
    reset?: boolean;
    verified?: boolean;
    coverage?: { unguaranteedBelow: number };
  },
): boolean {
  if (page.reset === true || pageIncomplete(page)) {
    agentStreamState.noteHistoryIncomplete(agentId);
  }
  if (page.reset === true && page.life !== undefined) return true;
  return agentStreamState.lifeDiffers(agentId, page.life);
}

/**
 * This viewer's cache key for an agent conversation, or null when the read must not touch the
 * cache: signed out, or the audit lens (`daemonId`: ANOTHER user's session). Split out of
 * fetchAgentTimeline so that seam stays under the complexity gate.
 */
function timelineCacheKey(wsId: string, agentId: string, daemonId?: string): string | null {
  const viewer = viewerCacheKeySegment(authState.user?.email);
  return cacheEnabled() && !daemonId && viewer ? agentTimelineKey(wsId, agentId, viewer) : null;
}

// How long a cold paint may hold up the surface it is trying to make feel instant.
// Matches DefaultSessionView's constant of the same name; see the call sites for why
// an unbounded IndexedDB read is a hang and not a slow paint.
const CACHE_PAINT_TIMEOUT_MS = 500;

/**
 * COLD PAINT: hydrate the stream from the cached page — a WHOLE prior page, never a
 * partial one — while the network read is still in flight. Returns whether it painted;
 * a true means the caller must FORCE the server page over the snapshot this seeded.
 * A miss does nothing at all and the network path runs exactly as it did before.
 */
async function coldPaintAgentTimeline(
  cacheKey: string,
  agentId: string,
  wsId: string,
  stale: () => boolean,
): Promise<boolean> {
  // BOUNDED, because an unbounded read here does not merely delay the paint — it
  // strands the whole fetch. `idb.ts`'s `onblocked` handler is empty, so while
  // another tab holds an older DB version (exactly what a deploy that bumps
  // DB_VERSION produces) `getPage` never settles. This await sits BEFORE
  // `await pending`, so the network answer that already arrived is never consumed
  // and the timeline hangs on a skeleton forever. Same 500ms shape
  // DefaultSessionView already uses for its own cold paint (CYBORG-992); a paint
  // that loses the race just yields a null and the network read carries on.
  // A cold paint that times out or throws is a MISS, not a failure: it yields null,
  // the network read proceeds untouched, and the surface renders from the server.
  // Reporting it would fire on every blocked-IDB tab and drown the real timeline
  // errors that onTimelineFetchFailed exists to raise.
  // intentional: a cache miss is an expected outcome with nothing to surface.
  const cached = await withTimeout(getPage(cacheKey), CACHE_PAINT_TIMEOUT_MS).catch(() => null);
  // The disk read is a read of the OLD life too: a stream cleared while IndexedDB was
  // pending must not be rebuilt from it (#3248 E3).
  if (!cached || stale() || agentStreamState.isHydrated(agentId)) return false;
  agentStreamState.hydrateFromTimeline(agentId, cached.items as Record<string, unknown>[], {
    hasOlder: cached.hasOlder,
    olderCursor: cached.olderCursor,
    maxSeq: cached.maxSeq ?? undefined,
    // The life the rows were cached under: a rewind made while away then reads as one.
    ...(cached.life ? { life: cached.life } : {}),
    // UNVERIFIED: rows off our own disk, not the server's word. See
    // `hydratedFromCache` — this is what keeps the daemon-online recovery retry
    // armed when the fetch this paint was covering for goes on to fail.
    fromCache: true,
  });
  return true;
}

// Tell the person, ONCE, that this conversation is closed to them. Rides the existing
// per-stream one-shot rather than a second mark, so a `suppressToast` caller neither
// speaks nor spends the notice the visible surface still needs — it hits the same gate
// on its own next call and gets told there. Same sentence as the read-only session wall
// (SESSION_READ_DENIED_REASON) and, like AgentsPane's denial toast, NO action button:
// nothing the person can click grants access.
function notifyTimelineReadDenied(agentId: string, suppressToast?: boolean): void {
  if (suppressToast) return;
  if (!agentStreamState.noteTimelineFetchFailed(agentId)) return;
  toast.error("This conversation is private", { description: SESSION_READ_DENIED_REASON });
}

// Should fetchAgentTimeline issue the RPC at all? `null` = yes, ask. Otherwise it is the
// answer the caller gets without a round-trip. Extracted for the same reason
// onTimelineFetchFailed was: to keep that function under the complexity gate.
//
// TRUE — already verified by a server read. isServerHydrated, NOT isHydrated: a stream
// painted from the local cache is "hydrated" but unverified, and returning true for it
// would skip the RPC the paint was covering for. Guarding on entry count instead was the
// older bug — a live event can create the stream before the mount fetch runs, and the
// history was then never fetched at all.
//
// FALSE — the relay REFUSED this read. A denial leaves the stream un-hydrated, which is
// exactly the condition all three recovery triggers on the agent route wait for, so the
// same forbidden read went out on every connection event and every daemon-online push:
// 18 refusals for one user in 30 minutes. Reconnecting cannot grant access. Only
// clear()/clearAll() re-arms it.
function skipTimelineRead(agentId: string, suppressToast?: boolean): boolean | null {
  // A mirror page is a copy; the source (the daemon) re-reads it when it is back.
  if (agentStreamState.isServerHydrated(agentId) && !agentStreamState.isMirrorHydrated(agentId))
    return true;
  if (!agentStreamState.isTimelineReadDenied(agentId)) return null;
  notifyTimelineReadDenied(agentId, suppressToast);
  return false;
}

// A refused read retires the outbox rows only that read could settle: every workspace
// open repaints them into a stream the catch-up reads, so keeping them re-asks the same
// refused read on every page load. Only this owner's rows, for this workspace and agent.
export async function retireRefusedAgentPrompts(
  agentId: string,
  workspaceId: string,
): Promise<void> {
  const owner = authState.user?.email.trim().toLowerCase();
  if (!owner) return;
  const rows = await getAgentPromptOutbox(owner, workspaceId);
  await Promise.all(
    rows
      .filter((row) => row.agentId === agentId)
      .map((row) => deleteAgentPromptOutbox(row.clientMessageId)),
  );
}

// A just-created session's first read can beat the relay's owner binding (#3380) and go
// blind to another daemon. A blind not_found for a session created here is "no history
// yet"; on any other session it stays a failed load.
const NEW_SESSION_WINDOW_MS = 2 * 60_000;

export function noteSessionCreatedHere(agentId: string, now = Date.now()): void {
  for (const [id, at] of sessionsCreatedHere) {
    if (now - at > NEW_SESSION_WINDOW_MS) sessionsCreatedHere.delete(id);
  }
  sessionsCreatedHere.set(agentId, now);
}

function isNewSessionBlindMiss(agentId: string, err: unknown, now = Date.now()): boolean {
  const e = err as { code?: unknown; routedBlind?: unknown } | null | undefined;
  if (e?.code !== "not_found" || e.routedBlind !== true) return false;
  const createdAt = sessionsCreatedHere.get(agentId);
  return createdAt !== undefined && now - createdAt <= NEW_SESSION_WINDOW_MS;
}

// Everything a failed timeline fetch owes: report it, then decide whether to say
// anything out loud. Split out of fetchAgentTimeline's catch so the decision has room
// to be read (and to keep that function under the complexity gate).
// Opens whose read ran out on a busy computer; the next read of the agent clears the mark.
const timedOutOpens = conversationSync.collection("timedOutOpens", new Set<string>());
export function openTimedOut(agentId: string): boolean {
  return timedOutOpens.has(agentId);
}

function onTimelineFetchFailed(
  agentId: string,
  workspaceId: string,
  err: unknown,
  suppressToast?: boolean,
): void {
  const newSession = isNewSessionBlindMiss(agentId, err);
  // History hydration failed → the conversation renders blank (only the live
  // chunk, if any). Surface it instead of showing an empty stream silently.
  // A new session's empty history is expected; otherwise the code decides the kind.
  const routedBlind = (err as { routedBlind?: unknown } | null)?.routedBlind === true;
  reportUiFailure(
    "agents.fetchAgentTimeline",
    err,
    {
      workspaceId,
      agentId,
      routedBlind: routedBlind || undefined,
      newSession: newSession || undefined,
    },
    newSession || (err as { code?: unknown } | null)?.code === "daemon_offline"
      ? { failureKind: "expected" }
      : {},
  );
  // No history yet: no toast, the one-shot mark unspent, the stream left un-hydrated.
  if (newSession) return;
  // CYBORG-990 — "NO DAEMON CONNECTED" IS A STATE, NOT A FAILED LOAD. `no_daemon` is
  // the relay's ONE stable non-denial code: a daemon-forward with nothing to forward
  // to (relay-standalone.ts respondError). client.ts's buildRpcError carries it on
  // the rejection as `.code` precisely so callers branch on it instead of the prose
  // (CYBORG-551). It is expected, not an infra fault: the agent DM route reads its
  // messages from PG (DefaultSessionView, fetch_dm_messages) and only the TIMELINE
  // needs a daemon — so with no daemon connected this toasted "Couldn't load
  // conversation history" on top of a conversation that was fully loaded and painted.
  // That state is already told truthfully, once, by the view's own read-only notice;
  // the toast was a second, wrong voice for it. Every OTHER rejection — including a
  // resolved owner's `not_found` — still toasts.
  //
  // Returns BEFORE noteTimelineFetchFailed for the same reason suppressToast
  // short-circuits it: this must not burn the one-shot mark, or a genuine failure
  // afterwards would go unreported.
  const code = err && typeof err === "object" ? (err as { code?: unknown }).code : undefined;
  if (code === "no_daemon") return;
  // A computer busy, or gone mid-freeze, is a wait, not a failed load: the view re-reads.
  if (code === "daemon_timeout" || code === "daemon_offline" || code === "daemon_disconnected") {
    timedOutOpens.add(agentId);
    return;
  }
  // A REFUSAL IS AN ANSWER. Marks the stream so fetchAgentTimeline stops issuing the RPC
  // at all — see AgentStream.timelineReadDenied for the 18-refusals-in-30-minutes this
  // closes. The mark is unconditional (no caller may keep asking); the notice is not.
  if (isSessionReadDenied(err)) {
    agentStreamState.noteTimelineReadDenied(agentId);
    void retireRefusedAgentPrompts(agentId, workspaceId);
    notifyTimelineReadDenied(agentId, suppressToast);
    return;
  }
  // ONCE PER STREAM, not once per attempt. The daemon_status:"online" recovery
  // retry (#2506) is gated on `!isHydrated`, which a failed fetch leaves false
  // forever — so without this mark every online push in the workspace re-fires
  // this load and raises another toast for the SAME unchanged failure. The retry
  // still runs (it is the only thing that recovers the surface); only the
  // notification is one-shot, and a successful hydrate re-arms it.
  if (!suppressToast && agentStreamState.noteTimelineFetchFailed(agentId)) {
    toast.error("Couldn't load conversation history");
  }
}

// What a failed RECONNECT CATCH-UP read owes (app.svelte.ts catchUpAgentTimelines). Same
// two facts as onTimelineFetchFailed, opposite voice: the person opened nothing, so this
// path never toasts — the notice belongs to the surface they open, which spends it at
// skipTimelineRead.
//
// A REFUSAL IS AN ANSWER: mark the stream so no later reconnect asks again. Only a stream
// that still exists is marked — noteTimelineReadDenied would otherwise `ensure()` one the
// clear this read raced already deleted, undoing the only re-arm there is.
export function onCatchUpTimelineFetchFailed(
  agentId: string,
  workspaceId: string,
  err: unknown,
): void {
  if (isSessionReadDenied(err)) {
    if (agentStreamState.streams.has(agentId) && agentStreamState.noteTimelineReadDenied(agentId)) {
      reportSessionReadRefused("catch_up", err, { workspaceId, agentId });
    }
    // The mark lasts one page; retiring the rows is what keeps the next page from asking.
    void retireRefusedAgentPrompts(agentId, workspaceId);
    return;
  }
  // NOT_FOUND IS AN ANSWER TOO when the relay routed the read to the session's owner: it
  // has no such session (a deleted or archived agent). Marked so the catch-up stops asking
  // for the rest of this page; opening it still reads. A GUESSED route's not_found
  // (routedBlind) proves nothing and stays retryable. Reported with its agentId.
  const gone = isTimelineNotFound(err);
  if (gone) agentStreamState.noteTimelineGone(agentId);
  // A timeout or a dropped socket is a real failure of a background read, left unmarked so
  // the next catch-up retries it. Reported, never swallowed: an empty catch is invisible.
  reportUiFailure(
    "agents.catchUpAgentTimelines",
    err,
    { workspaceId, agentId },
    gone ? { code: "not_found" } : {},
  );
}

// A not_found from the daemon the relay ROUTED to (buildRpcError threads `code` and the
// relay's `routedBlind` tag onto the Error). A blind guess's not_found is not one; nor is
// a timeout or a dropped socket, which carry no code.
function isTimelineNotFound(err: unknown): boolean {
  const e = err as { code?: unknown; routedBlind?: unknown } | null | undefined;
  return e?.code === "not_found" && e.routedBlind !== true;
}

// The workspace a timeline was fetched for owns the stream, so a delete reaches it before the roster binds.
function bindIfUnbound(agentId: string, wsId: string): void {
  if (!agentStreamState.workspaceForAgent(agentId))
    agentStreamState.bindAgentWorkspace(agentId, wsId);
}

// What a read of a stream the server already confirmed comes to; undefined: the usual read.
function readLoadedStream(
  client: TimelineFetchClient,
  agentId: string,
  opts?: { suppressToast?: boolean; daemonId?: string; untilTs?: number },
): Promise<boolean> | undefined {
  if (!agentStreamState.isServerHydrated(agentId) || agentStreamState.isMirrorHydrated(agentId)) {
    return undefined;
  }
  if (opts?.untilTs !== undefined) return readOpenOverLoaded(client, agentId, opts);
  if (!loadedStreamOwesRead(agentId)) return undefined;
  return reconcileAgentTranscript(client, agentId).then(() => true);
}

function loadedStreamOwesRead(agentId: string): boolean {
  return (
    agentStreamState.isTurnActive(agentId, rosterLifecycle(agentId)) ||
    // A notification announced a reply, or a turn's end named rows not on screen (#3244).
    agentStreamState.takeEntryRead(agentId) ||
    agentStreamState.turnEndOwed(agentId)
  );
}

// An open with unreads over a loaded stream may stop short of them, and live replies hold no
// persisted time. The open's own read answers both: the relay hedges it from the mirror, and
// a merge shows no recovery. A forward page opens its window only over a stream that ends on it.
async function readOpenOverLoaded(
  client: TimelineFetchClient,
  agentId: string,
  opts: { suppressToast?: boolean; daemonId?: string; untilTs?: number },
): Promise<boolean> {
  const wsId = workspaceState.current?.id;
  if (!wsId) return false;
  const stale = staleAfter(agentId, wsId);
  try {
    const page = await client.fetchAgentTimeline(wsId, agentId, {
      ...(opts.daemonId ? { daemonId: opts.daemonId } : {}),
      ...openAsk(opts),
    });
    if (stale()) return false;
    bindIfUnbound(agentId, wsId);
    if (pageTakesAnotherLife(agentId, page)) {
      void adoptNewLife(client, agentId, opts.daemonId);
      return true;
    }
    agentStreamState.mergeOpenPage(agentId, page.items, rosterLifecycle(agentId));
    const top = page.items.at(-1)?.seq;
    const endsHere = top === undefined || agentStreamState.highestHeldSeq(agentId) === top;
    if (page.hasNewer === true && page.nextCursor && endsHere) {
      newerWindows.open(agentId, page.nextCursor);
    }
    return true;
  } catch (err) {
    // A newer open replaced this read: report it, but no toast and no one-shot mark.
    if (stale()) {
      reportUiFailure(
        "agents.readOpenOverLoaded",
        err,
        { workspaceId: wsId, agentId, stale: true },
        { failureKind: "expected" },
      );
    } else {
      onTimelineFetchFailed(agentId, wsId, err, opts.suppressToast);
    }
    return false;
  }
}

export async function fetchAgentTimeline(
  client: TimelineFetchClient,
  agentId: string,
  // `daemonId` (audit viewer, #994): the KNOWN owning daemon, threaded so the
  // relay routes the read to the daemon that hosts the session in cloud
  // multi-daemon mode (required for ephemerals). Omitted outside a daemon-audit
  // context — resolution is then unchanged.
  // `untilTs` (the agent DM open's read cursor): the page reaches the first unread (CYBORG-1556).
  opts?: { suppressToast?: boolean; daemonId?: string; workSessionId?: string; untilTs?: number },
): Promise<boolean> {
  if (!workspaceState.current) return false;
  // "Hydrated once" is not "up to date" while a turn runs: a tab that left and came back
  // mid-stream shows the reply from where it re-joined. One tail page joins the durable
  // prefix on BEFORE the turn's terminal event (#3248 R5).
  // An explicit route wins before anything reads, the turn-active reconcile included.
  const route = opts?.daemonId;
  timedOutOpens.delete(agentId);
  agentStreamState.noteRoute(agentId, route);
  const loaded = readLoadedStream(client, agentId, opts);
  if (loaded) return loaded;
  // Both reasons not to issue this read at all, and the answer each one owes.
  const skip = skipTimelineRead(agentId, opts?.suppressToast);
  if (skip !== null) return skip;
  const wsId = workspaceState.current.id;
  // Local cache key for this conversation (CYBORG-987), or null when the cache is off
  // or this is the audit lens. See timelineCacheKey.
  const cacheKey = timelineCacheKey(wsId, agentId, route);
  const stale = staleAfter(agentId, wsId);
  try {
    // The network read starts FIRST, so the cache read below can never delay it. Its
    // rejection is captured into the settled shape here and re-thrown at the await, so
    // it can neither escape as an unhandled rejection while the cache read is in flight
    // nor change which `catch` handles it.
    const pending = client
      .fetchAgentTimeline(wsId, agentId, {
        lastPrompts: RECENT_PROMPTS,
        ...(route ? { daemonId: opts.daemonId } : {}),
        ...openAsk(opts),
      })
      .then(
        (page) => ({ ok: true as const, page }),
        (err: unknown) => ({ ok: false as const, err }),
      );
    // Paint what is already on disk while the round-trip is out (coldPaintAgentTimeline).
    if (cacheKey) await coldPaintAgentTimeline(cacheKey, agentId, wsId, stale);
    const settled = await pending;
    // Asked BEFORE the outcome is read: a rejection from the previous life must not deny,
    // toast or mark the current one any more than its page may hydrate it (#3248 E2).
    if (stale()) return false;
    if (!settled.ok) throw settled.err;
    bindIfUnbound(agentId, wsId);
    agentStreamState.noteSessionPage(opts?.workSessionId, agentId, settled.page);
    const accepted = applyServerTimelinePage(client, agentId, settled.page, cacheKey, route);
    // A conversation this read created has its stream only now.
    agentStreamState.noteRoute(agentId, route);
    // A server that answered `lastPrompts` already sent them; an older one pages back.
    if (accepted && settled.page.recentPrompts === undefined) {
      void loadRecentTurns(client, agentId, route);
    }
    return accepted;
  } catch (err) {
    onTimelineFetchFailed(agentId, wsId, err, opts?.suppressToast);
    return false;
  }
}

// The open's asks: its work session, and the agent DM's read cursor (CYBORG-1556).
// With the cursor it may answer a forward page from the read turn (newer-window.svelte.ts).
function openAsk(opts?: { workSessionId?: string; untilTs?: number }): {
  workSessionId?: string;
  untilTs?: number;
  pagesNewer?: boolean;
} {
  return {
    ...sessionAsk(opts?.workSessionId),
    ...(opts?.untilTs !== undefined ? { untilTs: opts.untilTs, pagesNewer: true } : {}),
  };
}

// The agent read names the work session it is for only when one asked (ws-unify B2).
function sessionAsk(workSessionId: string | undefined): { workSessionId?: string } {
  return workSessionId ? { workSessionId } : {};
}

// A work session opened as a projection of its agent's stream (ws-unify B2): the agent's own
// read, plus what its page says about the session. An already-hydrated stream skips that read,
// so the session's facts are then asked once and its rows merged like a reconcile page.
export async function openSessionProjection(
  client: TimelineFetchClient,
  agentId: string,
  sessionId: string,
): Promise<SessionFacts | undefined> {
  const read = await fetchAgentTimeline(client, agentId, { workSessionId: sessionId, suppressToast: true }); // prettier-ignore
  const known = agentStreamState.sessionFactsFor(sessionId, agentId);
  // A read that did not land (failed, refused, stale or not applied) is not re-sent at once; the next trigger asks (DFU.6).
  if (known || !read || !workspaceState.current || agentStreamState.isTimelineReadDenied(agentId)) {
    return known;
  }
  const wsId = workspaceState.current.id;
  const stale = staleAfter(agentId, wsId);
  try {
    const page = await client.fetchAgentTimeline(wsId, agentId, {
      lastPrompts: RECENT_PROMPTS,
      workSessionId: sessionId,
    });
    if (stale()) return undefined;
    agentStreamState.noteSessionPage(sessionId, agentId, page);
    if (!agentStreamState.lifeDiffers(agentId, page.life)) {
      agentStreamState.mergeDurableRows(agentId, page.items, rosterLifecycle(agentId));
    }
  } catch (err) {
    reportUiFailure("agents.openSessionProjection", err, { workspaceId: wsId, agentId, sessionId });
  }
  return agentStreamState.sessionFactsFor(sessionId, agentId);
}

interface SessionWorkFetchClient {
  fetchSessionWork(
    workspaceId: string,
    sessionId: string,
    opts?: { cursor?: string; limit?: number; direction?: "older" | "newer"; daemonId?: string },
  ): Promise<{
    items: Record<string, unknown>[];
    nextCursor: string | null;
    hasMore: boolean;
    olderCursor: string | null;
    hasOlder: boolean;
    maxSeq?: number;
  }>;
}

// Session-scoped work timeline (cyborg:fetch_session_work). State-layer entry point
// mirroring fetchAgentTimeline's error-reporting, but it RETURNS the page instead of
// hydrating a stream store: the session-work surface has no dedicated stream store yet
// (a later UI unit wires the renderer), and the page is the SAME AgentTimelineItem
// envelope the agent-timeline renderer already consumes. Returns null on error (after
// reporting it) so a caller can render an error state.
// ponytail: returns the page; add stream-store hydration when the session-work view lands.
export async function fetchSessionWork(
  client: SessionWorkFetchClient,
  sessionId: string,
  opts?: { cursor?: string; limit?: number; direction?: "older" | "newer"; daemonId?: string },
): Promise<{
  items: Record<string, unknown>[];
  hasMore: boolean;
  nextCursor: string | null;
  hasOlder: boolean;
  olderCursor: string | null;
  maxSeq?: number;
} | null> {
  if (!workspaceState.current) return null;
  const wsId = workspaceState.current.id;
  // A REFUSAL IS AN ANSWER here too. This seam is re-entered at every TURN BOUNDARY of a live
  // session (SessionWorkView's `isWorking` dep) and rides the same relay session-read gate as
  // the timeline, so a refused read has to latch or it is re-issued for the page's whole life.
  const streamKey = sessionStreamKey(sessionId);
  if (agentStreamState.isTimelineReadDenied(streamKey)) return null;
  const stale = staleAfter(streamKey, wsId);
  try {
    const { items, hasMore, nextCursor, hasOlder, olderCursor, maxSeq } =
      await client.fetchSessionWork(wsId, sessionId, opts);
    // The NEWEST page only, per viewer, on SUCCESS (CYBORG-987, caveat #29, CYBO-1846): a
    // `cursor` slice would cold-paint the next open mid-scroll; the audit lens is another's.
    const viewer = viewerCacheKeySegment(authState.user?.email);
    if (cacheEnabled() && viewer && !opts?.cursor && !opts?.daemonId) {
      const key = sessionWorkKey(wsId, sessionId, viewer);
      void putPage({ key, items, hasOlder, olderCursor, maxSeq });
    }
    return { items, hasMore, nextCursor, hasOlder, olderCursor, maxSeq };
  } catch (err) {
    // Reported once, as a refusal, when the mark is set; the mark stops every later ask.
    // A refusal of a read the stream moved past (clear, rewind, workspace switch) marks
    // nothing: the latch ensure()s, so it would recreate or deny the next life (#3373 F1).
    if (isSessionReadDenied(err)) {
      if (!stale() && agentStreamState.noteTimelineReadDenied(streamKey)) {
        reportSessionReadRefused("session_work", err, { workspaceId: wsId, sessionId });
      }
      return null;
    }
    reportUiFailure("agents.fetchSessionWork", err, { workspaceId: wsId, sessionId });
    return null;
  }
}

/**
 * The cached NEWEST page of a session's work, or null on a miss / with the cache off
 * (CYBORG-987). The read is separate from fetchSessionWork because that seam RETURNS a
 * page rather than painting one: the cold paint has to happen while the network read is
 * still in flight, and only the view knows the meta (`settled`, `force`) a hydrate of
 * this surface needs. Best-effort — never throws.
 */
export async function readCachedSessionWork(sessionId: string): Promise<CachedPage | null> {
  const viewer = viewerCacheKeySegment(authState.user?.email);
  if (!workspaceState.current || !viewer || !cacheEnabled()) return null;
  // Bounded for the same reason as coldPaintAgentTimeline: a blocked IndexedDB
  // upgrade never settles, and this read gates the surface behind it.
  return withTimeout(
    getPage(sessionWorkKey(workspaceState.current.id, sessionId, viewer)),
    CACHE_PAINT_TIMEOUT_MS,
    // intentional: a miss is null, which every caller of this already handles.
  ).catch(() => null);
}

// Scroll-up lazy-load for the SESSION work timeline — the session twin of
// loadOlderAgentTimeline, and the reason the session view can reach its own beginning.
//
// The agent-scoped loader CANNOT serve this surface. SessionWorkView renders into a
// SYNTHETIC `session:<id>` stream key (sessionStreamKey) rather than an agentId,
// deliberately: a work session is not 1:1 with an agent, since a cold resume mints a
// fresh one for the same session. Paging that key through fetch_agent_timeline would
// ask the daemon for an agent that does not exist. So this pages the SESSION read
// (cyborg:fetch_session_work, direction "older") and prepends into the same key — which
// also means the page it walks back through is session-scoped and therefore spans EVERY
// agent the session has had.
//
// Same shape and failure policy as loadOlderAgentTimeline otherwise: re-entry guarded, a
// no-op when nothing older remains, a stale result (workspace switch, clear/rewind) drops,
// and a failed page keeps hasOlder/olderCursor — it is not the end of history (#3244) —
// holding the auto-trigger off with the same backoff and bounded retry. fetchSessionWork
// already reports the error or latches a refusal; a null page is the failure signal here.
export async function loadOlderSessionWork(
  client: SessionWorkFetchClient,
  sessionId: string,
  streamKey: string,
): Promise<void> {
  if (!workspaceState.current) return;
  if (!agentStreamState.hasOlder(streamKey) || agentStreamState.isLoadingOlder(streamKey)) return;
  if (agentStreamState.olderLoadHeldOff(streamKey)) return;
  const cursor = agentStreamState.olderCursor(streamKey);
  if (!cursor) return;
  const stale = staleAfter(streamKey, workspaceState.current.id);
  agentStreamState.setLoadingOlder(streamKey, true);
  const marked = agentStreamState.streams.get(streamKey);
  try {
    const page = await fetchSessionWork(client, sessionId, { cursor, direction: "older" });
    if (stale()) return;
    if (!page) {
      // Refused: latched by fetchSessionWork — a wall, not a retry or a toast.
      if (agentStreamState.isTimelineReadDenied(streamKey)) return;
      const first = agentStreamState.noteOlderLoadFailed(streamKey);
      // The retry is this read's: after a switch or a new life it would ask the wrong
      // workspace for the wrong cursor (#3373 F2).
      agentStreamState.armOlderLoadRetry(streamKey, () => {
        if (!stale()) void loadOlderSessionWork(client, sessionId, streamKey);
      });
      if (first) toast.error("Couldn't load older history");
      return;
    }
    agentStreamState.prependTimeline(streamKey, page.items, {
      hasOlder: page.hasOlder,
      olderCursor: page.olderCursor,
    });
    agentStreamState.noteOlderLoadSucceeded(streamKey);
  } finally {
    releaseOlderLoad(streamKey, marked);
  }
}

// The older-page mark belongs to the stream it was set on. A rewind keeps that stream,
// and no newer load can start under the mark, so it is released — keyed on staleness it
// stayed set and wedged scroll-up for the rewound life. A clear() replaces the stream
// with one a newer load may own, which is left alone (#3248 C3).
function releaseOlderLoad(streamKey: string, marked: AgentStream | undefined): void {
  if (marked && agentStreamState.streams.get(streamKey) === marked) {
    agentStreamState.setLoadingOlder(streamKey, false);
  }
}

interface DmMessagesFetchClient {
  fetchDmMessages(
    workspaceId: string,
    peerId: string,
    opts?: { before?: string; limit?: number; after?: string; aroundId?: string },
  ): Promise<{
    // createdAt is load-bearing, not decoration: it is the merged entry's `ts`,
    // and without it AgentStreamView renders the row with no time at all. The
    // real client returns full `Message`s that always carry it; this structural
    // narrowing used to omit it, which silently dropped it on the floor here.
    // card is load-bearing for the same reason createdAt is: this structural
    // narrowing is the ONLY thing standing between the real client's full
    // `Message` and the merged entry, so a field it omits is silently dropped —
    // which is exactly how the integration-offer card reached AgentStreamView as
    // bare fallback text.
    messages: {
      id: string;
      fromId: string;
      text: string;
      seq: number;
      createdAt: number;
      parentId?: string | null;
      card?: MessageCard | null;
      attachments?: Attachment[] | null; // null: a row stored with none
      clientMsgId?: string | null;
      fromName?: string;
      workSummary?: WorkSummary; // the turn-end stamp: the missing-permission notice reads it
    }[];
    hasMore: boolean;
    // An `after` read only: more rows arrived than one page holds.
    hasMoreNewer?: boolean;
  }>;
}

/**
 * One ledger page exactly as `fetchDmMessages` answers it. Named only so the walk below
 * can hold its already-in-flight FIRST page in a typed local without restating the
 * structural narrowing above (restating it is how `createdAt` and `card` were dropped).
 */
type DmMessagesPage = Awaited<ReturnType<DmMessagesFetchClient["fetchDmMessages"]>>;

export interface MergeMissingCyboDmsResult {
  // True when the bounded page budget was exhausted while the ledger still
  // had older history (`hasMore` still true) — NOT "something looked unsafe
  // to merge", just "we stopped paging before reaching the true start of the
  // conversation". Surfaced as a "Load full conversation" affordance that
  // pages deeper (force: true) rather than a silent cap.
  moreHistoryAvailable: boolean;
}

// adversarial review round 5, finding #1 (BLOCKER): mergeInFlight blocks a
// genuinely CONCURRENT call for the same agent (e.g. the reconnect retry
// racing the mount effect) — it self-clears in the `finally` below the moment
// the in-flight fetch settles, so it is NOT a persistent latch across
// mounts/navigation. There is deliberately NO "last completed key" cache
// anymore: one existed here through round 4 (mergeLastKey, a module-level
// Map<agentId, dedupeKey> never invalidated on unmount/navigation/reconnect)
// and made the fix work EXACTLY ONCE per cybo per app session — after the
// first successful merge, fetchAgentTimeline's own
// `if (isHydrated(agentId)) return true` guard means hydrationNonce never
// bumps again for that agentId's lifetime, so the cached key matched forever
// and every later re-open silently skipped the RPC. That is verbatim the
// CEO's reported workflow (open the DM, navigate away, the cybo sends more
// DMs, come back — see nothing). Measured: the caller's $derived hydration-
// key hoist ALONE cuts effect re-fires from 26 to 5 across a stream-delta
// storm — the RPC-storm protection does not need a permanent latch, so
// removing mergeLastKey costs nothing there. mergePendingRetry below is a
// bounded (max one queued) exception: a call blocked by mergeInFlight queues
// itself to retry once the in-flight one finishes, so a request timeout on
// the in-flight call doesn't silently swallow a legitimate concurrent retry
// (finding #6).
//
// mergeRetryWaiters (round 6, finding #3): every caller blocked behind an
// in-flight fetch used to resolve IMMEDIATELY with the static `noop` while
// its queued retry ran in the background with the result thrown away
// (`void mergeMissingCyboDms(...)`, no `.then`). For most callers (the mount
// effect, the reconnect handler) that only meant a slightly stale
// `moreHistoryAvailable` for one tick. For "Load full conversation" it was a
// real UX defect: a click landing while the mount effect's merge was still
// in flight got told `moreHistoryAvailable: false` synchronously — the
// banner vanished — while the actual (possibly deep, force:true) fetch kept
// running silently with its true result discarded. A blocked call now
// returns a promise that stays pending until the retry that will eventually
// run on its behalf actually settles, then resolves with THAT result — every
// waiter queued behind the SAME agentId shares the one retry's outcome,
// mirroring mergePendingRetry's own "latest caller wins, but nobody is left
// hanging" semantics.
const mergeInFlight = conversationSync.collection("mergeInFlight", new Map<string, object>());
const mergePendingRetry = conversationSync.collection(
  "mergePendingRetry",
  new Map<string, () => void>(),
);
// A reset settles the callers still waiting: their merge belonged to the streams it dropped.
const mergeRetryWaiters = conversationSync.collection(
  "mergeRetryWaiters",
  new Map<string, ((result: MergeMissingCyboDmsResult) => void)[]>(),
  (held) => {
    for (const waiters of held.values()) {
      for (const resolve of waiters) resolve({ moreHistoryAvailable: false });
    }
  },
);

// Bounded page budgets for the pagination loop below. Not load-bearing for
// correctness (dedup is by messageId regardless of how many pages are
// fetched) — only for how far back into history a single catch-up reaches
// before stopping. `force` (the "Load full conversation" recovery action)
// pages much deeper.
const DEFAULT_PAGE_BUDGET = 3; // up to 150 messages back
const FULL_PAGE_BUDGET = 20; // "Load full conversation" — up to 1000 messages back
// How many ledger rows the cached window keeps — one default walk's worth, newest
// first. Bounds the stored row; a "Load full conversation" walk is not persisted
// wholesale for the same reason the session-list cache only persists page 1.
const MAX_CACHED_LEDGER_ROWS = DEFAULT_PAGE_BUDGET * 50;

/**
 * A ledger row as this merge consumes it — the ONLY shape that is cached (CYBORG-987):
 * the agent's rows, and the person's root rows with words. Deliberately not a `Message`: this function sees a narrowed
 * structural type whose widening has silently dropped fields twice (see `createdAt`
 * and `card` in DmMessagesFetchClient), so the cache stores what the merge actually
 * uses and nothing else.
 */
interface CyboDmLedgerRow {
  id: string;
  text: string;
  seq: number;
  createdAt: number;
  card?: MessageCard | null;
  attachments?: Attachment[] | null; // null: a row stored with none
  workSummary?: WorkSummary;
  // The person's own rows are cached too (their author and send id draw and pair them): a
  // second reload that stops at known territory still has an earlier session's prompt.
  fromId?: string;
  fromName?: string;
  clientMsgId?: string | null;
}

/**
 * Fold ONE fetched ledger page into the walk's running state: collect every
 * cybo-authored, non-thread-reply, non-empty-text row, and report how much of the page
 * we had ALREADY cached — which is what tells the walk it has reached known territory.
 * Takes the accumulator rather than returning a new list so the loop stays flat (and
 * under the nesting gate).
 *
 * THE SERVER ROW WINS (CYBORG-992). `candidates` is keyed by id and a fetched row
 * OVERWRITES whatever sat under that key. It used to `continue` on an id already
 * collected — and since the map is SEEDED from the cache, that meant a row whose text
 * or card had changed since it was cached was discarded in favour of the stale copy,
 * which was then merged AND re-persisted from this same set. An edit could not be
 * repaired by any number of re-opens.
 */
function collectLedgerPage(
  messages: {
    id: string;
    fromId: string;
    text: string;
    seq: number;
    createdAt: number;
    parentId?: string | null;
    card?: MessageCard | null;
    attachments?: Attachment[] | null; // null: a row stored with none
    workSummary?: WorkSummary;
  }[],
  cyboId: string,
  cachedIds: ReadonlySet<string>,
  candidates: Map<string, CyboDmLedgerRow>,
  people: Map<string, CyboDmLedgerRow>,
): { cyboRows: number; rows: number; alreadyCached: number } {
  let cyboRows = 0;
  let rows = 0;
  let alreadyCached = 0;
  for (const m of messages) {
    if (m.parentId) continue;
    if (m.fromId === cyboId) {
      if (!m.text.trim() && !m.attachments?.length) continue;
      cyboRows += 1;
      candidates.set(m.id, m);
    } else {
      if (!m.text.trim()) continue;
      people.set(m.id, m);
    }
    rows += 1;
    // A person's row the cache never held (one written before it kept them) is new ground too.
    if (cachedIds.has(m.id)) alreadyCached += 1;
  }
  return { cyboRows, rows, alreadyCached };
}

/**
 * Rows recovered from the cached ledger window, shape-checked. A corrupt/legacy row
 * is dropped rather than merged — the network walk below re-supplies it.
 */
function parseCachedLedgerRows(page: CachedPage | null): CyboDmLedgerRow[] {
  if (!page) return [];
  return page.items.filter((r): r is CyboDmLedgerRow => {
    const row = r as Partial<CyboDmLedgerRow> | null;
    return (
      typeof row === "object" &&
      row !== null &&
      typeof row.id === "string" &&
      typeof row.text === "string" &&
      typeof row.seq === "number" &&
      typeof row.createdAt === "number"
    );
  });
}

// A reply another session delivered is posted twice: its own row and the relay's line that quotes
// it (agent-dm-post.ts, `sysn_dm_`). The line is the one drawn.
function inRelayLine(m: CyboDmLedgerRow, rows: readonly CyboDmLedgerRow[]): boolean {
  const words = m.text.trim();
  return rows.some(
    (o) =>
      o.id.startsWith("sysn_") &&
      o.id !== m.id &&
      Math.abs(o.createdAt - m.createdAt) <= LEDGER_TWIN_MS &&
      o.text.includes(words),
  );
}

// A row whose words a loaded reply already holds near its time is on screen under another id (one
// turn's replies glued into one bubble, or a same-words twin), so the floor's window never redraws it.
function drawnNear(agentId: string, m: CyboDmLedgerRow): boolean {
  const words = m.text.trim();
  return agentStreamState
    .getEntries(agentId)
    .some(
      (e) =>
        e.kind === "text" &&
        e.ts !== undefined &&
        Math.abs(e.ts - m.createdAt) <= LEDGER_TWIN_MS &&
        e.content.includes(words),
    );
}

function reportLedgerDeltaFailure(err: unknown, workspaceId: string, agentId: string): void {
  reportUiFailure("agents.dmLedger.deltaFailed", err, { workspaceId, agentId });
}

// Catch-up merge for a PROMOTED default session's page (fix/dm-view-renders-
// conversation-not-one-session): fetch the cybo↔human DM ledger and merge in
// every CYBO-authored message this session's own timeline doesn't already
// show — sent via a DIFFERENT session (e.g. an ephemeral autonomous self-wake
// calling cyborg7_send_message, message-router.ts's invokeCyboForDefaultSession).
//
// NO BOUNDARY (round 5, second production-forensics pass — REPLACES the
// "newest-first, stop-at-first-dedupe" walk from the first round-5 pass).
// Production proof: the promoted-default session a real report was gated on
// (`agent_bindings.is_default=true`, 329 turns since binding) had ZERO
// cyborg7_send_message calls in its ENTIRE history — its own narration is
// posted to a channel, never DMed — so NO ledger row could EVER share a
// messageId with anything in ITS timeline. The walk's "stop at the first
// already-loaded row" boundary can then never be found, so `toMerge` was `[]`
// on EVERY attempt, forever — the fix was a permanent no-op for exactly this
// (routine, not exotic) shape of promoted default. The walk's original
// justification (avoid blind-dumping this session's own older, not-yet-loaded
// replies) only protects rows that ARE already rendered — and those are
// PRECISELY the rows a plain messageId-dedupe already removes. A row that
// ISN'T already rendered is not a duplicate; it's the missing content, which
// is the entire bug. So: merge every cybo-authored, non-thread-reply,
// non-empty-text ledger row whose id isn't already in the loaded timeline —
// full stop, no walk, no "is it safe" gate.
//
// FLOOR, not a walk (round 6, finding #2 — bare id-dedupe re-appends already-
// seen history for a LONG DM): the mount hydrate loads a bounded RECENT tail
// (session.ts's 200-row page) while this scans a bounded ledger window (up to
// 150/1000 messages back) — for a cybo DM with 40+ turns, the ledger fetch can
// reach turns that scrolled OUT of the loaded tail. Those rows' ids are then
// no longer in `loadedIds`, so bare "merge everything not in loadedIds" (round
// 5) re-pastes a block of already-seen messages at the bottom with no human
// turns between them. The CEO's Sprite case never hits this — that session
// never authored a DM at all, so nothing in the fetch ever matches loadedIds
// either way — which is exactly why the acceptance test below stayed green
// while this regressed every OTHER long-lived DM. Fix: among the fetched
// candidate rows, find the highest ledger `seq` that can be POSITIVELY
// confirmed already loaded (its id IS in loadedIds) — call it the floor.
// Anything at or below the floor is presumed already-seen, even if that
// SPECIFIC row's id isn't in loadedIds (it scrolled out, but something newer
// that's still loaded proves this point in the conversation was reached).
// Only rows strictly NEWER than the floor are genuinely missing content.
// DEFAULT IS MERGE-ALL: when nothing fetched matches loadedIds at all (the
// Sprite shape), the floor stays undefined and every candidate merges — this
// must never flip to merge-nothing.
//
// Also excludes thread replies (parentId set — matches getDmMessages'
// isNull(parentId) root-only filter server-side; kept here too, defensively,
// for symmetry with the live "dm" handler which has no server-side filter to
// rely on) and empty rows; an image-only reply (`text: ""` plus its attachments) merges,
// and the pane draws its files.
//
// CALLER MUST STILL GATE ON isHydrated-OR-EMPTY (finding #3): this reads
// agentStreamState.getEntries(agentId) to decide what's already shown, and
// hydrateFromTimeline UNCONDITIONALLY resets entries to [] and rebuilds when
// it runs. Calling this before hydration completes (and the entries are
// non-empty from a PRIOR hydrate/live event) risks the merge being silently
// wiped the instant a slower timeline fetch lands. The EMPTY-entries case is
// exactly why "gate on isHydrated alone" was wrong: a failed hydrate (daemon
// down — "Agent not found in workspace") permanently left entries at [] AND
// hydrated at false, blocking the one source (the ledger, served straight
// from PG, no daemon involved) that WAS available. The caller gates on
// `isHydrated OR getEntries(id).length === 0`.
//
// BOUNDED BACKWARD PAGINATION: pages via fetchDmMessages' `before` cursor, up
// to a small budget (3 pages / 150 messages by default). No longer
// load-bearing for correctness now that there's no boundary to find — it only
// affects how far back a single catch-up reaches. If the budget is exhausted
// while the ledger still has older history (`hasMore` still true) AND no
// floor was ever established (round 6, finding #3 — "gate the banner on
// something genuinely UNRENDERED"), reports `moreHistoryAvailable: true`
// rather than silently capping — the caller surfaces a "Load full
// conversation" affordance (force: true, a much larger budget) as the
// recovery action. Once a floor IS established, everything past it is
// presumed already-seen, so the banner does not show even if `hasMore` stayed
// true at the page budget — a long-lived DM with 150+ messages of ordinary,
// already-loaded history is not "missing content", and showing the banner
// forever for every such DM (the pre-fix behavior) trained it to be ignored.
//
// ORDERING: merged rows are INTERLEAVED with the loaded timeline's own entries
// by real time, not appended after them. Each row is handed to
// mergeCrossSessionReply with its ledger `createdAt`, and that inserts at the
// position `StreamEntry.ts` gives it (see insertIndexForTs); the ledger-seq
// ascending sort below only settles ties and keeps a batch self-consistent.
//
// This comment previously asserted the opposite and told readers not to
// re-derive it: that merged rows COULD NOT be interleaved because "no
// StreamEntry variant carries a timestamp". That has been false since
// CYBORG-588 — `StreamEntry.ts` (:221) is folded from the served item's ISO
// `ts` (tsFromItem, :1650) and already drives the pane's day separators and
// hover times. Appending regardless is what put week-old ledger rows, under
// their own day separators, at the BOTTOM of the transcript where the pane
// auto-pins — read as "it opened on a week-old chat, not today". See
// mergeCrossSessionReply for the mechanism and the live-path argument.
//
// AgentStreamState.mergeCrossSessionReply does the actual idempotent append.
// Best-effort: a failed fetch leaves the session's own timeline exactly as
// correct as it already was — never surfaced as a page-level error.
// oxlint-disable-next-line eslint/complexity -- pagination, dedup, and retry coordination share one transaction
export async function mergeMissingCyboDms(
  client: DmMessagesFetchClient,
  agentId: string,
  workspaceId: string,
  cyboId: string,
  // force: "Load full conversation" — page much deeper (FULL_PAGE_BUDGET)
  // instead of the default shallow budget.
  // aroundId: an alert's target that fell out of the timeline window — ONE read around that
  // ledger row instead of the tail walk (knowledge/117), merged by the same primitive.
  opts?: { force?: boolean; aroundId?: string },
): Promise<MergeMissingCyboDmsResult> {
  const noop: MergeMissingCyboDmsResult = { moreHistoryAvailable: false };
  // An alert target is its own one-shot read: it must not queue behind (or be replaced by) a
  // tail walk's in-flight slot, or the focus loop it unblocks would wait on the wrong result.
  if (opts?.aroundId) {
    await mergeLedgerAround(client, agentId, workspaceId, cyboId, opts.aroundId);
    return noop;
  }
  if (mergeInFlight.has(agentId)) {
    // Queue exactly one retry (the latest caller wins) for when the in-flight
    // call finishes — see the guards' doc comment (finding #6). Round 6,
    // finding #3: this call does NOT resolve immediately with `noop` anymore
    // — it registers as a waiter and stays pending until the retry that will
    // run on its behalf actually settles, then resolves with THAT real
    // result. Every waiter queued behind the same agentId (a third, fourth,
    // ... caller all landing before the in-flight fetch settles) shares the
    // SAME eventual result, since only one retry ever actually runs.
    mergePendingRetry.set(agentId, () => {
      void mergeMissingCyboDms(client, agentId, workspaceId, cyboId, opts).then((r) => {
        const waiters = mergeRetryWaiters.get(agentId) ?? [];
        mergeRetryWaiters.delete(agentId);
        for (const resolve of waiters) resolve(r);
        return undefined;
      });
    });
    return new Promise<MergeMissingCyboDmsResult>((resolve) => {
      const waiters = mergeRetryWaiters.get(agentId) ?? [];
      waiters.push(resolve);
      mergeRetryWaiters.set(agentId, waiters);
    });
  }
  const flight = {};
  mergeInFlight.set(agentId, flight);
  let result: MergeMissingCyboDmsResult = noop;
  // mergeCrossSessionReply ensure()s: a merge landing after a clear()/rewind or a
  // workspace switch would recreate the stream and splice this ledger into the next
  // transcript (#3242). A stale walk writes nothing. The workspace is the one on screen,
  // not `workspaceId`: a switch away is what makes the merge stale.
  const stale = staleAfter(agentId, workspaceState.current?.id);
  try {
    const isTextEntry = (e: StreamEntry): e is StreamEntry & { kind: "text" } => e.kind === "text";
    const loadedIds = new Set(
      agentStreamState
        .getEntries(agentId)
        .filter(isTextEntry)
        .map((e) => e.messageId)
        .filter((id): id is string => id !== undefined),
    );
    // The oldest time the loaded timeline shows. The floor stands for rows that scrolled out
    // ABOVE it; a row at or after it that is not on screen was never drawn (another session's
    // DM between this one's replies: a check-back's answer), so the floor never hides it.
    const shownSince = Math.min(
      ...agentStreamState
        .getEntries(agentId)
        .flatMap((e) =>
          (e.kind === "text" || e.kind === "user_message") && e.ts !== undefined ? [e.ts] : [],
        ),
    );
    // The session's own start, when the server's whole timeline of it is here: a row before it
    // is the pair's earlier session (a switch of AI starts a fresh one), never drawn in this one.
    // Its own entries only: a row an earlier pass drew from the ledger is not where it starts.
    // An empty timeline shows no start (a first prompt not yet appended): nothing is `before`.
    const ownEntries = agentStreamState
      .getEntries(agentId)
      .flatMap((e) =>
        (e.kind === "text" || e.kind === "user_message") &&
        e.ts !== undefined &&
        !agentStreamState.isLedgerBuilt(e.id)
          ? [{ ...e, ts: e.ts }]
          : [],
      );
    const sessionStart = Math.min(...ownEntries.map((e) => e.ts));
    const wholeSession =
      agentStreamState.isServerHydrated(agentId) &&
      !agentStreamState.hasOlder(agentId) &&
      Number.isFinite(sessionStart);
    const before = (m: { createdAt: number }) => wholeSession && m.createdAt < sessionStart;
    // The session's first prompt, when it opens on one: its ledger row is stamped BEFORE it
    // (the relay insert precedes the daemon append; an edit or a slow append moves the entry
    // later still), so it is `before` yet this session's own turn.
    const openingPrompt = ownEntries.find((e) => e.ts === sessionStart);

    const pageBudget = opts?.force ? FULL_PAGE_BUDGET : DEFAULT_PAGE_BUDGET;
    // Every cybo-authored, non-thread-reply, non-empty-text ledger row fetched
    // this pass — a SUPERSET of what actually gets merged. Unlike round 5,
    // this no longer excludes `loadedIds` members at collection time: they're
    // needed to compute the floor below (finding #2), which subsumes the
    // plain id-exclusion (a row establishing the floor has seq <= floorSeq by
    // definition of floorSeq being their max, so it's excluded by the final
    // `seq > floorSeq` filter regardless).
    //
    // SEEDED FROM THE CACHE (CYBORG-987). The rows this same walk kept last time are
    // merged exactly like freshly-fetched ones — the floor below still decides what is
    // genuinely missing — and they are what lets the walk STOP once it reaches known
    // territory instead of re-paying the full page budget on every re-open. On a first
    // open the seed is empty and every line below behaves exactly as it did before.
    // KEYED BY ID (CYBORG-992), so a freshly-fetched row REPLACES the cached one it
    // shadows instead of being thrown away — see collectLedgerPage. The rest of this
    // function reads `candidates.values()`, and nothing downstream depends on their
    // order (the floor is a max, the merge sorts by seq, the writeback sorts by seq).
    const me = viewerCacheKeySegment(authState.user?.email);
    const ledgerKey = cacheEnabled() && me ? cyboDmLedgerKey(workspaceId, cyboId, me) : null;
    // PAGE 0's REQUEST IS ISSUED BEFORE THE CACHE READ IS AWAITED — the same rule
    // fetchAgentTimeline states, and load-bearing twice over here. A cache read must
    // never delay the network; and `getDB()`'s `onblocked` deliberately never settles
    // (CYBORG-992), so awaiting the read first could strand this walk holding
    // `mergeInFlight` forever, with every queued caller — a mount retry, a reconnect,
    // a "Load full conversation" click — parked behind a retry that can never fire.
    // Issuing it here also keeps the first RPC SYNCHRONOUS with the call, which is what
    // makes "one in-flight walk + exactly one queued retry" observable at all.
    //
    // The rejection is parked in the settled shape now so it cannot escape as an
    // unhandled rejection while the cache read is out; it is re-thrown at the await in
    // the loop, into the same catch as before.
    type SettledPage = { ok: true; page: DmMessagesPage } | { ok: false; err: unknown };
    const settle = (p: Promise<DmMessagesPage>): Promise<SettledPage> =>
      p.then(
        (page) => ({ ok: true as const, page }),
        (err: unknown) => ({ ok: false as const, err }),
      );
    const readTail = () => settle(client.fetchDmMessages(workspaceId, cyboId, { limit: 50 }));
    // CYBORG-1210: a synced ledger reads only the rows after its cached window.
    const ledgerSyncedAt =
      ledgerKey && !opts?.force ? (scopeSyncedAt(ledgerKey) ?? undefined) : undefined;
    let firstPage: Promise<SettledPage> | undefined =
      ledgerSyncedAt === undefined ? readTail() : undefined;
    // Bounded: this await sits inside mergeInFlight, so a blocked IndexedDB read
    // strands the guard and every queued waiter behind it, permanently.
    const cachedRows = ledgerKey
      ? parseCachedLedgerRows(
          // A miss yields null, parseCachedLedgerRows turns that into an empty list, and
          // the merge falls back to the network exactly as it did before the cache.
          // intentional: an absent cached ledger page is normal, not an error.
          await withTimeout(getPage(ledgerKey), CACHE_PAINT_TIMEOUT_MS).catch(() => null),
        )
      : [];
    let deltaFirst = false;
    if (ledgerSyncedAt !== undefined) {
      const plan = planOpen({
        cached: cachedRows,
        syncedAt: ledgerSyncedAt,
        hasUnread: false,
        now: Date.now(),
      });
      deltaFirst = plan.kind === "delta";
      firstPage =
        plan.kind === "delta"
          ? settle(client.fetchDmMessages(workspaceId, cyboId, { after: plan.after }))
          : readTail();
    }
    const cachedIds = new Set(cachedRows.map((r) => r.id));
    // A row with no author is the agent's (a window cached before the person's rows were kept).
    const isPersonRow = (r: CyboDmLedgerRow): boolean => !!r.fromId && r.fromId !== cyboId;
    const candidates = new Map<string, CyboDmLedgerRow>(
      cachedRows.filter((r) => !isPersonRow(r)).map((r) => [r.id, r]),
    );
    // The viewer's own rows: never candidates, but one that never became a turn, or that ran in
    // the pair's earlier session, has no timeline twin and is only ever drawn from here.
    const cachedPeople = new Map<string, CyboDmLedgerRow>(
      cachedRows.filter(isPersonRow).map((r) => [r.id, r]),
    );
    // Every row this walk actually SAW — the proof of what the fetch covered, and the
    // only thing that licenses dropping a cached row (CYBORG-979, see
    // reconcileLedgerWindow). HUMAN rows included: each page is a contiguous slice of
    // the whole DM, so they are part of the range that was covered even though none of
    // them is ever a candidate.
    const fetchedRows: { id: string; createdAt: number; seq: number }[] = [];
    let cursor: string | undefined;
    let pageBudgetExhaustedWithMore = false;
    // Which ENDS of that range the walk can speak for (CYBORG-979, LedgerCoverage).
    // Both start closed: an end is opened only by the page that proves it.
    const coverage = { reachesNewest: false, reachesOldest: false };
    for (let page = 0; page < pageBudget; page++) {
      let fetched: DmMessagesPage;
      if (firstPage) {
        let settled = await firstPage;
        firstPage = undefined;
        // A gap is counted relay-side (message_delta_read); a failed delta is reported here.
        if (deltaFirst && !settled.ok) reportLedgerDeltaFailure(settled.err, workspaceId, agentId);
        if (deltaFirst && (!settled.ok || isDeltaGap(settled.page))) {
          deltaFirst = false;
          settled = await readTail();
        }
        if (!settled.ok) throw settled.err;
        fetched = settled.page;
      } else {
        fetched = await client.fetchDmMessages(
          workspaceId,
          cyboId,
          cursor ? { limit: 50, before: cursor } : { limit: 50 },
        );
      }
      const { messages, hasMore } = fetched;
      fetchedRows.push(...messages);
      // Page 0 carries no cursor, so what it answers IS the server's newest slice.
      if (page === 0 && messages.length > 0) coverage.reachesNewest = true;
      const {
        cyboRows: pageCyboRows,
        rows: pageRows,
        alreadyCached: pageAlreadyCached,
      } = collectLedgerPage(messages, cyboId, cachedIds, candidates, cachedPeople);
      if (page === 0 && deltaFirst) {
        coverage.reachesNewest = true;
        pageBudgetExhaustedWithMore = false;
        break;
      }
      if (!hasMore || messages.length === 0) {
        pageBudgetExhaustedWithMore = false;
        // The server said there is nothing older. The other two exits below say no such
        // thing, so they leave this closed and rows beneath the walk stay untouched.
        coverage.reachesOldest = !hasMore;
        break;
      }
      // KNOWN TERRITORY — the whole reason a re-open costs ONE rpc instead of three.
      // Every row on this page is already in the cached window, so this walk has
      // caught up with the one that wrote it and paging deeper only re-fetches rows we
      // already hold. Requires at least one cybo row on the page: a page of purely
      // human messages proves nothing, and an EMPTY cache makes this permanently false,
      // so a first open (and the never-DM'd-itself shape this walk exists for) still
      // pages the full budget. `force` — the "Load full conversation" recovery — must
      // reach PAST the cached window, so it never takes this exit.
      if (pageCyboRows > 0 && pageAlreadyCached === pageRows && !opts?.force) {
        pageBudgetExhaustedWithMore = false;
        break;
      }
      // fetchDmMessages returns each page oldest-first (mirrors getDmMessages'
      // toReversed()) — the OLDEST row in THIS page is the cursor to page
      // further back (matches DefaultSessionView.svelte's own usage).
      cursor = messages[0]?.id;
      pageBudgetExhaustedWithMore = true;
      if (!cursor) break;
    }

    // The floor (finding #2): the highest seq among fetched rows whose id IS
    // in loadedIds — i.e. positively confirmed already rendered. `undefined`
    // when nothing fetched ever matches (the never-DM'd-itself shape), in
    // which case every candidate merges (merge-all stays the default).
    let floorSeq: number | undefined;
    // OFFLINE DELETE (CYBORG-979): before anything is merged or re-persisted, drop the
    // cached rows the fetch PROVED are gone — and ONLY those. A delete that happened
    // while this client was away never broadcast a `delete_message` into the cache, and
    // the server answers by simply omitting the row, so nothing else in this walk can
    // ever take it out. The rule reconcileLedgerWindow holds: a cached row goes only
    // when the fetched pages covered its position and did not contain it; a row outside
    // that range is merely un-fetched, not deleted, and is left exactly alone.
    const collected = reconcileLedgerWindow([...candidates.values()], fetchedRows, coverage);
    const people = reconcileLedgerWindow([...cachedPeople.values()], fetchedRows, coverage);
    const humanRows = new Map(people.map((m) => [m.id, { ...m, fromId: m.fromId ?? "" }]));
    for (const m of collected) {
      if (loadedIds.has(m.id) && (floorSeq === undefined || m.seq > floorSeq)) {
        floorSeq = m.seq;
      }
    }
    // A row at or below the floor whose id IS on screen still goes through: it is the
    // complete text mergeCrossSessionReply repairs a cut entry from (#3193 F1).
    const toMerge =
      floorSeq === undefined
        ? collected
        : // A card / `sysn_` notice is never in the session timeline: keep it (ledger-floor.ts).
          collected.filter(
            (m) =>
              survivesLedgerFloor(m) ||
              m.seq > floorSeq ||
              (before(m) && !inRelayLine(m, collected) && !drawnNear(agentId, m)) ||
              loadedIds.has(m.id) ||
              (m.createdAt >= shownSince && !drawnNear(agentId, m)),
          );

    // Ledger-seq ascending overall, regardless of per-page order, so the
    // merged batch appends in chronological order among itself.
    const sorted = toMerge.sort((a, b) => a.seq - b.seq);
    if (stale()) return noop;
    rowWork.setFromMessages(collected);
    // A human row is drawn from the ledger ONLY with proof it never ran (the daemon's refusal
    // line for its clientMsgId) or ran in the pair's earlier session (`before`). A missing
    // timeline twin proves nothing (it lands late), so that alone never draws; twin pairing
    // still guards the proven case (ledger-floor.ts).
    const refused = await refusedHumanRows(workspaceId, humanRows.values(), [
      ...fetchedRows,
      ...candidates.values(),
    ]);
    if (stale()) return noop;
    // That first prompt's row: the newest human row before the session, when nothing else lies
    // between it and the session's start, or it holds the prompt's own words (a held prompt
    // run again on the new AI).
    const lastBefore = [...humanRows.values()]
      .filter(before)
      .reduce<(CyboDmLedgerRow & { fromId: string }) | undefined>(
        (a, m) => (!a || m.createdAt > a.createdAt ? m : a),
        undefined,
      );
    const isOpeningRow = (m: { id: string }): boolean => {
      if (openingPrompt?.kind !== "user_message" || m.id !== lastBefore?.id) return false;
      if (lastBefore.text.trim() === openingPrompt.content.trim()) return true;
      return ![...fetchedRows, ...candidates.values()].some(
        (o) =>
          o.id !== lastBefore.id &&
          o.createdAt > lastBefore.createdAt &&
          o.createdAt < sessionStart,
      );
    };
    // No start yet, so no row is `before`: place them once the session's first own row lands.
    if (humanRows.size > 0 && !Number.isFinite(sessionStart)) {
      agentStreamState.whenSessionStarts(agentId, () => {
        if (workspaceState.current?.id !== workspaceId) return;
        void mergeMissingCyboDms(client, agentId, workspaceId, cyboId);
      });
    }
    for (const m of [...humanRows.values()].sort((a, b) => a.seq - b.seq)) {
      if (!refused.has(m.id) && (!before(m) || isOpeningRow(m))) continue;
      const author = viewerRowAuthor(m);
      if (agentStreamState.adoptLedgerTwin(agentId, "user_message", m, author)) continue;
      agentStreamState.mergeLedgerUserMessage(agentId, m.id, m.text, m.createdAt, author);
    }
    for (const m of sorted) {
      agentStreamState.mergeCrossSessionReply(
        agentId,
        m.id,
        m.text,
        m.createdAt,
        m.card,
        m.attachments,
      );
    }
    // Persist the window for the next open (newest rows win the cap). Written after a
    // SUCCESSFUL walk only — this line is inside the try, so a failed fetch leaves the
    // previous window untouched rather than replacing it with a partial one.
    if (ledgerKey && collected.length + people.length > 0) {
      const newest = [...collected, ...people]
        .sort((a, b) => b.seq - a.seq)
        .slice(0, MAX_CACHED_LEDGER_ROWS);
      void putPage({ key: ledgerKey, items: newest });
      if (!deltaFirst && coverage.reachesNewest) markScopeSynced(ledgerKey, Date.now());
    } else if (ledgerKey && cachedRows.length > 0) {
      // Reconcile emptied a window that HAD rows. putPage refuses an empty page, so
      // without this the deleted rows stay on disk and are re-read (and re-dropped)
      // forever; the delete has to reach the store, not just the render.
      void deletePage(ledgerKey);
    }
    // moreHistoryAvailable (finding #3): only when the budget was genuinely
    // exhausted with `hasMore` still true AND no floor was ever established —
    // once a floor exists, anything further back is presumed already-seen, so
    // there is nothing genuinely UNRENDERED to recover with "Load full
    // conversation", regardless of how much more history the ledger has.
    result = {
      moreHistoryAvailable: pageBudgetExhaustedWithMore && floorSeq === undefined && !opts?.force,
    };
  } catch (err) {
    // Best-effort catch-up (see doc comment above): reported, never surfaced as a page error.
    reportUiFailure("agents.mergeMissingCyboDms", err, { workspaceId, agentId });
  } finally {
    // A reset while this was out: the slot and its queue belong to the next merge.
    if (mergeInFlight.get(agentId) === flight) settleMerge(agentId);
  }
  return result;
}

// Who signs a viewer-side ledger row: roster email, else the viewer's own account, else the
// row's own name, so the bubble is never unattributed.
function viewerRowAuthor(m: { fromId: string; fromName?: string }): PromptAuthor | undefined {
  const email =
    workspaceState.members?.find((u) => u.userId === m.fromId)?.email ??
    (m.fromId === authState.user?.id ? authState.user.email : undefined);
  if (email) return { kind: "human", email };
  return m.fromName ? { kind: "agent", name: m.fromName } : undefined;
}

// ONE read around an alert's target (knowledge/117), BOTH sides of the DM: the peer's rows go
// through the cybo merge primitive, the viewer's own as the prompt bubble they are. `peerId`
// is cyboId ?? agentId (the server's canonicalDmPeerId), so a plain session has a ledger too.
async function mergeLedgerAround(
  client: DmMessagesFetchClient,
  agentId: string,
  workspaceId: string,
  peerId: string,
  aroundId: string,
): Promise<void> {
  const stale = staleAfter(agentId, workspaceState.current?.id);
  try {
    const { messages } = await client.fetchDmMessages(workspaceId, peerId, { aroundId });
    if (stale()) return;
    // A forward sent with no note has no text, and is still a turn: its card is the content.
    const rows = messages
      .filter(
        (m) =>
          !m.parentId &&
          (m.text.trim() !== "" ||
            (m.fromId === peerId && !!m.attachments?.length) ||
            m.card?.kind === "forwarded"),
      )
      .sort((a, b) => a.seq - b.seq);
    for (const m of rows) {
      forwardedTurnCards.note(m);
      const mine = m.fromId !== peerId;
      // Sent by a session or agent for the viewer: the timeline's "From" row is the only one drawn.
      const relayed = m.card?.kind === "handoff";
      const author = mine && !relayed ? viewerRowAuthor(m) : undefined;
      // The window's old edge can overlap the timeline: the same message is already there
      // under its provider id, and a second bubble under the ledger id is a duplicate.
      if (agentStreamState.adoptLedgerTwin(agentId, mine ? "user_message" : "text", m, author))
        continue;
      if (relayed) continue;
      if (mine) {
        agentStreamState.mergeLedgerUserMessage(agentId, m.id, m.text, m.createdAt, author);
      } else {
        agentStreamState.mergeCrossSessionReply(
          agentId,
          m.id,
          m.text,
          m.createdAt,
          m.card,
          m.attachments,
        );
      }
    }
  } catch (err) {
    reportUiFailure("agents.mergeLedgerAround", err, { workspaceId, agentId });
  }
}

function settleMerge(agentId: string): void {
  mergeInFlight.delete(agentId);
  const retry = mergePendingRetry.get(agentId);
  if (retry) {
    mergePendingRetry.delete(agentId);
    retry();
  }
}

// A page that starts inside a reply is paged back from its cursor, or held stalled when
// nothing older is readable (#3239); it is never cached. One rule for the route open, the
// reconnect catch-up and a scroll-up page. Returns whether the head is partial.
export function pageBackIfHeadPartial(
  client: TimelineFetchClient,
  agentId: string,
  page: Pick<
    Awaited<ReturnType<TimelineFetchClient["fetchAgentTimeline"]>>,
    "items" | "hasOlder" | "headPartial" | "olderCursor"
  >,
  accepted: boolean,
  daemonId: string | undefined,
): boolean {
  if (!accepted) return false;
  if (!pageHeadCut(page)) {
    // A whole page reaching an earlier cut covers it: the reply is on screen from its start.
    agentStreamState.resolveHeadCut(agentId, firstRowSeq(page.items));
    return false;
  }
  agentStreamState.markHeadCut(agentId, firstRowSeq(page.items), page.olderCursor);
  // The screen already held this reply from its first chunk: nothing to read back.
  if (!agentStreamState.headCutOpen(agentId)) return true;
  // The SAME recovery the reconcile uses (#3248 G4): page back in bounded passes until
  // the reply starts at its first chunk, visibly pending meanwhile — not one page.
  if (page.olderCursor) {
    void runTranscriptRecovery(
      client,
      agentId,
      page.olderCursor,
      agentStreamState.generationOf(agentId),
      daemonId,
    );
  } else {
    // Nothing older to read: the open cut itself holds the recovery stalled, so a later
    // page that covers it releases it (a pass-set "stalled" would outlive the cut).
    agentStreamState.setTranscriptRecovery(agentId, "idle", null);
  }
  return true;
}

// The server page, once it has arrived: what to skip, how to hydrate, whether to page
// back for a cut head, and what to cache. Split from fetchAgentTimeline so each rule
// reads on its own.
function applyServerTimelinePage(
  client: TimelineFetchClient,
  agentId: string,
  page: Awaited<ReturnType<TimelineFetchClient["fetchAgentTimeline"]>>,
  cacheKey: string | null,
  daemonId: string | undefined,
): boolean {
  const { items, hasOlder, olderCursor, maxSeq, source, life } = page;
  const incomplete = pageIncomplete(page);
  // Re-read both flags at COMMIT time. A route load and reconnect catch-up can overlap;
  // either may have accepted server truth while this request was in flight. A
  // still-cache-painted stream is one exception: it is hydrated, but only by the
  // unverified snapshot this request exists to replace. A mirror-hydrated stream is the
  // other: a copy, outranked by the daemon's own page (#3193 F3).
  const mirrorHydrated = agentStreamState.isMirrorHydrated(agentId);
  if (agentStreamState.isServerHydrated(agentId) && !mirrorHydrated) return true;
  const cachePainted = agentStreamState.isCachePainted(agentId);
  if (!cachePainted && !mirrorHydrated && agentStreamState.isHydrated(agentId)) return true;
  const copyTail = agentStreamState.copyTailSeq(agentId);
  const accepted = agentStreamState.hydrateFromTimeline(agentId, items, {
    hasOlder,
    olderProven: olderProof(page),
    olderCursor,
    maxSeq,
    // The server page outranks OUR OWN cached snapshot and a mirror page. Without this a
    // cold resume (seq restarts at 1) would leave the monotonic guard reading the fresh
    // page as "behind" the cached one and the cache would stay on screen indefinitely.
    // force still cannot let an EMPTY page wipe a populated view — that rule is checked
    // first (timeline-monotonic-guard.ts). A MIRROR page itself is the exception: it can
    // lag what the cache painted live, so it faces the guard like any snapshot.
    source,
    life,
    mode: (cachePainted || mirrorHydrated) && source !== "mirror" ? "refresh" : undefined,
    incomplete,
    hasNewer: page.hasNewer,
    headPartial: page.headPartial,
    budgetCut: page.budgetCut,
  });
  if (accepted) agentStreamState.noteHoleOverCopy(agentId, copyTail, items, page);
  const headMayBePartial = pageBackIfHeadPartial(client, agentId, page, accepted, daemonId);
  const forward = page.hasNewer === true && !!page.nextCursor;
  if (accepted && forward) newerWindows.open(agentId, page.nextCursor as string);
  else if (accepted) newerWindows.close(agentId);
  // A successful RPC can still carry a stale page. Do not replace the disk copy with
  // rows the stream rejected, or the next mount loses the protected history. Nor with a
  // page that is not the whole history (or not its tail): a cold paint would show it as the conversation.
  if (accepted && cacheKey && !headMayBePartial && !incomplete && !forward) {
    void putPage({ key: cacheKey, items, hasOlder, olderCursor, maxSeq, life });
  }
  return accepted;
}

// One transcript recovery per agent at a time (#3248 C4): a second trigger while one
// runs asks for a fresh pass from the tail once it ends, never a second chain.
const recoveryRuns = conversationSync.collection(
  "recoveryRuns",
  new Map<string, { gen: number; rerun: boolean }>(),
);
const RECONCILE_PAGES_PER_PASS = 5;
const MAX_RECOVERY_RETRIES = 5;

// Another durable life (a rewind elsewhere) is taken whole, never merged into this one.
// One adoption per agent at a time: every live row of the new life asks for one. The
// frames that arrive meanwhile are held and replayed, in order, only once it ADOPTED.
const heldWhileAdopting = conversationSync.collection(
  "heldWhileAdopting",
  new Map<string, Array<{ replay: () => void; life?: string }>>(),
);
// Frames an unproven adoption could not place, carried into the next attempt (bounded).
const carriedWhileAdopting = conversationSync.collection(
  "carriedWhileAdopting",
  new Map<string, Array<{ replay: () => void; life?: string }>>(),
);
const CARRIED_FRAMES_MAX = 500;
// An adoption that did not land (a failed read, a mirror page with no life) is retried
// with a growing delay, and asks made meanwhile are refused: never a loop of reads.
const adoptionBackoff = conversationSync.collection(
  "adoptionBackoff",
  new Map<string, { until: number; attempt: number }>(),
);
const ADOPT_RETRY_BASE_MS = 2_000;
const ADOPT_RETRY_MAX_MS = 60_000;

// Held frames past CARRIED_FRAMES_MAX: rows with a seq are dropped (the durable timeline
// has them) and their range read back; every frame without one is kept, in arrival order,
// and placed once that range is on screen. While this exists every frame of the agent
// waits here — the read-back included — so nothing lands out of order.
interface HeldOverflow {
  // The life these rows belong to: a row of any other life is not part of this range.
  life: string | undefined;
  from: number;
  to: number;
  trailing: Array<() => void>;
}
const overflowWhileAdopting = conversationSync.collection(
  "overflowWhileAdopting",
  new Map<string, HeldOverflow>(),
);
// Live deltas with no seq (streamed text not yet committed) held for an adoption that
// keeps failing grew without limit. Past this many, further ones are not held: the rows
// they preview are durable, so the agent is marked to reconcile once its frames are placed.
const HELD_DELTAS_MAX = 2_000;
const heldDeltas = conversationSync.collection("heldDeltas", new Map<string, number>());
const deltasDropped = conversationSync.collection("deltasDropped", new Set<string>());
const retiredByReset = conversationSync.collection("retiredByReset", new Map<string, string>());

// A seq-less delta, held if there is room; one past the bound is dropped and remembered.
function holdDelta(agentId: string, hold: () => void): HoldOutcome {
  const count = heldDeltas.get(agentId) ?? 0;
  if (count >= HELD_DELTAS_MAX) {
    deltasDropped.add(agentId);
    return "dropped-delta";
  }
  heldDeltas.set(agentId, count + 1);
  hold();
  return "held-delta";
}

// What deferFrame did with a frame: journalled once per frame, as a run.
type HoldOutcome = "held-row" | "held-delta" | "held-event" | "dropped-delta" | "overflow-row";

// Every frame of the agent is placed: the bound starts over, and what it dropped is read
// back from the durable rows.
function settleHeldDeltas(client: TimelineFetchClient, agentId: string): void {
  heldDeltas.delete(agentId);
  if (!deltasDropped.delete(agentId)) return;
  // Owed until a reconcile after the turn reads the committed rows (see deltaDebt).
  agentStreamState.noteDroppedDeltas(agentId);
  void reconcileAgentTranscript(client, agentId);
}
// The agent whose held frames are being placed right now: they must not be held again.
let placingFor: string | null = null;
function place(agentId: string, frames: ReadonlyArray<() => void>): void {
  journal(agentId, "adoption", { outcome: "placed", frames: frames.length });
  placingFor = agentId;
  try {
    for (const replay of frames) replay();
  } finally {
    placingFor = null;
  }
}
// Bumped by a reset, never rewound: a retry scheduled before it belongs to streams that no
// longer exist.
let adoptionEpoch = 0;
conversationSync.own("lifeAdoptions", {
  reset: () => {
    adoptionEpoch += 1;
  },
});

// One conversation's adoption state: an adoption in flight finds its queue gone and takes
// nothing, like after a reset of conversationSync.
function forgetLifeAdoption(agentId: string): void {
  heldWhileAdopting.delete(agentId);
  carriedWhileAdopting.delete(agentId);
  overflowWhileAdopting.delete(agentId);
  heldDeltas.delete(agentId);
  deltasDropped.delete(agentId);
  retiredByReset.delete(agentId);
  for (const key of adoptionBackoff.keys()) {
    if (key.startsWith(`${agentId}\t`)) adoptionBackoff.delete(key);
  }
}

/** True (and the frame held for replay) while `agentId` is adopting another life. */
export function deferWhileAdopting(
  agentId: string,
  replay: () => void,
  life?: string,
  seq?: number,
  // A streamed delta (a timeline item) rather than a turn/control event: the only kind a
  // full hold may drop, since the committed rows carry what it previews.
  delta = false,
): boolean {
  if (placingFor === agentId) return false;
  const outcome = deferFrame(agentId, replay, life, seq, delta);
  if (!outcome) return false;
  journalRun(agentId, "adoption", outcome, seq);
  return true;
}

function deferFrame(
  agentId: string,
  replay: () => void,
  life: string | undefined,
  seq: number | undefined,
  delta: boolean,
): HoldOutcome | false {
  const over = overflowWhileAdopting.get(agentId);
  // A row of yet another life ends this recovery: its range and queue belong to a life
  // already rewound, and the row must reach the listener to start adopting its own.
  if (over && life !== undefined && over.life !== undefined && life !== over.life) {
    overflowWhileAdopting.delete(agentId);
  } else if (over) {
    if (seq === undefined && delta) return holdDelta(agentId, () => over.trailing.push(replay));
    if (seq === undefined) {
      over.trailing.push(replay);
      return "held-event";
    }
    Object.assign(over, { from: Math.min(over.from, seq), to: Math.max(over.to, seq) });
    return "overflow-row";
  }
  const held = heldWhileAdopting.get(agentId) ?? carriedWhileAdopting.get(agentId);
  if (!held) return false;
  if (held.length >= CARRIED_FRAMES_MAX && seq !== undefined) {
    overflowWhileAdopting.set(agentId, { life, from: seq, to: seq, trailing: [] });
    return "overflow-row";
  }
  if (seq === undefined && delta) return holdDelta(agentId, () => held.push({ replay, life }));
  held.push({ replay, life });
  return seq === undefined ? "held-event" : "held-row";
}

// "same": the page proved the life on screen is still the current one (nothing to adopt).
type AdoptOutcome = "adopted" | "same" | "unknown" | "lifeless" | "stale";

async function readNewLife(
  client: TimelineFetchClient,
  wsId: string,
  agentId: string,
  daemonId: string | undefined,
  stale: () => boolean,
): Promise<AdoptOutcome> {
  try {
    const page = await client.fetchAgentTimeline(
      wsId,
      agentId,
      daemonId ? { daemonId } : undefined,
    );
    if (stale()) return "stale";
    bindIfUnbound(agentId, wsId);
    // Only a page that proves another life may replace: not a mirror page, not ours again.
    if (page.life === undefined) return "lifeless";
    if (!agentStreamState.lifeReplaces(agentId, page.life)) return "same";
    const accepted = agentStreamState.hydrateFromTimeline(agentId, page.items, {
      hasOlder: page.hasOlder,
      olderProven: olderProof(page),
      olderCursor: page.olderCursor,
      maxSeq: page.maxSeq,
      source: page.source,
      life: page.life,
      mode: "rewind",
      incomplete: pageIncomplete(page),
      retired: lifeRetired(page),
    });
    if (!accepted) return "unknown";
    // A new life can open inside a reply like any page (#3239); depth is no substitute.
    pageBackIfHeadPartial(client, agentId, page, accepted, daemonId);
    void loadRecentTurns(client, agentId, daemonId);
    return "adopted";
  } catch (err) {
    reportUiFailure("agents.adoptNewLife", err, { agentId });
    return stale() ? "stale" : "unknown";
  }
}

// The relay's reset retired the view's life (knowledge/125): a life-less answer may drop it.
export function adoptRetiredLife(client: TimelineFetchClient, agentId: string, life: string) {
  retiredByReset.set(agentId, life);
  return adoptNewLife(client, agentId, undefined, life);
}

export async function adoptNewLife(
  client: TimelineFetchClient,
  agentId: string,
  daemonId?: string,
  // The life that asked, when known: a different life is not held back by this one's delay.
  life?: string,
  now: () => number = Date.now,
): Promise<void> {
  const backoff = adoptionBackoff.get(`${agentId}\t${life ?? ""}`);
  if (backoff && now() < backoff.until) return;
  const route = daemonId ?? agentStreamState.routeOf(agentId);
  await attemptAdoption(client, agentId, route, life, now);
}

// The rows dropped past the bound are durable: once the life is placed, read them back
// until EVERY one of them is on screen, then place the frames held after them. A stream
// that moved on drops it all; one never covered is marked stalled (explicit, retryable).
const OVERFLOW_READ_ATTEMPTS = 4;
async function readBackOverflow(
  client: TimelineFetchClient,
  agentId: string,
  stale: () => boolean,
): Promise<void> {
  // This read-back's own range: a reset or a newer recovery of the agent is not its to end.
  const op = overflowWhileAdopting.get(agentId);
  if (!op) return;
  const owns = () => overflowWhileAdopting.get(agentId) === op;
  const release = () => {
    if (owns()) overflowWhileAdopting.delete(agentId);
  };
  for (let attempt = 0; attempt < OVERFLOW_READ_ATTEMPTS; attempt += 1) {
    if (!owns()) return;
    if (!stale()) await reconcileAgentTranscript(client, agentId);
    if (!owns()) return;
    if (stale()) return release();
    if (agentStreamState.appliedAll(agentId, op.from, op.to)) {
      release();
      place(agentId, op.trailing);
      settleHeldDeltas(client, agentId);
      return;
    }
    await sleep(ADOPT_RETRY_BASE_MS * 2 ** attempt);
  }
  if (!owns()) return;
  release();
  if (stale()) return;
  agentStreamState.setTranscriptRecovery(agentId, "stalled");
  place(agentId, op.trailing);
  settleHeldDeltas(client, agentId);
  reportUiFailure(
    "agents.adoptNewLife",
    `rows ${op.from}..${op.to} dropped while adopting another life were not read back`,
    { agentId },
  );
}

async function attemptAdoption(
  client: TimelineFetchClient,
  agentId: string,
  daemonId: string | undefined,
  life: string | undefined,
  now: () => number,
): Promise<void> {
  const wsId = workspaceState.current?.id;
  // A late frame of a conversation in another workspace never reads from this one.
  const bound = agentStreamState.workspaceForAgent(agentId);
  if (!wsId || (bound !== null && bound !== wsId)) return;
  if (heldWhileAdopting.has(agentId)) return;
  const key = `${agentId}\t${life ?? ""}`;
  const held = carriedWhileAdopting.get(agentId) ?? [];
  heldWhileAdopting.set(agentId, held);
  carriedWhileAdopting.delete(agentId);
  const epoch = adoptionEpoch;
  let stale = staleAfter(agentId, wsId);
  const outcome = await readNewLife(client, wsId, agentId, daemonId, stale);
  // A reset meanwhile makes every queue a newer operation's: this one takes nothing.
  if (epoch !== adoptionEpoch || heldWhileAdopting.get(agentId) !== held) return;
  heldWhileAdopting.delete(agentId);
  // Stale: the frames belong to a view no longer shown; the next open reads them.
  if (outcome === "stale") {
    overflowWhileAdopting.delete(agentId);
    heldDeltas.delete(agentId);
    deltasDropped.delete(agentId);
    return;
  }
  // A frame is placed only on the life it belongs to. Adopted: all of them. The page
  // proved the life on screen ("same"): those naming no life, or this one. Unknown: only
  // those naming this life — no life is not "the same". The rest wait for the retry.
  const placeable = (f: { life?: string }): boolean => {
    if (outcome === "adopted") return true;
    if (f.life === undefined) return outcome === "same";
    return !agentStreamState.lifeDiffers(agentId, f.life);
  };
  const carried = held.filter((f) => !placeable(f));
  place(
    agentId,
    held.filter(placeable).map((f) => f.replay),
  );
  const retired = retiredByReset.get(agentId);
  if (outcome === "lifeless" && dropViewOfOldLife(agentStreamState, agentId, retired)) {
    retiredByReset.delete(agentId);
    stale = staleAfter(agentId, wsId);
  }
  // Done once the life is placed and nothing waits; an unknown answer always retries.
  if (carried.length === 0 && outcome !== "unknown" && outcome !== "lifeless") {
    adoptionBackoff.delete(key);
    retiredByReset.delete(agentId);
    // With no overflow the frames just placed were all of them; with one, its read-back
    // places the rest and settles.
    if (!overflowWhileAdopting.has(agentId)) settleHeldDeltas(client, agentId);
    // The adoption's rewind started a new generation: the read-back belongs to that one.
    void readBackOverflow(client, agentId, staleAfter(agentId, wsId));
    return;
  }
  carriedWhileAdopting.set(agentId, carried);
  const attempt = (adoptionBackoff.get(key)?.attempt ?? 0) + 1;
  const delay = Math.min(ADOPT_RETRY_BASE_MS * 2 ** (attempt - 1), ADOPT_RETRY_MAX_MS);
  adoptionBackoff.set(key, { until: now() + delay, attempt });
  setTimeout(() => {
    if (epoch !== adoptionEpoch) return;
    if (stale()) {
      // Only its own queue: a later conversation under this id may have one now.
      if (carriedWhileAdopting.get(agentId) === carried) carriedWhileAdopting.delete(agentId);
      return;
    }
    void attemptAdoption(client, agentId, daemonId, life, now);
  }, delay);
}

// One pass of transcript recovery for `gen`: from `from` (the tail when null), page back
// while the reply's head is still partial, merging EACH page as it lands so confirmed
// progress is never lost, and recording the cursor the next page resumes from. The
// machine (AgentStream.recovery) says what the screen may claim: a pass that runs out
// of its page budget stays `pending` and arms the next pass; a rejected page leaves it
// `failed` and arms a bounded retry from the recorded cursor; a cursor that does not
// advance with the head still partial leaves it `stalled` (no automatic work, an
// explicit retry resumes); only a page whose head is whole ends in `idle`. A
// clear()/rewind/workspace switch makes the rest of the pass someone else's (B4).
// One recovery page: the tail when `cursor` is null, else the page older than it. A read
// from the stream's own pagination cursor CONTINUES its chain, and the page then
// advances hasOlder/olderCursor too, so scroll-up never re-reads it.
async function readRecoveryPage(
  client: TimelineFetchClient,
  wsId: string,
  agentId: string,
  cursor: string | null,
  daemonId: string | undefined,
): Promise<
  Awaited<ReturnType<TimelineFetchClient["fetchAgentTimeline"]>> & { continuesChain: boolean }
> {
  const continuesChain = cursor !== null && cursor === agentStreamState.olderCursor(agentId);
  const route = daemonId ? { daemonId } : {};
  const page = await (cursor
    ? client.fetchAgentTimeline(wsId, agentId, { cursor, direction: "older", ...route })
    : client.fetchAgentTimeline(wsId, agentId, daemonId ? { daemonId } : undefined));
  return { ...page, ...olderPagination(page), continuesChain };
}

// The olderCursor's epoch; "committed" when a one-page history left none on a screen with a
// durable life (its daemon reads a cursor by seq); undefined for an older daemon's epochless one.
function screenCursorEpoch(agentId: string): string | undefined {
  const older = agentStreamState.olderCursor(agentId);
  if (older !== null) return cursorEpoch(older);
  if (forwardRefused.has(forwardKey(agentId))) return undefined;
  return agentStreamState.heldLife(agentId) === null ? undefined : "committed";
}

// Conversations (per generation) whose daemon answered a "committed" cursor with something
// other than the rows after it (an older daemon's tail): it is not asked that way again.
const forwardRefused = conversationSync.collection("forwardRefused", new Set<string>());
const forwardKey = (agentId: string) => `${agentId}\t${agentStreamState.generationOf(agentId)}`;

// The cursor for "everything after the newest row on screen", or null when there is none.
function deltaCursor(agentId: string): string | null {
  const top = agentStreamState.highestHeldSeq(agentId);
  const epoch = screenCursorEpoch(agentId);
  if (top === undefined || epoch === undefined) return null;
  return JSON.stringify({ epoch, seq: top });
}

function refusedForward(agentId: string): false {
  forwardRefused.add(forwardKey(agentId));
  return false;
}

// A forward answer to "after `held`": its rows start right after it, none at or below it.
function forwardAfter(items: ReadonlyArray<Record<string, unknown>>, held: number): boolean {
  const oldest = oldestSeq(items);
  return oldest === undefined || oldest === held + 1;
}

function seqOfCursor(cursor: string): number | undefined {
  try {
    const seq = (JSON.parse(cursor) as { seq?: unknown }).seq;
    return typeof seq === "number" ? seq : undefined;
  } catch {
    // intentional: an unreadable cursor names no seq; the page is then taken as it comes
    return undefined;
  }
}

// Forward pages after `cursor` (the legacy "after" read every daemon serves), merged as
// they land. True once the daemon says nothing newer remains; false when the budget ran
// out first or a page did not advance. A page from another life hands off to adoptNewLife.
async function readDeltaPages(
  client: TimelineFetchClient,
  wsId: string,
  agentId: string,
  cursor: string,
  daemonId: string | undefined,
  stale: () => boolean,
): Promise<boolean> {
  let at = cursor;
  let held = agentStreamState.highestHeldSeq(agentId) ?? seqOfCursor(cursor);
  for (let read = 0; read < RECONCILE_PAGES_PER_PASS; read += 1) {
    const page = await client.fetchAgentTimeline(wsId, agentId, {
      cursor: at,
      life: agentStreamState.heldLife(agentId),
      ...(daemonId ? { daemonId } : {}),
    });
    if (stale()) return false;
    // A page from another durable life is a rewind made elsewhere: take it whole, never
    // merge it into this one (the tail read below does the same).
    if (pageTakesAnotherLife(agentId, page)) {
      void adoptNewLife(client, agentId, daemonId);
      return true;
    }
    // Not the rows right after the screen (a hole, or rows it holds: a tail or a mirror page
    // answered the cursor): the tail read covers it, with its own rules.
    if (held !== undefined && !forwardAfter(page.items, held)) return refusedForward(agentId);
    held = Math.max(held ?? 0, ...page.items.map((r) => (typeof r.seq === "number" ? r.seq : 0)));
    // A chain's page is not the turn's end: only its last page closes the turn (one card).
    agentStreamState.mergeDurableRows(agentId, page.items, rosterLifecycle(agentId), { more: page.hasMore }); // prettier-ignore
    markDeltaHeadCut(agentId, page);
    if (!page.hasMore) return true;
    if (!page.nextCursor || page.nextCursor === at) return false;
    at = page.nextCursor;
  }
  return false;
}

// A reconnect asks only for the rows after the newest one on screen, like a turn's end
// (CYBORG-1193). True when that settled it; false when there is no honest cursor or the
// read did not cover everything, and the caller reads the tail as before.
export async function catchUpTimelineByDelta(
  client: TimelineFetchClient,
  wsId: string,
  agentId: string,
  daemonId?: string,
): Promise<boolean> {
  let at = deltaCursor(agentId);
  let held = agentStreamState.highestHeldSeq(agentId);
  // Held durable rows are read forward only, however shallow the view (CYBORG-1207): a
  // byte-capped page can hold fewer than RECENT_PROMPTS, so a depth test re-crawled forever.
  if (!at || held === undefined) return false;
  const stale = staleAfter(agentId, wsId);
  for (let read = 0; read < RECONCILE_PAGES_PER_PASS; read += 1) {
    const page = await client.fetchAgentTimeline(wsId, agentId, {
      cursor: at,
      life: agentStreamState.heldLife(agentId),
      ...(daemonId ? { daemonId } : {}),
    });
    if (stale()) return true;
    if (pageTakesAnotherLife(agentId, page)) {
      void adoptNewLife(client, agentId, daemonId);
      return true;
    }
    // Not the rows right after the screen (a restarted daemon answers an unknown epoch with
    // a reset tail page; a mirror may answer its tail): the tail path reads it, with its rules.
    if (!forwardAfter(page.items, held)) return refusedForward(agentId);
    // A chain's page is not the turn's end: only its last page closes the turn (one card).
    agentStreamState.mergeDurableRows(agentId, page.items, rosterLifecycle(agentId), { more: page.hasMore }); // prettier-ignore
    markDeltaHeadCut(agentId, page);
    if (!page.hasMore) return true;
    if (!page.nextCursor || page.nextCursor === at) return false;
    at = page.nextCursor;
    for (const row of page.items) {
      const seq = row.seq;
      if (typeof seq === "number" && Number.isSafeInteger(seq) && seq > held) held = seq;
    }
  }
  return false;
}

function reportRecoveryFailure(agentId: string, err: unknown): void {
  reportUiFailure("agents.reconcileAgentTranscript", err, { agentId });
}

// A refused recovery read latches the same mark the mount read obeys and is never retried:
// a cache paint makes a refused stream "hydrated", so these paths re-asked it per trigger.
function latchRefusedRead(agentId: string, err: unknown, read: RefusedRead): boolean {
  if (!isSessionReadDenied(err)) return false;
  if (agentStreamState.streams.has(agentId) && agentStreamState.noteTimelineReadDenied(agentId)) {
    reportSessionReadRefused(read, err, { agentId });
  }
  return true;
}

type RefusedRead = "catch_up" | "session_work" | "recovery" | "gap" | "older";

// Reported once, when the latch first closes: the latch stops the asking, so there is no storm.
function reportSessionReadRefused(
  read: RefusedRead,
  err: unknown,
  ids: { workspaceId?: string; agentId?: string; sessionId?: string },
): void {
  reportUiFailure("agents.session_read_refused", err, { ...ids, read });
}

// A refusal ends the recovery with no retry and nothing left spinning; any other failure
// is reported and retried from `cursor` with backoff.
function onRecoveryPageFailed(
  agentId: string,
  err: unknown,
  cursor: string | null,
  retry: () => void,
): void {
  if (latchRefusedRead(agentId, err, "recovery")) {
    agentStreamState.setTranscriptRecovery(agentId, "idle");
    return;
  }
  reportRecoveryFailure(agentId, err);
  agentStreamState.setTranscriptRecovery(agentId, "failed", cursor);
  agentStreamState.armRecoveryRetry(agentId, MAX_RECOVERY_RETRIES, retry);
}

// A delta answered by a reset tail page can open inside a reply (#3239). Only an explicit
// flag: an ordinary delta opens on the continuation of a reply already on screen.
function markDeltaHeadCut(
  agentId: string,
  page: Pick<
    Awaited<ReturnType<TimelineFetchClient["fetchAgentTimeline"]>>,
    "items" | "headPartial" | "olderCursor"
  >,
): void {
  if (page.headPartial !== true) return;
  agentStreamState.markHeadCut(agentId, firstRowSeq(page.items), page.olderCursor);
}

// "settled" when the delta read ended this pass: it covered everything (the recovery is
// idle, or stalled on a cut head nothing reads back) or the world it was read for is
// gone. "cut": covered, but a reply cut at its head is still open and has a cursor — newer
// rows say nothing about it (#3239). "unfinished": the tail read goes on as before.
async function deltaSettles(
  client: TimelineFetchClient,
  wsId: string,
  agentId: string,
  cursor: string,
  daemonId: string | undefined,
  stale: () => boolean,
): Promise<"settled" | "cut" | "unfinished"> {
  const readAfterTurn = turnIsOver(agentId);
  const debtRevision = agentStreamState.deltaDebtRevision(agentId);
  const covered = await readDeltaPages(client, wsId, agentId, cursor, daemonId, stale);
  if (stale()) return "settled";
  if (!covered) return "unfinished";
  // A covered delta read is a read of the tail.
  agentStreamState.noteTailRead(agentId, readAfterTurn, debtRevision);
  if (agentStreamState.headCutCursor(agentId)) return "cut";
  agentStreamState.setTranscriptRecovery(agentId, "idle");
  agentStreamState.noteTailCovered(agentId);
  return "settled";
}

// Whether the agent's turn is over now (the daemon roster's word counts too).
function turnIsOver(agentId: string): boolean {
  return !agentStreamState.isTurnActive(agentId, rosterLifecycle(agentId));
}

// Release this pass's run slot; a pass asked for while it ran starts fresh from the tail.
function finishRecoveryRun(
  client: TimelineFetchClient,
  agentId: string,
  run: { gen: number; rerun: boolean },
  daemonId: string | undefined,
): void {
  if (recoveryRuns.get(agentId) !== run) return;
  recoveryRuns.delete(agentId);
  if (!run.rerun) return;
  setTimeout(() => {
    void runTranscriptRecovery(client, agentId, null, run.gen, daemonId);
  }, 0);
}

// The workspace a recovery pass for `gen` may read in, or null when it must not start:
// no workspace, a newer generation, or a stream whose timeline read was refused.
function recoveryWorkspace(agentId: string, gen: number): string | null {
  const wsId = workspaceState.current?.id;
  if (!wsId || agentStreamState.generationOf(agentId) !== gen) return null;
  if (!readsFromWorkspace(agentId, wsId)) return null;
  return agentStreamState.isTimelineReadDenied(agentId) ? null : wsId;
}

// A conversation bound to another workspace is never read through the selected one: a late
// frame of A while B is on screen would otherwise ask B's relay scope for A's rows.
function readsFromWorkspace(agentId: string, wsId: string): boolean {
  const owner = agentStreamState.workspaceForAgent(agentId);
  return owner === null || owner === wsId;
}

async function runTranscriptRecovery(
  client: TimelineFetchClient,
  agentId: string,
  from: string | null,
  gen: number,
  daemonId?: string,
): Promise<void> {
  const wsId = recoveryWorkspace(agentId, gen);
  if (!wsId) return;
  const running = recoveryRuns.get(agentId);
  if (running && running.gen === gen) {
    running.rerun = true;
    return;
  }
  const run = { gen, rerun: false };
  recoveryRuns.set(agentId, run);
  const stale = (): boolean =>
    workspaceState.current?.id !== wsId || agentStreamState.generationOf(agentId) !== gen;
  let cursor = from;
  agentStreamState.setTranscriptRecovery(agentId, "reading");
  try {
    // A pass from the tail over a screen that already holds durable rows asks only for
    // what came AFTER them (CYBORG-1177): a turn's end used to re-read the whole tail
    // page, up to 520 KB, to learn about a handful of rows.
    // An unfinished delta (page budget spent) falls through to the tail read below.
    const delta = from === null ? deltaCursor(agentId) : null;
    const settled = delta
      ? await deltaSettles(client, wsId, agentId, delta, daemonId, stale)
      : "unfinished";
    if (settled === "settled") {
      if (!stale()) readAgainIfTurnEndOwed(client, agentId, gen, daemonId);
      return;
    }
    if (settled === "cut") cursor = agentStreamState.headCutCursor(agentId);
    for (let read = 0; read < RECONCILE_PAGES_PER_PASS; read += 1) {
      const tailAfterTurn = cursor === null && turnIsOver(agentId);
      const debtRevision = agentStreamState.deltaDebtRevision(agentId);
      const page = await readRecoveryPage(client, wsId, agentId, cursor, daemonId);
      if (stale()) return;
      if (pageTakesAnotherLife(agentId, page)) {
        void adoptNewLife(client, agentId, daemonId);
        return;
      }
      const heldTop = agentStreamState.highestHeldSeq(agentId);
      agentStreamState.mergeDurableRows(agentId, page.items, rosterLifecycle(agentId));
      readBackUnderCutTail(client, agentId, page, cursor, heldTop);
      agentStreamState.noteTailRead(agentId, tailAfterTurn, debtRevision);
      if (page.continuesChain) {
        agentStreamState.adoptPagination(
          agentId,
          page.hasOlder,
          page.olderCursor,
          olderProof(page),
        );
      }
      const next = nextRecoveryStep(agentId, page, cursor);
      if (next === "done") {
        agentStreamState.noteTailCovered(agentId);
        return readAgainIfTurnEndOwed(client, agentId, gen, daemonId);
      }
      // The open cut holds the recovery stalled, and releases it once a page covers it. Both
      // stalls are reported with their cause: neither shows the reader a failed read.
      if (next === "held") {
        agentStreamState.invariants.report("recovery_stalled", {
          agentId,
          cause: "head_cut_no_cursor",
        });
        return agentStreamState.setTranscriptRecovery(agentId, "idle", null);
      }
      if (next === "stuck") {
        agentStreamState.invariants.report("recovery_stalled", { agentId, cause: "stuck" });
        return agentStreamState.setTranscriptRecovery(agentId, "stalled", cursor);
      }
      cursor = next;
      agentStreamState.setTranscriptRecovery(agentId, "pending", cursor);
    }
    const resume = cursor;
    setTimeout(() => {
      void runTranscriptRecovery(client, agentId, resume, gen, daemonId);
    }, 0);
  } catch (err) {
    if (stale()) return;
    const resume = cursor;
    onRecoveryPageFailed(agentId, err, resume, () => {
      void runTranscriptRecovery(client, agentId, resume, gen, daemonId);
    });
  } finally {
    finishRecoveryRun(client, agentId, run, daemonId);
  }
}

// Under a tail page the budget cut above the screen, the rows between are read back.
function readBackUnderCutTail(
  client: TimelineFetchClient,
  agentId: string,
  page: { items: ReadonlyArray<Record<string, unknown>>; budgetCut?: boolean },
  cursor: string | null,
  heldTop: number | undefined,
): void {
  const floor = oldestSeq(page.items);
  if (cursor !== null || page.budgetCut !== true || heldTop === undefined) return;
  if (floor !== undefined && floor > heldTop + 1) {
    void recoverTimelineRange(client, agentId, heldTop, floor);
  }
}

// A pass that reached the tail but not the end a turn named read from behind it (a lagging
// mirror): the end stays owed and the pass is read again, bounded (knowledge/124 T1).
function readAgainIfTurnEndOwed(
  client: TimelineFetchClient,
  agentId: string,
  gen: number,
  daemonId: string | undefined,
): void {
  if (agentStreamState.settleTurnEnd(agentId)) return;
  const retry = () => void runTranscriptRecovery(client, agentId, null, gen, daemonId);
  if (agentStreamState.armRecoveryRetry(agentId, MAX_RECOVERY_RETRIES, retry, "turnEndReadAgains"))
    return;
  // Once, when the budget runs out; the end stays owed for the next trigger.
  if (agentStreamState.turnEndReadAgains(agentId) !== MAX_RECOVERY_RETRIES + 1) return;
  agentStreamState.giveUpTurnEnd(agentId); // the deferred sweep runs once (B4)
  // Transient only with redial proof; a reachable daemon, or one gone without it, is a fault.
  const daemon = daemonId ?? workspaceState.agents.find((a) => a.agentId === agentId)?.daemonId;
  const daemonReconnecting = !!daemon && daemonState.isReconnecting(daemon, Date.now());
  reportUiFailure(
    "agents.turn_end_unread",
    new Error("no read reached the turn's end"),
    {
      ...agentStreamState.reportSubject(agentId, daemon),
      owedGap: agentStreamState.turnEndGap(agentId),
      daemonReconnecting,
    },
    { code: "turn_end_unread", failureKind: daemonReconnecting ? "transient" : "fault" },
  );
}

// The first row's seq of a page; undefined for an older daemon's rows (no seq), NaN for an
// empty page — it covers nothing, so it neither resolves nor places a cut.
function firstRowSeq(items: ReadonlyArray<Record<string, unknown>>): number | undefined {
  if (items.length === 0) return Number.NaN;
  const seq = items[0]?.seq;
  return typeof seq === "number" ? seq : undefined;
}

// After a recovery page merged: "done" (idle), the cursor to read next, "held" (a cut with
// nothing older to read: the open cut holds the recovery stalled) or "stuck" (the cursor
// did not move: the pass's own stall). A cut is never called whole for want of pages.
function nextRecoveryStep(
  agentId: string,
  page: Awaited<ReturnType<TimelineFetchClient["fetchAgentTimeline"]>>,
  cursor: string | null,
): string | "done" | "held" | "stuck" {
  if (pageHeadCut(page)) {
    agentStreamState.markHeadCut(agentId, firstRowSeq(page.items), page.olderCursor);
    return stepFrom(page.olderCursor, cursor);
  }
  agentStreamState.resolveHeadCut(agentId, firstRowSeq(page.items));
  if (!agentStreamState.headCutOpen(agentId)) {
    agentStreamState.setTranscriptRecovery(agentId, "idle");
    return "done";
  }
  return stepFrom(agentStreamState.headCutCursor(agentId), cursor);
}

function stepFrom(next: string | null, cursor: string | null): string | "held" | "stuck" {
  if (!next) return "held";
  return next === cursor ? "stuck" : next;
}

// The daemon roster's lifecycle for this agent: the mid-turn signal a suspended tab still
// trusts (it may have missed turn_started) — the same input the composer feeds isTurnActive.
function rosterLifecycle(agentId: string): string | null | undefined {
  return workspaceState.agents.find((a) => a.agentId === agentId)?.lifecycle;
}

// After a turn ends, when the app returns to the foreground, and on re-entering a chat
// mid-turn: what the screen shows is what the browser managed to apply; what the daemon
// holds is what was said. The durable rows settle the difference chunk by chunk —
// completing a cut or holed entry AND inserting a durable turn the suspended tab never
// applied at all (#115), touching nothing else (#3193 F10).

export async function reconcileAgentTranscript(
  client: TimelineFetchClient,
  agentId: string,
): Promise<void> {
  if (!workspaceState.current || !agentStreamState.isHydrated(agentId)) return;
  // A forward window is short of the tail on purpose: its last page reconciles (load-newer.ts).
  if (newerWindows.has(agentId)) return;
  // A hole a read-back could not fill is retried by every recovery trigger (the Retry
  // button, a turn's end, the foreground), not only by the next gap that happens to open.
  const gen = agentStreamState.generationOf(agentId);
  const lost = agentStreamState.lostRange(agentId);
  const scope = lost ? gapReadScope(agentId) : undefined;
  if (lost && scope && scope !== "reconcile") {
    await recoverTimelineRange(client, agentId, lost.after, lost.before);
    // A rewind or clear while the hole was read is a newer transcript: not this pass's.
    if (agentStreamState.generationOf(agentId) !== gen) return;
  }
  await runTranscriptRecovery(client, agentId, null, gen, agentStreamState.routeOf(agentId));
}

export const GAP_READ_MAX_ROWS = 1000;
const GAP_READ_RETRIES = 3;
export const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export function cursorEpoch(cursor: string | null): string | undefined {
  try {
    const epoch = cursor ? (JSON.parse(cursor) as { epoch?: unknown }).epoch : undefined;
    return typeof epoch === "string" ? epoch : undefined;
  } catch {
    // intentional: an unparseable cursor is an older daemon's; the caller falls back.
    return undefined;
  }
}

export function oldestSeq(items: ReadonlyArray<Record<string, unknown>>): number | undefined {
  const seqs = items.map((r) => r.seq).filter((n): n is number => Number.isSafeInteger(n));
  return seqs.length ? Math.min(...seqs) : undefined;
}

// Where a gap read-back may read from: nowhere for a conversation of another workspace or a
// refused stream; a whole-transcript reconcile when there is no cursor epoch to page by.
function gapReadScope(agentId: string): { wsId: string; epoch: string } | "reconcile" | null {
  const wsId = workspaceState.current?.id;
  if (wsId && !readsFromWorkspace(agentId, wsId)) return null;
  const epoch = screenCursorEpoch(agentId);
  if (!wsId || epoch === undefined) return "reconcile";
  return agentStreamState.isTimelineReadDenied(agentId) ? null : { wsId, epoch };
}

// Read back the committed rows strictly between `after` and `before`, one read-back per agent
// and generation at a time; what it cannot read is named on the stream (lostRange). A request
// a rewind, clear or workspace switch overtook while it waited reads nothing.
export async function recoverTimelineRange(
  client: TimelineFetchClient,
  agentId: string,
  after: number,
  before: number,
  wait: (ms: number) => Promise<void> = sleep,
): Promise<void> {
  const gen = agentStreamState.generationOf(agentId);
  const key = `${agentId}\t${gen}`;
  const running = gapReadQueues.get(key);
  if (running) {
    // The running read covers a hole only with the part it has not read yet (`remaining`).
    const covered = running.find((r) => {
      const span = r.remaining ?? r;
      return span.after <= after && span.before >= before;
    });
    if (covered) return covered.done;
    const request = gapReadRequest(after, before, workspaceState.current?.id);
    running.push(request);
    return request.done;
  }
  const queue = [gapReadRequest(after, before, workspaceState.current?.id)];
  gapReadQueues.set(key, queue);
  try {
    while (queue.length > 0) {
      const range = queue[0];
      const current =
        agentStreamState.generationOf(agentId) === gen && workspaceState.current?.id === range.wsId;
      const progress = (from: number, to: number) => (range.remaining = { after: from, before: to }); // prettier-ignore
      if (current) await readRangeBack(client, agentId, range.after, range.before, wait, progress);
      queue.shift();
      range.settle();
    }
  } finally {
    if (gapReadQueues.get(key) === queue) gapReadQueues.delete(key);
    for (const range of queue.splice(0)) range.settle();
  }
}

interface GapReadRequest {
  // Once it runs: the part of the range not read yet.
  remaining?: { after: number; before: number };
  after: number;
  before: number;
  wsId: string | undefined;
  done: Promise<void>;
  settle: () => void;
}
function gapReadRequest(after: number, before: number, wsId: string | undefined): GapReadRequest {
  let settle: () => void = () => {};
  const done = new Promise<void>((resolve) => {
    settle = resolve;
  });
  return { after, before, wsId, done, settle };
}
const gapReadQueues = conversationSync.collection(
  "gapReadQueues",
  new Map<string, GapReadRequest[]>(),
);

// At most 30,000 rows and 30 reads per read-back (a server answering a row at a time is
// bounded by the reads); past either, what is left is named as lost rather than read in
// one go. Past the reconnect bridge's CATCH_UP_GAP_MAX_ROWS on purpose: that bridge hands its
// larger holes to this read-back, after the tail is on screen (#3338).
const GAP_READBACK_MAX_ROWS = HOLE_KEEP_MAX_ROWS;
const GAP_READBACK_MAX_READS = 30;

type RangePage = { next: number } | { forward: true } | { problem: unknown } | "done";

// A merged text group of the page (coalescedBase) that cannot land: it reaches below `after`
// or over a row the screen holds (unmergedOverHeld would drop it and reopen its seqs).
function mergeOverHeld(
  agentId: string,
  items: ReadonlyArray<Record<string, unknown>>,
  after: number,
): boolean {
  const groups = new Map<number, number[]>();
  for (const r of items) {
    const g = r.coalescedBase;
    if (typeof g === "number" && typeof r.seq === "number") groups.set(g, [...(groups.get(g) ?? []), r.seq]); // prettier-ignore
  }
  const held = (q: number) => q <= after || agentStreamState.appliedAll(agentId, q, q);
  return [...groups.values()].some((seqs) => seqs.some(held));
}
type ForwardRangePage = { from: number } | { older: true } | { problem: unknown } | "done";

// The rows after `from`, up to `before`: no merged rows. `older`: the page is not contiguous.
async function readRangeForward(
  client: TimelineFetchClient,
  agentId: string,
  scope: { wsId: string; epoch: string; daemonId: string | undefined },
  from: number,
  before: number,
  stale: () => boolean,
  progress: (from: number, cursor: number) => void = () => {},
): Promise<ForwardRangePage> {
  try {
    const daemonId = scope.daemonId;
    const page = await client.fetchAgentTimeline(scope.wsId, agentId, {
      cursor: JSON.stringify({ epoch: scope.epoch, seq: from }),
      // No `life`: a read-back fills a hole of this screen, whose life may not be known yet.
      limit: Math.min(before - from - 1, GAP_READ_MAX_ROWS),
      ...(daemonId ? { daemonId } : {}),
    });
    if (stale()) return "done";
    if (pageTakesAnotherLife(agentId, page)) {
      void adoptNewLife(client, agentId);
      return "done";
    }
    const oldest = oldestSeq(page.items);
    if (oldest !== undefined && oldest > from + 1) return { older: true };
    const rows = page.items.filter((r) => typeof r.seq !== "number" || r.seq < before);
    const seqs = rows.map((r) => (typeof r.seq === "number" ? r.seq : from));
    const next = page.hasMore && page.nextCursor ? seqOfCursor(page.nextCursor) : undefined;
    const reached = Math.max(from, next ?? from, ...seqs);
    if (reached > from) progress(reached, before);
    agentStreamState.mergeDurableRows(agentId, rows, rosterLifecycle(agentId), {
      deliberate: true,
    });
    if (reached > from) return { from: reached };
    return { problem: new Error(`page made no progress past seq ${from} (${page.source ?? "daemon"})`) }; // prettier-ignore
  } catch (err) {
    // intentional: returned to readRangeBack, which retries it and then reports it.
    if (stale()) return "done";
    if (latchRefusedRead(agentId, err, "gap")) return "done";
    return { problem: err };
  }
}

async function readRangePage(
  client: TimelineFetchClient,
  agentId: string,
  scope: { wsId: string; epoch: string; daemonId: string | undefined },
  after: number,
  cursor: number,
  stale: () => boolean,
  progress: (from: number, cursor: number) => void = () => {},
): Promise<RangePage> {
  try {
    const daemonId = scope.daemonId;
    const page = await client.fetchAgentTimeline(scope.wsId, agentId, {
      cursor: JSON.stringify({ epoch: scope.epoch, seq: cursor }),
      direction: "older",
      limit: Math.min(cursor - after - 1, GAP_READ_MAX_ROWS),
      ...(daemonId ? { daemonId } : {}),
    });
    if (stale()) return "done";
    if (pageTakesAnotherLife(agentId, page)) {
      void adoptNewLife(client, agentId);
      return "done";
    }
    // Its merged text cannot be placed over the rows held: read the range forward instead.
    if (mergeOverHeld(agentId, page.items, after)) return { forward: true };
    const oldest = oldestSeq(page.items);
    if (oldest !== undefined && oldest < cursor) progress(after, oldest);
    agentStreamState.mergeDurableRows(agentId, page.items, rosterLifecycle(agentId), {
      deliberate: true,
    });
    if (oldest !== undefined && oldest < cursor) return { next: oldest };
    return {
      problem: new Error(`page made no progress below seq ${cursor} (${page.source ?? "daemon"})`),
    };
  } catch (err) {
    // intentional: returned to readRangeBack, which retries it and then reports it
    // (reportGapReadFailure). A refusal of a read the stream moved past is not latched.
    if (stale()) return "done";
    if (latchRefusedRead(agentId, err, "gap")) return "done";
    return { problem: err };
  }
}

async function readRangeBack(
  client: TimelineFetchClient,
  agentId: string,
  after: number,
  before: number,
  wait: (ms: number) => Promise<void>,
  progress: (from: number, cursor: number) => void = () => {},
): Promise<void> {
  const scope = gapReadScope(agentId);
  if (scope === "reconcile") return reconcileAgentTranscript(client, agentId);
  if (!scope) return;
  const stale = staleAfter(agentId, scope.wsId);
  // The whole read-back goes to one daemon: a route change invalidates it (noteRoute).
  const routed = { ...scope, daemonId: agentStreamState.routeOf(agentId) };
  const attempted = { after, before };
  // Older from `before` (`cursor` falls); forward from `after` (`from` climbs) once an older page
  // merges text over held rows; older for good once a forward page is not contiguous.
  let from = after;
  let cursor = before;
  let forward = false;
  let forwardLeft = false;
  let failures = 0;
  let reads = 0;
  while (cursor - from > 1 && !stale()) {
    if (
      before - cursor + (from - after) >= GAP_READBACK_MAX_ROWS ||
      reads >= GAP_READBACK_MAX_READS
    ) {
      // prettier-ignore
      agentStreamState.noteLostRange(agentId, from, cursor, attempted);
      return;
    }
    reads += 1;
    progress(from, cursor);
    let outcome: RangePage | ForwardRangePage = forward
      ? await readRangeForward(client, agentId, routed, from, cursor, stale, progress)
      : await readRangePage(client, agentId, routed, from, cursor, stale, progress);
    if (outcome === "done") return;
    if ("older" in outcome) {
      forward = false;
      forwardLeft = true;
      continue;
    }
    if ("forward" in outcome) {
      if (!forwardLeft) {
        forward = true;
        continue;
      }
      outcome = { problem: new Error(`merged rows over held rows below seq ${cursor}`) };
    }
    if ("from" in outcome || "next" in outcome) {
      if ("from" in outcome) from = outcome.from;
      else cursor = outcome.next;
      failures = 0;
      continue;
    }
    failures += 1;
    if (failures > GAP_READ_RETRIES) {
      reportGapReadFailure(agentId, from, cursor, outcome.problem);
      agentStreamState.noteLostRange(agentId, from, cursor, attempted);
      return;
    }
    await wait(1_000 * 2 ** (failures - 1));
  }
  if (!stale()) agentStreamState.noteRangeRecovered(agentId, after, before);
}

function reportGapReadFailure(agentId: string, after: number, before: number, problem: unknown) {
  reportUiFailure("agents.recoverTimelineRange", problem, {
    agentId,
    gapAfter: after,
    gapBefore: before,
  });
}

export function reportGapUnreadable(
  agentId: string,
  held: number,
  cursor: number,
  problem: unknown,
): void {
  reportUiFailure("agents.readCatchUpGap", problem, {
    agentId,
    gapAfter: held,
    gapBefore: cursor,
  });
}

// Whether a page begins inside a reply: the server's `headPartial`, never inferred from
// older pages existing (#3239); an older daemon that does not say is read by shape.
export function pageHeadCut(
  page: Pick<
    Awaited<ReturnType<TimelineFetchClient["fetchAgentTimeline"]>>,
    "items" | "hasOlder" | "headPartial"
  >,
): boolean {
  if (page.headPartial !== undefined) return page.headPartial;
  return page.hasOlder && pageHeadMayBePartial(page.items, undefined);
}

// Whether a served page may begin inside a reply. A server that knows says so
// (`headPartial`); one that does not (an older daemon) is read by shape: a page that
// opens on an assistant chunk with older history behind it may be one.
export function pageHeadMayBePartial(
  items: ReadonlyArray<Record<string, unknown>>,
  headPartial: boolean | undefined,
): boolean {
  if (headPartial === true) return true;
  if (headPartial === false) return false;
  return items[0]?.type === "assistant_message";
}

// Scroll-up lazy-load: fetch the page of history OLDER than what's currently loaded
// and prepend it (the daemon pages backward from `olderCursor`). Re-entry-guarded;
// a no-op when nothing older remains.
// A reply is stored a row per streamed chunk, so one page of rows can hold a single turn:
// after a reload the conversation read as its last answer. Page back to a readable depth.
export const RECENT_PROMPTS = 10;
// Rows per scroll-up page the conversation view asks for: half the server's default, so one
// frame of tool-heavy rows stays well under the 1.5 MB page cap and a pong is not queued
// behind it. The byte cap itself is server-side (TIMELINE_PAGE_BYTE_BUDGET).
export const OLDER_PAGE_ROWS = 50;
const RECENT_PAGES = 6;
const RECENT_PAGE_ROWS = 500;
// Bytes one page-back pass may read (CYBORG-1207): heavy rows made 6 pages 7 MB.
export const RECENT_PAGE_BACK_BYTES = 600_000;
const RECOVERY_WAIT_MS = 50;
const RECOVERY_WAIT_MAX_MS = 10_000;

// A reply cut at the page's head is being read back by the transcript recovery; paging
// alongside it reads the same rows twice. False when it is still running at the deadline.
async function afterTranscriptRecovery(agentId: string): Promise<boolean> {
  for (let waited = 0; waited < RECOVERY_WAIT_MAX_MS; waited += RECOVERY_WAIT_MS) {
    if (!agentStreamState.isTranscriptRecoveryPending(agentId)) return true;
    await new Promise((resolve) => setTimeout(resolve, RECOVERY_WAIT_MS));
  }
  return !agentStreamState.isTranscriptRecoveryPending(agentId);
}

export async function loadRecentTurns(
  client: TimelineFetchClient,
  agentId: string,
  daemonId?: string,
): Promise<void> {
  const stale = workspaceState.current
    ? staleAfter(agentId, workspaceState.current.id)
    : () => true;
  let bytes = 0;
  const counting: TimelineFetchClient = {
    fetchAgentTimeline: async (...args) => {
      const page = await client.fetchAgentTimeline(...args);
      bytes += JSON.stringify(page.items).length;
      return page;
    },
  };
  for (let page = 0; page < RECENT_PAGES; page += 1) {
    if (bytes >= RECENT_PAGE_BACK_BYTES) return;
    if (!(await afterTranscriptRecovery(agentId))) return;
    // Durable prompts only: a local send still awaiting its row is not history.
    const prompts = agentStreamState
      .getEntries(agentId)
      .filter((e) => e.kind === "user_message" && seqsOf(e).length > 0).length;
    if (stale() || prompts >= RECENT_PROMPTS || !agentStreamState.hasOlder(agentId)) return;
    const cursor = agentStreamState.olderCursor(agentId);
    await loadOlderAgentTimeline(counting, agentId, daemonId, RECENT_PAGE_ROWS);
    if (agentStreamState.olderCursor(agentId) === cursor) return;
  }
}

export async function loadOlderAgentTimeline(
  client: TimelineFetchClient,
  agentId: string,
  // `daemonId` (audit viewer, #994): the KNOWN owning daemon, so scroll-up pages route to the
  // daemon the hydrate used, not daemon_agents (null for ephemeral / idle cross-daemon sessions).
  daemonId?: string,
  limit?: number,
  untilTs?: number, // the read reaches the first row after it (the open's unread cursor)
): Promise<void> {
  if (!workspaceState.current) return;
  if (!agentStreamState.hasOlder(agentId) || agentStreamState.isLoadingOlder(agentId)) return;
  if (agentStreamState.olderLoadHeldOff(agentId)) return;
  if (agentStreamState.isTimelineReadDenied(agentId)) return;
  const cursor = agentStreamState.olderCursor(agentId);
  if (!cursor) return;
  const wsId = workspaceState.current.id;
  // The life this read belongs to. A clear()/rewind while it is out makes every effect
  // of it — the page, the failure, the retry — someone else's (#3248 C3); `loadingOlder`
  // is the stream's, see releaseOlderLoad.
  const stale = staleAfter(agentId, wsId);
  agentStreamState.setLoadingOlder(agentId, true);
  const marked = agentStreamState.streams.get(agentId);
  try {
    const page = await client.fetchAgentTimeline(wsId, agentId, {
      cursor,
      direction: "older",
      ...(daemonId ? { daemonId } : {}),
      ...(limit ? { limit } : {}),
      ...(untilTs !== undefined ? { untilTs } : {}),
    });
    const { items, source, headPartial } = page;
    const { hasOlder, olderCursor } = olderPagination(page);
    if (stale()) return;
    if (pageTakesAnotherLife(agentId, page)) {
      void adoptNewLife(client, agentId, daemonId);
      return;
    }
    agentStreamState.prependTimeline(agentId, items, {
      hasOlder,
      olderCursor,
      source,
      olderProven: olderProof(page),
    });
    agentStreamState.noteOlderLoadSucceeded(agentId);
    // An older page can open inside a reply too (#3239). Only a server that says so: an
    // older daemon never snaps older pages, and the next scroll-up reads that head anyway.
    if (headPartial === true) {
      pageBackIfHeadPartial(
        client,
        agentId,
        { items, hasOlder, headPartial, olderCursor },
        true,
        daemonId,
      );
    }
  } catch (err) {
    if (stale()) return;
    // Refused: no backoff retry and no "Couldn't load older history" for a wall.
    if (latchRefusedRead(agentId, err, "older")) return;
    // Hold the scroll-up auto-trigger off with a growing backoff so a permanent error
    // ("Agent not found in workspace" after a daemon restart) cannot re-fire this fetch
    // every frame — but keep hasOlder/olderCursor: a failed page is not the end of the
    // history, and clearing them rendered a 47k-row session as fully loaded (#3244).
    const first = agentStreamState.noteOlderLoadFailed(agentId);
    // The store fires the loader again once the backoff elapses; loadOlderAgentTimeline
    // settles its own errors, so the retried call never rejects (#3248 R4). Only while
    // this read's workspace and life still hold (#3373 F2).
    // The SAME read: the open's read to the first unread retries as that read, not a plain page.
    agentStreamState.armOlderLoadRetry(agentId, () => {
      if (!stale()) void loadOlderAgentTimeline(client, agentId, daemonId, limit, untilTs);
    });
    reportUiFailure("agents.loadOlderAgentTimeline", err, { workspaceId: wsId, agentId });
    if (first) toast.error("Couldn't load older history");
  } finally {
    releaseOlderLoad(agentId, marked);
  }
}

// "Is the world this read was issued for gone?" — the workspace switched, or the
// stream was cleared or rewound (its generation moved on). Captured before the await,
// asked after every one.
export function staleAfter(agentId: string, wsId: string | undefined): () => boolean {
  const gen = agentStreamState.generationOf(agentId);
  return () =>
    workspaceState.current?.id !== wsId || agentStreamState.generationOf(agentId) !== gen;
}

export async function fetchAgentCommands(
  client: {
    listAgentCommands(workspaceId: string, agentId: string): Promise<AgentSlashCommand[]>;
  },
  agentId: string,
): Promise<AgentSlashCommand[]> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  return client.listAgentCommands(workspaceState.current.id, agentId);
}

export async function fetchSessions(client: {
  listArchivedSessions(
    workspaceId: string,
    opts?: { limit?: number; cursor?: string },
  ): Promise<{ sessions: ArchivedSession[]; nextCursor: string | null }>;
}): Promise<void> {
  if (!workspaceState.current) return;
  const wsId = workspaceState.current.id;
  sessionState.loading = true;
  // Reset paging state for the new fetch so a stale "load more" in flight can't
  // leave the affordance stuck.
  sessionState.loadingMore = false;
  try {
    const page = await client.listArchivedSessions(wsId, {
      limit: ARCHIVED_SESSIONS_PAGE_SIZE,
    });
    // Workspace-switch race: a switch mid-fetch must not overwrite the new
    // workspace's list with this (now stale) response.
    if (workspaceState.current?.id !== wsId) return;
    sessionState.list = page.sessions;
    sessionState.nextCursor = page.nextCursor;
    sessionState.fetched = true;
  } catch {
    if (workspaceState.current?.id !== wsId) return;
    // Fail CLOSED, same contract as the app-barrel fetchSessions this duplicates
    // (state/app.svelte.ts) — both write the SAME `sessionState` singleton, so a
    // blanking catch here would resurrect the data loss that one just fixed.
    // Keep the list; recovery is the next successful fetch or live event.
  } finally {
    sessionState.loading = false;
  }
}
